import { parsePhoneNumberFromString, getCountryCallingCode, isSupportedCountry, getExampleNumber } from 'libphonenumber-js';
import exemples from 'libphonenumber-js/examples.mobile.json';

// src/localeAffichage.ts
var DEFAULT_LOCALE = "fr-FR";
var etatLocale = globalThis[Symbol.for("donoor.utils.locale")] ??= { courante: DEFAULT_LOCALE, fournisseur: null };
function definirLocaleAffichage(locale) {
  etatLocale.courante = locale;
}
function definirFournisseurLocale(fournisseur) {
  etatLocale.fournisseur = fournisseur;
}
function localeAffichage() {
  return etatLocale.fournisseur?.() ?? etatLocale.courante;
}

// src/i18nMots.ts
var LANGUES_SUPPORTEES = ["fr", "en", "de", "es", "it", "nl"];
function langueDepuisLocale(locale) {
  const code = (locale ?? "").slice(0, 2).toLowerCase();
  return LANGUES_SUPPORTEES.includes(code) ? code : "fr";
}
var LOCALE_INTL_PAR_LANGUE = {
  fr: "fr-FR",
  en: "en-GB",
  de: "de-DE",
  es: "es-ES",
  it: "it-IT",
  nl: "nl-NL"
};
function plFr(n, singulier, pluriel) {
  return Math.abs(n) < 2 ? singulier : pluriel;
}
function plStd(n, singulier, pluriel) {
  return n === 1 ? singulier : pluriel;
}
var MOTS_RELATIFS = {
  fr: {
    aLInstant: "\xC0 l'instant",
    ilYAMinute: (n) => `Il y a ${n} ${plFr(n, "minute", "minutes")}`,
    ilYAHeure: (n) => `Il y a ${n} ${plFr(n, "heure", "heures")}`,
    ilYAJour: (n) => `Il y a ${n} ${plFr(n, "jour", "jours")}`,
    ilYASemaine: (n) => `Il y a ${n} ${plFr(n, "semaine", "semaines")}`,
    ilYAMois: (n) => `Il y a ${n} mois`,
    ilYAAn: (n) => `Il y a ${n} ${plFr(n, "an", "ans")}`,
    ilYAMinuteCompact: (n) => `Il y a ${n} min`,
    ilYAHeureCompact: (n) => `Il y a ${n}h`,
    ilYAJourCompact: (n) => `Il y a ${n}j`,
    ilYASemaineCompact: (n) => `Il y a ${n} sem.`,
    ilYAMoisCompact: (n) => `Il y a ${n} mois`,
    ilYAAnCompact: (n) => `Il y a ${n} ${plFr(n, "an", "ans")}`,
    hierA: (h) => `Hier \xE0 ${h}`,
    avantHierA: (h) => `Avant-hier \xE0 ${h}`,
    aujourdhuiAbrege: (h) => `aujd ${h}`,
    hierAbrege: (h) => `hier ${h}`,
    dateEtHeure: (date, h) => `${date} \xE0 ${h}`
  },
  en: {
    aLInstant: "Just now",
    ilYAMinute: (n) => `${n} ${plStd(n, "minute", "minutes")} ago`,
    ilYAHeure: (n) => `${n} ${plStd(n, "hour", "hours")} ago`,
    ilYAJour: (n) => `${n} ${plStd(n, "day", "days")} ago`,
    ilYASemaine: (n) => `${n} ${plStd(n, "week", "weeks")} ago`,
    ilYAMois: (n) => `${n} ${plStd(n, "month", "months")} ago`,
    ilYAAn: (n) => `${n} ${plStd(n, "year", "years")} ago`,
    ilYAMinuteCompact: (n) => `${n} min ago`,
    ilYAHeureCompact: (n) => `${n}h ago`,
    ilYAJourCompact: (n) => `${n}d ago`,
    ilYASemaineCompact: (n) => `${n} wk ago`,
    ilYAMoisCompact: (n) => `${n} mo ago`,
    ilYAAnCompact: (n) => `${n}y ago`,
    hierA: (h) => `Yesterday at ${h}`,
    avantHierA: (h) => `The day before yesterday at ${h}`,
    aujourdhuiAbrege: (h) => `today ${h}`,
    hierAbrege: (h) => `yesterday ${h}`,
    dateEtHeure: (date, h) => `${date} at ${h}`
  },
  de: {
    aLInstant: "Gerade eben",
    // "vor" régit le datif : Minuten/Stunden/Tagen/Wochen/Monaten/Jahren au pluriel.
    ilYAMinute: (n) => `Vor ${n} ${plStd(n, "Minute", "Minuten")}`,
    ilYAHeure: (n) => `Vor ${n} ${plStd(n, "Stunde", "Stunden")}`,
    ilYAJour: (n) => `Vor ${n} ${plStd(n, "Tag", "Tagen")}`,
    ilYASemaine: (n) => `Vor ${n} ${plStd(n, "Woche", "Wochen")}`,
    ilYAMois: (n) => `Vor ${n} ${plStd(n, "Monat", "Monaten")}`,
    ilYAAn: (n) => `Vor ${n} ${plStd(n, "Jahr", "Jahren")}`,
    ilYAMinuteCompact: (n) => `Vor ${n} Min.`,
    ilYAHeureCompact: (n) => `Vor ${n} Std.`,
    ilYAJourCompact: (n) => `Vor ${n} T.`,
    ilYASemaineCompact: (n) => `Vor ${n} Wo.`,
    ilYAMoisCompact: (n) => `Vor ${n} Mon.`,
    ilYAAnCompact: (n) => `Vor ${n} J.`,
    hierA: (h) => `Gestern um ${h}`,
    avantHierA: (h) => `Vorgestern um ${h}`,
    aujourdhuiAbrege: (h) => `heute ${h}`,
    hierAbrege: (h) => `gestern ${h}`,
    dateEtHeure: (date, h) => `${date} um ${h}`
  },
  es: {
    aLInstant: "Ahora mismo",
    ilYAMinute: (n) => `Hace ${n} ${plStd(n, "minuto", "minutos")}`,
    ilYAHeure: (n) => `Hace ${n} ${plStd(n, "hora", "horas")}`,
    ilYAJour: (n) => `Hace ${n} ${plStd(n, "d\xEDa", "d\xEDas")}`,
    ilYASemaine: (n) => `Hace ${n} ${plStd(n, "semana", "semanas")}`,
    ilYAMois: (n) => `Hace ${n} ${plStd(n, "mes", "meses")}`,
    ilYAAn: (n) => `Hace ${n} ${plStd(n, "a\xF1o", "a\xF1os")}`,
    ilYAMinuteCompact: (n) => `Hace ${n} min`,
    ilYAHeureCompact: (n) => `Hace ${n} h`,
    ilYAJourCompact: (n) => `Hace ${n} d`,
    ilYASemaineCompact: (n) => `Hace ${n} sem.`,
    ilYAMoisCompact: (n) => `Hace ${n} ${plStd(n, "mes", "meses")}`,
    ilYAAnCompact: (n) => `Hace ${n} ${plStd(n, "a\xF1o", "a\xF1os")}`,
    hierA: (h) => `Ayer a las ${h}`,
    avantHierA: (h) => `Anteayer a las ${h}`,
    aujourdhuiAbrege: (h) => `hoy ${h}`,
    hierAbrege: (h) => `ayer ${h}`,
    dateEtHeure: (date, h) => `${date} a las ${h}`
  },
  it: {
    aLInstant: "Proprio ora",
    ilYAMinute: (n) => `${n} ${plStd(n, "minuto", "minuti")} fa`,
    ilYAHeure: (n) => `${n} ${plStd(n, "ora", "ore")} fa`,
    ilYAJour: (n) => `${n} ${plStd(n, "giorno", "giorni")} fa`,
    ilYASemaine: (n) => `${n} ${plStd(n, "settimana", "settimane")} fa`,
    ilYAMois: (n) => `${n} ${plStd(n, "mese", "mesi")} fa`,
    ilYAAn: (n) => `${n} ${plStd(n, "anno", "anni")} fa`,
    ilYAMinuteCompact: (n) => `${n} min fa`,
    ilYAHeureCompact: (n) => `${n} h fa`,
    ilYAJourCompact: (n) => `${n}g fa`,
    ilYASemaineCompact: (n) => `${n} sett. fa`,
    ilYAMoisCompact: (n) => `${n} ${plStd(n, "mese", "mesi")} fa`,
    ilYAAnCompact: (n) => `${n} ${plStd(n, "anno", "anni")} fa`,
    hierA: (h) => `Ieri alle ${h}`,
    avantHierA: (h) => `L'altro ieri alle ${h}`,
    aujourdhuiAbrege: (h) => `oggi ${h}`,
    hierAbrege: (h) => `ieri ${h}`,
    dateEtHeure: (date, h) => `${date} alle ${h}`
  },
  nl: {
    aLInstant: "Zojuist",
    ilYAMinute: (n) => `${n} ${plStd(n, "minuut", "minuten")} geleden`,
    ilYAHeure: (n) => `${n} uur geleden`,
    // "uur" invariable au pluriel.
    ilYAJour: (n) => `${n} ${plStd(n, "dag", "dagen")} geleden`,
    ilYASemaine: (n) => `${n} ${plStd(n, "week", "weken")} geleden`,
    ilYAMois: (n) => `${n} ${plStd(n, "maand", "maanden")} geleden`,
    ilYAAn: (n) => `${n} jaar geleden`,
    // "jaar" invariable au pluriel.
    ilYAMinuteCompact: (n) => `${n} min geleden`,
    ilYAHeureCompact: (n) => `${n} u geleden`,
    ilYAJourCompact: (n) => `${n} d geleden`,
    ilYASemaineCompact: (n) => `${n} wk geleden`,
    ilYAMoisCompact: (n) => `${n} mnd geleden`,
    ilYAAnCompact: (n) => `${n} jr geleden`,
    hierA: (h) => `Gisteren om ${h}`,
    avantHierA: (h) => `Eergisteren om ${h}`,
    aujourdhuiAbrege: (h) => `vandaag ${h}`,
    hierAbrege: (h) => `gisteren ${h}`,
    dateEtHeure: (date, h) => `${date} om ${h}`
  }
};

// src/dates.ts
var ORG_TIMEZONE = "Europe/Paris";
var PARTS_FORMATTERS = /* @__PURE__ */ new Map();
function formatterParties(fuseau) {
  let f2 = PARTS_FORMATTERS.get(fuseau);
  if (!f2) {
    f2 = new Intl.DateTimeFormat("en-US", {
      timeZone: fuseau,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
    PARTS_FORMATTERS.set(fuseau, f2);
  }
  return f2;
}
function partsInZone(date, fuseau = ORG_TIMEZONE) {
  const map = {};
  for (const p of formatterParties(fuseau).formatToParts(date)) {
    if (p.type !== "literal") map[p.type] = p.value;
  }
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second)
  };
}
function zoneOffsetMinutes(date, fuseau = ORG_TIMEZONE) {
  const p = partsInZone(date, fuseau);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - date.getTime()) / 6e4);
}
function zonedWallTimeToUtc(year, month, day, hour, minute, second, ms, fuseau = ORG_TIMEZONE) {
  const naiveUtc = Date.UTC(year, month - 1, day, hour, minute, second, ms);
  let offset = zoneOffsetMinutes(new Date(naiveUtc), fuseau);
  let candidate = naiveUtc - offset * 6e4;
  const offset2 = zoneOffsetMinutes(new Date(candidate), fuseau);
  if (offset2 !== offset) {
    offset = offset2;
    candidate = naiveUtc - offset * 6e4;
  }
  return new Date(candidate);
}
function parisDayStartToUtc(year, month, day, fuseau = ORG_TIMEZONE) {
  return zonedWallTimeToUtc(year, month, day, 0, 0, 0, 0, fuseau);
}
function parisDayEndToUtc(year, month, day, fuseau = ORG_TIMEZONE) {
  return zonedWallTimeToUtc(year, month, day, 23, 59, 59, 999, fuseau);
}
function instantDepuisHeureLocale(jour, heure = "00:00", fuseau = ORG_TIMEZONE) {
  const [y, m, d] = jour.split("-").map(Number);
  const [h, min] = heure.split(":").map(Number);
  return zonedWallTimeToUtc(y, m, d, h ?? 0, min ?? 0, 0, 0, fuseau);
}
function heureLocaleDepuisInstant(date, fuseau = ORG_TIMEZONE) {
  const p = partsInZone(parseDbInstant(date), fuseau);
  const pad = (n) => String(n).padStart(2, "0");
  return { jour: `${p.year}-${pad(p.month)}-${pad(p.day)}`, heure: `${pad(p.hour)}:${pad(p.minute)}` };
}
function toDayKey(date, fuseau = ORG_TIMEZONE) {
  const d = parseDbInstant(date);
  const p = partsInZone(d, fuseau);
  const mm = String(p.month).padStart(2, "0");
  const dd = String(p.day).padStart(2, "0");
  return `${p.year}-${mm}-${dd}`;
}
function toDayBounds(date, fuseau = ORG_TIMEZONE) {
  const d = parseDbInstant(date);
  const p = partsInZone(d, fuseau);
  return {
    from: parisDayStartToUtc(p.year, p.month, p.day, fuseau).toISOString(),
    to: parisDayEndToUtc(p.year, p.month, p.day, fuseau).toISOString()
  };
}
function periodBounds(period, ref = /* @__PURE__ */ new Date(), fuseau = ORG_TIMEZONE) {
  const refDate = typeof ref === "string" ? new Date(ref) : ref;
  const { year, month, day } = partsInZone(refDate, fuseau);
  switch (period) {
    case "day":
      return {
        from: parisDayStartToUtc(year, month, day, fuseau).toISOString(),
        to: parisDayEndToUtc(year, month, day, fuseau).toISOString()
      };
    case "week": {
      const dow = parisWeekdayMondayZero(year, month, day);
      const monday = addParisDays(year, month, day, -dow);
      const sunday = addParisDays(monday.year, monday.month, monday.day, 6);
      return {
        from: parisDayStartToUtc(monday.year, monday.month, monday.day, fuseau).toISOString(),
        to: parisDayEndToUtc(sunday.year, sunday.month, sunday.day, fuseau).toISOString()
      };
    }
    case "month": {
      const lastDay = daysInMonth(year, month);
      return {
        from: parisDayStartToUtc(year, month, 1, fuseau).toISOString(),
        to: parisDayEndToUtc(year, month, lastDay, fuseau).toISOString()
      };
    }
    case "quarter": {
      const qStartMonth = Math.floor((month - 1) / 3) * 3 + 1;
      const qEndMonth = qStartMonth + 2;
      const lastDay = daysInMonth(year, qEndMonth);
      return {
        from: parisDayStartToUtc(year, qStartMonth, 1, fuseau).toISOString(),
        to: parisDayEndToUtc(year, qEndMonth, lastDay, fuseau).toISOString()
      };
    }
    case "year":
      return {
        from: parisDayStartToUtc(year, 1, 1, fuseau).toISOString(),
        to: parisDayEndToUtc(year, 12, 31, fuseau).toISOString()
      };
    default: {
      throw new Error(`Unknown period: ${String(period)}`);
    }
  }
}
function hasExplicitTimezone(value) {
  if (!value.includes("T") && !value.includes(" ")) return true;
  if (/[zZ]$/.test(value)) return true;
  return /\d{2}:\d{2}(:\d{2})?(\.\d+)?\s*[+-]\d{2}(:?\d{2})?$/.test(value);
}
function parseDbInstant(date) {
  if (typeof date !== "string") return date;
  const trimmed = date.trim();
  if (!hasExplicitTimezone(trimmed)) {
    return /* @__PURE__ */ new Date(trimmed.replace(" ", "T") + "Z");
  }
  return new Date(trimmed);
}
function formatInParis(date, opts = {}, locale = localeAffichage(), fuseau = ORG_TIMEZONE) {
  const d = parseDbInstant(date);
  return new Intl.DateTimeFormat(locale, {
    ...opts,
    timeZone: fuseau
  }).format(d);
}
function formatInZone(date, fuseau, opts = {}, locale = localeAffichage()) {
  return formatInParis(date, opts, locale, fuseau || ORG_TIMEZONE);
}
function formatDateTimeInParis(date, fuseau = ORG_TIMEZONE) {
  if (!date) return "\u2014";
  const d = parseDbInstant(date);
  if (Number.isNaN(d.getTime())) return "\u2014";
  const parts = new Intl.DateTimeFormat("fr-FR", {
    timeZone: fuseau,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("day")}/${get("month")}/${get("year")} ${get("hour")}:${get("minute")}`;
}
function formatDateAHeureInParis(date, fuseau = ORG_TIMEZONE) {
  const s = formatDateTimeInParis(date, fuseau);
  return s === "\u2014" ? s : s.replace(" ", " \xE0 ");
}
function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
function parisWeekdayMondayZero(year, month, day) {
  const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return (dow + 6) % 7;
}
function addParisDays(year, month, day, delta) {
  const d = new Date(Date.UTC(year, month - 1, day + delta));
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate()
  };
}
function formatSmartDateTimeInParis(date, now = /* @__PURE__ */ new Date(), fuseau = ORG_TIMEZONE, langue = langueDepuisLocale(localeAffichage())) {
  if (!date) return "\u2014";
  const d = parseDbInstant(date);
  if (Number.isNaN(d.getTime())) return "\u2014";
  const mots2 = MOTS_RELATIFS[langue];
  const time = new Intl.DateTimeFormat("fr-FR", {
    timeZone: fuseau,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(d);
  const key = toDayKey(d, fuseau);
  if (key === toDayKey(now, fuseau)) return time;
  if (key === toDayKey(new Date(now.getTime() - 864e5), fuseau)) {
    return mots2.hierA(time);
  }
  if (key === toDayKey(new Date(now.getTime() - 2 * 864e5), fuseau)) {
    return mots2.avantHierA(time);
  }
  const [year = "", month = "", day = ""] = key.split("-");
  return mots2.dateEtHeure(`${day}/${month}/${year}`, time);
}
function formatSmartDateTimeNumeriqueInParis(date, now = /* @__PURE__ */ new Date(), fuseau = ORG_TIMEZONE, langue = langueDepuisLocale(localeAffichage())) {
  if (!date) return "\u2014";
  const d = parseDbInstant(date);
  if (Number.isNaN(d.getTime())) return "\u2014";
  const mots2 = MOTS_RELATIFS[langue];
  const time = new Intl.DateTimeFormat("fr-FR", {
    timeZone: fuseau,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(d);
  const key = toDayKey(d, fuseau);
  if (key === toDayKey(now, fuseau)) return time;
  if (key === toDayKey(new Date(now.getTime() - 864e5), fuseau)) {
    return mots2.hierA(time);
  }
  const [year = "", month = "", day = ""] = key.split("-");
  return mots2.dateEtHeure(`${day}/${month}/${year}`, time);
}
function formatLastAccessInParis(date, now = /* @__PURE__ */ new Date(), fuseau = ORG_TIMEZONE, langue = langueDepuisLocale(localeAffichage())) {
  if (!date) return "\u2014";
  const d = parseDbInstant(date);
  if (Number.isNaN(d.getTime())) return "\u2014";
  const mots2 = MOTS_RELATIFS[langue];
  const time = new Intl.DateTimeFormat("fr-FR", {
    timeZone: fuseau,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(d).replace(":", "h");
  const key = toDayKey(d, fuseau);
  if (key === toDayKey(now, fuseau)) return mots2.aujourdhuiAbrege(time);
  if (key === toDayKey(new Date(now.getTime() - 864e5), fuseau)) {
    return mots2.hierAbrege(time);
  }
  const day = new Intl.DateTimeFormat(LOCALE_INTL_PAR_LANGUE[langue], {
    timeZone: fuseau,
    day: "2-digit",
    month: "short"
  }).format(d);
  return mots2.dateEtHeure(day, time);
}
function isMultiDayEvent(startDate, endDate) {
  if (!startDate || !endDate) return false;
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const DAY_MS = 24 * 60 * 60 * 1e3;
  return Number.isFinite(start) && Number.isFinite(end) && end - start >= DAY_MS;
}

// src/format.ts
var MONTANT_MASQUE = "****";
var DEFAULT_CURRENCY = "EUR";
var SUPPORTED_CURRENCIES = ["EUR", "USD", "GBP", "MAD"];
function formatCurrency(amount, options) {
  const numeric = typeof amount === "string" ? Number(amount.trim()) : amount;
  const value = typeof numeric === "number" && Number.isFinite(numeric) ? numeric : 0;
  const currency = options?.currency ?? DEFAULT_CURRENCY;
  const decimals = options?.decimals ?? 2;
  const withSymbol = options?.withSymbol ?? true;
  const locale = options?.locale ?? localeAffichage();
  const isWhole = Number.isInteger(value);
  const minimumFractionDigits = options?.fixedDecimals || !isWhole ? decimals : 0;
  const numberFormatOptions = {
    minimumFractionDigits,
    maximumFractionDigits: decimals
  };
  if (withSymbol) {
    numberFormatOptions.style = "currency";
    numberFormatOptions.currency = currency;
  }
  return new Intl.NumberFormat(locale, numberFormatOptions).format(value);
}
function formatCurrencyParts(amount, options) {
  const value = typeof amount === "number" && Number.isFinite(amount) ? amount : 0;
  const decimals = options?.decimals ?? 2;
  const minimumFractionDigits = options?.fixedDecimals || !Number.isInteger(value) ? decimals : 0;
  return new Intl.NumberFormat(options?.locale ?? localeAffichage(), {
    style: "currency",
    currency: options?.currency ?? DEFAULT_CURRENCY,
    minimumFractionDigits,
    maximumFractionDigits: decimals
  }).formatToParts(value);
}
function formatFileSize(octets, options) {
  if (!octets) return "";
  const n = typeof octets === "string" ? parseInt(octets, 10) : octets;
  if (!Number.isFinite(n)) return "";
  const [valeur, unite] = n < 1024 ? [n, "byte"] : n < 1024 * 1024 ? [n / 1024, "kilobyte"] : [n / 1024 / 1024, "megabyte"];
  return new Intl.NumberFormat(options?.locale ?? localeAffichage(), {
    style: "unit",
    unit: unite,
    unitDisplay: "short",
    maximumFractionDigits: 1
  }).format(valeur);
}
function formatCurrencyFromCents(cents, options) {
  if (cents == null) return MONTANT_MASQUE;
  const numeric = typeof cents === "string" ? Number(cents.trim()) : cents;
  const amount = typeof numeric === "number" && Number.isFinite(numeric) ? numeric / 100 : 0;
  return formatCurrency(amount, options);
}
function formatPercent(raw, options) {
  if (raw === null || raw === void 0 || raw === "") {
    return options?.withSymbol === false ? "0" : "0 %";
  }
  const n = typeof raw === "number" ? raw : Number.parseFloat(String(raw));
  if (!Number.isFinite(n)) {
    return options?.withSymbol === false ? "0" : "0 %";
  }
  const decimals = options?.decimals ?? 2;
  const stripZeros = options?.stripTrailingZeros ?? true;
  const locale = options?.locale ?? localeAffichage();
  const withSymbol = options?.withSymbol ?? true;
  let formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  }).format(n);
  if (stripZeros) {
    formatted = formatted.replace(/,0+$/, "").replace(/,(\d*?)0+$/, ",$1");
  }
  return withSymbol ? `${formatted} %` : formatted;
}
function formatDate(date, options) {
  if (date === null || date === void 0 || date === "") return "";
  const d = typeof date === "string" ? parseDbInstant(date) : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const format = options?.format ?? "short";
  const locale = options?.locale ?? localeAffichage();
  const timeZone = options?.timeZone ?? ORG_TIMEZONE;
  switch (format) {
    case "iso":
      return d.toISOString();
    case "short":
      return new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone
      }).format(d);
    case "compact":
      return new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        timeZone
      }).format(d);
    case "medium":
      return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone
      }).format(d);
    case "long":
      return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone
      }).format(d);
    case "time":
      return new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
        timeZone
      }).format(d);
    case "day-month":
      return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "long",
        timeZone
      }).format(d);
    case "weekday":
      return new Intl.DateTimeFormat(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        timeZone
      }).format(d);
    case "weekday-long":
      return new Intl.DateTimeFormat(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone
      }).format(d);
    case "month-year":
      return new Intl.DateTimeFormat(locale, {
        month: "long",
        year: "numeric",
        timeZone
      }).format(d);
  }
}
function formatRelativeTime(date, options) {
  const d = typeof date === "string" ? parseDbInstant(date) : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const now = options?.now instanceof Date ? options.now.getTime() : typeof options?.now === "number" ? options.now : (/* @__PURE__ */ new Date()).getTime();
  const locale = options?.locale ?? localeAffichage();
  const mots2 = MOTS_RELATIFS[langueDepuisLocale(locale)];
  const style = options?.style ?? "long";
  const diffMs = d.getTime() - now;
  const diffSec = Math.round(diffMs / 1e3);
  const absSec = Math.abs(diffSec);
  const secondsAgo = Math.floor((now - d.getTime()) / 1e3);
  const fallbackDays = options?.dateFallbackAfterDays;
  if (typeof fallbackDays === "number" && secondsAgo >= 0 && secondsAgo >= 86400 * fallbackDays) {
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short"
    }).format(d);
  }
  if (style === "capitalized") {
    const ago = Math.max(0, secondsAgo);
    if (ago < 60) return mots2.aLInstant;
    if (ago < 3600) return mots2.ilYAMinute(Math.floor(ago / 60));
    if (ago < 86400) return mots2.ilYAHeure(Math.floor(ago / 3600));
    if (ago < 86400 * 7) return mots2.ilYAJour(Math.floor(ago / 86400));
    if (ago < 86400 * 30) return mots2.ilYASemaine(Math.floor(ago / (86400 * 7)));
    if (ago < 86400 * 365) return mots2.ilYAMois(Math.floor(ago / (86400 * 30)));
    return mots2.ilYAAn(Math.floor(ago / (86400 * 365)));
  }
  if (style === "compact") {
    const ago = Math.max(0, secondsAgo);
    if (ago < 60) return mots2.aLInstant;
    if (ago < 3600) return mots2.ilYAMinuteCompact(Math.floor(ago / 60));
    if (ago < 86400) return mots2.ilYAHeureCompact(Math.floor(ago / 3600));
    if (ago < 86400 * 7) return mots2.ilYAJourCompact(Math.floor(ago / 86400));
    if (ago < 86400 * 30)
      return mots2.ilYASemaineCompact(Math.floor(ago / (86400 * 7)));
    if (ago < 86400 * 365)
      return mots2.ilYAMoisCompact(Math.floor(ago / (86400 * 30)));
    return mots2.ilYAAnCompact(Math.floor(ago / (86400 * 365)));
  }
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (absSec < 60) return rtf.format(diffSec, "second");
  if (absSec < 3600) return rtf.format(Math.round(diffSec / 60), "minute");
  if (absSec < 86400) return rtf.format(Math.round(diffSec / 3600), "hour");
  if (absSec < 86400 * 7) return rtf.format(Math.round(diffSec / 86400), "day");
  if (absSec < 86400 * 30)
    return rtf.format(Math.round(diffSec / (86400 * 7)), "week");
  if (absSec < 86400 * 365)
    return rtf.format(Math.round(diffSec / (86400 * 30)), "month");
  return rtf.format(Math.round(diffSec / (86400 * 365)), "year");
}
function formatRelativeTimeShort(unixTimestampSec) {
  if (!Number.isFinite(unixTimestampSec)) return "";
  const now = Date.now();
  const date = new Date(unixTimestampSec * 1e3);
  const diffSec = Math.floor((now - date.getTime()) / 1e3);
  if (diffSec < 60) return `${diffSec}s`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`;
  return `${Math.floor(diffSec / 86400)}j`;
}
function parseAmountInput(raw) {
  if (raw === null || raw === void 0) return null;
  const cleaned = String(raw).replace(/[^\d,.-]/g, "").replace(",", ".");
  if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}
function formatNumber(value, options) {
  const numeric = typeof value === "string" ? Number(value.trim()) : value;
  if (numeric === null || numeric === void 0 || !Number.isFinite(numeric)) {
    return "0";
  }
  const decimals = options?.decimals;
  const locale = options?.locale ?? localeAffichage();
  const minimumFractionDigits = decimals !== void 0 && !options?.fixedDecimals && Number.isInteger(numeric) ? 0 : decimals;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits: decimals
  }).format(numeric);
}
function formatCompactNumber(value, options) {
  if (value === null || value === void 0 || !Number.isFinite(value)) {
    return "0";
  }
  const locale = options?.locale ?? localeAffichage();
  return new Intl.NumberFormat(locale, {
    notation: "compact",
    maximumFractionDigits: 1
  }).format(value);
}
function formatCurrencyCompact(amount, options) {
  const numeric = typeof amount === "string" ? Number(amount.trim()) : amount;
  const value = typeof numeric === "number" && Number.isFinite(numeric) ? numeric : 0;
  const currency = options?.currency ?? DEFAULT_CURRENCY;
  const locale = options?.locale ?? localeAffichage();
  const threshold = options?.compactThreshold ?? 1e4;
  if (Math.abs(value) < threshold) {
    return formatCurrency(value, { currency, locale });
  }
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    notation: "compact",
    maximumFractionDigits: 2
  }).format(value);
}
function formatCurrencyCompactFromCents(cents, options) {
  if (cents == null) return MONTANT_MASQUE;
  const numeric = typeof cents === "string" ? Number(cents.trim()) : cents;
  const amount = typeof numeric === "number" && Number.isFinite(numeric) ? numeric / 100 : 0;
  return formatCurrencyCompact(amount, options);
}

// src/money.ts
function asCents(value) {
  if (value === null || value === void 0) return 0;
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (!Number.isFinite(n)) return 0;
  return Math.round(n);
}
function asEuros(value) {
  if (value === null || value === void 0) return 0;
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (!Number.isFinite(n)) return 0;
  return n;
}
function centsToEuros(cents) {
  return cents / 100;
}
function eurosToCents(euros) {
  return Math.round(euros * 100);
}
function dbAmountToEuros(value, storedAs) {
  if (value === null || value === void 0) return 0;
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (!Number.isFinite(n)) return 0;
  return storedAs === "cents" ? n / 100 : n;
}
function dbAmountToCents(value, storedAs) {
  if (value === null || value === void 0) return 0;
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (!Number.isFinite(n)) return 0;
  return Math.round(storedAs === "cents" ? n : n * 100);
}
function formatCents(cents, currency = "EUR") {
  return formatCurrency(Number(cents) / 100, { currency });
}
function formatEuros(euros, currency = "EUR") {
  return formatCurrency(Number(euros), { currency });
}
function formatDbAmount(value, storedAs, currency = "EUR") {
  return formatCurrency(dbAmountToEuros(value, storedAs), { currency });
}
function sumCents(...values) {
  let total = 0;
  for (const v of values) total += Number(v) || 0;
  return Math.round(total);
}
function sumEuros(...values) {
  let totalCents = 0;
  for (const v of values) totalCents += Math.round((Number(v) || 0) * 100);
  return totalCents / 100;
}
var STRIPE_EU_RATE = 0.015;
var STRIPE_EU_FIXED_FEE = 0.25;
function estimateStripeFeeEuros(amount) {
  const n = Number(amount) || 0;
  if (n <= 0) return 0;
  return Math.round((n * STRIPE_EU_RATE + STRIPE_EU_FIXED_FEE) * 100) / 100;
}
function estimateNetReceivedEuros(amount) {
  const n = Number(amount) || 0;
  if (n <= 0) return 0;
  return Math.max(0, Math.round((n - estimateStripeFeeEuros(n)) * 100) / 100);
}
function splitOptionRemovalRefunds(paidAmounts, mode, partialTotalEuros) {
  const total = paidAmounts.reduce((s, p) => s + (p > 0 ? p : 0), 0);
  let distributed = 0;
  return paidAmounts.map((paid, i) => {
    if (paid <= 0 || mode === "none") return { mode: "none" };
    if (mode === "full") return { mode: "full" };
    if (total <= 0) return { mode: "none" };
    const isLast = i === paidAmounts.length - 1;
    let share = isLast ? Math.round((partialTotalEuros - distributed) * 100) / 100 : Math.round(partialTotalEuros * (paid / total) * 100) / 100;
    share = Math.min(share, paid);
    distributed = Math.round((distributed + share) * 100) / 100;
    return share > 0 ? { mode: "partial", refundAmount: share } : { mode: "none" };
  });
}
function euroField(value) {
  if (value === null || value === void 0) return 0;
  const n = typeof value === "string" ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : 0;
}
function roundCents(value) {
  return Math.round(value * 100) / 100;
}
function donoorTipEuros(tx) {
  return Math.max(0, roundCents(euroField(tx.donoor_donation_amount)));
}
function orgGrossEuros(tx) {
  return Math.max(0, roundCents(euroField(tx.amount) - donoorTipEuros(tx)));
}
function orgBorneRefundEuros(tx) {
  return Math.max(
    0,
    roundCents(euroField(tx.refunded_amount) - euroField(tx.refunded_amount_platform))
  );
}
function orgNetKeptEuros(tx) {
  const base = tx.net_amount === null || tx.net_amount === void 0 ? orgGrossEuros(tx) : euroField(tx.net_amount);
  return Math.max(0, roundCents(base - orgBorneRefundEuros(tx)));
}
function sumOrgNetKeptEuros(transactions) {
  return roundCents(
    transactions.reduce((total, tx) => total + orgNetKeptEuros(tx), 0)
  );
}
function sumDonoorTipEuros(transactions) {
  return roundCents(
    transactions.reduce((total, tx) => total + donoorTipEuros(tx), 0)
  );
}
function apiToDbShape(t) {
  return {
    amount: t?.amount ?? 0,
    net_amount: t?.netAmount ?? null,
    donoor_donation_amount: t?.donateToUs ?? 0,
    refunded_amount: t?.refundedAmount ?? 0,
    refunded_amount_platform: t?.refundedPlatform ?? 0
  };
}
function soutienDonoor(t) {
  return donoorTipEuros(apiToDbShape(t));
}
function brutAsso(t) {
  return orgGrossEuros(apiToDbShape(t));
}
function netAsso(t) {
  const net = t?.netAmount;
  const n = typeof net === "string" ? parseFloat(net) : net;
  if (typeof n === "number" && Number.isFinite(n) && n > 0) return n;
  return brutAsso(t);
}
function dejaRembourseParAsso(t) {
  return orgBorneRefundEuros(apiToDbShape(t));
}
function plafondRemboursableAsso(t) {
  return Math.max(0, roundCents(netAsso(t) - dejaRembourseParAsso(t)));
}
function encaisseParDonoor(t) {
  const fee = typeof t?.feeAmount === "string" ? parseFloat(t.feeAmount) : t?.feeAmount ?? 0;
  return Math.max(0, roundCents((Number.isFinite(fee) ? fee : 0) + soutienDonoor(t)));
}
function eurosOuZero(v) {
  const n = typeof v === "string" ? parseFloat(v) : v ?? 0;
  return Number.isFinite(n) ? n : 0;
}
function gardeParDonoor(t) {
  if (!t || t.status === "failed") return 0;
  const reste = encaisseParDonoor(t) - eurosOuZero(t.refundedPlatform) - eurosOuZero(t.tipReattributed);
  return Math.max(0, roundCents(reste));
}
function partDonoorPct(donoorRevenue, orgRevenue) {
  const donoor = typeof donoorRevenue === "number" ? donoorRevenue : 0;
  const org = typeof orgRevenue === "number" ? orgRevenue : 0;
  if (!Number.isFinite(donoor) || !Number.isFinite(org)) return null;
  if (org <= 0 || donoor <= 0) return null;
  const pct = donoor / org * 100;
  return pct < 10 ? Math.round(pct * 10) / 10 : Math.round(pct);
}
function partDonoorPctLabel(donoorRevenue, orgRevenue) {
  const pct = partDonoorPct(donoorRevenue, orgRevenue);
  if (pct === null) return null;
  return `(${pct.toLocaleString("fr-FR", { maximumFractionDigits: 1 })}%)`;
}

// src/tip.ts
var PLAFOND_SUGGESTION = 30;
var PLAFOND_SUGGESTION_ACHAT = 10;
var PLANCHER_POURCENTAGE = 0.08;
var PALIERS = [
  [10, 2],
  [30, 3],
  [50, 4],
  [70, 5],
  [130, 8]
];
function getDonoorTipAmount(donationAmount, plafond = PLAFOND_SUGGESTION) {
  if (donationAmount <= 0) return 0;
  let palier = 0;
  for (const [seuil, montant] of PALIERS) {
    if (donationAmount <= seuil) {
      palier = montant;
      break;
    }
  }
  const plancher = Math.ceil(donationAmount * PLANCHER_POURCENTAGE);
  return Math.min(plafond, Math.max(palier, plancher));
}
function getDonoorTipAmountAchat(amount) {
  return getDonoorTipAmount(amount, PLAFOND_SUGGESTION_ACHAT);
}

// src/textesStripe/en.ts
var textes = {
  "{liste}, ou {piste}": "{liste}, or {piste}",
  "4 derniers chiffres du num\xE9ro de s\xE9curit\xE9 sociale": "Last 4 digits of social security number",
  "Acceptation des conditions d'utilisation Stripe": "Acceptance of Stripe terms of service",
  "Activit\xE9 dans un secteur r\xE9glement\xE9": "Activity in a regulated sector",
  "Adresse de l'organisation (compl\xE9ment)": "Organisation address (line 2)",
  "Adresse de l'organisation (rue)": "Organisation address (street)",
  "Adresse du service donateurs / support": "Donor / support address",
  "Adresse du site web": "Website URL",
  "Adresse email": "Email address",
  "Adresse IP d'acceptation des CGU": "IP address of terms of service acceptance",
  "Adresse personnelle (compl\xE9ment)": "Personal address (additional line)",
  "Adresse personnelle (rue)": "Personal address (street)",
  "associations caritatives sans statut d'enregistrement au dossier (par exemple un enregistrement 501(c)(3) ou une classification \xE9quivalente)": "charities without a registration status on file (for example a 501(c)(3) registration or an equivalent classification)",
  "Aucun propri\xE9taire n'a \xE9t\xE9 indiqu\xE9. Fournissez des informations sur tous les propri\xE9taires.": "No owner has been specified. Please provide information on all owners.",
  "Cadre dirigeant \u2014 {champ}": "Executive \u2014 {champ}",
  "Cat\xE9gorie d'activit\xE9 (code MCC)": "Business category (MCC code)",
  "Ce type d'organisation n'est pas pris en charge par Stripe.": "This type of organisation is not supported by Stripe.",
  "Code postal": "Postcode",
  "Code postal de l'organisation": "Organisation postal code",
  "Compte bancaire pour les versements (IBAN)": "Bank account for payouts (IBAN)",
  "Compte en cours de v\xE9rification": "Account under review",
  "Compte refus\xE9": "Account refused",
  "Compte rejet\xE9": "Account rejected",
  "Compte rejet\xE9 \u2014 Conditions d'utilisation": "Account rejected \u2014 Terms of Service",
  "Compte rejet\xE9 \u2014 v\xE9rification incompl\xE8te": "Account rejected \u2014 incomplete verification",
  "Compte restreint par Stripe": "Account restricted by Stripe",
  "Compte suspendu par Donoor": "Account suspended by Donoor",
  "Confirmation que tous les cadres dirigeants ont \xE9t\xE9 d\xE9clar\xE9s": "Confirmation that all executives have been declared",
  "Confirmation que tous les dirigeants ont \xE9t\xE9 d\xE9clar\xE9s": "Confirmation that all directors have been declared",
  "Confirmation que tous les propri\xE9taires ont \xE9t\xE9 d\xE9clar\xE9s": "Confirmation that all owners have been declared",
  "Contactez le service d'assistance de Stripe pour conna\xEEtre les raisons de l'\xE9chec de la v\xE9rification.": "Please contact Stripe support to find out why verification failed.",
  "Coordonn\xE9es du service client": "Customer support details",
  "Date de naissance": "Date of birth",
  "D\xE9cret minist\xE9riel (le cas \xE9ch\xE9ant)": "Ministerial decree (if applicable)",
  "Demande d'informations de Stripe": "Information request from Stripe",
  "Demande de conformit\xE9": "Compliance request",
  "D\xE9partement / r\xE9gion": "County / region",
  "D\xE9partement / r\xE9gion de l'organisation": "Organisation department / region",
  "Des cadres dirigeants n'ont pas \xE9t\xE9 indiqu\xE9s.": "Some senior executives have not been specified.",
  "Des dirigeants en trop ont \xE9t\xE9 d\xE9clar\xE9s.": "Too many directors have been declared.",
  "Description de votre activit\xE9": "Description of your activity",
  "Description des produits ou services": "Description of products or services",
  "Dirigeant \u2014 {champ}": "Director \u2014 {champ}",
  "Document": "Document",
  "Document officiel de l'organisation": "Official organisation document",
  "Document refus\xE9 (mode test).": "Document rejected (test mode).",
  "Elle ne peut pas \xEAtre un bo\xEEtier de contrat routier.": "It cannot be a contract mail box (a mail box rented under a contract with a road-based carrier).",
  "Elle ne peut pas \xEAtre une bo\xEEte postale priv\xE9e.": "It cannot be a private post box.",
  "Email du service donateurs / support": "Donor / support email",
  "Examen de la nature de votre activit\xE9": "Review of your business type",
  "Fonction au sein de l'organisation": "Role within the organisation",
  "Fonctionnalit\xE9s \xE0 demander": "Features to request",
  "Fonds retenus pour raison l\xE9gale": "Funds withheld for legal reasons",
  "Forme juridique de l'organisation": "Legal form of organisation",
  "Fournissez un nouveau fichier : image en couleur, 10 Mo maximum, pi\xE8ces d'identit\xE9 en JPG ou PNG, justificatifs d'adresse ou d'entit\xE9 juridique en JPG, PNG ou PDF, toutes les pages incluses, sans mot de passe.": "Upload a new file: colour image, maximum 10 MB, identity documents in JPG or PNG, proof of address or legal entity documents in JPG, PNG or PDF, all pages included, no password.",
  "Genre": "Gender",
  "Il se peut que le document ait \xE9t\xE9 modifi\xE9. Contactez le service d'assistance de Stripe pour conna\xEEtre les raisons de l'\xE9chec de la v\xE9rification.": "The document may have been altered. Please contact Stripe support to find out why verification failed.",
  "Informations \xE0 confirmer": "Information to confirm",
  "Informations \xE0 fournir": "Information needed",
  "Informations compl\xE9mentaires sur votre activit\xE9": "Additional information about your business",
  "Informations sur l'organisation": "Organisation information",
  "Informations sur la nature de votre activit\xE9": "Information about the nature of your activity",
  "Intervention de la plateforme": "Platform intervention",
  "Justificatif d'enregistrement de l'organisation": "Proof of organisation registration",
  "Justificatif de domicile (facture d'\xE9nergie, quittance de loyer, etc.)": "Proof of address (utility bill, rent receipt, etc.)",
  "Justificatif du num\xE9ro fiscal (SIRET)": "Proof of tax number (SIRET)",
  "L'acceptation des conditions d'utilisation de Stripe n'est plus valide.": "The acceptance of Stripe's terms of service is no longer valid.",
  "L'administration fiscale n'a pas pu v\xE9rifier les informations fournies. Corrigez le nom de l'organisation ou le num\xE9ro fiscal, ou t\xE9l\xE9chargez un document justificatif.": "The tax authority was unable to verify the information provided. Please correct the organisation name or tax number, or upload a supporting document.",
  "L'adresse de r\xE9sidence n'a pas pu \xEAtre v\xE9rifi\xE9e.": "The residential address could not be verified.",
  "L'adresse doit \xEAtre une adresse physique valide \xE0 partir de laquelle le compte exerce ses activit\xE9s.": "The address must be a valid physical address from which the account operates.",
  "L'adresse du site correspond \xE0 un site g\xE9n\xE9rique que Stripe consid\xE8re comme sans lien avec le compte. Fournissez une adresse propre \xE0 votre organisation.": "The website address points to a generic site that Stripe considers unrelated to the account. Provide an address specific to your organisation.",
  "L'adresse du site de l'organisation doit faire r\xE9f\xE9rence aux produits et services qu'elle fournit. Stripe pourrait exiger une modification de cette adresse avant de pouvoir prendre le compte en charge.": "The organisation's website address must refer to the products and services it provides. Stripe may require this address to be changed before it can take on the account.",
  "L'adresse fournie est celle d'un agent enregistr\xE9, qui n'est pas accept\xE9e.": "The address provided is that of a registered agent, which is not accepted.",
  "L'adresse fournie est celle d'un service de r\xE9ception de courrier (CMRA), qui n'est pas accept\xE9e.": "The address provided is that of a commercial mail receiving agency (CMRA), which is not accepted.",
  "L'organisation pourrait vendre des produits ou des services prot\xE9g\xE9s par le droit d'auteur. Stripe a besoin d'informations suppl\xE9mentaires pour v\xE9rifier que le compte est autoris\xE9 \xE0 les vendre.": "The organisation may sell products or services protected by copyright. Stripe needs additional information to verify that the account is authorised to sell them.",
  "La date de naissance doit se situer dans les 120 derni\xE8res ann\xE9es.": "The date of birth must be within the last 120 years.",
  "La description doit comporter au moins 10 caract\xE8res.": "The description must be at least 10 characters long.",
  "La description doit \xEAtre diff\xE9rente de l'adresse du site web.": "The description must be different from the website address.",
  "La liste des dirigeants n'a pas \xE9t\xE9 fournie. Mettez \xE0 jour le compte et t\xE9l\xE9chargez un document d'immatriculation avec la liste des dirigeants actuels.": "The list of directors was not provided. Please update the account and upload a registration document showing the current list of directors.",
  "La personne doit avoir au moins 13 ans.": "The person must be at least 13 years old.",
  "La personne doit avoir au moins 18 ans.": "The person must be at least 18 years old.",
  "La personne responsable du compte doit v\xE9rifier son identit\xE9 en t\xE9l\xE9chargeant une pi\xE8ce d'identit\xE9 officielle et un selfie.": "The person responsible for the account must verify their identity by uploading an official identity document and a selfie.",
  "La plateforme a initi\xE9 une intervention sur ce compte.": "The platform has initiated an intervention on this account.",
  "La plateforme Donoor a suspendu ce compte. Contactez le support Donoor.": "The Donoor platform has suspended this account. Please contact Donoor support.",
  "La valeur fournie n'est pas valide.": "The value provided is not valid.",
  "Le compte est en cours de v\xE9rification par Stripe.": "The account is being verified by Stripe.",
  "Le compte Stripe de l'organisation doit contenir une description exacte des produits ou services.": "The organisation's Stripe account must contain an accurate description of its products or services.",
  "Le document a \xE9t\xE9 refus\xE9 : il est en niveaux de gris.": "The document was rejected: it is in greyscale.",
  "Le document a \xE9t\xE9 refus\xE9 : il s'agit d'une copie.": "The document was rejected: it is a copy.",
  "Le document est en niveaux de gris.": "The document is greyscale.",
  "Le document est incomplet.": "The document is incomplete.",
  "Le document est une copie.": "The document is a copy.",
  "Le document n'est pas lisible.": "The document is unreadable.",
  "Le document n'est pas sign\xE9.": "The document isn't signed.",
  "Le document ne comporte pas de date de d\xE9livrance ou d'expiration, ou il est expir\xE9. Une pi\xE8ce d'identit\xE9 doit \xEAtre valide \xE0 la date d'envoi ; un justificatif de domicile doit dater de moins de six mois.": "The document has no issue or expiry date, or it has expired. An identity document must be valid on the date it is submitted; a proof of address must be dated within the last six months.",
  "Le document t\xE9l\xE9charg\xE9 ne contient pas toutes les informations requises. T\xE9l\xE9chargez un autre document contenant les informations manquantes.": "The uploaded document does not contain all the required information. Please upload a different document containing the missing information.",
  "Le fichier est corrompu.": "The file is corrupted.",
  "Le fichier est trop volumineux.": "The file is too large.",
  "Le fichier fourni n'est pas une pi\xE8ce accept\xE9e par Stripe (pays pris en charge, type de document attendu pour ce type d'organisation). Fournissez un nouveau fichier conforme.": "The file provided is not a document accepted by Stripe (supported country, expected document type for this type of organisation). Please provide a new, compliant file.",
  "Le fichier n'a pas \xE9t\xE9 t\xE9l\xE9charg\xE9.": "The file wasn't uploaded.",
  "Le format de l'adresse du site est incorrect. Fournissez une adresse correctement format\xE9e, par exemple https://example.com.": "The website address format is incorrect. Provide a correctly formatted address, for example https://example.com.",
  "Le libell\xE9 de relev\xE9 bancaire doit comporter au moins 5 caract\xE8res.": "The statement descriptor must be at least 5 characters long.",
  "Le libell\xE9 de relev\xE9 bancaire doit \xEAtre similaire au nom de l'organisation, \xE0 sa d\xE9nomination l\xE9gale ou \xE0 l'adresse de son site web.": "The statement descriptor must be similar to the organisation name, its legal name, or its website address.",
  "Le libell\xE9 de relev\xE9 bancaire ne peut pas correspondre \xE0 un nom d'entreprise g\xE9n\xE9rique ou bien connu.": "The statement descriptor can't match a generic or well-known business name.",
  "Le m\xEAme type de document a \xE9t\xE9 fourni deux fois.": "The same type of document was provided twice.",
  "Le nom de l'organisation doit correspondre \xE0 l'activit\xE9 du compte et ne peut pas \xEAtre un nom g\xE9n\xE9rique ou bien connu.": "The organisation name must match the account's activity and can't be a generic or well-known name.",
  "Le nom de l'organisation doit \xEAtre facile \xE0 comprendre et compos\xE9 de mots reconnaissables.": "The organisation name must be easy to understand and made up of recognisable words.",
  "Le nom l\xE9gal de l'organisation ne peut pas \xEAtre un nom g\xE9n\xE9rique ou bien connu.": "The organisation's legal name can't be a generic or well-known name.",
  "Le num\xE9ro fiscal doit \xEAtre compos\xE9 de 9 chiffres, sans tirets ni autres caract\xE8res sp\xE9ciaux.": "The tax ID must be 9 digits long, without dashes or other special characters.",
  "Le num\xE9ro IEC de l'organisation n'a pas pu \xEAtre v\xE9rifi\xE9.": "The organisation's IEC number could not be verified.",
  "Le pays du repr\xE9sentant n'est pas accept\xE9 pour ce compte.": "The representative's country isn't supported for this account.",
  "Le pr\xE9fixe du libell\xE9 de relev\xE9 bancaire doit correspondre \xE0 votre libell\xE9, au nom de votre organisation, \xE0 sa d\xE9nomination l\xE9gale ou \xE0 votre site web.": "The statement descriptor prefix must match your descriptor, your organisation name, its legal name, or your website.",
  "Le recto du document est manquant.": "The front of the document is missing.",
  "Le signataire du document n'a pas pu \xEAtre v\xE9rifi\xE9.": "The document signatory could not be verified.",
  "Le site web ne comporte pas de nom d'organisation ni de description claire des biens et services propos\xE9s.": "The website doesn't include an organisation name or a clear description of the goods and services offered.",
  "Le site web ne contient pas d'informations sur le service client.": "The website doesn't contain customer service information.",
  "Le site web ne contient pas de conditions d'annulation.": "The website doesn't contain cancellation terms.",
  "Le site web ne contient pas de conditions g\xE9n\xE9rales.": "The website doesn't contain terms and conditions.",
  "Le site web ne contient pas de politique de remboursement.": "The website doesn't contain a refund policy.",
  "Le site web ne contient pas de politique ni de processus de retour.": "The website doesn't contain a returns policy or process.",
  "Le site web ne contient pas les mentions applicables aux produits et services soumis \xE0 des restrictions juridiques ou \xE0 l'exportation.": "The website doesn't include the disclosures required for products and services subject to legal or export restrictions.",
  "Le type ou la structure de l'organisation ne semble pas correct. Indiquez le type et la structure appropri\xE9s pour ce compte.": "The organisation's type or structure does not appear to be correct. Please specify the appropriate type and structure for this account.",
  "Le verso du document est manquant.": "The back of the document is missing.",
  "Les conditions d'utilisation de Stripe interdisent la prise en charge de cette organisation en raison du niveau de risque qu'elle pr\xE9sente. Le titulaire du compte peut contester cette d\xE9cision.": "Stripe's terms of service prohibit supporting this organisation due to the level of risk it presents. The account holder can appeal this decision.",
  "Les conditions d'utilisation de Stripe interdisent la prise en charge de l'activit\xE9. Le compte peut faire appel de cette d\xE9cision.": "Stripe's terms of use prohibit support for this activity. The account may appeal this decision.",
  "Les dirigeants d\xE9clar\xE9s ne correspondent pas \xE0 ceux que Stripe a trouv\xE9s.": "The directors declared do not match those Stripe found.",
  "Les dirigeants mentionn\xE9s dans le document ne figurent pas dans le compte. Mettez \xE0 jour le compte et t\xE9l\xE9chargez un document d'immatriculation avec la liste des dirigeants actuels.": "The directors named in the document are not on the account. Please update the account and upload a registration document showing the current list of directors.",
  "Les informations du site web ne correspondent pas \xE0 celles du compte Stripe.": "The website information doesn't match the Stripe account information.",
  "Les informations figurant sur la pi\xE8ce d'identit\xE9 ne correspondent pas \xE0 celles fournies. V\xE9rifiez et corrigez les informations fournies.": "The information on the identity document does not match the information provided. Please check and correct the information you have provided.",
  "Les informations figurant sur le document ne correspondent pas \xE0 celles fournies. V\xE9rifiez-les, puis corrigez-les ou t\xE9l\xE9chargez un document correspondant.": "The information on the document does not match the information provided. Please check it, then correct the information or upload a matching document.",
  "Libell\xE9 de relev\xE9 bancaire": "Bank statement descriptor",
  "Libell\xE9 qui appara\xEEtra sur le relev\xE9 bancaire des donateurs": "Label that will appear on donors' bank statements",
  "Licence ou autorisation d'exercer": "Licence or authorisation to operate",
  "Motif indiqu\xE9 par Stripe : {motif}.": "Reason given by Stripe: {motif}.",
  "Nationalit\xE9": "Nationality",
  "Nom commercial / d'affichage de l'organisation": "Organisation's trading / display name",
  "Nom de famille": "Last name",
  "Nom l\xE9gal de l'organisation": "Legal name of organisation",
  "Non": "No",
  "Num\xE9ro d'enregistrement de l'organisation": "Organisation registration number",
  "Num\xE9ro d'identification (\xE9quivalent du num\xE9ro de s\xE9curit\xE9 sociale)": "Identification number (equivalent to a social security number)",
  "Num\xE9ro de t\xE9l\xE9phone": "Phone number",
  "Num\xE9ro de t\xE9l\xE9phone de l'organisation": "Organisation phone number",
  "Num\xE9ro de TVA intracommunautaire": "Intra-Community VAT number",
  "Num\xE9ro SIRET / identifiant fiscal": "SIRET number / tax identifier",
  "Oui": "Yes",
  "Pays": "Country",
  "Pays de l'organisation": "Organisation country",
  "Pi\xE8ce d'identit\xE9 (passeport, carte nationale d'identit\xE9 ou permis de conduire)": "Identity document (passport, national identity card or driving licence)",
  "Politique d'ex\xE9cution des commandes": "Order fulfilment policy",
  "Pourcentage de propri\xE9t\xE9": "Ownership percentage",
  "Pr\xE9fixe du libell\xE9 sur le relev\xE9 bancaire": "Statement descriptor prefix",
  "Pr\xE9nom": "First name",
  "Preuve de pr\xE9sence (selfie)": "Proof of presence (selfie)",
  "Produits ou services prot\xE9g\xE9s par le droit d'auteur": "Products or services protected by copyright",
  "Propri\xE9taire \u2014 {champ}": "Owner \u2014 {champ}",
  "Recours contre un refus": "Appeal against a refusal",
  "Recours contre un refus de prise en charge": "Appeal against a refusal of support",
  "Recours contre une r\xE9serve sur le compte": "Appeal against an account reserve",
  "Recto de la pi\xE8ce d'identit\xE9": "Front of identity document",
  "Repr\xE9sentant l\xE9gal \u2014 {champ}": "Representative \u2014 {champ}",
  "Selon Stripe, ce compte semble proposer des produits ou services relevant d'une cat\xE9gorie interdite ou restreinte par le contrat de services Stripe. Cat\xE9gorie : {categorie}. Plus d'informations : {lien}": "According to Stripe, this account appears to offer products or services falling within a category prohibited or restricted by the Stripe Services Agreement. Category: {categorie}. More information: {lien}",
  "Selon Stripe, certaines fonctionnalit\xE9s doivent encore \xEAtre demand\xE9es pour ce compte par la plateforme Donoor.": "According to Stripe, certain features still need to be requested for this account by the Donoor platform.",
  "Selon Stripe, cette demande ne se traite pas dans un formulaire : contactez le service d'assistance de Stripe.": "According to Stripe, this request cannot be handled through a form: contact Stripe support.",
  "Selon Stripe, cette demande se r\xE8gle par l'interm\xE9diaire d'un tiers.": "According to Stripe, this request is settled through a third party.",
  "Selon Stripe, cette demande se remplit dans un formulaire Stripe.": "According to Stripe, this request is completed through a Stripe form.",
  "Selon Stripe, des informations suppl\xE9mentaires sont requises pour activer les fonctionnalit\xE9s de ce compte.": "According to Stripe, additional information is required to activate the features of this account.",
  "Selon Stripe, l'organisation peut exercer ses activit\xE9s dans un secteur r\xE9glement\xE9. Stripe pourrait avoir besoin d'informations suppl\xE9mentaires sur la nature de l'activit\xE9 ou sur les licences requises afin de v\xE9rifier qu'il peut prendre le compte en charge.": "According to Stripe, the organisation may operate in a regulated sector. Stripe may need additional information about the nature of the activity or the licences required in order to verify that it can support the account.",
  "Selon Stripe, le compte pourrait figurer sur une liste de personnes ou d'entreprises interdites. Stripe effectue une v\xE9rification et pourra refuser ou r\xE9tablir l'acc\xE8s au compte en cons\xE9quence.": "According to Stripe, the account may appear on a list of prohibited persons or businesses. Stripe is conducting a review and may refuse or restore access to the account accordingly.",
  "Selon Stripe, vous devez r\xE9pondre vous-m\xEAme \xE0 cette demande de v\xE9rification.": "According to Stripe, you must respond to this verification request yourself.",
  "Site web de l'organisation": "Organisation's website",
  "Statut d'exposition politique": "Political exposure status",
  "Statuts de l'organisation": "Organisation articles of association",
  "Stripe a besoin d'informations suppl\xE9mentaires pour v\xE9rifier qu'il peut prendre le compte en charge.": "Stripe needs additional information to verify that it can take on the account.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur l'organisation.": "Stripe needs additional information about the organisation.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur la conformit\xE9.": "Stripe needs additional compliance information.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur la nature de l'activit\xE9 afin de v\xE9rifier qu'il peut prendre le compte en charge.": "Stripe needs additional information about the nature of the activity in order to verify that it can support the account.",
  "Stripe a besoin d'un libell\xE9 de relev\xE9 bancaire qui corresponde exactement \xE0 l'activit\xE9 de l'organisation.": "Stripe needs a statement descriptor that exactly matches the organisation's activity.",
  "Stripe a demand\xE9 des informations suppl\xE9mentaires dans le cadre de son \xE9valuation des risques.": "Stripe has requested additional information as part of its risk assessment.",
  "Stripe a d\xE9tect\xE9 que ce compte utilise un site web, un profil sur les r\xE9seaux sociaux ou une application pour vendre ou promouvoir des produits ou services, mais aucune adresse n'a \xE9t\xE9 fournie. Fournissez une adresse.": "Stripe has detected that this account uses a website, social media profile or app to sell or promote products or services, but no address has been provided. Provide an address.",
  "Stripe a impos\xE9 une r\xE9serve au compte, ce qui n'affecte pas sa capacit\xE9 \xE0 accepter des paiements. Le titulaire du compte peut contester cette d\xE9cision.": "Stripe has placed a reserve on the account, which does not affect its ability to accept payments. The account holder can appeal this decision.",
  "Stripe a refus\xE9 le compte, car il figure sur une liste de personnes ou d'entreprises interdites \xE9tablie par un tiers (par exemple un fournisseur de services financiers ou un gouvernement).": "Stripe has refused the account because it appears on a list of prohibited persons or businesses compiled by a third party (for example a financial services provider or a government).",
  "Stripe a refus\xE9 le motif d'exemption relatif \xE0 la propri\xE9t\xE9. Choisissez un autre motif ou t\xE9l\xE9chargez un justificatif de b\xE9n\xE9ficiaire effectif.": "Stripe has rejected the exemption reason relating to ownership. Please choose another reason or upload a proof of beneficial ownership.",
  "Stripe a rejet\xE9 le compte pour suspicion d'infraction \xE0 ses conditions d'utilisation.": "Stripe has rejected the account on suspicion of a breach of its Terms of Service.",
  "Stripe a rejet\xE9 le compte pour suspicion de fraude ou d'activit\xE9s ill\xE9gales.": "Stripe has rejected the account on suspicion of fraud or illegal activity.",
  "Stripe a rejet\xE9 le compte pour un autre motif, sans le pr\xE9ciser.": "Stripe has rejected the account for another reason, without specifying it.",
  "Stripe a rejet\xE9 le compte, car les exigences de v\xE9rification n'ont pas \xE9t\xE9 satisfaites dans le d\xE9lai requis.": "Stripe has rejected the account because the verification requirements were not met within the required time.",
  "Stripe a restreint le compte sans pr\xE9ciser de motif.": "Stripe has restricted the account without giving a reason.",
  "Stripe demande les statuts de soci\xE9t\xE9s qui d\xE9tiennent une participation importante.": "Stripe requires the articles of association of companies holding a significant stake.",
  "Stripe demande un justificatif d'immatriculation de soci\xE9t\xE9s qui d\xE9tiennent une participation importante.": "Stripe requires proof of registration of companies holding a significant stake.",
  "Stripe doit v\xE9rifier l'adresse de l'organisation, avec les justificatifs appropri\xE9s.": "Stripe must verify the organisation's address, with appropriate supporting documents.",
  "Stripe doit v\xE9rifier la politique d'ex\xE9cution des commandes de l'organisation.": "Stripe must verify the organisation's order fulfilment policy.",
  "Stripe doit v\xE9rifier les coordonn\xE9es du service client de l'organisation.": "Stripe must verify the organisation's customer support details.",
  "Stripe doit v\xE9rifier les informations du compte bancaire associ\xE9 \xE0 l'organisation.": "Stripe must verify the bank account details associated with the organisation.",
  "Stripe doit v\xE9rifier que l'activit\xE9 de ce compte peut \xEAtre prise en charge sur sa plateforme.": "Stripe must verify that this account's activity can be supported on its platform.",
  "Stripe doit v\xE9rifier que l'organisation n'est pas en relation avec une personne ou une juridiction sanctionn\xE9e.": "Stripe must verify that the organisation is not connected with a sanctioned person or jurisdiction.",
  "Stripe doit v\xE9rifier que l'organisation n'est pas li\xE9e \xE0 une personne d'int\xE9r\xEAt ou \xE0 une personne politiquement expos\xE9e.": "Stripe must verify that the organisation is not connected with a person of interest or a politically exposed person.",
  "Stripe doit v\xE9rifier que le titulaire du compte contr\xF4le bien l'adresse ou le nom de domaine qu'il a fourni.": "Stripe must verify that the account holder controls the domain or web address they have provided.",
  "Stripe est tenu de conserver des fonds pour une raison l\xE9gale. Il peut \xEAtre n\xE9cessaire de remettre des fonds \xE0 un tiers.": "Stripe is required to hold funds for legal reasons. It may be necessary to release funds to a third party.",
  "Stripe n'a pas pu acc\xE9der au site web. Si vous bloquez l'acc\xE8s \xE0 votre site dans certaines r\xE9gions, d\xE9sactivez temporairement ce blocage jusqu'\xE0 la v\xE9rification.": "Stripe couldn't access the website. If you block access to your site in certain regions, temporarily disable this block until verification.",
  "Stripe n'a pas pu confirmer ces informations : {champs}. Corrigez-les si elles sont fausses, ou fournissez \xE0 la place {pistes}.": "Stripe could not confirm this information: {champs}. Correct it if it is wrong, or provide {pistes} instead.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Corrigez-les si elles sont fausses, ou prouvez l'identit\xE9 autrement.": "Stripe could not confirm the identity from the information entered. Correct it if it is wrong, or prove the identity another way.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Fournissez {pistes} : une seule suffit. Si une information est fausse, vous pouvez aussi la corriger.": "Stripe could not confirm the identity from the information entered. Provide {pistes}: just one is enough. If any information is wrong, you can also correct it.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Fournissez {pistes}. Si une information est fausse, vous pouvez aussi la corriger.": "Stripe could not confirm the identity from the information entered. Provide {pistes}. If any information is wrong, you can also correct it.",
  "Stripe n'a pas pu valider l'ensemble ville, r\xE9gion et code postal de l'adresse fournie.": "Stripe was unable to validate the combination of city, region and postcode of the address provided.",
  "Stripe n'a pas pu valider le nom de la rue ni le num\xE9ro de l'adresse fournie.": "Stripe was unable to validate the street name or house number of the address provided.",
  "Stripe n'a pas pu valider le num\xE9ro de t\xE9l\xE9phone. Son format doit correspondre \xE0 celui du pays de la personne concern\xE9e.": "Stripe couldn't validate the phone number. Its format must match the country of the person concerned.",
  "Stripe n'a pas pu v\xE9rifier l'activit\xE9 du compte \xE0 l'adresse fournie (site web, profil de r\xE9seaux sociaux ou application).": "Stripe couldn't verify the account's activity at the address provided (website, social media profile or app).",
  "Stripe n'a pas pu v\xE9rifier le site web, car il est prot\xE9g\xE9 par un mot de passe.": "Stripe couldn't verify the website because it is password-protected.",
  "Stripe n'a pas pu v\xE9rifier le site web, car il est toujours en construction.": "Stripe couldn't verify the website because it is still under construction.",
  "Stripe n'a pas pu v\xE9rifier le site web, car il ne pr\xE9sente aucun contenu.": "Stripe couldn't verify the website because it has no content.",
  "Stripe n'a pas pu v\xE9rifier le site web, car son acc\xE8s est bloqu\xE9 dans certaines r\xE9gions. D\xE9sactivez temporairement ce blocage jusqu'\xE0 la v\xE9rification.": "Stripe couldn't verify the website because access is blocked in certain regions. Temporarily disable this block until verification.",
  "Stripe n'a pas pu v\xE9rifier les informations du compte. Vous pouvez t\xE9l\xE9charger un document de v\xE9rification ou mettre \xE0 jour vos informations.": "Stripe was unable to verify the account information. You can upload a verification document or update your information.",
  "Stripe n'a pas pu v\xE9rifier les pouvoirs de la personne qui autorise le compte. Remplacez-la par une personne d\xFBment enregistr\xE9e en tant que repr\xE9sentant autoris\xE9.": "Stripe was unable to verify the authority of the person authorising the account. Please replace them with a person who is duly registered as an authorised representative.",
  "Stripe n'a pas pu v\xE9rifier les pouvoirs du repr\xE9sentant du compte. Ajoutez une personne qui autorise le compte et fournissez une lettre d'autorisation sign\xE9e par elle.": "Stripe was unable to verify the authority of the account representative. Please add a person who authorises the account and provide a signed letter of authorisation from them.",
  "Stripe n'a pas trouv\xE9 d'informations \xE0 partir des donn\xE9es saisies. V\xE9rifiez qu'elles sont correctes.": "Stripe could not find information based on the details entered. Please check that they are correct.",
  "Stripe signale une erreur de v\xE9rification (code : {code}).": "Stripe reported a verification error (code: {code}).",
  "Stripe v\xE9rifie actuellement les informations du compte. Aucune action n'est requise.": "Stripe is currently verifying the account information. No action is required.",
  "T\xE9l\xE9phone du service donateurs / support": "Donor / support phone number",
  "Une information est manquante.": "Some information is missing.",
  "Une information est manquante. D\xE9tail donn\xE9 par Stripe : \xAB {detail} \xBB": "Some information is missing. Detail provided by Stripe: \u2018{detail}\u2019",
  "une pi\xE8ce d'identit\xE9": "an identity document",
  "une pi\xE8ce d'identit\xE9 ou un justificatif de domicile": "an identity document or proof of address",
  "une v\xE9rification par selfie": "a selfie verification",
  "URL du service donateurs / support": "Donor / support URL",
  "V\xE9rification d'identit\xE9": "Identity verification",
  "V\xE9rification de l'adresse": "Address verification",
  "V\xE9rification des personnes politiquement expos\xE9es": "Politically exposed persons screening",
  "V\xE9rification du compte bancaire": "Bank account verification",
  "V\xE9rification du nom de domaine": "Domain verification",
  "V\xE9rification li\xE9e aux sanctions": "Sanctions screening",
  "V\xE9rification Stripe en cours": "Stripe verification in progress",
  "Verso de la pi\xE8ce d'identit\xE9": "Back of identity document",
  "Ville": "City",
  "Ville de l'organisation": "Organisation city"
};
var en_default = textes;

// src/textesStripe/de.ts
var textes2 = {
  "{liste}, ou {piste}": "{liste} oder {piste}",
  "4 derniers chiffres du num\xE9ro de s\xE9curit\xE9 sociale": "Letzte 4 Ziffern der Sozialversicherungsnummer",
  "Acceptation des conditions d'utilisation Stripe": "Annahme der Stripe-Nutzungsbedingungen",
  "Activit\xE9 dans un secteur r\xE9glement\xE9": "T\xE4tigkeit in einem regulierten Sektor",
  "Adresse de l'organisation (compl\xE9ment)": "Adresse der Organisation (Adresszusatz)",
  "Adresse de l'organisation (rue)": "Adresse der Organisation (Stra\xDFe)",
  "Adresse du service donateurs / support": "Adresse des Spender- / Support-Teams",
  "Adresse du site web": "Website-Adresse",
  "Adresse email": "E-Mail-Adresse",
  "Adresse IP d'acceptation des CGU": "IP-Adresse bei Annahme der Nutzungsbedingungen",
  "Adresse personnelle (compl\xE9ment)": "Pers\xF6nliche Adresse (Adresszusatz)",
  "Adresse personnelle (rue)": "Pers\xF6nliche Adresse (Stra\xDFe)",
  "associations caritatives sans statut d'enregistrement au dossier (par exemple un enregistrement 501(c)(3) ou une classification \xE9quivalente)": "gemeinn\xFCtzige Organisationen ohne Registrierungsstatus in der Akte (z. B. eine 501(c)(3)-Registrierung oder eine gleichwertige Einstufung)",
  "Aucun propri\xE9taire n'a \xE9t\xE9 indiqu\xE9. Fournissez des informations sur tous les propri\xE9taires.": "Es wurde kein Eigent\xFCmer angegeben. Geben Sie Informationen zu allen Eigent\xFCmern an.",
  "Cadre dirigeant \u2014 {champ}": "F\xFChrungskraft \u2014 {champ}",
  "Cat\xE9gorie d'activit\xE9 (code MCC)": "Gesch\xE4ftskategorie (MCC-Code)",
  "Ce type d'organisation n'est pas pris en charge par Stripe.": "Diese Art von Organisation wird von Stripe nicht unterst\xFCtzt.",
  "Code postal": "Postleitzahl",
  "Code postal de l'organisation": "Postleitzahl der Organisation",
  "Compte bancaire pour les versements (IBAN)": "Bankkonto f\xFCr Auszahlungen (IBAN)",
  "Compte en cours de v\xE9rification": "Konto wird \xFCberpr\xFCft",
  "Compte refus\xE9": "Konto verweigert",
  "Compte rejet\xE9": "Konto abgelehnt",
  "Compte rejet\xE9 \u2014 Conditions d'utilisation": "Konto abgelehnt \u2014 Nutzungsbedingungen",
  "Compte rejet\xE9 \u2014 v\xE9rification incompl\xE8te": "Konto abgelehnt \u2014 \xDCberpr\xFCfung unvollst\xE4ndig",
  "Compte restreint par Stripe": "Konto von Stripe eingeschr\xE4nkt",
  "Compte suspendu par Donoor": "Konto von Donoor gesperrt",
  "Confirmation que tous les cadres dirigeants ont \xE9t\xE9 d\xE9clar\xE9s": "Best\xE4tigung, dass alle F\xFChrungskr\xE4fte angegeben wurden",
  "Confirmation que tous les dirigeants ont \xE9t\xE9 d\xE9clar\xE9s": "Best\xE4tigung, dass alle Gesch\xE4ftsf\xFChrer angegeben wurden",
  "Confirmation que tous les propri\xE9taires ont \xE9t\xE9 d\xE9clar\xE9s": "Best\xE4tigung, dass alle Eigent\xFCmer angegeben wurden",
  "Contactez le service d'assistance de Stripe pour conna\xEEtre les raisons de l'\xE9chec de la v\xE9rification.": "Wenden Sie sich an den Support von Stripe, um die Gr\xFCnde f\xFCr das Scheitern der Verifizierung zu erfahren.",
  "Coordonn\xE9es du service client": "Kontaktdaten des Kundenservices",
  "Date de naissance": "Geburtsdatum",
  "D\xE9cret minist\xE9riel (le cas \xE9ch\xE9ant)": "Ministerieller Erlass (falls zutreffend)",
  "Demande d'informations de Stripe": "Anfrage nach Informationen von Stripe",
  "Demande de conformit\xE9": "Compliance-Anfrage",
  "D\xE9partement / r\xE9gion": "Bundesland / Region",
  "D\xE9partement / r\xE9gion de l'organisation": "Bundesland / Region der Organisation",
  "Des cadres dirigeants n'ont pas \xE9t\xE9 indiqu\xE9s.": "Einige F\xFChrungskr\xE4fte wurden nicht angegeben.",
  "Des dirigeants en trop ont \xE9t\xE9 d\xE9clar\xE9s.": "Es wurden zu viele F\xFChrungskr\xE4fte angegeben.",
  "Description de votre activit\xE9": "Beschreibung Ihrer T\xE4tigkeit",
  "Description des produits ou services": "Beschreibung der Produkte oder Dienstleistungen",
  "Dirigeant \u2014 {champ}": "Gesch\xE4ftsf\xFChrer \u2014 {champ}",
  "Document": "Dokument",
  "Document officiel de l'organisation": "Amtliches Dokument der Organisation",
  "Document refus\xE9 (mode test).": "Dokument abgelehnt (Testmodus).",
  "Elle ne peut pas \xEAtre un bo\xEEtier de contrat routier.": "Sie darf kein Postfach im Rahmen eines Routenvertrags sein.",
  "Elle ne peut pas \xEAtre une bo\xEEte postale priv\xE9e.": "Sie darf kein privates Postfach sein.",
  "Email du service donateurs / support": "E-Mail-Adresse des Spender- / Support-Teams",
  "Examen de la nature de votre activit\xE9": "Pr\xFCfung der Art Ihrer T\xE4tigkeit",
  "Fonction au sein de l'organisation": "Funktion innerhalb der Organisation",
  "Fonctionnalit\xE9s \xE0 demander": "Anzufordernde Funktionen",
  "Fonds retenus pour raison l\xE9gale": "Aus rechtlichen Gr\xFCnden einbehaltene Gelder",
  "Forme juridique de l'organisation": "Rechtsform der Organisation",
  "Fournissez un nouveau fichier : image en couleur, 10 Mo maximum, pi\xE8ces d'identit\xE9 en JPG ou PNG, justificatifs d'adresse ou d'entit\xE9 juridique en JPG, PNG ou PDF, toutes les pages incluses, sans mot de passe.": "Laden Sie eine neue Datei hoch: Farbbild, maximal 10 MB, Ausweisdokumente als JPG oder PNG, Adressnachweise oder Nachweise der Rechtsform als JPG, PNG oder PDF, alle Seiten enthalten, nicht passwortgesch\xFCtzt.",
  "Genre": "Geschlecht",
  "Il se peut que le document ait \xE9t\xE9 modifi\xE9. Contactez le service d'assistance de Stripe pour conna\xEEtre les raisons de l'\xE9chec de la v\xE9rification.": "Das Dokument wurde m\xF6glicherweise ver\xE4ndert. Wenden Sie sich an den Support von Stripe, um die Gr\xFCnde f\xFCr das Scheitern der Verifizierung zu erfahren.",
  "Informations \xE0 confirmer": "Zu best\xE4tigende Angaben",
  "Informations \xE0 fournir": "Erforderliche Informationen",
  "Informations compl\xE9mentaires sur votre activit\xE9": "Zus\xE4tzliche Informationen zu Ihrer T\xE4tigkeit",
  "Informations sur l'organisation": "Informationen zur Organisation",
  "Informations sur la nature de votre activit\xE9": "Informationen zur Art Ihrer Gesch\xE4ftst\xE4tigkeit",
  "Intervention de la plateforme": "Eingriff durch die Plattform",
  "Justificatif d'enregistrement de l'organisation": "Registrierungsnachweis der Organisation",
  "Justificatif de domicile (facture d'\xE9nergie, quittance de loyer, etc.)": "Adressnachweis (Strom- oder Gasrechnung, Mietquittung usw.)",
  "Justificatif du num\xE9ro fiscal (SIRET)": "Nachweis der Steuernummer (SIRET)",
  "L'acceptation des conditions d'utilisation de Stripe n'est plus valide.": "Die Zustimmung zu den Nutzungsbedingungen von Stripe ist nicht mehr g\xFCltig.",
  "L'administration fiscale n'a pas pu v\xE9rifier les informations fournies. Corrigez le nom de l'organisation ou le num\xE9ro fiscal, ou t\xE9l\xE9chargez un document justificatif.": "Die Steuerbeh\xF6rde konnte die angegebenen Informationen nicht verifizieren. Korrigieren Sie den Namen der Organisation oder die Steuernummer oder laden Sie einen Nachweis hoch.",
  "L'adresse de r\xE9sidence n'a pas pu \xEAtre v\xE9rifi\xE9e.": "Die Wohnadresse konnte nicht verifiziert werden.",
  "L'adresse doit \xEAtre une adresse physique valide \xE0 partir de laquelle le compte exerce ses activit\xE9s.": "Die Adresse muss eine g\xFCltige physische Adresse sein, unter der das Konto seine Gesch\xE4ftst\xE4tigkeit aus\xFCbt.",
  "L'adresse du site correspond \xE0 un site g\xE9n\xE9rique que Stripe consid\xE8re comme sans lien avec le compte. Fournissez une adresse propre \xE0 votre organisation.": "Die Website-Adresse verweist auf eine generische Website, die Stripe als nicht mit dem Konto verbunden ansieht. Geben Sie eine Adresse an, die f\xFCr Ihre Organisation eigen ist.",
  "L'adresse du site de l'organisation doit faire r\xE9f\xE9rence aux produits et services qu'elle fournit. Stripe pourrait exiger une modification de cette adresse avant de pouvoir prendre le compte en charge.": "Die Website-Adresse der Organisation muss sich auf die von ihr angebotenen Produkte und Dienstleistungen beziehen. Stripe k\xF6nnte eine \xC4nderung dieser Adresse verlangen, bevor das Konto \xFCbernommen werden kann.",
  "L'adresse fournie est celle d'un agent enregistr\xE9, qui n'est pas accept\xE9e.": "Die angegebene Adresse ist die eines registrierten Agenten, der nicht akzeptiert wird.",
  "L'adresse fournie est celle d'un service de r\xE9ception de courrier (CMRA), qui n'est pas accept\xE9e.": "Die angegebene Adresse ist die eines Postdienstleisters (CMRA), der nicht akzeptiert wird.",
  "L'organisation pourrait vendre des produits ou des services prot\xE9g\xE9s par le droit d'auteur. Stripe a besoin d'informations suppl\xE9mentaires pour v\xE9rifier que le compte est autoris\xE9 \xE0 les vendre.": "Die Organisation verkauft m\xF6glicherweise urheberrechtlich gesch\xFCtzte Produkte oder Dienstleistungen. Stripe ben\xF6tigt zus\xE4tzliche Informationen, um zu pr\xFCfen, ob das Konto zum Verkauf berechtigt ist.",
  "La date de naissance doit se situer dans les 120 derni\xE8res ann\xE9es.": "Das Geburtsdatum muss innerhalb der letzten 120 Jahre liegen.",
  "La description doit comporter au moins 10 caract\xE8res.": "Die Beschreibung muss mindestens 10 Zeichen umfassen.",
  "La description doit \xEAtre diff\xE9rente de l'adresse du site web.": "Die Beschreibung muss sich von der Adresse der Website unterscheiden.",
  "La liste des dirigeants n'a pas \xE9t\xE9 fournie. Mettez \xE0 jour le compte et t\xE9l\xE9chargez un document d'immatriculation avec la liste des dirigeants actuels.": "Die Liste der F\xFChrungskr\xE4fte wurde nicht \xFCbermittelt. Aktualisieren Sie das Konto und laden Sie einen Registerauszug mit der aktuellen Liste der F\xFChrungskr\xE4fte hoch.",
  "La personne doit avoir au moins 13 ans.": "Die Person muss mindestens 13 Jahre alt sein.",
  "La personne doit avoir au moins 18 ans.": "Die Person muss mindestens 18 Jahre alt sein.",
  "La personne responsable du compte doit v\xE9rifier son identit\xE9 en t\xE9l\xE9chargeant une pi\xE8ce d'identit\xE9 officielle et un selfie.": "Die f\xFCr das Konto verantwortliche Person muss ihre Identit\xE4t durch den Upload eines amtlichen Ausweises und eines Selfies best\xE4tigen.",
  "La plateforme a initi\xE9 une intervention sur ce compte.": "Die Plattform hat einen Eingriff f\xFCr dieses Konto veranlasst.",
  "La plateforme Donoor a suspendu ce compte. Contactez le support Donoor.": "Die Plattform Donoor hat dieses Konto gesperrt. Wenden Sie sich an den Support von Donoor.",
  "La valeur fournie n'est pas valide.": "Der angegebene Wert ist ung\xFCltig.",
  "Le compte est en cours de v\xE9rification par Stripe.": "Das Konto wird von Stripe \xFCberpr\xFCft.",
  "Le compte Stripe de l'organisation doit contenir une description exacte des produits ou services.": "Das Stripe-Konto der Organisation muss eine genaue Beschreibung der Produkte oder Dienstleistungen enthalten.",
  "Le document a \xE9t\xE9 refus\xE9 : il est en niveaux de gris.": "Das Dokument wurde abgelehnt: Es ist in Graustufen.",
  "Le document a \xE9t\xE9 refus\xE9 : il s'agit d'une copie.": "Das Dokument wurde abgelehnt: Es handelt sich um eine Kopie.",
  "Le document est en niveaux de gris.": "Das Dokument ist in Graustufen.",
  "Le document est incomplet.": "Das Dokument ist unvollst\xE4ndig.",
  "Le document est une copie.": "Das Dokument ist eine Kopie.",
  "Le document n'est pas lisible.": "Das Dokument ist nicht lesbar.",
  "Le document n'est pas sign\xE9.": "Das Dokument ist nicht unterschrieben.",
  "Le document ne comporte pas de date de d\xE9livrance ou d'expiration, ou il est expir\xE9. Une pi\xE8ce d'identit\xE9 doit \xEAtre valide \xE0 la date d'envoi ; un justificatif de domicile doit dater de moins de six mois.": "Das Dokument enth\xE4lt kein Ausstellungs- oder Ablaufdatum oder ist abgelaufen. Ein Ausweisdokument muss zum Zeitpunkt der Einreichung g\xFCltig sein; ein Adressnachweis darf nicht \xE4lter als sechs Monate sein.",
  "Le document t\xE9l\xE9charg\xE9 ne contient pas toutes les informations requises. T\xE9l\xE9chargez un autre document contenant les informations manquantes.": "Das hochgeladene Dokument enth\xE4lt nicht alle erforderlichen Informationen. Laden Sie ein anderes Dokument hoch, das die fehlenden Informationen enth\xE4lt.",
  "Le fichier est corrompu.": "Die Datei ist besch\xE4digt.",
  "Le fichier est trop volumineux.": "Die Datei ist zu gro\xDF.",
  "Le fichier fourni n'est pas une pi\xE8ce accept\xE9e par Stripe (pays pris en charge, type de document attendu pour ce type d'organisation). Fournissez un nouveau fichier conforme.": "Die bereitgestellte Datei ist kein von Stripe akzeptierter Nachweis (unterst\xFCtztes Land, f\xFCr diese Art von Organisation erwarteter Dokumenttyp). Laden Sie eine neue, konforme Datei hoch.",
  "Le fichier n'a pas \xE9t\xE9 t\xE9l\xE9charg\xE9.": "Die Datei wurde nicht hochgeladen.",
  "Le format de l'adresse du site est incorrect. Fournissez une adresse correctement format\xE9e, par exemple https://example.com.": "Das Format der Website-Adresse ist falsch. Geben Sie eine korrekt formatierte Adresse an, zum Beispiel https://example.com.",
  "Le libell\xE9 de relev\xE9 bancaire doit comporter au moins 5 caract\xE8res.": "Der Kontoauszugsname muss mindestens 5 Zeichen umfassen.",
  "Le libell\xE9 de relev\xE9 bancaire doit \xEAtre similaire au nom de l'organisation, \xE0 sa d\xE9nomination l\xE9gale ou \xE0 l'adresse de son site web.": "Der Kontoauszugsname muss dem Namen der Organisation, ihrer rechtlichen Bezeichnung oder der Adresse ihrer Website \xE4hnlich sein.",
  "Le libell\xE9 de relev\xE9 bancaire ne peut pas correspondre \xE0 un nom d'entreprise g\xE9n\xE9rique ou bien connu.": "Der Kontoauszugsname darf nicht einem generischen oder bekannten Unternehmensnamen entsprechen.",
  "Le m\xEAme type de document a \xE9t\xE9 fourni deux fois.": "Dieselbe Art von Dokument wurde zweimal eingereicht.",
  "Le nom de l'organisation doit correspondre \xE0 l'activit\xE9 du compte et ne peut pas \xEAtre un nom g\xE9n\xE9rique ou bien connu.": "Der Name der Organisation muss zur T\xE4tigkeit des Kontos passen und darf kein generischer oder bekannter Name sein.",
  "Le nom de l'organisation doit \xEAtre facile \xE0 comprendre et compos\xE9 de mots reconnaissables.": "Der Name der Organisation muss leicht verst\xE4ndlich sein und aus erkennbaren W\xF6rtern bestehen.",
  "Le nom l\xE9gal de l'organisation ne peut pas \xEAtre un nom g\xE9n\xE9rique ou bien connu.": "Der rechtliche Name der Organisation darf kein generischer oder bekannter Name sein.",
  "Le num\xE9ro fiscal doit \xEAtre compos\xE9 de 9 chiffres, sans tirets ni autres caract\xE8res sp\xE9ciaux.": "Die Steuernummer muss aus 9 Ziffern bestehen, ohne Bindestriche oder andere Sonderzeichen.",
  "Le num\xE9ro IEC de l'organisation n'a pas pu \xEAtre v\xE9rifi\xE9.": "Die IEC-Nummer der Organisation konnte nicht verifiziert werden.",
  "Le pays du repr\xE9sentant n'est pas accept\xE9 pour ce compte.": "Das Land des Vertreters wird f\xFCr dieses Konto nicht akzeptiert.",
  "Le pr\xE9fixe du libell\xE9 de relev\xE9 bancaire doit correspondre \xE0 votre libell\xE9, au nom de votre organisation, \xE0 sa d\xE9nomination l\xE9gale ou \xE0 votre site web.": "Das Pr\xE4fix des Kontoauszugsnamens muss mit Ihrem Kontoauszugsnamen, dem Namen Ihrer Organisation, ihrer rechtlichen Bezeichnung oder Ihrer Website \xFCbereinstimmen.",
  "Le recto du document est manquant.": "Die Vorderseite des Dokuments fehlt.",
  "Le signataire du document n'a pas pu \xEAtre v\xE9rifi\xE9.": "Der Unterzeichner des Dokuments konnte nicht verifiziert werden.",
  "Le site web ne comporte pas de nom d'organisation ni de description claire des biens et services propos\xE9s.": "Die Website enth\xE4lt weder einen Namen der Organisation noch eine klare Beschreibung der angebotenen Waren und Dienstleistungen.",
  "Le site web ne contient pas d'informations sur le service client.": "Die Website enth\xE4lt keine Informationen zum Kundenservice.",
  "Le site web ne contient pas de conditions d'annulation.": "Die Website enth\xE4lt keine Stornierungsbedingungen.",
  "Le site web ne contient pas de conditions g\xE9n\xE9rales.": "Die Website enth\xE4lt keine Allgemeinen Gesch\xE4ftsbedingungen.",
  "Le site web ne contient pas de politique de remboursement.": "Die Website enth\xE4lt keine Erstattungsrichtlinie.",
  "Le site web ne contient pas de politique ni de processus de retour.": "Die Website enth\xE4lt keine R\xFCckgaberichtlinie und keinen R\xFCckgabeprozess.",
  "Le site web ne contient pas les mentions applicables aux produits et services soumis \xE0 des restrictions juridiques ou \xE0 l'exportation.": "Die Website enth\xE4lt nicht die f\xFCr Produkte und Dienstleistungen mit rechtlichen oder Exportbeschr\xE4nkungen geltenden Hinweise.",
  "Le type ou la structure de l'organisation ne semble pas correct. Indiquez le type et la structure appropri\xE9s pour ce compte.": "Art oder Rechtsform der Organisation scheint nicht korrekt zu sein. Geben Sie die f\xFCr dieses Konto passende Art und Rechtsform an.",
  "Le verso du document est manquant.": "Die R\xFCckseite des Dokuments fehlt.",
  "Les conditions d'utilisation de Stripe interdisent la prise en charge de cette organisation en raison du niveau de risque qu'elle pr\xE9sente. Le titulaire du compte peut contester cette d\xE9cision.": "Die Nutzungsbedingungen von Stripe untersagen die \xDCbernahme dieser Organisation aufgrund des von ihr ausgehenden Risikoniveaus. Der Kontoinhaber kann diese Entscheidung anfechten.",
  "Les conditions d'utilisation de Stripe interdisent la prise en charge de l'activit\xE9. Le compte peut faire appel de cette d\xE9cision.": "Die Nutzungsbedingungen von Stripe untersagen die \xDCbernahme der T\xE4tigkeit. Das Konto kann gegen diese Entscheidung Widerspruch einlegen.",
  "Les dirigeants d\xE9clar\xE9s ne correspondent pas \xE0 ceux que Stripe a trouv\xE9s.": "Die angegebenen F\xFChrungskr\xE4fte stimmen nicht mit denen \xFCberein, die Stripe gefunden hat.",
  "Les dirigeants mentionn\xE9s dans le document ne figurent pas dans le compte. Mettez \xE0 jour le compte et t\xE9l\xE9chargez un document d'immatriculation avec la liste des dirigeants actuels.": "Die im Dokument genannten F\xFChrungskr\xE4fte sind im Konto nicht enthalten. Aktualisieren Sie das Konto und laden Sie einen Registerauszug mit der aktuellen Liste der F\xFChrungskr\xE4fte hoch.",
  "Les informations du site web ne correspondent pas \xE0 celles du compte Stripe.": "Die Website-Informationen stimmen nicht mit denen des Stripe-Kontos \xFCberein.",
  "Les informations figurant sur la pi\xE8ce d'identit\xE9 ne correspondent pas \xE0 celles fournies. V\xE9rifiez et corrigez les informations fournies.": "Die Angaben auf dem Ausweisdokument stimmen nicht mit den angegebenen Informationen \xFCberein. Pr\xFCfen Sie die angegebenen Informationen und korrigieren Sie sie.",
  "Les informations figurant sur le document ne correspondent pas \xE0 celles fournies. V\xE9rifiez-les, puis corrigez-les ou t\xE9l\xE9chargez un document correspondant.": "Die Angaben auf dem Dokument stimmen nicht mit den angegebenen Informationen \xFCberein. Pr\xFCfen Sie diese und korrigieren Sie sie oder laden Sie ein passendes Dokument hoch.",
  "Libell\xE9 de relev\xE9 bancaire": "Bezeichnung auf dem Kontoauszug",
  "Libell\xE9 qui appara\xEEtra sur le relev\xE9 bancaire des donateurs": "Name, der auf dem Kontoauszug der Spender erscheint",
  "Licence ou autorisation d'exercer": "Lizenz oder Zulassung zur Aus\xFCbung",
  "Motif indiqu\xE9 par Stripe : {motif}.": "Von Stripe angegebener Grund: {motif}.",
  "Nationalit\xE9": "Staatsangeh\xF6rigkeit",
  "Nom commercial / d'affichage de l'organisation": "Name der Organisation (Handelsname / Anzeigename)",
  "Nom de famille": "Nachname",
  "Nom l\xE9gal de l'organisation": "Rechtlicher Name der Organisation",
  "Non": "Nein",
  "Num\xE9ro d'enregistrement de l'organisation": "Registernummer der Organisation",
  "Num\xE9ro d'identification (\xE9quivalent du num\xE9ro de s\xE9curit\xE9 sociale)": "Identifikationsnummer (Entsprechung der Sozialversicherungsnummer)",
  "Num\xE9ro de t\xE9l\xE9phone": "Telefonnummer",
  "Num\xE9ro de t\xE9l\xE9phone de l'organisation": "Telefonnummer der Organisation",
  "Num\xE9ro de TVA intracommunautaire": "Umsatzsteuer-Identifikationsnummer (USt-IdNr.)",
  "Num\xE9ro SIRET / identifiant fiscal": "SIRET-Nummer / Steuerkennung",
  "Oui": "Ja",
  "Pays": "Land",
  "Pays de l'organisation": "Land der Organisation",
  "Pi\xE8ce d'identit\xE9 (passeport, carte nationale d'identit\xE9 ou permis de conduire)": "Ausweisdokument (Reisepass, Personalausweis oder F\xFChrerschein)",
  "Politique d'ex\xE9cution des commandes": "Richtlinie zur Auftragsabwicklung",
  "Pourcentage de propri\xE9t\xE9": "Eigentumsanteil in Prozent",
  "Pr\xE9fixe du libell\xE9 sur le relev\xE9 bancaire": "Pr\xE4fix des Namens auf dem Kontoauszug",
  "Pr\xE9nom": "Vorname",
  "Preuve de pr\xE9sence (selfie)": "Anwesenheitsnachweis (Selfie)",
  "Produits ou services prot\xE9g\xE9s par le droit d'auteur": "Urheberrechtlich gesch\xFCtzte Produkte oder Dienstleistungen",
  "Propri\xE9taire \u2014 {champ}": "Eigent\xFCmer \u2014 {champ}",
  "Recours contre un refus": "Einspruch gegen eine Ablehnung",
  "Recours contre un refus de prise en charge": "Widerspruch gegen eine Ablehnung der Annahme",
  "Recours contre une r\xE9serve sur le compte": "Einspruch gegen einen Einbehalt auf dem Konto",
  "Recto de la pi\xE8ce d'identit\xE9": "Vorderseite des Ausweisdokuments",
  "Repr\xE9sentant l\xE9gal \u2014 {champ}": "Gesetzlicher Vertreter \u2014 {champ}",
  "Selon Stripe, ce compte semble proposer des produits ou services relevant d'une cat\xE9gorie interdite ou restreinte par le contrat de services Stripe. Cat\xE9gorie : {categorie}. Plus d'informations : {lien}": "Laut Stripe scheint dieses Konto Produkte oder Dienstleistungen anzubieten, die unter eine Kategorie fallen, die im Stripe-Servicevertrag verboten oder eingeschr\xE4nkt ist. Kategorie: {categorie}. Weitere Informationen: {lien}",
  "Selon Stripe, certaines fonctionnalit\xE9s doivent encore \xEAtre demand\xE9es pour ce compte par la plateforme Donoor.": "Laut Stripe m\xFCssen f\xFCr dieses Konto noch Funktionen von der Plattform Donoor angefordert werden.",
  "Selon Stripe, cette demande ne se traite pas dans un formulaire : contactez le service d'assistance de Stripe.": "Laut Stripe l\xE4sst sich diese Anfrage nicht \xFCber ein Formular bearbeiten: Wenden Sie sich an den Stripe-Support.",
  "Selon Stripe, cette demande se r\xE8gle par l'interm\xE9diaire d'un tiers.": "Laut Stripe wird diese Anfrage \xFCber einen Dritten gekl\xE4rt.",
  "Selon Stripe, cette demande se remplit dans un formulaire Stripe.": "Laut Stripe wird diese Anfrage \xFCber ein Stripe-Formular ausgef\xFCllt.",
  "Selon Stripe, des informations suppl\xE9mentaires sont requises pour activer les fonctionnalit\xE9s de ce compte.": "Laut Stripe sind zus\xE4tzliche Informationen erforderlich, um die Funktionen dieses Kontos zu aktivieren.",
  "Selon Stripe, l'organisation peut exercer ses activit\xE9s dans un secteur r\xE9glement\xE9. Stripe pourrait avoir besoin d'informations suppl\xE9mentaires sur la nature de l'activit\xE9 ou sur les licences requises afin de v\xE9rifier qu'il peut prendre le compte en charge.": "Laut Stripe kann die Organisation in einem regulierten Sektor t\xE4tig sein. Stripe ben\xF6tigt m\xF6glicherweise zus\xE4tzliche Informationen zur Art der T\xE4tigkeit oder zu den erforderlichen Lizenzen, um zu pr\xFCfen, ob das Konto \xFCbernommen werden kann.",
  "Selon Stripe, le compte pourrait figurer sur une liste de personnes ou d'entreprises interdites. Stripe effectue une v\xE9rification et pourra refuser ou r\xE9tablir l'acc\xE8s au compte en cons\xE9quence.": "Laut Stripe k\xF6nnte das Konto auf einer Liste verbotener Personen oder Unternehmen stehen. Stripe f\xFChrt eine Pr\xFCfung durch und kann den Zugang zum Konto entsprechend verweigern oder wiederherstellen.",
  "Selon Stripe, vous devez r\xE9pondre vous-m\xEAme \xE0 cette demande de v\xE9rification.": "Laut Stripe m\xFCssen Sie diese Verifizierungsanfrage selbst beantworten.",
  "Site web de l'organisation": "Website der Organisation",
  "Statut d'exposition politique": "Status der politischen Exponierung",
  "Statuts de l'organisation": "Satzung der Organisation",
  "Stripe a besoin d'informations suppl\xE9mentaires pour v\xE9rifier qu'il peut prendre le compte en charge.": "Stripe ben\xF6tigt weitere Informationen, um zu pr\xFCfen, ob das Konto \xFCbernommen werden kann.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur l'organisation.": "Stripe ben\xF6tigt weitere Informationen zur Organisation.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur la conformit\xE9.": "Stripe ben\xF6tigt weitere Informationen zur Compliance.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur la nature de l'activit\xE9 afin de v\xE9rifier qu'il peut prendre le compte en charge.": "Stripe ben\xF6tigt zus\xE4tzliche Informationen zur Art der Gesch\xE4ftst\xE4tigkeit, um zu pr\xFCfen, ob das Konto \xFCbernommen werden kann.",
  "Stripe a besoin d'un libell\xE9 de relev\xE9 bancaire qui corresponde exactement \xE0 l'activit\xE9 de l'organisation.": "Stripe ben\xF6tigt eine Bezeichnung f\xFCr den Kontoauszug, die genau der T\xE4tigkeit der Organisation entspricht.",
  "Stripe a demand\xE9 des informations suppl\xE9mentaires dans le cadre de son \xE9valuation des risques.": "Stripe hat im Rahmen seiner Risikobewertung zus\xE4tzliche Informationen angefordert.",
  "Stripe a d\xE9tect\xE9 que ce compte utilise un site web, un profil sur les r\xE9seaux sociaux ou une application pour vendre ou promouvoir des produits ou services, mais aucune adresse n'a \xE9t\xE9 fournie. Fournissez une adresse.": "Stripe hat festgestellt, dass dieses Konto eine Website, ein Social-Media-Profil oder eine App nutzt, um Produkte oder Dienstleistungen zu verkaufen oder zu bewerben, es wurde jedoch keine Adresse angegeben. Geben Sie eine Adresse an.",
  "Stripe a impos\xE9 une r\xE9serve au compte, ce qui n'affecte pas sa capacit\xE9 \xE0 accepter des paiements. Le titulaire du compte peut contester cette d\xE9cision.": "Stripe hat einen Einbehalt auf das Konto verh\xE4ngt. Dieser wirkt sich nicht auf die M\xF6glichkeit aus, Zahlungen anzunehmen. Der Kontoinhaber kann diese Entscheidung anfechten.",
  "Stripe a refus\xE9 le compte, car il figure sur une liste de personnes ou d'entreprises interdites \xE9tablie par un tiers (par exemple un fournisseur de services financiers ou un gouvernement).": "Stripe hat das Konto verweigert, da es auf einer Liste verbotener Personen oder Unternehmen steht, die von einem Dritten erstellt wurde (z. B. einem Finanzdienstleister oder einer Regierung).",
  "Stripe a refus\xE9 le motif d'exemption relatif \xE0 la propri\xE9t\xE9. Choisissez un autre motif ou t\xE9l\xE9chargez un justificatif de b\xE9n\xE9ficiaire effectif.": "Stripe hat die Ausnahmebegr\xFCndung in Bezug auf das wirtschaftliche Eigentum abgelehnt. W\xE4hlen Sie eine andere Begr\xFCndung oder laden Sie einen Nachweis \xFCber den wirtschaftlich Berechtigten hoch.",
  "Stripe a rejet\xE9 le compte pour suspicion d'infraction \xE0 ses conditions d'utilisation.": "Stripe hat das Konto wegen des Verdachts auf einen Versto\xDF gegen die Nutzungsbedingungen abgelehnt.",
  "Stripe a rejet\xE9 le compte pour suspicion de fraude ou d'activit\xE9s ill\xE9gales.": "Stripe hat das Konto wegen des Verdachts auf Betrug oder illegale Aktivit\xE4ten abgelehnt.",
  "Stripe a rejet\xE9 le compte pour un autre motif, sans le pr\xE9ciser.": "Stripe hat das Konto aus einem anderen, nicht n\xE4her angegebenen Grund abgelehnt.",
  "Stripe a rejet\xE9 le compte, car les exigences de v\xE9rification n'ont pas \xE9t\xE9 satisfaites dans le d\xE9lai requis.": "Stripe hat das Konto abgelehnt, da die \xDCberpr\xFCfungsanforderungen innerhalb der vorgegebenen Frist nicht erf\xFCllt wurden.",
  "Stripe a restreint le compte sans pr\xE9ciser de motif.": "Stripe hat das Konto ohne Angabe eines Grundes eingeschr\xE4nkt.",
  "Stripe demande les statuts de soci\xE9t\xE9s qui d\xE9tiennent une participation importante.": "Stripe fordert die Satzung von Gesellschaften an, die eine wesentliche Beteiligung halten.",
  "Stripe demande un justificatif d'immatriculation de soci\xE9t\xE9s qui d\xE9tiennent une participation importante.": "Stripe fordert einen Registerauszug von Gesellschaften an, die eine wesentliche Beteiligung halten.",
  "Stripe doit v\xE9rifier l'adresse de l'organisation, avec les justificatifs appropri\xE9s.": "Stripe muss die Adresse der Organisation mit geeigneten Nachweisen \xFCberpr\xFCfen.",
  "Stripe doit v\xE9rifier la politique d'ex\xE9cution des commandes de l'organisation.": "Stripe muss die Richtlinie zur Auftragsabwicklung der Organisation \xFCberpr\xFCfen.",
  "Stripe doit v\xE9rifier les coordonn\xE9es du service client de l'organisation.": "Stripe muss die Kontaktdaten des Kundenservices der Organisation \xFCberpr\xFCfen.",
  "Stripe doit v\xE9rifier les informations du compte bancaire associ\xE9 \xE0 l'organisation.": "Stripe muss die Angaben zum mit der Organisation verbundenen Bankkonto \xFCberpr\xFCfen.",
  "Stripe doit v\xE9rifier que l'activit\xE9 de ce compte peut \xEAtre prise en charge sur sa plateforme.": "Stripe muss pr\xFCfen, ob die T\xE4tigkeit dieses Kontos auf seiner Plattform unterst\xFCtzt werden kann.",
  "Stripe doit v\xE9rifier que l'organisation n'est pas en relation avec une personne ou une juridiction sanctionn\xE9e.": "Stripe muss pr\xFCfen, ob die Organisation nicht mit einer sanktionierten Person oder einem sanktionierten Rechtsgebiet in Verbindung steht.",
  "Stripe doit v\xE9rifier que l'organisation n'est pas li\xE9e \xE0 une personne d'int\xE9r\xEAt ou \xE0 une personne politiquement expos\xE9e.": "Stripe muss pr\xFCfen, ob die Organisation nicht mit einer Person von besonderem Interesse oder einer politisch exponierten Person verbunden ist.",
  "Stripe doit v\xE9rifier que le titulaire du compte contr\xF4le bien l'adresse ou le nom de domaine qu'il a fourni.": "Stripe muss \xFCberpr\xFCfen, ob der Kontoinhaber tats\xE4chlich die Kontrolle \xFCber die von ihm angegebene Adresse bzw. den angegebenen Domainnamen hat.",
  "Stripe est tenu de conserver des fonds pour une raison l\xE9gale. Il peut \xEAtre n\xE9cessaire de remettre des fonds \xE0 un tiers.": "Stripe ist gesetzlich verpflichtet, Gelder einzubehalten. Gegebenenfalls m\xFCssen Gelder an einen Dritten ausgezahlt werden.",
  "Stripe n'a pas pu acc\xE9der au site web. Si vous bloquez l'acc\xE8s \xE0 votre site dans certaines r\xE9gions, d\xE9sactivez temporairement ce blocage jusqu'\xE0 la v\xE9rification.": "Stripe konnte nicht auf die Website zugreifen. Falls Sie den Zugriff auf Ihre Website in bestimmten Regionen blockieren, deaktivieren Sie diese Sperre vor\xFCbergehend bis zur \xDCberpr\xFCfung.",
  "Stripe n'a pas pu confirmer ces informations : {champs}. Corrigez-les si elles sont fausses, ou fournissez \xE0 la place {pistes}.": "Stripe konnte diese Angaben nicht best\xE4tigen: {champs}. Korrigieren Sie sie, falls sie falsch sind, oder reichen Sie stattdessen {pistes} ein.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Corrigez-les si elles sont fausses, ou prouvez l'identit\xE9 autrement.": "Stripe konnte die Identit\xE4t anhand der eingegebenen Angaben nicht best\xE4tigen. Korrigieren Sie sie, falls sie falsch sind, oder weisen Sie die Identit\xE4t auf andere Weise nach.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Fournissez {pistes} : une seule suffit. Si une information est fausse, vous pouvez aussi la corriger.": "Stripe konnte die Identit\xE4t anhand der eingegebenen Angaben nicht best\xE4tigen. Reichen Sie {pistes} ein \u2013 eines davon gen\xFCgt. Falls eine Angabe falsch ist, k\xF6nnen Sie sie auch korrigieren.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Fournissez {pistes}. Si une information est fausse, vous pouvez aussi la corriger.": "Stripe konnte die Identit\xE4t anhand der eingegebenen Angaben nicht best\xE4tigen. Reichen Sie {pistes} ein. Falls eine Angabe falsch ist, k\xF6nnen Sie sie auch korrigieren.",
  "Stripe n'a pas pu valider l'ensemble ville, r\xE9gion et code postal de l'adresse fournie.": "Stripe konnte die Kombination aus Stadt, Region und Postleitzahl der angegebenen Adresse nicht best\xE4tigen.",
  "Stripe n'a pas pu valider le nom de la rue ni le num\xE9ro de l'adresse fournie.": "Stripe konnte den Stra\xDFennamen und die Hausnummer der angegebenen Adresse nicht best\xE4tigen.",
  "Stripe n'a pas pu valider le num\xE9ro de t\xE9l\xE9phone. Son format doit correspondre \xE0 celui du pays de la personne concern\xE9e.": "Stripe konnte die Telefonnummer nicht best\xE4tigen. Ihr Format muss dem Format des Landes der betreffenden Person entsprechen.",
  "Stripe n'a pas pu v\xE9rifier l'activit\xE9 du compte \xE0 l'adresse fournie (site web, profil de r\xE9seaux sociaux ou application).": "Stripe konnte die T\xE4tigkeit des Kontos unter der angegebenen Adresse (Website, Social-Media-Profil oder App) nicht \xFCberpr\xFCfen.",
  "Stripe n'a pas pu v\xE9rifier le site web, car il est prot\xE9g\xE9 par un mot de passe.": "Stripe konnte die Website nicht \xFCberpr\xFCfen, da sie passwortgesch\xFCtzt ist.",
  "Stripe n'a pas pu v\xE9rifier le site web, car il est toujours en construction.": "Stripe konnte die Website nicht \xFCberpr\xFCfen, da sie sich noch im Aufbau befindet.",
  "Stripe n'a pas pu v\xE9rifier le site web, car il ne pr\xE9sente aucun contenu.": "Stripe konnte die Website nicht \xFCberpr\xFCfen, da sie keine Inhalte enth\xE4lt.",
  "Stripe n'a pas pu v\xE9rifier le site web, car son acc\xE8s est bloqu\xE9 dans certaines r\xE9gions. D\xE9sactivez temporairement ce blocage jusqu'\xE0 la v\xE9rification.": "Stripe konnte die Website nicht \xFCberpr\xFCfen, da der Zugriff in bestimmten Regionen blockiert ist. Deaktivieren Sie diese Sperre vor\xFCbergehend bis zur \xDCberpr\xFCfung.",
  "Stripe n'a pas pu v\xE9rifier les informations du compte. Vous pouvez t\xE9l\xE9charger un document de v\xE9rification ou mettre \xE0 jour vos informations.": "Stripe konnte die Kontoinformationen nicht verifizieren. Sie k\xF6nnen ein Verifizierungsdokument hochladen oder Ihre Informationen aktualisieren.",
  "Stripe n'a pas pu v\xE9rifier les pouvoirs de la personne qui autorise le compte. Remplacez-la par une personne d\xFBment enregistr\xE9e en tant que repr\xE9sentant autoris\xE9.": "Stripe konnte die Vollmacht der Person, die das Konto autorisiert, nicht verifizieren. Ersetzen Sie diese Person durch eine ordnungsgem\xE4\xDF als bevollm\xE4chtigter Vertreter registrierte Person.",
  "Stripe n'a pas pu v\xE9rifier les pouvoirs du repr\xE9sentant du compte. Ajoutez une personne qui autorise le compte et fournissez une lettre d'autorisation sign\xE9e par elle.": "Stripe konnte die Vollmacht des Kontovertreters nicht verifizieren. F\xFCgen Sie eine Person hinzu, die das Konto autorisiert, und legen Sie ein von dieser Person unterzeichnetes Autorisierungsschreiben vor.",
  "Stripe n'a pas trouv\xE9 d'informations \xE0 partir des donn\xE9es saisies. V\xE9rifiez qu'elles sont correctes.": "Stripe hat anhand der eingegebenen Daten keine Informationen gefunden. Pr\xFCfen Sie, ob diese korrekt sind.",
  "Stripe signale une erreur de v\xE9rification (code : {code}).": "Stripe meldet einen Verifizierungsfehler (Code: {code}).",
  "Stripe v\xE9rifie actuellement les informations du compte. Aucune action n'est requise.": "Stripe \xFCberpr\xFCft derzeit die Kontoinformationen. Es ist keine Ma\xDFnahme erforderlich.",
  "T\xE9l\xE9phone du service donateurs / support": "Telefonnummer des Spender- / Support-Teams",
  "Une information est manquante.": "Eine Angabe fehlt.",
  "Une information est manquante. D\xE9tail donn\xE9 par Stripe : \xAB {detail} \xBB": "Eine Angabe fehlt. Von Stripe angegebenes Detail: \u201E{detail}\u201C",
  "une pi\xE8ce d'identit\xE9": "ein Ausweisdokument",
  "une pi\xE8ce d'identit\xE9 ou un justificatif de domicile": "ein Ausweisdokument oder einen Adressnachweis",
  "une v\xE9rification par selfie": "eine Selfie-Verifizierung",
  "URL du service donateurs / support": "URL des Spender- / Support-Teams",
  "V\xE9rification d'identit\xE9": "Identit\xE4tspr\xFCfung",
  "V\xE9rification de l'adresse": "Adresspr\xFCfung",
  "V\xE9rification des personnes politiquement expos\xE9es": "Pr\xFCfung politisch exponierter Personen",
  "V\xE9rification du compte bancaire": "Pr\xFCfung des Bankkontos",
  "V\xE9rification du nom de domaine": "\xDCberpr\xFCfung des Domainnamens",
  "V\xE9rification li\xE9e aux sanctions": "Sanktionspr\xFCfung",
  "V\xE9rification Stripe en cours": "Stripe-\xDCberpr\xFCfung l\xE4uft",
  "Verso de la pi\xE8ce d'identit\xE9": "R\xFCckseite des Ausweisdokuments",
  "Ville": "Stadt",
  "Ville de l'organisation": "Ort der Organisation"
};
var de_default = textes2;

// src/textesStripe/es.ts
var textes3 = {
  "{liste}, ou {piste}": "{liste} o {piste}",
  "4 derniers chiffres du num\xE9ro de s\xE9curit\xE9 sociale": "\xDAltimos 4 d\xEDgitos del n\xFAmero de la seguridad social",
  "Acceptation des conditions d'utilisation Stripe": "Aceptaci\xF3n de las condiciones de uso de Stripe",
  "Activit\xE9 dans un secteur r\xE9glement\xE9": "Actividad en un sector regulado",
  "Adresse de l'organisation (compl\xE9ment)": "Direcci\xF3n de la organizaci\xF3n (complemento)",
  "Adresse de l'organisation (rue)": "Direcci\xF3n de la organizaci\xF3n (calle)",
  "Adresse du service donateurs / support": "Direcci\xF3n del servicio para donantes / soporte",
  "Adresse du site web": "Direcci\xF3n del sitio web",
  "Adresse email": "Correo electr\xF3nico",
  "Adresse IP d'acceptation des CGU": "Direcci\xF3n IP de aceptaci\xF3n de las condiciones de uso",
  "Adresse personnelle (compl\xE9ment)": "Direcci\xF3n personal (complemento)",
  "Adresse personnelle (rue)": "Direcci\xF3n personal (calle)",
  "associations caritatives sans statut d'enregistrement au dossier (par exemple un enregistrement 501(c)(3) ou une classification \xE9quivalente)": "organizaciones ben\xE9ficas sin estatus de inscripci\xF3n en el expediente (por ejemplo, una inscripci\xF3n 501(c)(3) o una clasificaci\xF3n equivalente)",
  "Aucun propri\xE9taire n'a \xE9t\xE9 indiqu\xE9. Fournissez des informations sur tous les propri\xE9taires.": "No se ha indicado ning\xFAn titular. Proporcione informaci\xF3n sobre todos los titulares.",
  "Cadre dirigeant \u2014 {champ}": "Directivo \u2014 {champ}",
  "Cat\xE9gorie d'activit\xE9 (code MCC)": "Categor\xEDa de actividad (c\xF3digo MCC)",
  "Ce type d'organisation n'est pas pris en charge par Stripe.": "Stripe no admite este tipo de organizaci\xF3n.",
  "Code postal": "C\xF3digo postal",
  "Code postal de l'organisation": "C\xF3digo postal de la organizaci\xF3n",
  "Compte bancaire pour les versements (IBAN)": "Cuenta bancaria para los pagos (IBAN)",
  "Compte en cours de v\xE9rification": "Cuenta en proceso de verificaci\xF3n",
  "Compte refus\xE9": "Cuenta denegada",
  "Compte rejet\xE9": "Cuenta rechazada",
  "Compte rejet\xE9 \u2014 Conditions d'utilisation": "Cuenta rechazada \u2014 Condiciones de uso",
  "Compte rejet\xE9 \u2014 v\xE9rification incompl\xE8te": "Cuenta rechazada \u2014 verificaci\xF3n incompleta",
  "Compte restreint par Stripe": "Cuenta restringida por Stripe",
  "Compte suspendu par Donoor": "Cuenta suspendida por Donoor",
  "Confirmation que tous les cadres dirigeants ont \xE9t\xE9 d\xE9clar\xE9s": "Confirmaci\xF3n de que se han declarado todos los directivos",
  "Confirmation que tous les dirigeants ont \xE9t\xE9 d\xE9clar\xE9s": "Confirmaci\xF3n de que se han declarado todos los directores",
  "Confirmation que tous les propri\xE9taires ont \xE9t\xE9 d\xE9clar\xE9s": "Confirmaci\xF3n de que se han declarado todos los propietarios",
  "Contactez le service d'assistance de Stripe pour conna\xEEtre les raisons de l'\xE9chec de la v\xE9rification.": "P\xF3ngase en contacto con el servicio de atenci\xF3n al cliente de Stripe para conocer los motivos por los que ha fallado la verificaci\xF3n.",
  "Coordonn\xE9es du service client": "Datos de contacto del servicio de atenci\xF3n al cliente",
  "Date de naissance": "Fecha de nacimiento",
  "D\xE9cret minist\xE9riel (le cas \xE9ch\xE9ant)": "Decreto ministerial (si procede)",
  "Demande d'informations de Stripe": "Solicitud de informaci\xF3n de Stripe",
  "Demande de conformit\xE9": "Solicitud de cumplimiento normativo",
  "D\xE9partement / r\xE9gion": "Provincia / regi\xF3n",
  "D\xE9partement / r\xE9gion de l'organisation": "Provincia / regi\xF3n de la organizaci\xF3n",
  "Des cadres dirigeants n'ont pas \xE9t\xE9 indiqu\xE9s.": "No se han indicado algunos directivos.",
  "Des dirigeants en trop ont \xE9t\xE9 d\xE9clar\xE9s.": "Se han declarado directivos de m\xE1s.",
  "Description de votre activit\xE9": "Descripci\xF3n de su actividad",
  "Description des produits ou services": "Descripci\xF3n de los productos o servicios",
  "Dirigeant \u2014 {champ}": "Director \u2014 {champ}",
  "Document": "Documento",
  "Document officiel de l'organisation": "Documento oficial de la organizaci\xF3n",
  "Document refus\xE9 (mode test).": "Documento rechazado (modo de prueba).",
  "Elle ne peut pas \xEAtre un bo\xEEtier de contrat routier.": "No puede tratarse de un apartado de correos de una empresa de transporte por carretera.",
  "Elle ne peut pas \xEAtre une bo\xEEte postale priv\xE9e.": "No puede tratarse de un apartado de correos privado.",
  "Email du service donateurs / support": "Correo electr\xF3nico del servicio para donantes / soporte",
  "Examen de la nature de votre activit\xE9": "Revisi\xF3n de la naturaleza de su actividad",
  "Fonction au sein de l'organisation": "Cargo en la organizaci\xF3n",
  "Fonctionnalit\xE9s \xE0 demander": "Funciones que solicitar",
  "Fonds retenus pour raison l\xE9gale": "Fondos retenidos por motivos legales",
  "Forme juridique de l'organisation": "Forma jur\xEDdica de la organizaci\xF3n",
  "Fournissez un nouveau fichier : image en couleur, 10 Mo maximum, pi\xE8ces d'identit\xE9 en JPG ou PNG, justificatifs d'adresse ou d'entit\xE9 juridique en JPG, PNG ou PDF, toutes les pages incluses, sans mot de passe.": "Suba un nuevo archivo: imagen en color, 10 MB como m\xE1ximo, documentos de identidad en JPG o PNG, justificantes de domicilio o de entidad jur\xEDdica en JPG, PNG o PDF, con todas las p\xE1ginas incluidas y sin contrase\xF1a.",
  "Genre": "G\xE9nero",
  "Il se peut que le document ait \xE9t\xE9 modifi\xE9. Contactez le service d'assistance de Stripe pour conna\xEEtre les raisons de l'\xE9chec de la v\xE9rification.": "Es posible que el documento haya sido modificado. P\xF3ngase en contacto con el servicio de atenci\xF3n al cliente de Stripe para conocer los motivos por los que ha fallado la verificaci\xF3n.",
  "Informations \xE0 confirmer": "Informaci\xF3n por confirmar",
  "Informations \xE0 fournir": "Informaci\xF3n que proporcionar",
  "Informations compl\xE9mentaires sur votre activit\xE9": "Informaci\xF3n adicional sobre su actividad",
  "Informations sur l'organisation": "Informaci\xF3n sobre la organizaci\xF3n",
  "Informations sur la nature de votre activit\xE9": "Informaci\xF3n sobre la naturaleza de su actividad",
  "Intervention de la plateforme": "Intervenci\xF3n de la plataforma",
  "Justificatif d'enregistrement de l'organisation": "Justificante de registro de la organizaci\xF3n",
  "Justificatif de domicile (facture d'\xE9nergie, quittance de loyer, etc.)": "Comprobante de domicilio (factura de suministros, recibo de alquiler, etc.)",
  "Justificatif du num\xE9ro fiscal (SIRET)": "Justificante del n\xFAmero fiscal (SIRET)",
  "L'acceptation des conditions d'utilisation de Stripe n'est plus valide.": "La aceptaci\xF3n de los t\xE9rminos de servicio de Stripe ya no es v\xE1lida.",
  "L'administration fiscale n'a pas pu v\xE9rifier les informations fournies. Corrigez le nom de l'organisation ou le num\xE9ro fiscal, ou t\xE9l\xE9chargez un document justificatif.": "La administraci\xF3n tributaria no ha podido verificar la informaci\xF3n proporcionada. Corrija el nombre de la organizaci\xF3n o el n\xFAmero de identificaci\xF3n fiscal, o suba un documento justificativo.",
  "L'adresse de r\xE9sidence n'a pas pu \xEAtre v\xE9rifi\xE9e.": "No se ha podido verificar la direcci\xF3n de residencia.",
  "L'adresse doit \xEAtre une adresse physique valide \xE0 partir de laquelle le compte exerce ses activit\xE9s.": "La direcci\xF3n debe ser una direcci\xF3n f\xEDsica v\xE1lida desde la que la cuenta desarrolla su actividad.",
  "L'adresse du site correspond \xE0 un site g\xE9n\xE9rique que Stripe consid\xE8re comme sans lien avec le compte. Fournissez une adresse propre \xE0 votre organisation.": "La direcci\xF3n del sitio corresponde a un sitio gen\xE9rico que Stripe considera no relacionado con la cuenta. Proporcione una direcci\xF3n propia de su organizaci\xF3n.",
  "L'adresse du site de l'organisation doit faire r\xE9f\xE9rence aux produits et services qu'elle fournit. Stripe pourrait exiger une modification de cette adresse avant de pouvoir prendre le compte en charge.": "La direcci\xF3n del sitio web de la organizaci\xF3n debe hacer referencia a los productos y servicios que ofrece. Stripe podr\xEDa exigir una modificaci\xF3n de esta direcci\xF3n antes de poder aceptar la cuenta.",
  "L'adresse fournie est celle d'un agent enregistr\xE9, qui n'est pas accept\xE9e.": "La direcci\xF3n proporcionada corresponde a un agente registrado, que no se acepta.",
  "L'adresse fournie est celle d'un service de r\xE9ception de courrier (CMRA), qui n'est pas accept\xE9e.": "La direcci\xF3n proporcionada corresponde a un servicio de recepci\xF3n de correo (CMRA), que no se acepta.",
  "L'organisation pourrait vendre des produits ou des services prot\xE9g\xE9s par le droit d'auteur. Stripe a besoin d'informations suppl\xE9mentaires pour v\xE9rifier que le compte est autoris\xE9 \xE0 les vendre.": "La organizaci\xF3n podr\xEDa vender productos o servicios protegidos por derechos de autor. Stripe necesita informaci\xF3n adicional para verificar que la cuenta est\xE1 autorizada a venderlos.",
  "La date de naissance doit se situer dans les 120 derni\xE8res ann\xE9es.": "La fecha de nacimiento debe estar dentro de los \xFAltimos 120 a\xF1os.",
  "La description doit comporter au moins 10 caract\xE8res.": "La descripci\xF3n debe tener al menos 10 caracteres.",
  "La description doit \xEAtre diff\xE9rente de l'adresse du site web.": "La descripci\xF3n debe ser distinta de la direcci\xF3n del sitio web.",
  "La liste des dirigeants n'a pas \xE9t\xE9 fournie. Mettez \xE0 jour le compte et t\xE9l\xE9chargez un document d'immatriculation avec la liste des dirigeants actuels.": "No se ha proporcionado la lista de directivos. Actualice la cuenta y suba un documento de registro con la lista de directivos actuales.",
  "La personne doit avoir au moins 13 ans.": "La persona debe tener al menos 13 a\xF1os.",
  "La personne doit avoir au moins 18 ans.": "La persona debe tener al menos 18 a\xF1os.",
  "La personne responsable du compte doit v\xE9rifier son identit\xE9 en t\xE9l\xE9chargeant une pi\xE8ce d'identit\xE9 officielle et un selfie.": "La persona responsable de la cuenta debe verificar su identidad subiendo un documento de identidad oficial y un selfi.",
  "La plateforme a initi\xE9 une intervention sur ce compte.": "La plataforma ha iniciado una intervenci\xF3n en esta cuenta.",
  "La plateforme Donoor a suspendu ce compte. Contactez le support Donoor.": "La plataforma Donoor ha suspendido esta cuenta. P\xF3ngase en contacto con el servicio de asistencia de Donoor.",
  "La valeur fournie n'est pas valide.": "El valor proporcionado no es v\xE1lido.",
  "Le compte est en cours de v\xE9rification par Stripe.": "Stripe est\xE1 verificando la cuenta.",
  "Le compte Stripe de l'organisation doit contenir une description exacte des produits ou services.": "La cuenta de Stripe de la organizaci\xF3n debe contener una descripci\xF3n exacta de los productos o servicios.",
  "Le document a \xE9t\xE9 refus\xE9 : il est en niveaux de gris.": "El documento ha sido rechazado: est\xE1 en escala de grises.",
  "Le document a \xE9t\xE9 refus\xE9 : il s'agit d'une copie.": "El documento ha sido rechazado: se trata de una copia.",
  "Le document est en niveaux de gris.": "El documento est\xE1 en escala de grises.",
  "Le document est incomplet.": "El documento est\xE1 incompleto.",
  "Le document est une copie.": "El documento es una copia.",
  "Le document n'est pas lisible.": "El documento no es legible.",
  "Le document n'est pas sign\xE9.": "El documento no est\xE1 firmado.",
  "Le document ne comporte pas de date de d\xE9livrance ou d'expiration, ou il est expir\xE9. Une pi\xE8ce d'identit\xE9 doit \xEAtre valide \xE0 la date d'envoi ; un justificatif de domicile doit dater de moins de six mois.": "El documento no incluye fecha de expedici\xF3n o de caducidad, o est\xE1 caducado. Un documento de identidad debe estar vigente en la fecha de env\xEDo; un justificante de domicilio debe tener menos de seis meses.",
  "Le document t\xE9l\xE9charg\xE9 ne contient pas toutes les informations requises. T\xE9l\xE9chargez un autre document contenant les informations manquantes.": "El documento subido no contiene toda la informaci\xF3n necesaria. Suba otro documento que contenga la informaci\xF3n que falta.",
  "Le fichier est corrompu.": "El archivo est\xE1 da\xF1ado.",
  "Le fichier est trop volumineux.": "El archivo es demasiado grande.",
  "Le fichier fourni n'est pas une pi\xE8ce accept\xE9e par Stripe (pays pris en charge, type de document attendu pour ce type d'organisation). Fournissez un nouveau fichier conforme.": "El archivo proporcionado no es un documento aceptado por Stripe (pa\xEDs admitido, tipo de documento esperado para este tipo de organizaci\xF3n). Proporcione un nuevo archivo conforme.",
  "Le fichier n'a pas \xE9t\xE9 t\xE9l\xE9charg\xE9.": "El archivo no se ha cargado.",
  "Le format de l'adresse du site est incorrect. Fournissez une adresse correctement format\xE9e, par exemple https://example.com.": "El formato de la direcci\xF3n del sitio es incorrecto. Proporcione una direcci\xF3n con formato correcto, por ejemplo https://example.com.",
  "Le libell\xE9 de relev\xE9 bancaire doit comporter au moins 5 caract\xE8res.": "El descriptor del extracto bancario debe tener al menos 5 caracteres.",
  "Le libell\xE9 de relev\xE9 bancaire doit \xEAtre similaire au nom de l'organisation, \xE0 sa d\xE9nomination l\xE9gale ou \xE0 l'adresse de son site web.": "El descriptor del extracto bancario debe ser similar al nombre de la organizaci\xF3n, a su denominaci\xF3n legal o a la direcci\xF3n de su sitio web.",
  "Le libell\xE9 de relev\xE9 bancaire ne peut pas correspondre \xE0 un nom d'entreprise g\xE9n\xE9rique ou bien connu.": "El descriptor del extracto bancario no puede coincidir con un nombre de empresa gen\xE9rico o muy conocido.",
  "Le m\xEAme type de document a \xE9t\xE9 fourni deux fois.": "Se ha proporcionado dos veces el mismo tipo de documento.",
  "Le nom de l'organisation doit correspondre \xE0 l'activit\xE9 du compte et ne peut pas \xEAtre un nom g\xE9n\xE9rique ou bien connu.": "El nombre de la organizaci\xF3n debe corresponder a la actividad de la cuenta y no puede ser un nombre gen\xE9rico o muy conocido.",
  "Le nom de l'organisation doit \xEAtre facile \xE0 comprendre et compos\xE9 de mots reconnaissables.": "El nombre de la organizaci\xF3n debe ser f\xE1cil de entender y estar formado por palabras reconocibles.",
  "Le nom l\xE9gal de l'organisation ne peut pas \xEAtre un nom g\xE9n\xE9rique ou bien connu.": "El nombre legal de la organizaci\xF3n no puede ser un nombre gen\xE9rico o muy conocido.",
  "Le num\xE9ro fiscal doit \xEAtre compos\xE9 de 9 chiffres, sans tirets ni autres caract\xE8res sp\xE9ciaux.": "El n\xFAmero de identificaci\xF3n fiscal debe tener 9 d\xEDgitos, sin guiones ni otros caracteres especiales.",
  "Le num\xE9ro IEC de l'organisation n'a pas pu \xEAtre v\xE9rifi\xE9.": "No se ha podido verificar el n\xFAmero IEC de la organizaci\xF3n.",
  "Le pays du repr\xE9sentant n'est pas accept\xE9 pour ce compte.": "El pa\xEDs del representante no se acepta para esta cuenta.",
  "Le pr\xE9fixe du libell\xE9 de relev\xE9 bancaire doit correspondre \xE0 votre libell\xE9, au nom de votre organisation, \xE0 sa d\xE9nomination l\xE9gale ou \xE0 votre site web.": "El prefijo del descriptor del extracto bancario debe corresponder a su descriptor, al nombre de su organizaci\xF3n, a su denominaci\xF3n legal o a su sitio web.",
  "Le recto du document est manquant.": "Falta el anverso del documento.",
  "Le signataire du document n'a pas pu \xEAtre v\xE9rifi\xE9.": "No se ha podido verificar la persona firmante del documento.",
  "Le site web ne comporte pas de nom d'organisation ni de description claire des biens et services propos\xE9s.": "El sitio web no incluye el nombre de la organizaci\xF3n ni una descripci\xF3n clara de los bienes y servicios que ofrece.",
  "Le site web ne contient pas d'informations sur le service client.": "El sitio web no contiene informaci\xF3n de atenci\xF3n al cliente.",
  "Le site web ne contient pas de conditions d'annulation.": "El sitio web no contiene condiciones de cancelaci\xF3n.",
  "Le site web ne contient pas de conditions g\xE9n\xE9rales.": "El sitio web no contiene t\xE9rminos y condiciones generales.",
  "Le site web ne contient pas de politique de remboursement.": "El sitio web no contiene una pol\xEDtica de reembolsos.",
  "Le site web ne contient pas de politique ni de processus de retour.": "El sitio web no contiene una pol\xEDtica ni un proceso de devoluciones.",
  "Le site web ne contient pas les mentions applicables aux produits et services soumis \xE0 des restrictions juridiques ou \xE0 l'exportation.": "El sitio web no contiene las menciones aplicables a los productos y servicios sujetos a restricciones legales o a exportaci\xF3n.",
  "Le type ou la structure de l'organisation ne semble pas correct. Indiquez le type et la structure appropri\xE9s pour ce compte.": "El tipo o la estructura de la organizaci\xF3n no parece correcto. Indique el tipo y la estructura adecuados para esta cuenta.",
  "Le verso du document est manquant.": "Falta el reverso del documento.",
  "Les conditions d'utilisation de Stripe interdisent la prise en charge de cette organisation en raison du niveau de risque qu'elle pr\xE9sente. Le titulaire du compte peut contester cette d\xE9cision.": "Las condiciones de uso de Stripe proh\xEDben aceptar esta organizaci\xF3n debido al nivel de riesgo que presenta. El titular de la cuenta puede impugnar esta decisi\xF3n.",
  "Les conditions d'utilisation de Stripe interdisent la prise en charge de l'activit\xE9. Le compte peut faire appel de cette d\xE9cision.": "Las condiciones de uso de Stripe proh\xEDben aceptar la actividad. La cuenta puede presentar un recurso contra esta decisi\xF3n.",
  "Les dirigeants d\xE9clar\xE9s ne correspondent pas \xE0 ceux que Stripe a trouv\xE9s.": "Los directivos declarados no coinciden con los que Stripe ha encontrado.",
  "Les dirigeants mentionn\xE9s dans le document ne figurent pas dans le compte. Mettez \xE0 jour le compte et t\xE9l\xE9chargez un document d'immatriculation avec la liste des dirigeants actuels.": "Los directivos mencionados en el documento no figuran en la cuenta. Actualice la cuenta y suba un documento de registro con la lista de directivos actuales.",
  "Les informations du site web ne correspondent pas \xE0 celles du compte Stripe.": "La informaci\xF3n del sitio web no coincide con la de la cuenta de Stripe.",
  "Les informations figurant sur la pi\xE8ce d'identit\xE9 ne correspondent pas \xE0 celles fournies. V\xE9rifiez et corrigez les informations fournies.": "La informaci\xF3n que figura en el documento de identidad no coincide con la que ha proporcionado. Compruebe la informaci\xF3n facilitada y corr\xEDjala.",
  "Les informations figurant sur le document ne correspondent pas \xE0 celles fournies. V\xE9rifiez-les, puis corrigez-les ou t\xE9l\xE9chargez un document correspondant.": "La informaci\xF3n que figura en el documento no coincide con la que ha proporcionado. Compru\xE9bela y corr\xEDjala o suba un documento que coincida.",
  "Libell\xE9 de relev\xE9 bancaire": "Descriptor del extracto bancario",
  "Libell\xE9 qui appara\xEEtra sur le relev\xE9 bancaire des donateurs": "Texto que aparecer\xE1 en el extracto bancario de los donantes",
  "Licence ou autorisation d'exercer": "Licencia o autorizaci\xF3n para ejercer",
  "Motif indiqu\xE9 par Stripe : {motif}.": "Motivo indicado por Stripe: {motif}.",
  "Nationalit\xE9": "Nacionalidad",
  "Nom commercial / d'affichage de l'organisation": "Nombre comercial / visible de la organizaci\xF3n",
  "Nom de famille": "Apellidos",
  "Nom l\xE9gal de l'organisation": "Nombre legal de la organizaci\xF3n",
  "Non": "No",
  "Num\xE9ro d'enregistrement de l'organisation": "N\xFAmero de registro de la organizaci\xF3n",
  "Num\xE9ro d'identification (\xE9quivalent du num\xE9ro de s\xE9curit\xE9 sociale)": "N\xFAmero de identificaci\xF3n (equivalente al n\xFAmero de la seguridad social)",
  "Num\xE9ro de t\xE9l\xE9phone": "N\xFAmero de tel\xE9fono",
  "Num\xE9ro de t\xE9l\xE9phone de l'organisation": "N\xFAmero de tel\xE9fono de la organizaci\xF3n",
  "Num\xE9ro de TVA intracommunautaire": "N\xFAmero de IVA intracomunitario",
  "Num\xE9ro SIRET / identifiant fiscal": "N\xFAmero SIRET / identificador fiscal",
  "Oui": "S\xED",
  "Pays": "Pa\xEDs",
  "Pays de l'organisation": "Pa\xEDs de la organizaci\xF3n",
  "Pi\xE8ce d'identit\xE9 (passeport, carte nationale d'identit\xE9 ou permis de conduire)": "Documento de identidad (pasaporte, documento nacional de identidad o permiso de conducci\xF3n)",
  "Politique d'ex\xE9cution des commandes": "Pol\xEDtica de ejecuci\xF3n de pedidos",
  "Pourcentage de propri\xE9t\xE9": "Porcentaje de propiedad",
  "Pr\xE9fixe du libell\xE9 sur le relev\xE9 bancaire": "Prefijo del texto en el extracto bancario",
  "Pr\xE9nom": "Nombre",
  "Preuve de pr\xE9sence (selfie)": "Prueba de presencia (selfi)",
  "Produits ou services prot\xE9g\xE9s par le droit d'auteur": "Productos o servicios protegidos por derechos de autor",
  "Propri\xE9taire \u2014 {champ}": "Propietario \u2014 {champ}",
  "Recours contre un refus": "Recurso contra una denegaci\xF3n",
  "Recours contre un refus de prise en charge": "Recurso contra una denegaci\xF3n de aceptaci\xF3n",
  "Recours contre une r\xE9serve sur le compte": "Recurso contra una retenci\xF3n en la cuenta",
  "Recto de la pi\xE8ce d'identit\xE9": "Anverso del documento de identidad",
  "Repr\xE9sentant l\xE9gal \u2014 {champ}": "Representante legal \u2014 {champ}",
  "Selon Stripe, ce compte semble proposer des produits ou services relevant d'une cat\xE9gorie interdite ou restreinte par le contrat de services Stripe. Cat\xE9gorie : {categorie}. Plus d'informations : {lien}": "Seg\xFAn Stripe, esta cuenta parece ofrecer productos o servicios pertenecientes a una categor\xEDa prohibida o restringida por el contrato de servicios de Stripe. Categor\xEDa: {categorie}. M\xE1s informaci\xF3n: {lien}",
  "Selon Stripe, certaines fonctionnalit\xE9s doivent encore \xEAtre demand\xE9es pour ce compte par la plateforme Donoor.": "Seg\xFAn Stripe, la plataforma Donoor a\xFAn debe solicitar algunas funciones para esta cuenta.",
  "Selon Stripe, cette demande ne se traite pas dans un formulaire : contactez le service d'assistance de Stripe.": "Seg\xFAn Stripe, esta solicitud no se tramita mediante un formulario: p\xF3ngase en contacto con el servicio de asistencia de Stripe.",
  "Selon Stripe, cette demande se r\xE8gle par l'interm\xE9diaire d'un tiers.": "Seg\xFAn Stripe, esta solicitud se gestiona a trav\xE9s de un tercero.",
  "Selon Stripe, cette demande se remplit dans un formulaire Stripe.": "Seg\xFAn Stripe, esta solicitud se completa en un formulario de Stripe.",
  "Selon Stripe, des informations suppl\xE9mentaires sont requises pour activer les fonctionnalit\xE9s de ce compte.": "Seg\xFAn Stripe, se requiere informaci\xF3n adicional para activar las funciones de esta cuenta.",
  "Selon Stripe, l'organisation peut exercer ses activit\xE9s dans un secteur r\xE9glement\xE9. Stripe pourrait avoir besoin d'informations suppl\xE9mentaires sur la nature de l'activit\xE9 ou sur les licences requises afin de v\xE9rifier qu'il peut prendre le compte en charge.": "Seg\xFAn Stripe, la organizaci\xF3n podr\xEDa desarrollar su actividad en un sector regulado. Stripe podr\xEDa necesitar informaci\xF3n adicional sobre la naturaleza de la actividad o sobre las licencias requeridas para verificar que puede aceptar la cuenta.",
  "Selon Stripe, le compte pourrait figurer sur une liste de personnes ou d'entreprises interdites. Stripe effectue une v\xE9rification et pourra refuser ou r\xE9tablir l'acc\xE8s au compte en cons\xE9quence.": "Seg\xFAn Stripe, la cuenta podr\xEDa figurar en una lista de personas o empresas prohibidas. Stripe realiza una verificaci\xF3n y podr\xE1 rechazar la cuenta o restablecer el acceso a ella en consecuencia.",
  "Selon Stripe, vous devez r\xE9pondre vous-m\xEAme \xE0 cette demande de v\xE9rification.": "Seg\xFAn Stripe, usted debe responder personalmente a esta solicitud de verificaci\xF3n.",
  "Site web de l'organisation": "Sitio web de la organizaci\xF3n",
  "Statut d'exposition politique": "Estado de exposici\xF3n pol\xEDtica",
  "Statuts de l'organisation": "Estatutos de la organizaci\xF3n",
  "Stripe a besoin d'informations suppl\xE9mentaires pour v\xE9rifier qu'il peut prendre le compte en charge.": "Stripe necesita informaci\xF3n adicional para verificar que puede aceptar la cuenta.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur l'organisation.": "Stripe necesita informaci\xF3n adicional sobre la organizaci\xF3n.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur la conformit\xE9.": "Stripe necesita informaci\xF3n adicional sobre cumplimiento normativo.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur la nature de l'activit\xE9 afin de v\xE9rifier qu'il peut prendre le compte en charge.": "Stripe necesita informaci\xF3n adicional sobre la naturaleza de la actividad para verificar que puede aceptar la cuenta.",
  "Stripe a besoin d'un libell\xE9 de relev\xE9 bancaire qui corresponde exactement \xE0 l'activit\xE9 de l'organisation.": "Stripe necesita un descriptor del extracto bancario que coincida exactamente con la actividad de la organizaci\xF3n.",
  "Stripe a demand\xE9 des informations suppl\xE9mentaires dans le cadre de son \xE9valuation des risques.": "Stripe ha solicitado informaci\xF3n adicional como parte de su evaluaci\xF3n de riesgos.",
  "Stripe a d\xE9tect\xE9 que ce compte utilise un site web, un profil sur les r\xE9seaux sociaux ou une application pour vendre ou promouvoir des produits ou services, mais aucune adresse n'a \xE9t\xE9 fournie. Fournissez une adresse.": "Stripe ha detectado que esta cuenta utiliza un sitio web, un perfil en redes sociales o una aplicaci\xF3n para vender o promocionar productos o servicios, pero no se ha proporcionado ninguna direcci\xF3n. Proporcione una direcci\xF3n.",
  "Stripe a impos\xE9 une r\xE9serve au compte, ce qui n'affecte pas sa capacit\xE9 \xE0 accepter des paiements. Le titulaire du compte peut contester cette d\xE9cision.": "Stripe ha impuesto una retenci\xF3n en la cuenta, lo que no afecta a su capacidad para aceptar pagos. El titular de la cuenta puede impugnar esta decisi\xF3n.",
  "Stripe a refus\xE9 le compte, car il figure sur une liste de personnes ou d'entreprises interdites \xE9tablie par un tiers (par exemple un fournisseur de services financiers ou un gouvernement).": "Stripe ha denegado la cuenta porque figura en una lista de personas o empresas prohibidas elaborada por un tercero (por ejemplo, un proveedor de servicios financieros o un gobierno).",
  "Stripe a refus\xE9 le motif d'exemption relatif \xE0 la propri\xE9t\xE9. Choisissez un autre motif ou t\xE9l\xE9chargez un justificatif de b\xE9n\xE9ficiaire effectif.": "Stripe ha rechazado el motivo de exenci\xF3n relativo a la titularidad real. Elija otro motivo o suba un justificante de titular real.",
  "Stripe a rejet\xE9 le compte pour suspicion d'infraction \xE0 ses conditions d'utilisation.": "Stripe ha rechazado la cuenta por sospecha de infracci\xF3n de sus condiciones de uso.",
  "Stripe a rejet\xE9 le compte pour suspicion de fraude ou d'activit\xE9s ill\xE9gales.": "Stripe ha rechazado la cuenta por sospecha de fraude o de actividades ilegales.",
  "Stripe a rejet\xE9 le compte pour un autre motif, sans le pr\xE9ciser.": "Stripe ha rechazado la cuenta por otro motivo, sin especificarlo.",
  "Stripe a rejet\xE9 le compte, car les exigences de v\xE9rification n'ont pas \xE9t\xE9 satisfaites dans le d\xE9lai requis.": "Stripe ha rechazado la cuenta porque no se han cumplido los requisitos de verificaci\xF3n en el plazo exigido.",
  "Stripe a restreint le compte sans pr\xE9ciser de motif.": "Stripe ha restringido la cuenta sin especificar el motivo.",
  "Stripe demande les statuts de soci\xE9t\xE9s qui d\xE9tiennent une participation importante.": "Stripe solicita los estatutos de las sociedades que poseen una participaci\xF3n significativa.",
  "Stripe demande un justificatif d'immatriculation de soci\xE9t\xE9s qui d\xE9tiennent une participation importante.": "Stripe solicita un justificante de inscripci\xF3n registral de las sociedades que poseen una participaci\xF3n significativa.",
  "Stripe doit v\xE9rifier l'adresse de l'organisation, avec les justificatifs appropri\xE9s.": "Stripe debe verificar la direcci\xF3n de la organizaci\xF3n, con la documentaci\xF3n justificativa adecuada.",
  "Stripe doit v\xE9rifier la politique d'ex\xE9cution des commandes de l'organisation.": "Stripe debe verificar la pol\xEDtica de ejecuci\xF3n de pedidos de la organizaci\xF3n.",
  "Stripe doit v\xE9rifier les coordonn\xE9es du service client de l'organisation.": "Stripe debe verificar los datos de contacto del servicio de atenci\xF3n al cliente de la organizaci\xF3n.",
  "Stripe doit v\xE9rifier les informations du compte bancaire associ\xE9 \xE0 l'organisation.": "Stripe debe verificar los datos de la cuenta bancaria asociada a la organizaci\xF3n.",
  "Stripe doit v\xE9rifier que l'activit\xE9 de ce compte peut \xEAtre prise en charge sur sa plateforme.": "Stripe debe verificar que la actividad de esta cuenta puede ser admitida en su plataforma.",
  "Stripe doit v\xE9rifier que l'organisation n'est pas en relation avec une personne ou une juridiction sanctionn\xE9e.": "Stripe debe verificar que la organizaci\xF3n no est\xE1 relacionada con ninguna persona o jurisdicci\xF3n sancionada.",
  "Stripe doit v\xE9rifier que l'organisation n'est pas li\xE9e \xE0 une personne d'int\xE9r\xEAt ou \xE0 une personne politiquement expos\xE9e.": "Stripe debe verificar que la organizaci\xF3n no est\xE1 vinculada a una persona de inter\xE9s o a una persona pol\xEDticamente expuesta.",
  "Stripe doit v\xE9rifier que le titulaire du compte contr\xF4le bien l'adresse ou le nom de domaine qu'il a fourni.": "Stripe debe verificar que el titular de la cuenta controla realmente la direcci\xF3n o el nombre de dominio que ha facilitado.",
  "Stripe est tenu de conserver des fonds pour une raison l\xE9gale. Il peut \xEAtre n\xE9cessaire de remettre des fonds \xE0 un tiers.": "Stripe est\xE1 obligado a retener fondos por motivos legales. Puede ser necesario entregar fondos a un tercero.",
  "Stripe n'a pas pu acc\xE9der au site web. Si vous bloquez l'acc\xE8s \xE0 votre site dans certaines r\xE9gions, d\xE9sactivez temporairement ce blocage jusqu'\xE0 la v\xE9rification.": "Stripe no ha podido acceder al sitio web. Si bloquea el acceso a su sitio en algunas regiones, desactive temporalmente este bloqueo hasta que se complete la verificaci\xF3n.",
  "Stripe n'a pas pu confirmer ces informations : {champs}. Corrigez-les si elles sont fausses, ou fournissez \xE0 la place {pistes}.": "Stripe no pudo confirmar esta informaci\xF3n: {champs}. Corr\xEDjala si es incorrecta o proporcione en su lugar {pistes}.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Corrigez-les si elles sont fausses, ou prouvez l'identit\xE9 autrement.": "Stripe no pudo confirmar la identidad con la informaci\xF3n introducida. Corr\xEDjala si es incorrecta o demuestre la identidad de otra forma.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Fournissez {pistes} : une seule suffit. Si une information est fausse, vous pouvez aussi la corriger.": "Stripe no pudo confirmar la identidad con la informaci\xF3n introducida. Proporcione {pistes}: basta con uno. Si alg\xFAn dato es incorrecto, tambi\xE9n puede corregirlo.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Fournissez {pistes}. Si une information est fausse, vous pouvez aussi la corriger.": "Stripe no pudo confirmar la identidad con la informaci\xF3n introducida. Proporcione {pistes}. Si alg\xFAn dato es incorrecto, tambi\xE9n puede corregirlo.",
  "Stripe n'a pas pu valider l'ensemble ville, r\xE9gion et code postal de l'adresse fournie.": "Stripe no ha podido validar la combinaci\xF3n de ciudad, regi\xF3n y c\xF3digo postal de la direcci\xF3n proporcionada.",
  "Stripe n'a pas pu valider le nom de la rue ni le num\xE9ro de l'adresse fournie.": "Stripe no ha podido validar el nombre de la calle ni el n\xFAmero de la direcci\xF3n proporcionada.",
  "Stripe n'a pas pu valider le num\xE9ro de t\xE9l\xE9phone. Son format doit correspondre \xE0 celui du pays de la personne concern\xE9e.": "Stripe no ha podido validar el n\xFAmero de tel\xE9fono. Su formato debe corresponder al del pa\xEDs de la persona en cuesti\xF3n.",
  "Stripe n'a pas pu v\xE9rifier l'activit\xE9 du compte \xE0 l'adresse fournie (site web, profil de r\xE9seaux sociaux ou application).": "Stripe no ha podido verificar la actividad de la cuenta en la direcci\xF3n proporcionada (sitio web, perfil de redes sociales o aplicaci\xF3n).",
  "Stripe n'a pas pu v\xE9rifier le site web, car il est prot\xE9g\xE9 par un mot de passe.": "Stripe no ha podido verificar el sitio web, ya que est\xE1 protegido por una contrase\xF1a.",
  "Stripe n'a pas pu v\xE9rifier le site web, car il est toujours en construction.": "Stripe no ha podido verificar el sitio web, ya que todav\xEDa est\xE1 en construcci\xF3n.",
  "Stripe n'a pas pu v\xE9rifier le site web, car il ne pr\xE9sente aucun contenu.": "Stripe no ha podido verificar el sitio web, ya que no tiene contenido.",
  "Stripe n'a pas pu v\xE9rifier le site web, car son acc\xE8s est bloqu\xE9 dans certaines r\xE9gions. D\xE9sactivez temporairement ce blocage jusqu'\xE0 la v\xE9rification.": "Stripe no ha podido verificar el sitio web, ya que su acceso est\xE1 bloqueado en algunas regiones. Desactive temporalmente este bloqueo hasta que se complete la verificaci\xF3n.",
  "Stripe n'a pas pu v\xE9rifier les informations du compte. Vous pouvez t\xE9l\xE9charger un document de v\xE9rification ou mettre \xE0 jour vos informations.": "Stripe no ha podido verificar la informaci\xF3n de la cuenta. Puede subir un documento de verificaci\xF3n o actualizar su informaci\xF3n.",
  "Stripe n'a pas pu v\xE9rifier les pouvoirs de la personne qui autorise le compte. Remplacez-la par une personne d\xFBment enregistr\xE9e en tant que repr\xE9sentant autoris\xE9.": "Stripe no ha podido verificar los poderes de la persona que autoriza la cuenta. Sustit\xFAyala por una persona debidamente registrada como representante autorizado.",
  "Stripe n'a pas pu v\xE9rifier les pouvoirs du repr\xE9sentant du compte. Ajoutez une personne qui autorise le compte et fournissez une lettre d'autorisation sign\xE9e par elle.": "Stripe no ha podido verificar los poderes del representante de la cuenta. A\xF1ada una persona que autorice la cuenta y proporcione una carta de autorizaci\xF3n firmada por ella.",
  "Stripe n'a pas trouv\xE9 d'informations \xE0 partir des donn\xE9es saisies. V\xE9rifiez qu'elles sont correctes.": "Stripe no ha encontrado informaci\xF3n a partir de los datos introducidos. Compruebe que sean correctos.",
  "Stripe signale une erreur de v\xE9rification (code : {code}).": "Stripe indica un error de verificaci\xF3n (c\xF3digo: {code}).",
  "Stripe v\xE9rifie actuellement les informations du compte. Aucune action n'est requise.": "Stripe est\xE1 verificando actualmente la informaci\xF3n de la cuenta. No es necesario realizar ninguna acci\xF3n.",
  "T\xE9l\xE9phone du service donateurs / support": "Tel\xE9fono del servicio para donantes / soporte",
  "Une information est manquante.": "Falta una informaci\xF3n.",
  "Une information est manquante. D\xE9tail donn\xE9 par Stripe : \xAB {detail} \xBB": "Falta una informaci\xF3n. Detalle proporcionado por Stripe: \xAB{detail}\xBB",
  "une pi\xE8ce d'identit\xE9": "un documento de identidad",
  "une pi\xE8ce d'identit\xE9 ou un justificatif de domicile": "un documento de identidad o un justificante de domicilio",
  "une v\xE9rification par selfie": "una verificaci\xF3n con selfi",
  "URL du service donateurs / support": "URL del servicio para donantes / soporte",
  "V\xE9rification d'identit\xE9": "Verificaci\xF3n de identidad",
  "V\xE9rification de l'adresse": "Verificaci\xF3n de la direcci\xF3n",
  "V\xE9rification des personnes politiquement expos\xE9es": "Verificaci\xF3n de personas pol\xEDticamente expuestas",
  "V\xE9rification du compte bancaire": "Verificaci\xF3n de la cuenta bancaria",
  "V\xE9rification du nom de domaine": "Verificaci\xF3n del nombre de dominio",
  "V\xE9rification li\xE9e aux sanctions": "Verificaci\xF3n relacionada con sanciones",
  "V\xE9rification Stripe en cours": "Verificaci\xF3n de Stripe en curso",
  "Verso de la pi\xE8ce d'identit\xE9": "Reverso del documento de identidad",
  "Ville": "Ciudad",
  "Ville de l'organisation": "Ciudad de la organizaci\xF3n"
};
var es_default = textes3;

// src/textesStripe/it.ts
var textes4 = {
  "{liste}, ou {piste}": "{liste} oppure {piste}",
  "4 derniers chiffres du num\xE9ro de s\xE9curit\xE9 sociale": "Ultime 4 cifre del numero di previdenza sociale",
  "Acceptation des conditions d'utilisation Stripe": "Accettazione dei termini di servizio di Stripe",
  "Activit\xE9 dans un secteur r\xE9glement\xE9": "Attivit\xE0 in un settore regolamentato",
  "Adresse de l'organisation (compl\xE9ment)": "Indirizzo dell'organizzazione (complemento)",
  "Adresse de l'organisation (rue)": "Indirizzo dell'organizzazione (via)",
  "Adresse du service donateurs / support": "Indirizzo del servizio donatori / assistenza",
  "Adresse du site web": "Indirizzo del sito web",
  "Adresse email": "Indirizzo e-mail",
  "Adresse IP d'acceptation des CGU": "Indirizzo IP di accettazione dei termini di servizio",
  "Adresse personnelle (compl\xE9ment)": "Indirizzo personale (complemento)",
  "Adresse personnelle (rue)": "Indirizzo personale (via)",
  "associations caritatives sans statut d'enregistrement au dossier (par exemple un enregistrement 501(c)(3) ou une classification \xE9quivalente)": "organizzazioni benefiche senza uno status di registrazione agli atti (ad esempio una registrazione 501(c)(3) o una classificazione equivalente)",
  "Aucun propri\xE9taire n'a \xE9t\xE9 indiqu\xE9. Fournissez des informations sur tous les propri\xE9taires.": "Non \xE8 stato indicato alcun titolare effettivo. Fornisca le informazioni relative a tutti i titolari effettivi.",
  "Cadre dirigeant \u2014 {champ}": "Alto dirigente \u2014 {champ}",
  "Cat\xE9gorie d'activit\xE9 (code MCC)": "Categoria di attivit\xE0 (codice MCC)",
  "Ce type d'organisation n'est pas pris en charge par Stripe.": "Questo tipo di organizzazione non \xE8 supportato da Stripe.",
  "Code postal": "CAP",
  "Code postal de l'organisation": "CAP dell'organizzazione",
  "Compte bancaire pour les versements (IBAN)": "Conto bancario per i pagamenti (IBAN)",
  "Compte en cours de v\xE9rification": "Account in fase di verifica",
  "Compte refus\xE9": "Account negato",
  "Compte rejet\xE9": "Account rifiutato",
  "Compte rejet\xE9 \u2014 Conditions d'utilisation": "Account rifiutato \u2014 Termini di servizio",
  "Compte rejet\xE9 \u2014 v\xE9rification incompl\xE8te": "Account rifiutato \u2014 verifica incompleta",
  "Compte restreint par Stripe": "Account limitato da Stripe",
  "Compte suspendu par Donoor": "Account sospeso da Donoor",
  "Confirmation que tous les cadres dirigeants ont \xE9t\xE9 d\xE9clar\xE9s": "Conferma che tutti gli alti dirigenti sono stati dichiarati",
  "Confirmation que tous les dirigeants ont \xE9t\xE9 d\xE9clar\xE9s": "Conferma che tutti i dirigenti sono stati dichiarati",
  "Confirmation que tous les propri\xE9taires ont \xE9t\xE9 d\xE9clar\xE9s": "Conferma che tutti i titolari effettivi sono stati dichiarati",
  "Contactez le service d'assistance de Stripe pour conna\xEEtre les raisons de l'\xE9chec de la v\xE9rification.": "Contatti il servizio di assistenza di Stripe per conoscere i motivi del mancato superamento della verifica.",
  "Coordonn\xE9es du service client": "Recapiti del servizio clienti",
  "Date de naissance": "Data di nascita",
  "D\xE9cret minist\xE9riel (le cas \xE9ch\xE9ant)": "Decreto ministeriale (se applicabile)",
  "Demande d'informations de Stripe": "Richiesta di informazioni da parte di Stripe",
  "Demande de conformit\xE9": "Richiesta di conformit\xE0",
  "D\xE9partement / r\xE9gion": "Provincia / regione",
  "D\xE9partement / r\xE9gion de l'organisation": "Provincia / regione dell'organizzazione",
  "Des cadres dirigeants n'ont pas \xE9t\xE9 indiqu\xE9s.": "Alcuni dirigenti non sono stati indicati.",
  "Des dirigeants en trop ont \xE9t\xE9 d\xE9clar\xE9s.": "Sono stati dichiarati dirigenti in eccesso.",
  "Description de votre activit\xE9": "Descrizione della sua attivit\xE0",
  "Description des produits ou services": "Descrizione dei prodotti o servizi",
  "Dirigeant \u2014 {champ}": "Dirigente \u2014 {champ}",
  "Document": "Documento",
  "Document officiel de l'organisation": "Documento ufficiale dell'organizzazione",
  "Document refus\xE9 (mode test).": "Documento rifiutato (modalit\xE0 test).",
  "Elle ne peut pas \xEAtre un bo\xEEtier de contrat routier.": "Non pu\xF2 trattarsi di una casella di contratto stradale.",
  "Elle ne peut pas \xEAtre une bo\xEEte postale priv\xE9e.": "Non pu\xF2 trattarsi di una casella postale privata.",
  "Email du service donateurs / support": "Email del servizio donatori / assistenza",
  "Examen de la nature de votre activit\xE9": "Verifica della natura della sua attivit\xE0",
  "Fonction au sein de l'organisation": "Ruolo all'interno dell'organizzazione",
  "Fonctionnalit\xE9s \xE0 demander": "Funzionalit\xE0 da richiedere",
  "Fonds retenus pour raison l\xE9gale": "Fondi trattenuti per motivi legali",
  "Forme juridique de l'organisation": "Forma giuridica dell'organizzazione",
  "Fournissez un nouveau fichier : image en couleur, 10 Mo maximum, pi\xE8ces d'identit\xE9 en JPG ou PNG, justificatifs d'adresse ou d'entit\xE9 juridique en JPG, PNG ou PDF, toutes les pages incluses, sans mot de passe.": "Carichi un nuovo file: immagine a colori, 10 MB al massimo, documenti d'identit\xE0 in JPG o PNG, comprovanti di indirizzo o di entit\xE0 giuridica in JPG, PNG o PDF, tutte le pagine incluse, senza password.",
  "Genre": "Sesso",
  "Il se peut que le document ait \xE9t\xE9 modifi\xE9. Contactez le service d'assistance de Stripe pour conna\xEEtre les raisons de l'\xE9chec de la v\xE9rification.": "Il documento potrebbe essere stato modificato. Contatti il servizio di assistenza di Stripe per conoscere i motivi del mancato superamento della verifica.",
  "Informations \xE0 confirmer": "Informazioni da confermare",
  "Informations \xE0 fournir": "Informazioni da fornire",
  "Informations compl\xE9mentaires sur votre activit\xE9": "Informazioni aggiuntive sulla sua attivit\xE0",
  "Informations sur l'organisation": "Informazioni sull'organizzazione",
  "Informations sur la nature de votre activit\xE9": "Informazioni sulla natura della Sua attivit\xE0",
  "Intervention de la plateforme": "Intervento della piattaforma",
  "Justificatif d'enregistrement de l'organisation": "Attestazione di registrazione dell'organizzazione",
  "Justificatif de domicile (facture d'\xE9nergie, quittance de loyer, etc.)": "Prova di residenza (bolletta delle utenze, ricevuta d'affitto, ecc.)",
  "Justificatif du num\xE9ro fiscal (SIRET)": "Attestazione del numero fiscale (SIRET)",
  "L'acceptation des conditions d'utilisation de Stripe n'est plus valide.": "L'accettazione delle condizioni d'uso di Stripe non \xE8 pi\xF9 valida.",
  "L'administration fiscale n'a pas pu v\xE9rifier les informations fournies. Corrigez le nom de l'organisation ou le num\xE9ro fiscal, ou t\xE9l\xE9chargez un document justificatif.": "L'Agenzia delle Entrate non \xE8 riuscita a verificare le informazioni fornite. Corregga la denominazione dell'organizzazione o il codice fiscale, oppure carichi un documento a supporto.",
  "L'adresse de r\xE9sidence n'a pas pu \xEAtre v\xE9rifi\xE9e.": "Non \xE8 stato possibile verificare l'indirizzo di residenza.",
  "L'adresse doit \xEAtre une adresse physique valide \xE0 partir de laquelle le compte exerce ses activit\xE9s.": "L'indirizzo deve essere un indirizzo fisico valido da cui il account svolge la propria attivit\xE0.",
  "L'adresse du site correspond \xE0 un site g\xE9n\xE9rique que Stripe consid\xE8re comme sans lien avec le compte. Fournissez une adresse propre \xE0 votre organisation.": "L'indirizzo del sito corrisponde a un sito generico che Stripe considera non collegato all'account. Fornisca un indirizzo specifico della sua organizzazione.",
  "L'adresse du site de l'organisation doit faire r\xE9f\xE9rence aux produits et services qu'elle fournit. Stripe pourrait exiger une modification de cette adresse avant de pouvoir prendre le compte en charge.": "L'indirizzo del sito dell'organizzazione deve fare riferimento ai prodotti e ai servizi che essa fornisce. Stripe potrebbe richiedere una modifica di tale indirizzo prima di poter prendere in carico l'account.",
  "L'adresse fournie est celle d'un agent enregistr\xE9, qui n'est pas accept\xE9e.": "L'indirizzo fornito \xE8 quello di un agente registrato, che non \xE8 accettato.",
  "L'adresse fournie est celle d'un service de r\xE9ception de courrier (CMRA), qui n'est pas accept\xE9e.": "L'indirizzo fornito \xE8 quello di un servizio di ricezione posta (CMRA), che non \xE8 accettato.",
  "L'organisation pourrait vendre des produits ou des services prot\xE9g\xE9s par le droit d'auteur. Stripe a besoin d'informations suppl\xE9mentaires pour v\xE9rifier que le compte est autoris\xE9 \xE0 les vendre.": "L'organizzazione potrebbe vendere prodotti o servizi tutelati dal diritto d'autore. Stripe necessita di informazioni aggiuntive per verificare che l'account sia autorizzato a venderli.",
  "La date de naissance doit se situer dans les 120 derni\xE8res ann\xE9es.": "La data di nascita deve rientrare negli ultimi 120 anni.",
  "La description doit comporter au moins 10 caract\xE8res.": "La descrizione deve contenere almeno 10 caratteri.",
  "La description doit \xEAtre diff\xE9rente de l'adresse du site web.": "La descrizione deve essere diversa dall'indirizzo del sito web.",
  "La liste des dirigeants n'a pas \xE9t\xE9 fournie. Mettez \xE0 jour le compte et t\xE9l\xE9chargez un document d'immatriculation avec la liste des dirigeants actuels.": "L'elenco dei dirigenti non \xE8 stato fornito. Aggiorni l'account e carichi un documento di iscrizione al registro con l'elenco dei dirigenti attuali.",
  "La personne doit avoir au moins 13 ans.": "La persona deve avere almeno 13 anni.",
  "La personne doit avoir au moins 18 ans.": "La persona deve avere almeno 18 anni.",
  "La personne responsable du compte doit v\xE9rifier son identit\xE9 en t\xE9l\xE9chargeant une pi\xE8ce d'identit\xE9 officielle et un selfie.": "La persona responsabile dell'account deve verificare la propria identit\xE0 caricando un documento di identit\xE0 ufficiale e un selfie.",
  "La plateforme a initi\xE9 une intervention sur ce compte.": "La piattaforma ha avviato un intervento su questo account.",
  "La plateforme Donoor a suspendu ce compte. Contactez le support Donoor.": "La piattaforma Donoor ha sospeso questo account. Contatti l'assistenza Donoor.",
  "La valeur fournie n'est pas valide.": "Il valore fornito non \xE8 valido.",
  "Le compte est en cours de v\xE9rification par Stripe.": "L'account \xE8 in fase di verifica da parte di Stripe.",
  "Le compte Stripe de l'organisation doit contenir une description exacte des produits ou services.": "L'account Stripe dell'organizzazione deve contenere una descrizione accurata dei prodotti o dei servizi.",
  "Le document a \xE9t\xE9 refus\xE9 : il est en niveaux de gris.": "Il documento \xE8 stato rifiutato: \xE8 in scala di grigi.",
  "Le document a \xE9t\xE9 refus\xE9 : il s'agit d'une copie.": "Il documento \xE8 stato rifiutato: si tratta di una copia.",
  "Le document est en niveaux de gris.": "Il documento \xE8 in scala di grigi.",
  "Le document est incomplet.": "Il documento \xE8 incompleto.",
  "Le document est une copie.": "Il documento \xE8 una copia.",
  "Le document n'est pas lisible.": "Il documento non \xE8 leggibile.",
  "Le document n'est pas sign\xE9.": "Il documento non \xE8 firmato.",
  "Le document ne comporte pas de date de d\xE9livrance ou d'expiration, ou il est expir\xE9. Une pi\xE8ce d'identit\xE9 doit \xEAtre valide \xE0 la date d'envoi ; un justificatif de domicile doit dater de moins de six mois.": "Il documento non riporta una data di rilascio o di scadenza, oppure \xE8 scaduto. Un documento d'identit\xE0 deve essere valido alla data di invio; un documento che attesta il domicilio deve risalire a meno di sei mesi fa.",
  "Le document t\xE9l\xE9charg\xE9 ne contient pas toutes les informations requises. T\xE9l\xE9chargez un autre document contenant les informations manquantes.": "Il documento caricato non contiene tutte le informazioni richieste. Carichi un altro documento che contenga le informazioni mancanti.",
  "Le fichier est corrompu.": "Il file \xE8 danneggiato.",
  "Le fichier est trop volumineux.": "Il file \xE8 troppo grande.",
  "Le fichier fourni n'est pas une pi\xE8ce accept\xE9e par Stripe (pays pris en charge, type de document attendu pour ce type d'organisation). Fournissez un nouveau fichier conforme.": "Il file fornito non \xE8 un documento accettato da Stripe (Paese supportato, tipo di documento previsto per questo tipo di organizzazione). Carichi un nuovo file conforme.",
  "Le fichier n'a pas \xE9t\xE9 t\xE9l\xE9charg\xE9.": "Il file non \xE8 stato caricato.",
  "Le format de l'adresse du site est incorrect. Fournissez une adresse correctement format\xE9e, par exemple https://example.com.": "Il formato dell'indirizzo del sito non \xE8 corretto. Fornisca un indirizzo formattato correttamente, ad esempio https://example.com.",
  "Le libell\xE9 de relev\xE9 bancaire doit comporter au moins 5 caract\xE8res.": "La descrizione dell'estratto conto deve contenere almeno 5 caratteri.",
  "Le libell\xE9 de relev\xE9 bancaire doit \xEAtre similaire au nom de l'organisation, \xE0 sa d\xE9nomination l\xE9gale ou \xE0 l'adresse de son site web.": "La descrizione dell'estratto conto deve essere simile al nome dell'organizzazione, alla sua denominazione legale o all'indirizzo del suo sito web.",
  "Le libell\xE9 de relev\xE9 bancaire ne peut pas correspondre \xE0 un nom d'entreprise g\xE9n\xE9rique ou bien connu.": "La descrizione dell'estratto conto non pu\xF2 corrispondere a un nome di azienda generico o noto.",
  "Le m\xEAme type de document a \xE9t\xE9 fourni deux fois.": "Lo stesso tipo di documento \xE8 stato fornito due volte.",
  "Le nom de l'organisation doit correspondre \xE0 l'activit\xE9 du compte et ne peut pas \xEAtre un nom g\xE9n\xE9rique ou bien connu.": "Il nome dell'organizzazione deve corrispondere all'attivit\xE0 dell'account e non pu\xF2 essere un nome generico o noto.",
  "Le nom de l'organisation doit \xEAtre facile \xE0 comprendre et compos\xE9 de mots reconnaissables.": "Il nome dell'organizzazione deve essere facile da capire e composto da parole riconoscibili.",
  "Le nom l\xE9gal de l'organisation ne peut pas \xEAtre un nom g\xE9n\xE9rique ou bien connu.": "Il nome legale dell'organizzazione non pu\xF2 essere un nome generico o noto.",
  "Le num\xE9ro fiscal doit \xEAtre compos\xE9 de 9 chiffres, sans tirets ni autres caract\xE8res sp\xE9ciaux.": "Il numero di identificazione fiscale deve essere composto da 9 cifre, senza trattini n\xE9 altri caratteri speciali.",
  "Le num\xE9ro IEC de l'organisation n'a pas pu \xEAtre v\xE9rifi\xE9.": "Non \xE8 stato possibile verificare il numero IEC dell'organizzazione.",
  "Le pays du repr\xE9sentant n'est pas accept\xE9 pour ce compte.": "Il paese del rappresentante non \xE8 accettato per questo account.",
  "Le pr\xE9fixe du libell\xE9 de relev\xE9 bancaire doit correspondre \xE0 votre libell\xE9, au nom de votre organisation, \xE0 sa d\xE9nomination l\xE9gale ou \xE0 votre site web.": "Il prefisso della descrizione dell'estratto conto deve corrispondere alla sua descrizione, al nome dell'organizzazione, alla sua denominazione legale o al suo sito web.",
  "Le recto du document est manquant.": "Il fronte del documento \xE8 mancante.",
  "Le signataire du document n'a pas pu \xEAtre v\xE9rifi\xE9.": "Non \xE8 stato possibile verificare il firmatario del documento.",
  "Le site web ne comporte pas de nom d'organisation ni de description claire des biens et services propos\xE9s.": "Il sito web non riporta il nome dell'organizzazione n\xE9 una descrizione chiara dei beni e dei servizi offerti.",
  "Le site web ne contient pas d'informations sur le service client.": "Il sito web non contiene informazioni sull'assistenza clienti.",
  "Le site web ne contient pas de conditions d'annulation.": "Il sito web non contiene condizioni di annullamento.",
  "Le site web ne contient pas de conditions g\xE9n\xE9rales.": "Il sito web non contiene termini e condizioni generali.",
  "Le site web ne contient pas de politique de remboursement.": "Il sito web non contiene una politica di rimborso.",
  "Le site web ne contient pas de politique ni de processus de retour.": "Il sito web non contiene una politica n\xE9 una procedura di reso.",
  "Le site web ne contient pas les mentions applicables aux produits et services soumis \xE0 des restrictions juridiques ou \xE0 l'exportation.": "Il sito web non contiene le indicazioni previste per i prodotti e i servizi soggetti a restrizioni legali o all'esportazione.",
  "Le type ou la structure de l'organisation ne semble pas correct. Indiquez le type et la structure appropri\xE9s pour ce compte.": "Il tipo o la struttura dell'organizzazione non sembra corretto. Indichi il tipo e la struttura appropriati per questo account.",
  "Le verso du document est manquant.": "Il retro del documento \xE8 mancante.",
  "Les conditions d'utilisation de Stripe interdisent la prise en charge de cette organisation en raison du niveau de risque qu'elle pr\xE9sente. Le titulaire du compte peut contester cette d\xE9cision.": "I termini di servizio di Stripe vietano di prendere in carico questa organizzazione a causa del livello di rischio che essa presenta. Il titolare dell'account pu\xF2 contestare questa decisione.",
  "Les conditions d'utilisation de Stripe interdisent la prise en charge de l'activit\xE9. Le compte peut faire appel de cette d\xE9cision.": "Le condizioni d'uso di Stripe vietano la presa in carico dell'attivit\xE0. L'account pu\xF2 presentare ricorso contro tale decisione.",
  "Les dirigeants d\xE9clar\xE9s ne correspondent pas \xE0 ceux que Stripe a trouv\xE9s.": "I dirigenti dichiarati non corrispondono a quelli individuati da Stripe.",
  "Les dirigeants mentionn\xE9s dans le document ne figurent pas dans le compte. Mettez \xE0 jour le compte et t\xE9l\xE9chargez un document d'immatriculation avec la liste des dirigeants actuels.": "I dirigenti menzionati nel documento non figurano nell'account. Aggiorni l'account e carichi un documento di iscrizione al registro con l'elenco dei dirigenti attuali.",
  "Les informations du site web ne correspondent pas \xE0 celles du compte Stripe.": "Le informazioni del sito web non corrispondono a quelle dell'account Stripe.",
  "Les informations figurant sur la pi\xE8ce d'identit\xE9 ne correspondent pas \xE0 celles fournies. V\xE9rifiez et corrigez les informations fournies.": "Le informazioni riportate sul documento d'identit\xE0 non corrispondono a quelle fornite. Verifichi e corregga le informazioni fornite.",
  "Les informations figurant sur le document ne correspondent pas \xE0 celles fournies. V\xE9rifiez-les, puis corrigez-les ou t\xE9l\xE9chargez un document correspondant.": "Le informazioni riportate sul documento non corrispondono a quelle fornite. Le verifichi, quindi le corregga oppure carichi un documento corrispondente.",
  "Libell\xE9 de relev\xE9 bancaire": "Descrizione nell'estratto conto",
  "Libell\xE9 qui appara\xEEtra sur le relev\xE9 bancaire des donateurs": "Descrittore che comparir\xE0 sull'estratto conto bancario dei donatori",
  "Licence ou autorisation d'exercer": "Licenza o autorizzazione all'esercizio",
  "Motif indiqu\xE9 par Stripe : {motif}.": "Motivo indicato da Stripe: {motif}.",
  "Nationalit\xE9": "Cittadinanza",
  "Nom commercial / d'affichage de l'organisation": "Nome commerciale / visualizzato dell'organizzazione",
  "Nom de famille": "Cognome",
  "Nom l\xE9gal de l'organisation": "Ragione sociale dell'organizzazione",
  "Non": "No",
  "Num\xE9ro d'enregistrement de l'organisation": "Numero di registrazione dell'organizzazione",
  "Num\xE9ro d'identification (\xE9quivalent du num\xE9ro de s\xE9curit\xE9 sociale)": "Numero di identificazione (equivalente del numero di previdenza sociale)",
  "Num\xE9ro de t\xE9l\xE9phone": "Numero di telefono",
  "Num\xE9ro de t\xE9l\xE9phone de l'organisation": "Numero di telefono dell'organizzazione",
  "Num\xE9ro de TVA intracommunautaire": "Numero di partita IVA intracomunitaria",
  "Num\xE9ro SIRET / identifiant fiscal": "Numero SIRET / codice fiscale",
  "Oui": "S\xEC",
  "Pays": "Paese",
  "Pays de l'organisation": "Paese dell'organizzazione",
  "Pi\xE8ce d'identit\xE9 (passeport, carte nationale d'identit\xE9 ou permis de conduire)": "Documento d'identit\xE0 (passaporto, carta d'identit\xE0 o patente di guida)",
  "Politique d'ex\xE9cution des commandes": "Politica di evasione degli ordini",
  "Pourcentage de propri\xE9t\xE9": "Percentuale di propriet\xE0",
  "Pr\xE9fixe du libell\xE9 sur le relev\xE9 bancaire": "Prefisso del descrittore sull'estratto conto bancario",
  "Pr\xE9nom": "Nome",
  "Preuve de pr\xE9sence (selfie)": "Prova di presenza (selfie)",
  "Produits ou services prot\xE9g\xE9s par le droit d'auteur": "Prodotti o servizi tutelati dal diritto d'autore",
  "Propri\xE9taire \u2014 {champ}": "Titolare effettivo \u2014 {champ}",
  "Recours contre un refus": "Ricorso contro un rifiuto",
  "Recours contre un refus de prise en charge": "Ricorso contro un rifiuto di presa in carico",
  "Recours contre une r\xE9serve sur le compte": "Ricorso contro una restrizione dell'account",
  "Recto de la pi\xE8ce d'identit\xE9": "Fronte del documento d'identit\xE0",
  "Repr\xE9sentant l\xE9gal \u2014 {champ}": "Rappresentante legale \u2014 {champ}",
  "Selon Stripe, ce compte semble proposer des produits ou services relevant d'une cat\xE9gorie interdite ou restreinte par le contrat de services Stripe. Cat\xE9gorie : {categorie}. Plus d'informations : {lien}": "Secondo Stripe, questo account sembra offrire prodotti o servizi appartenenti a una categoria vietata o limitata dal Contratto di servizi Stripe. Categoria: {categorie}. Maggiori informazioni: {lien}",
  "Selon Stripe, certaines fonctionnalit\xE9s doivent encore \xEAtre demand\xE9es pour ce compte par la plateforme Donoor.": "Secondo Stripe, alcune funzionalit\xE0 devono ancora essere richieste per questo account dalla piattaforma Donoor.",
  "Selon Stripe, cette demande ne se traite pas dans un formulaire : contactez le service d'assistance de Stripe.": "Secondo Stripe, questa richiesta non si gestisce tramite un modulo: contatti il servizio di assistenza di Stripe.",
  "Selon Stripe, cette demande se r\xE8gle par l'interm\xE9diaire d'un tiers.": "Secondo Stripe, questa richiesta va risolta tramite una terza parte.",
  "Selon Stripe, cette demande se remplit dans un formulaire Stripe.": "Secondo Stripe, questa richiesta va compilata in un modulo Stripe.",
  "Selon Stripe, des informations suppl\xE9mentaires sont requises pour activer les fonctionnalit\xE9s de ce compte.": "Secondo Stripe, sono necessarie informazioni aggiuntive per attivare le funzionalit\xE0 di questo account.",
  "Selon Stripe, l'organisation peut exercer ses activit\xE9s dans un secteur r\xE9glement\xE9. Stripe pourrait avoir besoin d'informations suppl\xE9mentaires sur la nature de l'activit\xE9 ou sur les licences requises afin de v\xE9rifier qu'il peut prendre le compte en charge.": "Secondo Stripe, l'organizzazione potrebbe svolgere la propria attivit\xE0 in un settore regolamentato. Stripe potrebbe aver bisogno di informazioni aggiuntive sulla natura dell'attivit\xE0 o sulle licenze richieste per verificare di poter prendere in carico l'account.",
  "Selon Stripe, le compte pourrait figurer sur une liste de personnes ou d'entreprises interdites. Stripe effectue une v\xE9rification et pourra refuser ou r\xE9tablir l'acc\xE8s au compte en cons\xE9quence.": "Secondo Stripe, l'account potrebbe comparire in un elenco di persone o aziende vietate. Stripe effettua una verifica e potr\xE0 rifiutare o ripristinare l'accesso all'account di conseguenza.",
  "Selon Stripe, vous devez r\xE9pondre vous-m\xEAme \xE0 cette demande de v\xE9rification.": "Secondo Stripe, \xE8 necessario che Lei risponda personalmente a questa richiesta di verifica.",
  "Site web de l'organisation": "Sito web dell'organizzazione",
  "Statut d'exposition politique": "Stato di persona politicamente esposta",
  "Statuts de l'organisation": "Statuto dell'organizzazione",
  "Stripe a besoin d'informations suppl\xE9mentaires pour v\xE9rifier qu'il peut prendre le compte en charge.": "Stripe necessita di ulteriori informazioni per verificare che possa prendere in carico l'account.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur l'organisation.": "Stripe necessita di ulteriori informazioni sull'organizzazione.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur la conformit\xE9.": "Stripe necessita di ulteriori informazioni sulla conformit\xE0.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur la nature de l'activit\xE9 afin de v\xE9rifier qu'il peut prendre le compte en charge.": "Stripe necessita di informazioni aggiuntive sulla natura dell'attivit\xE0 per verificare di poter prendere in carico l'account.",
  "Stripe a besoin d'un libell\xE9 de relev\xE9 bancaire qui corresponde exactement \xE0 l'activit\xE9 de l'organisation.": "Stripe necessita di una descrizione nell'estratto conto che corrisponda esattamente all'attivit\xE0 dell'organizzazione.",
  "Stripe a demand\xE9 des informations suppl\xE9mentaires dans le cadre de son \xE9valuation des risques.": "Stripe ha richiesto informazioni aggiuntive nell'ambito della sua valutazione del rischio.",
  "Stripe a d\xE9tect\xE9 que ce compte utilise un site web, un profil sur les r\xE9seaux sociaux ou une application pour vendre ou promouvoir des produits ou services, mais aucune adresse n'a \xE9t\xE9 fournie. Fournissez une adresse.": "Stripe ha rilevato che questo account utilizza un sito web, un profilo sui social network o un'applicazione per vendere o promuovere prodotti o servizi, ma non \xE8 stato fornito alcun indirizzo. Fornisca un indirizzo.",
  "Stripe a impos\xE9 une r\xE9serve au compte, ce qui n'affecte pas sa capacit\xE9 \xE0 accepter des paiements. Le titulaire du compte peut contester cette d\xE9cision.": "Stripe ha applicato una restrizione all'account, che non compromette la sua capacit\xE0 di accettare pagamenti. Il titolare dell'account pu\xF2 contestare questa decisione.",
  "Stripe a refus\xE9 le compte, car il figure sur une liste de personnes ou d'entreprises interdites \xE9tablie par un tiers (par exemple un fournisseur de services financiers ou un gouvernement).": "Stripe ha negato l'account perch\xE9 figura in un elenco di persone o aziende vietate stilato da una terza parte (ad esempio un fornitore di servizi finanziari o un governo).",
  "Stripe a refus\xE9 le motif d'exemption relatif \xE0 la propri\xE9t\xE9. Choisissez un autre motif ou t\xE9l\xE9chargez un justificatif de b\xE9n\xE9ficiaire effectif.": "Stripe ha rifiutato il motivo di esenzione relativo alla titolarit\xE0 effettiva. Scelga un altro motivo oppure carichi un documento che attesti il titolare effettivo.",
  "Stripe a rejet\xE9 le compte pour suspicion d'infraction \xE0 ses conditions d'utilisation.": "Stripe ha rifiutato l'account per sospetta violazione dei propri Termini di servizio.",
  "Stripe a rejet\xE9 le compte pour suspicion de fraude ou d'activit\xE9s ill\xE9gales.": "Stripe ha rifiutato l'account per sospetta frode o attivit\xE0 illecite.",
  "Stripe a rejet\xE9 le compte pour un autre motif, sans le pr\xE9ciser.": "Stripe ha rifiutato l'account per un altro motivo, non specificato.",
  "Stripe a rejet\xE9 le compte, car les exigences de v\xE9rification n'ont pas \xE9t\xE9 satisfaites dans le d\xE9lai requis.": "Stripe ha rifiutato l'account perch\xE9 i requisiti di verifica non sono stati soddisfatti entro il termine richiesto.",
  "Stripe a restreint le compte sans pr\xE9ciser de motif.": "Stripe ha limitato l'account senza specificarne il motivo.",
  "Stripe demande les statuts de soci\xE9t\xE9s qui d\xE9tiennent une participation importante.": "Stripe richiede lo statuto delle societ\xE0 che detengono una partecipazione rilevante.",
  "Stripe demande un justificatif d'immatriculation de soci\xE9t\xE9s qui d\xE9tiennent une participation importante.": "Stripe richiede un certificato di iscrizione al registro delle imprese delle societ\xE0 che detengono una partecipazione rilevante.",
  "Stripe doit v\xE9rifier l'adresse de l'organisation, avec les justificatifs appropri\xE9s.": "Stripe deve verificare l'indirizzo dell'organizzazione, con la documentazione appropriata.",
  "Stripe doit v\xE9rifier la politique d'ex\xE9cution des commandes de l'organisation.": "Stripe deve verificare la politica di evasione degli ordini dell'organizzazione.",
  "Stripe doit v\xE9rifier les coordonn\xE9es du service client de l'organisation.": "Stripe deve verificare i recapiti del servizio clienti dell'organizzazione.",
  "Stripe doit v\xE9rifier les informations du compte bancaire associ\xE9 \xE0 l'organisation.": "Stripe deve verificare le informazioni del conto bancario associato all'organizzazione.",
  "Stripe doit v\xE9rifier que l'activit\xE9 de ce compte peut \xEAtre prise en charge sur sa plateforme.": "Stripe deve verificare che l'attivit\xE0 di questo account possa essere supportata sulla propria piattaforma.",
  "Stripe doit v\xE9rifier que l'organisation n'est pas en relation avec une personne ou une juridiction sanctionn\xE9e.": "Stripe deve verificare che l'organizzazione non sia in relazione con una persona o una giurisdizione soggetta a sanzioni.",
  "Stripe doit v\xE9rifier que l'organisation n'est pas li\xE9e \xE0 une personne d'int\xE9r\xEAt ou \xE0 une personne politiquement expos\xE9e.": "Stripe deve verificare che l'organizzazione non sia collegata a una persona di interesse o a una persona politicamente esposta.",
  "Stripe doit v\xE9rifier que le titulaire du compte contr\xF4le bien l'adresse ou le nom de domaine qu'il a fourni.": "Stripe deve verificare che il titolare dell'account controlli effettivamente l'indirizzo o il nome di dominio che ha fornito.",
  "Stripe est tenu de conserver des fonds pour une raison l\xE9gale. Il peut \xEAtre n\xE9cessaire de remettre des fonds \xE0 un tiers.": "Stripe \xE8 tenuto a trattenere fondi per motivi legali. Potrebbe essere necessario trasferire fondi a una terza parte.",
  "Stripe n'a pas pu acc\xE9der au site web. Si vous bloquez l'acc\xE8s \xE0 votre site dans certaines r\xE9gions, d\xE9sactivez temporairement ce blocage jusqu'\xE0 la v\xE9rification.": "Impossibile accedere al sito web. Se blocca l'accesso al sito in alcune regioni, disattivi temporaneamente tale blocco fino alla verifica.",
  "Stripe n'a pas pu confirmer ces informations : {champs}. Corrigez-les si elles sont fausses, ou fournissez \xE0 la place {pistes}.": "Stripe non \xE8 riuscito a confermare queste informazioni: {champs}. Correggetele se sono errate, oppure fornite al loro posto {pistes}.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Corrigez-les si elles sont fausses, ou prouvez l'identit\xE9 autrement.": "Stripe non \xE8 riuscito a confermare l'identit\xE0 a partire dalle informazioni inserite. Correggetele se sono errate, oppure dimostrate l'identit\xE0 in un altro modo.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Fournissez {pistes} : une seule suffit. Si une information est fausse, vous pouvez aussi la corriger.": "Stripe non \xE8 riuscito a confermare l'identit\xE0 a partire dalle informazioni inserite. Fornite {pistes}: ne basta uno. Se un'informazione \xE8 errata, potete anche correggerla.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Fournissez {pistes}. Si une information est fausse, vous pouvez aussi la corriger.": "Stripe non \xE8 riuscito a confermare l'identit\xE0 a partire dalle informazioni inserite. Fornite {pistes}. Se un'informazione \xE8 errata, potete anche correggerla.",
  "Stripe n'a pas pu valider l'ensemble ville, r\xE9gion et code postal de l'adresse fournie.": "Stripe non \xE8 riuscita a convalidare la combinazione di citt\xE0, regione e codice postale dell'indirizzo fornito.",
  "Stripe n'a pas pu valider le nom de la rue ni le num\xE9ro de l'adresse fournie.": "Stripe non \xE8 riuscita a convalidare il nome della via n\xE9 il numero civico dell'indirizzo fornito.",
  "Stripe n'a pas pu valider le num\xE9ro de t\xE9l\xE9phone. Son format doit correspondre \xE0 celui du pays de la personne concern\xE9e.": "Impossibile convalidare il numero di telefono. Il formato deve corrispondere a quello del paese della persona interessata.",
  "Stripe n'a pas pu v\xE9rifier l'activit\xE9 du compte \xE0 l'adresse fournie (site web, profil de r\xE9seaux sociaux ou application).": "Impossibile verificare l'attivit\xE0 dell'account all'indirizzo fornito (sito web, profilo sui social network o applicazione).",
  "Stripe n'a pas pu v\xE9rifier le site web, car il est prot\xE9g\xE9 par un mot de passe.": "Impossibile verificare il sito web, perch\xE9 \xE8 protetto da password.",
  "Stripe n'a pas pu v\xE9rifier le site web, car il est toujours en construction.": "Impossibile verificare il sito web, perch\xE9 \xE8 ancora in costruzione.",
  "Stripe n'a pas pu v\xE9rifier le site web, car il ne pr\xE9sente aucun contenu.": "Impossibile verificare il sito web, perch\xE9 non contiene alcun contenuto.",
  "Stripe n'a pas pu v\xE9rifier le site web, car son acc\xE8s est bloqu\xE9 dans certaines r\xE9gions. D\xE9sactivez temporairement ce blocage jusqu'\xE0 la v\xE9rification.": "Impossibile verificare il sito web, perch\xE9 l'accesso \xE8 bloccato in alcune regioni. Disattivi temporaneamente tale blocco fino alla verifica.",
  "Stripe n'a pas pu v\xE9rifier les informations du compte. Vous pouvez t\xE9l\xE9charger un document de v\xE9rification ou mettre \xE0 jour vos informations.": "Non \xE8 stato possibile per Stripe verificare le informazioni dell'account. Pu\xF2 caricare un documento di verifica oppure aggiornare le Sue informazioni.",
  "Stripe n'a pas pu v\xE9rifier les pouvoirs de la personne qui autorise le compte. Remplacez-la par une personne d\xFBment enregistr\xE9e en tant que repr\xE9sentant autoris\xE9.": "Non \xE8 stato possibile per Stripe verificare i poteri della persona che autorizza l'account. La sostituisca con una persona debitamente registrata come rappresentante autorizzato.",
  "Stripe n'a pas pu v\xE9rifier les pouvoirs du repr\xE9sentant du compte. Ajoutez une personne qui autorise le compte et fournissez une lettre d'autorisation sign\xE9e par elle.": "Non \xE8 stato possibile per Stripe verificare i poteri del rappresentante dell'account. Aggiunga una persona che autorizza l'account e fornisca una lettera di autorizzazione firmata da tale persona.",
  "Stripe n'a pas trouv\xE9 d'informations \xE0 partir des donn\xE9es saisies. V\xE9rifiez qu'elles sont correctes.": "Stripe non ha trovato informazioni corrispondenti ai dati inseriti. Verifichi che siano corretti.",
  "Stripe signale une erreur de v\xE9rification (code : {code}).": "Stripe ha segnalato un errore di verifica (codice: {code}).",
  "Stripe v\xE9rifie actuellement les informations du compte. Aucune action n'est requise.": "Stripe sta attualmente verificando le informazioni dell'account. Non \xE8 richiesta alcuna azione.",
  "T\xE9l\xE9phone du service donateurs / support": "Telefono del servizio donatori / assistenza",
  "Une information est manquante.": "Manca un'informazione.",
  "Une information est manquante. D\xE9tail donn\xE9 par Stripe : \xAB {detail} \xBB": "Manca un'informazione. Dettaglio fornito da Stripe: \xAB {detail} \xBB",
  "une pi\xE8ce d'identit\xE9": "un documento d'identit\xE0",
  "une pi\xE8ce d'identit\xE9 ou un justificatif de domicile": "un documento d'identit\xE0 o una prova di residenza",
  "une v\xE9rification par selfie": "una verifica tramite selfie",
  "URL du service donateurs / support": "URL del servizio donatori / assistenza",
  "V\xE9rification d'identit\xE9": "Verifica dell'identit\xE0",
  "V\xE9rification de l'adresse": "Verifica dell'indirizzo",
  "V\xE9rification des personnes politiquement expos\xE9es": "Verifica delle persone politicamente esposte",
  "V\xE9rification du compte bancaire": "Verifica del conto bancario",
  "V\xE9rification du nom de domaine": "Verifica del nome di dominio",
  "V\xE9rification li\xE9e aux sanctions": "Verifica relativa alle sanzioni",
  "V\xE9rification Stripe en cours": "Verifica Stripe in corso",
  "Verso de la pi\xE8ce d'identit\xE9": "Retro del documento d'identit\xE0",
  "Ville": "Citt\xE0",
  "Ville de l'organisation": "Citt\xE0 dell'organizzazione"
};
var it_default = textes4;

// src/textesStripe/nl.ts
var textes5 = {
  "{liste}, ou {piste}": "{liste} of {piste}",
  "4 derniers chiffres du num\xE9ro de s\xE9curit\xE9 sociale": "Laatste 4 cijfers van het burgerservicenummer",
  "Acceptation des conditions d'utilisation Stripe": "Acceptatie van de gebruiksvoorwaarden van Stripe",
  "Activit\xE9 dans un secteur r\xE9glement\xE9": "Activiteit in een gereguleerde sector",
  "Adresse de l'organisation (compl\xE9ment)": "Adres van de organisatie (aanvullend)",
  "Adresse de l'organisation (rue)": "Adres van de organisatie (straat)",
  "Adresse du service donateurs / support": "Adres donateursservice / support",
  "Adresse du site web": "Website-adres",
  "Adresse email": "E-mailadres",
  "Adresse IP d'acceptation des CGU": "IP-adres van acceptatie van de gebruiksvoorwaarden",
  "Adresse personnelle (compl\xE9ment)": "Particulier adres (aanvulling)",
  "Adresse personnelle (rue)": "Particulier adres (straat)",
  "associations caritatives sans statut d'enregistrement au dossier (par exemple un enregistrement 501(c)(3) ou une classification \xE9quivalente)": "liefdadigheidsinstellingen zonder registratiestatus in het dossier (bijvoorbeeld een 501(c)(3)-registratie of een gelijkwaardige classificatie)",
  "Aucun propri\xE9taire n'a \xE9t\xE9 indiqu\xE9. Fournissez des informations sur tous les propri\xE9taires.": "Er is geen eigenaar opgegeven. Geef informatie over alle eigenaren op.",
  "Cadre dirigeant \u2014 {champ}": "Leidinggevende \u2014 {champ}",
  "Cat\xE9gorie d'activit\xE9 (code MCC)": "Bedrijfscategorie (MCC-code)",
  "Ce type d'organisation n'est pas pris en charge par Stripe.": "Dit type organisatie wordt niet ondersteund door Stripe.",
  "Code postal": "Postcode",
  "Code postal de l'organisation": "Postcode van de organisatie",
  "Compte bancaire pour les versements (IBAN)": "Bankrekening voor uitbetalingen (IBAN)",
  "Compte en cours de v\xE9rification": "Account wordt geverifieerd",
  "Compte refus\xE9": "Account geweigerd",
  "Compte rejet\xE9": "Account afgewezen",
  "Compte rejet\xE9 \u2014 Conditions d'utilisation": "Account afgewezen \u2014 Servicevoorwaarden",
  "Compte rejet\xE9 \u2014 v\xE9rification incompl\xE8te": "Account afgewezen \u2014 verificatie onvolledig",
  "Compte restreint par Stripe": "Account beperkt door Stripe",
  "Compte suspendu par Donoor": "Account opgeschort door Donoor",
  "Confirmation que tous les cadres dirigeants ont \xE9t\xE9 d\xE9clar\xE9s": "Bevestiging dat alle leidinggevenden zijn opgegeven",
  "Confirmation que tous les dirigeants ont \xE9t\xE9 d\xE9clar\xE9s": "Bevestiging dat alle bestuurders zijn opgegeven",
  "Confirmation que tous les propri\xE9taires ont \xE9t\xE9 d\xE9clar\xE9s": "Bevestiging dat alle eigenaren zijn opgegeven",
  "Contactez le service d'assistance de Stripe pour conna\xEEtre les raisons de l'\xE9chec de la v\xE9rification.": "Neem contact op met de ondersteuning van Stripe om te achterhalen waarom de verificatie is mislukt.",
  "Coordonn\xE9es du service client": "Contactgegevens van de klantenservice",
  "Date de naissance": "Geboortedatum",
  "D\xE9cret minist\xE9riel (le cas \xE9ch\xE9ant)": "Ministerieel besluit (indien van toepassing)",
  "Demande d'informations de Stripe": "Verzoek om informatie van Stripe",
  "Demande de conformit\xE9": "Verzoek over naleving",
  "D\xE9partement / r\xE9gion": "Provincie / regio",
  "D\xE9partement / r\xE9gion de l'organisation": "Provincie / regio van de organisatie",
  "Des cadres dirigeants n'ont pas \xE9t\xE9 indiqu\xE9s.": "Niet alle leidinggevenden zijn opgegeven.",
  "Des dirigeants en trop ont \xE9t\xE9 d\xE9clar\xE9s.": "Er zijn te veel bestuurders opgegeven.",
  "Description de votre activit\xE9": "Beschrijving van uw activiteit",
  "Description des produits ou services": "Beschrijving van producten of diensten",
  "Dirigeant \u2014 {champ}": "Bestuurder \u2014 {champ}",
  "Document": "Document",
  "Document officiel de l'organisation": "Officieel document van de organisatie",
  "Document refus\xE9 (mode test).": "Document geweigerd (testmodus).",
  "Elle ne peut pas \xEAtre un bo\xEEtier de contrat routier.": "Het mag geen postbus op een landelijke route zijn.",
  "Elle ne peut pas \xEAtre une bo\xEEte postale priv\xE9e.": "Het mag geen particuliere postbus zijn.",
  "Email du service donateurs / support": "E-mailadres donateursservice / support",
  "Examen de la nature de votre activit\xE9": "Beoordeling van de aard van uw activiteit",
  "Fonction au sein de l'organisation": "Functie binnen de organisatie",
  "Fonctionnalit\xE9s \xE0 demander": "Functies die moeten worden aangevraagd",
  "Fonds retenus pour raison l\xE9gale": "Om juridische redenen ingehouden middelen",
  "Forme juridique de l'organisation": "Rechtsvorm van de organisatie",
  "Fournissez un nouveau fichier : image en couleur, 10 Mo maximum, pi\xE8ces d'identit\xE9 en JPG ou PNG, justificatifs d'adresse ou d'entit\xE9 juridique en JPG, PNG ou PDF, toutes les pages incluses, sans mot de passe.": "Upload een nieuw bestand: kleurenafbeelding, maximaal 10 MB, identiteitsbewijzen in JPG of PNG, bewijzen van adres of rechtspersoon in JPG, PNG of PDF, alle pagina's inbegrepen, zonder wachtwoord.",
  "Genre": "Geslacht",
  "Il se peut que le document ait \xE9t\xE9 modifi\xE9. Contactez le service d'assistance de Stripe pour conna\xEEtre les raisons de l'\xE9chec de la v\xE9rification.": "Het document is mogelijk gewijzigd. Neem contact op met de ondersteuning van Stripe om te achterhalen waarom de verificatie is mislukt.",
  "Informations \xE0 confirmer": "Te bevestigen gegevens",
  "Informations \xE0 fournir": "Gegevens die moeten worden verstrekt",
  "Informations compl\xE9mentaires sur votre activit\xE9": "Aanvullende informatie over uw activiteit",
  "Informations sur l'organisation": "Informatie over de organisatie",
  "Informations sur la nature de votre activit\xE9": "Informatie over de aard van uw activiteit",
  "Intervention de la plateforme": "Tussenkomst van het platform",
  "Justificatif d'enregistrement de l'organisation": "Registratiebewijs van de organisatie",
  "Justificatif de domicile (facture d'\xE9nergie, quittance de loyer, etc.)": "Bewijs van adres (energierekening, huurquittantie, enz.)",
  "Justificatif du num\xE9ro fiscal (SIRET)": "Bewijs van fiscaal nummer (SIRET)",
  "L'acceptation des conditions d'utilisation de Stripe n'est plus valide.": "De aanvaarding van de gebruiksvoorwaarden van Stripe is niet meer geldig.",
  "L'administration fiscale n'a pas pu v\xE9rifier les informations fournies. Corrigez le nom de l'organisation ou le num\xE9ro fiscal, ou t\xE9l\xE9chargez un document justificatif.": "De belastingdienst kon de opgegeven gegevens niet verifi\xEBren. Corrigeer de naam van de organisatie of het fiscaal nummer, of upload een bewijsdocument.",
  "L'adresse de r\xE9sidence n'a pas pu \xEAtre v\xE9rifi\xE9e.": "Het woonadres kon niet worden geverifieerd.",
  "L'adresse doit \xEAtre une adresse physique valide \xE0 partir de laquelle le compte exerce ses activit\xE9s.": "Het adres moet een geldig fysiek adres zijn waarop het account zijn activiteiten uitoefent.",
  "L'adresse du site correspond \xE0 un site g\xE9n\xE9rique que Stripe consid\xE8re comme sans lien avec le compte. Fournissez une adresse propre \xE0 votre organisation.": "Het webadres verwijst naar een generieke site die Stripe als niet gelinkt aan het account beschouwt. Geef een adres op dat uniek is voor uw organisatie.",
  "L'adresse du site de l'organisation doit faire r\xE9f\xE9rence aux produits et services qu'elle fournit. Stripe pourrait exiger une modification de cette adresse avant de pouvoir prendre le compte en charge.": "Het websiteadres van de organisatie moet verwijzen naar de producten en diensten die zij levert. Stripe kan vereisen dat dit adres wordt gewijzigd voordat het account kan worden beheerd.",
  "L'adresse fournie est celle d'un agent enregistr\xE9, qui n'est pas accept\xE9e.": "Het opgegeven adres is dat van een geregistreerde agent, die niet wordt geaccepteerd.",
  "L'adresse fournie est celle d'un service de r\xE9ception de courrier (CMRA), qui n'est pas accept\xE9e.": "Het opgegeven adres is dat van een postontvangstdienst (CMRA), die niet wordt geaccepteerd.",
  "L'organisation pourrait vendre des produits ou des services prot\xE9g\xE9s par le droit d'auteur. Stripe a besoin d'informations suppl\xE9mentaires pour v\xE9rifier que le compte est autoris\xE9 \xE0 les vendre.": "De organisatie verkoopt mogelijk producten of diensten die beschermd zijn door auteursrecht. Stripe heeft aanvullende informatie nodig om te controleren of het account hiervoor gemachtigd is.",
  "La date de naissance doit se situer dans les 120 derni\xE8res ann\xE9es.": "De geboortedatum moet binnen de laatste 120 jaar liggen.",
  "La description doit comporter au moins 10 caract\xE8res.": "De beschrijving moet minstens 10 tekens bevatten.",
  "La description doit \xEAtre diff\xE9rente de l'adresse du site web.": "De beschrijving moet verschillen van het webadres.",
  "La liste des dirigeants n'a pas \xE9t\xE9 fournie. Mettez \xE0 jour le compte et t\xE9l\xE9chargez un document d'immatriculation avec la liste des dirigeants actuels.": "De lijst met bestuurders is niet aangeleverd. Werk het account bij en upload een inschrijvingsdocument met de actuele lijst van bestuurders.",
  "La personne doit avoir au moins 13 ans.": "De persoon moet minstens 13 jaar oud zijn.",
  "La personne doit avoir au moins 18 ans.": "De persoon moet minstens 18 jaar oud zijn.",
  "La personne responsable du compte doit v\xE9rifier son identit\xE9 en t\xE9l\xE9chargeant une pi\xE8ce d'identit\xE9 officielle et un selfie.": "De persoon die verantwoordelijk is voor het account moet zijn identiteit verifi\xEBren door een officieel identiteitsbewijs en een selfie te uploaden.",
  "La plateforme a initi\xE9 une intervention sur ce compte.": "Het platform heeft een tussenkomst voor dit account gestart.",
  "La plateforme Donoor a suspendu ce compte. Contactez le support Donoor.": "Het Donoor-platform heeft dit account opgeschort. Neem contact op met de Donoor-ondersteuning.",
  "La valeur fournie n'est pas valide.": "De opgegeven waarde is niet geldig.",
  "Le compte est en cours de v\xE9rification par Stripe.": "Het account wordt momenteel door Stripe geverifieerd.",
  "Le compte Stripe de l'organisation doit contenir une description exacte des produits ou services.": "Het Stripe-account van de organisatie moet een nauwkeurige beschrijving van de producten of diensten bevatten.",
  "Le document a \xE9t\xE9 refus\xE9 : il est en niveaux de gris.": "Het document is geweigerd: het is in grijstinten.",
  "Le document a \xE9t\xE9 refus\xE9 : il s'agit d'une copie.": "Het document is geweigerd: het gaat om een kopie.",
  "Le document est en niveaux de gris.": "Het document is in grijstinten.",
  "Le document est incomplet.": "Het document is onvolledig.",
  "Le document est une copie.": "Het document is een kopie.",
  "Le document n'est pas lisible.": "Het document is niet leesbaar.",
  "Le document n'est pas sign\xE9.": "Het document is niet ondertekend.",
  "Le document ne comporte pas de date de d\xE9livrance ou d'expiration, ou il est expir\xE9. Une pi\xE8ce d'identit\xE9 doit \xEAtre valide \xE0 la date d'envoi ; un justificatif de domicile doit dater de moins de six mois.": "Het document bevat geen uitgiftedatum of vervaldatum, of het is verlopen. Een identiteitsbewijs moet geldig zijn op de verzenddatum; een bewijs van woonadres mag niet ouder zijn dan zes maanden.",
  "Le document t\xE9l\xE9charg\xE9 ne contient pas toutes les informations requises. T\xE9l\xE9chargez un autre document contenant les informations manquantes.": "Het ge\xFCploade document bevat niet alle vereiste gegevens. Upload een ander document met de ontbrekende gegevens.",
  "Le fichier est corrompu.": "Het bestand is beschadigd.",
  "Le fichier est trop volumineux.": "Het bestand is te groot.",
  "Le fichier fourni n'est pas une pi\xE8ce accept\xE9e par Stripe (pays pris en charge, type de document attendu pour ce type d'organisation). Fournissez un nouveau fichier conforme.": "Het aangeleverde bestand is geen document dat Stripe accepteert (ondersteund land, verwacht documenttype voor dit type organisatie). Lever een nieuw, conform bestand aan.",
  "Le fichier n'a pas \xE9t\xE9 t\xE9l\xE9charg\xE9.": "Het bestand is niet ge\xFCpload.",
  "Le format de l'adresse du site est incorrect. Fournissez une adresse correctement format\xE9e, par exemple https://example.com.": "De notatie van het webadres is onjuist. Geef een correct opgemaakt adres op, bijvoorbeeld https://example.com.",
  "Le libell\xE9 de relev\xE9 bancaire doit comporter au moins 5 caract\xE8res.": "De omschrijving op het bankafschrift moet minstens 5 tekens bevatten.",
  "Le libell\xE9 de relev\xE9 bancaire doit \xEAtre similaire au nom de l'organisation, \xE0 sa d\xE9nomination l\xE9gale ou \xE0 l'adresse de son site web.": "De omschrijving op het bankafschrift moet lijken op de naam van de organisatie, de wettelijke naam of het webadres van de organisatie.",
  "Le libell\xE9 de relev\xE9 bancaire ne peut pas correspondre \xE0 un nom d'entreprise g\xE9n\xE9rique ou bien connu.": "De omschrijving op het bankafschrift mag niet overeenkomen met de naam van een generiek of algemeen bekend bedrijf.",
  "Le m\xEAme type de document a \xE9t\xE9 fourni deux fois.": "Hetzelfde type document is twee keer aangeleverd.",
  "Le nom de l'organisation doit correspondre \xE0 l'activit\xE9 du compte et ne peut pas \xEAtre un nom g\xE9n\xE9rique ou bien connu.": "De naam van de organisatie moet overeenkomen met de activiteit van het account en mag geen generieke of algemeen bekende naam zijn.",
  "Le nom de l'organisation doit \xEAtre facile \xE0 comprendre et compos\xE9 de mots reconnaissables.": "De naam van de organisatie moet gemakkelijk te begrijpen zijn en uit herkenbare woorden bestaan.",
  "Le nom l\xE9gal de l'organisation ne peut pas \xEAtre un nom g\xE9n\xE9rique ou bien connu.": "De wettelijke naam van de organisatie mag geen generieke of algemeen bekende naam zijn.",
  "Le num\xE9ro fiscal doit \xEAtre compos\xE9 de 9 chiffres, sans tirets ni autres caract\xE8res sp\xE9ciaux.": "Het fiscaal nummer moet uit 9 cijfers bestaan, zonder streepjes of andere speciale tekens.",
  "Le num\xE9ro IEC de l'organisation n'a pas pu \xEAtre v\xE9rifi\xE9.": "Het IEC-nummer van de organisatie kon niet worden geverifieerd.",
  "Le pays du repr\xE9sentant n'est pas accept\xE9 pour ce compte.": "Het land van de vertegenwoordiger wordt niet geaccepteerd voor dit account.",
  "Le pr\xE9fixe du libell\xE9 de relev\xE9 bancaire doit correspondre \xE0 votre libell\xE9, au nom de votre organisation, \xE0 sa d\xE9nomination l\xE9gale ou \xE0 votre site web.": "Het voorvoegsel van de omschrijving op het bankafschrift moet overeenkomen met uw omschrijving, de naam van uw organisatie, de wettelijke naam of uw website.",
  "Le recto du document est manquant.": "De voorkant van het document ontbreekt.",
  "Le signataire du document n'a pas pu \xEAtre v\xE9rifi\xE9.": "De ondertekenaar van het document kon niet worden geverifieerd.",
  "Le site web ne comporte pas de nom d'organisation ni de description claire des biens et services propos\xE9s.": "De website bevat geen naam van de organisatie en geen duidelijke beschrijving van de aangeboden goederen en diensten.",
  "Le site web ne contient pas d'informations sur le service client.": "De website bevat geen informatie over de klantenservice.",
  "Le site web ne contient pas de conditions d'annulation.": "De website bevat geen annuleringsvoorwaarden.",
  "Le site web ne contient pas de conditions g\xE9n\xE9rales.": "De website bevat geen algemene voorwaarden.",
  "Le site web ne contient pas de politique de remboursement.": "De website bevat geen terugbetalingsbeleid.",
  "Le site web ne contient pas de politique ni de processus de retour.": "De website bevat geen beleid of procedure voor retourzendingen.",
  "Le site web ne contient pas les mentions applicables aux produits et services soumis \xE0 des restrictions juridiques ou \xE0 l'exportation.": "De website bevat niet de vermeldingen die van toepassing zijn op producten en diensten die aan wettelijke of exportbeperkingen zijn onderworpen.",
  "Le type ou la structure de l'organisation ne semble pas correct. Indiquez le type et la structure appropri\xE9s pour ce compte.": "Het type of de rechtsvorm van de organisatie lijkt niet correct. Geef het juiste type en de juiste rechtsvorm voor dit account op.",
  "Le verso du document est manquant.": "De achterkant van het document ontbreekt.",
  "Les conditions d'utilisation de Stripe interdisent la prise en charge de cette organisation en raison du niveau de risque qu'elle pr\xE9sente. Le titulaire du compte peut contester cette d\xE9cision.": "De gebruiksvoorwaarden van Stripe verbieden het beheren van deze organisatie vanwege het risiconiveau dat zij vormt. De accounthouder kan deze beslissing betwisten.",
  "Les conditions d'utilisation de Stripe interdisent la prise en charge de l'activit\xE9. Le compte peut faire appel de cette d\xE9cision.": "De gebruiksvoorwaarden van Stripe verbieden het accepteren van deze activiteit. Het account kan tegen dit besluit in beroep gaan.",
  "Les dirigeants d\xE9clar\xE9s ne correspondent pas \xE0 ceux que Stripe a trouv\xE9s.": "De opgegeven bestuurders komen niet overeen met de bestuurders die Stripe heeft gevonden.",
  "Les dirigeants mentionn\xE9s dans le document ne figurent pas dans le compte. Mettez \xE0 jour le compte et t\xE9l\xE9chargez un document d'immatriculation avec la liste des dirigeants actuels.": "De bestuurders die in het document worden vermeld, staan niet in het account. Werk het account bij en upload een inschrijvingsdocument met de actuele lijst van bestuurders.",
  "Les informations du site web ne correspondent pas \xE0 celles du compte Stripe.": "De gegevens van de website komen niet overeen met die van het Stripe-account.",
  "Les informations figurant sur la pi\xE8ce d'identit\xE9 ne correspondent pas \xE0 celles fournies. V\xE9rifiez et corrigez les informations fournies.": "De gegevens op het identiteitsbewijs komen niet overeen met de opgegeven gegevens. Controleer de opgegeven gegevens en corrigeer ze.",
  "Les informations figurant sur le document ne correspondent pas \xE0 celles fournies. V\xE9rifiez-les, puis corrigez-les ou t\xE9l\xE9chargez un document correspondant.": "De gegevens op het document komen niet overeen met de opgegeven gegevens. Controleer ze en corrigeer ze, of upload een overeenkomend document.",
  "Libell\xE9 de relev\xE9 bancaire": "Omschrijving op bankafschrift",
  "Libell\xE9 qui appara\xEEtra sur le relev\xE9 bancaire des donateurs": "Beschrijving die op het bankafschrift van donateurs verschijnt",
  "Licence ou autorisation d'exercer": "Licentie of vergunning om activiteiten uit te oefenen",
  "Motif indiqu\xE9 par Stripe : {motif}.": "Reden opgegeven door Stripe: {motif}.",
  "Nationalit\xE9": "Nationaliteit",
  "Nom commercial / d'affichage de l'organisation": "Handelsnaam / weergavenaam van de organisatie",
  "Nom de famille": "Achternaam",
  "Nom l\xE9gal de l'organisation": "Wettelijke naam van de organisatie",
  "Non": "Nee",
  "Num\xE9ro d'enregistrement de l'organisation": "Registratienummer van de organisatie",
  "Num\xE9ro d'identification (\xE9quivalent du num\xE9ro de s\xE9curit\xE9 sociale)": "Identificatienummer (gelijkwaardig aan het burgerservicenummer)",
  "Num\xE9ro de t\xE9l\xE9phone": "Telefoonnummer",
  "Num\xE9ro de t\xE9l\xE9phone de l'organisation": "Telefoonnummer van de organisatie",
  "Num\xE9ro de TVA intracommunautaire": "Intracommunautair btw-nummer",
  "Num\xE9ro SIRET / identifiant fiscal": "SIRET-nummer / fiscaal identificatienummer",
  "Oui": "Ja",
  "Pays": "Land",
  "Pays de l'organisation": "Land van de organisatie",
  "Pi\xE8ce d'identit\xE9 (passeport, carte nationale d'identit\xE9 ou permis de conduire)": "Identiteitsbewijs (paspoort, identiteitskaart of rijbewijs)",
  "Politique d'ex\xE9cution des commandes": "Beleid voor orderafhandeling",
  "Pourcentage de propri\xE9t\xE9": "Eigendomspercentage",
  "Pr\xE9fixe du libell\xE9 sur le relev\xE9 bancaire": "Voorvoegsel van de beschrijving op het bankafschrift",
  "Pr\xE9nom": "Voornaam",
  "Preuve de pr\xE9sence (selfie)": "Bewijs van aanwezigheid (selfie)",
  "Produits ou services prot\xE9g\xE9s par le droit d'auteur": "Producten of diensten beschermd door auteursrecht",
  "Propri\xE9taire \u2014 {champ}": "Eigenaar \u2014 {champ}",
  "Recours contre un refus": "Bezwaar tegen een weigering",
  "Recours contre un refus de prise en charge": "Beroep tegen weigering van acceptatie",
  "Recours contre une r\xE9serve sur le compte": "Bezwaar tegen een beperking op het account",
  "Recto de la pi\xE8ce d'identit\xE9": "Voorkant van het identiteitsbewijs",
  "Repr\xE9sentant l\xE9gal \u2014 {champ}": "Wettelijk vertegenwoordiger \u2014 {champ}",
  "Selon Stripe, ce compte semble proposer des produits ou services relevant d'une cat\xE9gorie interdite ou restreinte par le contrat de services Stripe. Cat\xE9gorie : {categorie}. Plus d'informations : {lien}": "Volgens Stripe lijkt dit account producten of diensten aan te bieden die behoren tot een categorie die door de Stripe-serviceovereenkomst is verboden of beperkt. Categorie: {categorie}. Meer informatie: {lien}",
  "Selon Stripe, certaines fonctionnalit\xE9s doivent encore \xEAtre demand\xE9es pour ce compte par la plateforme Donoor.": "Volgens Stripe moeten voor dit account nog bepaalde functies worden aangevraagd door het Donoor-platform.",
  "Selon Stripe, cette demande ne se traite pas dans un formulaire : contactez le service d'assistance de Stripe.": "Volgens Stripe kan dit verzoek niet via een formulier worden afgehandeld. Neem contact op met de ondersteuning van Stripe.",
  "Selon Stripe, cette demande se r\xE8gle par l'interm\xE9diaire d'un tiers.": "Volgens Stripe wordt dit verzoek afgehandeld via een derde partij.",
  "Selon Stripe, cette demande se remplit dans un formulaire Stripe.": "Volgens Stripe wordt dit verzoek ingevuld in een formulier van Stripe.",
  "Selon Stripe, des informations suppl\xE9mentaires sont requises pour activer les fonctionnalit\xE9s de ce compte.": "Volgens Stripe zijn aanvullende gegevens vereist om de functies van dit account te activeren.",
  "Selon Stripe, l'organisation peut exercer ses activit\xE9s dans un secteur r\xE9glement\xE9. Stripe pourrait avoir besoin d'informations suppl\xE9mentaires sur la nature de l'activit\xE9 ou sur les licences requises afin de v\xE9rifier qu'il peut prendre le compte en charge.": "Volgens Stripe kan de organisatie activiteiten uitoefenen in een gereguleerde sector. Stripe heeft mogelijk aanvullende informatie nodig over de aard van de activiteit of over de vereiste vergunningen om te controleren of het account kan worden beheerd.",
  "Selon Stripe, le compte pourrait figurer sur une liste de personnes ou d'entreprises interdites. Stripe effectue une v\xE9rification et pourra refuser ou r\xE9tablir l'acc\xE8s au compte en cons\xE9quence.": "Volgens Stripe zou het account op een lijst van uitgesloten personen of bedrijven kunnen staan. Stripe voert een controle uit en kan de toegang tot het account dienovereenkomstig weigeren of herstellen.",
  "Selon Stripe, vous devez r\xE9pondre vous-m\xEAme \xE0 cette demande de v\xE9rification.": "Volgens Stripe moet u dit verificatieverzoek zelf beantwoorden.",
  "Site web de l'organisation": "Website van de organisatie",
  "Statut d'exposition politique": "Status politieke blootstelling",
  "Statuts de l'organisation": "Statuten van de organisatie",
  "Stripe a besoin d'informations suppl\xE9mentaires pour v\xE9rifier qu'il peut prendre le compte en charge.": "Stripe heeft aanvullende informatie nodig om te controleren of het account kan worden beheerd.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur l'organisation.": "Stripe heeft aanvullende informatie over de organisatie nodig.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur la conformit\xE9.": "Stripe heeft aanvullende informatie over naleving nodig.",
  "Stripe a besoin d'informations suppl\xE9mentaires sur la nature de l'activit\xE9 afin de v\xE9rifier qu'il peut prendre le compte en charge.": "Stripe heeft aanvullende informatie over de aard van de activiteit nodig om te kunnen controleren of het account kan worden beheerd.",
  "Stripe a besoin d'un libell\xE9 de relev\xE9 bancaire qui corresponde exactement \xE0 l'activit\xE9 de l'organisation.": "Stripe heeft een omschrijving op het bankafschrift nodig die precies overeenkomt met de activiteit van de organisatie.",
  "Stripe a demand\xE9 des informations suppl\xE9mentaires dans le cadre de son \xE9valuation des risques.": "Stripe heeft aanvullende informatie opgevraagd als onderdeel van de risicobeoordeling.",
  "Stripe a d\xE9tect\xE9 que ce compte utilise un site web, un profil sur les r\xE9seaux sociaux ou une application pour vendre ou promouvoir des produits ou services, mais aucune adresse n'a \xE9t\xE9 fournie. Fournissez une adresse.": "Stripe heeft vastgesteld dat dit account een website, een profiel op sociale media of een app gebruikt om producten of diensten te verkopen of te promoten, maar er is geen adres opgegeven. Geef een adres op.",
  "Stripe a impos\xE9 une r\xE9serve au compte, ce qui n'affecte pas sa capacit\xE9 \xE0 accepter des paiements. Le titulaire du compte peut contester cette d\xE9cision.": "Stripe heeft een beperking op het account geplaatst, die de mogelijkheid om betalingen te accepteren niet be\xEFnvloedt. De accounthouder kan deze beslissing betwisten.",
  "Stripe a refus\xE9 le compte, car il figure sur une liste de personnes ou d'entreprises interdites \xE9tablie par un tiers (par exemple un fournisseur de services financiers ou un gouvernement).": "Stripe heeft het account geweigerd, omdat het voorkomt op een lijst van uitgesloten personen of bedrijven die door een derde partij is opgesteld (bijvoorbeeld een financi\xEBle dienstverlener of een overheid).",
  "Stripe a refus\xE9 le motif d'exemption relatif \xE0 la propri\xE9t\xE9. Choisissez un autre motif ou t\xE9l\xE9chargez un justificatif de b\xE9n\xE9ficiaire effectif.": "Stripe heeft de vrijstellingsgrond met betrekking tot eigendom geweigerd. Kies een andere grond of upload een bewijs van uiteindelijk belanghebbende.",
  "Stripe a rejet\xE9 le compte pour suspicion d'infraction \xE0 ses conditions d'utilisation.": "Stripe heeft het account afgewezen wegens vermoedelijke schending van de Servicevoorwaarden.",
  "Stripe a rejet\xE9 le compte pour suspicion de fraude ou d'activit\xE9s ill\xE9gales.": "Stripe heeft het account afgewezen wegens vermoedelijke fraude of illegale activiteiten.",
  "Stripe a rejet\xE9 le compte pour un autre motif, sans le pr\xE9ciser.": "Stripe heeft het account om een andere reden afgewezen, zonder deze te vermelden.",
  "Stripe a rejet\xE9 le compte, car les exigences de v\xE9rification n'ont pas \xE9t\xE9 satisfaites dans le d\xE9lai requis.": "Stripe heeft het account afgewezen, omdat niet binnen de vereiste termijn aan de verificatievereisten is voldaan.",
  "Stripe a restreint le compte sans pr\xE9ciser de motif.": "Stripe heeft het account beperkt zonder een reden op te geven.",
  "Stripe demande les statuts de soci\xE9t\xE9s qui d\xE9tiennent une participation importante.": "Stripe vraagt om de statuten van vennootschappen die een aanzienlijk belang aanhouden.",
  "Stripe demande un justificatif d'immatriculation de soci\xE9t\xE9s qui d\xE9tiennent une participation importante.": "Stripe vraagt om een inschrijvingsbewijs van vennootschappen die een aanzienlijk belang aanhouden.",
  "Stripe doit v\xE9rifier l'adresse de l'organisation, avec les justificatifs appropri\xE9s.": "Stripe moet het adres van de organisatie verifi\xEBren, met de juiste bewijsstukken.",
  "Stripe doit v\xE9rifier la politique d'ex\xE9cution des commandes de l'organisation.": "Stripe moet het beleid voor orderafhandeling van de organisatie verifi\xEBren.",
  "Stripe doit v\xE9rifier les coordonn\xE9es du service client de l'organisation.": "Stripe moet de contactgegevens van de klantenservice van de organisatie verifi\xEBren.",
  "Stripe doit v\xE9rifier les informations du compte bancaire associ\xE9 \xE0 l'organisation.": "Stripe moet de gegevens van de bankrekening die aan de organisatie is gekoppeld, verifi\xEBren.",
  "Stripe doit v\xE9rifier que l'activit\xE9 de ce compte peut \xEAtre prise en charge sur sa plateforme.": "Stripe moet controleren of de activiteit van dit account op zijn platform kan worden ondersteund.",
  "Stripe doit v\xE9rifier que l'organisation n'est pas en relation avec une personne ou une juridiction sanctionn\xE9e.": "Stripe moet verifi\xEBren dat de organisatie geen banden heeft met een gesanctioneerde persoon of jurisdictie.",
  "Stripe doit v\xE9rifier que l'organisation n'est pas li\xE9e \xE0 une personne d'int\xE9r\xEAt ou \xE0 une personne politiquement expos\xE9e.": "Stripe moet verifi\xEBren dat de organisatie niet is gelieerd aan een persoon van belang of aan een politiek prominente persoon.",
  "Stripe doit v\xE9rifier que le titulaire du compte contr\xF4le bien l'adresse ou le nom de domaine qu'il a fourni.": "Stripe moet verifi\xEBren dat de accounthouder daadwerkelijk eigenaar is van het adres of de domeinnaam die hij heeft opgegeven.",
  "Stripe est tenu de conserver des fonds pour une raison l\xE9gale. Il peut \xEAtre n\xE9cessaire de remettre des fonds \xE0 un tiers.": "Stripe is wettelijk verplicht om middelen te reserveren. Het kan nodig zijn om middelen aan een derde partij over te maken.",
  "Stripe n'a pas pu acc\xE9der au site web. Si vous bloquez l'acc\xE8s \xE0 votre site dans certaines r\xE9gions, d\xE9sactivez temporairement ce blocage jusqu'\xE0 la v\xE9rification.": "Stripe had geen toegang tot de website. Als u de toegang tot uw site in bepaalde regio's blokkeert, schakel deze blokkering dan tijdelijk uit totdat de verificatie is afgerond.",
  "Stripe n'a pas pu confirmer ces informations : {champs}. Corrigez-les si elles sont fausses, ou fournissez \xE0 la place {pistes}.": "Stripe kon deze gegevens niet bevestigen: {champs}. Corrigeer ze als ze onjuist zijn, of lever in plaats daarvan {pistes} aan.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Corrigez-les si elles sont fausses, ou prouvez l'identit\xE9 autrement.": "Stripe kon de identiteit niet bevestigen op basis van de ingevoerde gegevens. Corrigeer ze als ze onjuist zijn, of bewijs de identiteit op een andere manier.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Fournissez {pistes} : une seule suffit. Si une information est fausse, vous pouvez aussi la corriger.": "Stripe kon de identiteit niet bevestigen op basis van de ingevoerde gegevens. Lever {pistes} aan: \xE9\xE9n daarvan is voldoende. Als een gegeven onjuist is, kunt u het ook corrigeren.",
  "Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Fournissez {pistes}. Si une information est fausse, vous pouvez aussi la corriger.": "Stripe kon de identiteit niet bevestigen op basis van de ingevoerde gegevens. Lever {pistes} aan. Als een gegeven onjuist is, kunt u het ook corrigeren.",
  "Stripe n'a pas pu valider l'ensemble ville, r\xE9gion et code postal de l'adresse fournie.": "Stripe kon de combinatie van plaats, regio en postcode van het opgegeven adres niet valideren.",
  "Stripe n'a pas pu valider le nom de la rue ni le num\xE9ro de l'adresse fournie.": "Stripe kon de straatnaam en het huisnummer van het opgegeven adres niet valideren.",
  "Stripe n'a pas pu valider le num\xE9ro de t\xE9l\xE9phone. Son format doit correspondre \xE0 celui du pays de la personne concern\xE9e.": "Stripe kon het telefoonnummer niet valideren. De notatie moet overeenkomen met die van het land van de betreffende persoon.",
  "Stripe n'a pas pu v\xE9rifier l'activit\xE9 du compte \xE0 l'adresse fournie (site web, profil de r\xE9seaux sociaux ou application).": "Stripe kon de activiteit van het account niet verifi\xEBren op het opgegeven adres (website, profiel op sociale media of app).",
  "Stripe n'a pas pu v\xE9rifier le site web, car il est prot\xE9g\xE9 par un mot de passe.": "Stripe kon de website niet verifi\xEBren omdat deze met een wachtwoord is beveiligd.",
  "Stripe n'a pas pu v\xE9rifier le site web, car il est toujours en construction.": "Stripe kon de website niet verifi\xEBren omdat deze nog in aanbouw is.",
  "Stripe n'a pas pu v\xE9rifier le site web, car il ne pr\xE9sente aucun contenu.": "Stripe kon de website niet verifi\xEBren omdat deze geen inhoud bevat.",
  "Stripe n'a pas pu v\xE9rifier le site web, car son acc\xE8s est bloqu\xE9 dans certaines r\xE9gions. D\xE9sactivez temporairement ce blocage jusqu'\xE0 la v\xE9rification.": "Stripe kon de website niet verifi\xEBren omdat de toegang in bepaalde regio's is geblokkeerd. Schakel deze blokkering tijdelijk uit totdat de verificatie is afgerond.",
  "Stripe n'a pas pu v\xE9rifier les informations du compte. Vous pouvez t\xE9l\xE9charger un document de v\xE9rification ou mettre \xE0 jour vos informations.": "Stripe kon de accountgegevens niet verifi\xEBren. U kunt een verificatiedocument uploaden of uw gegevens bijwerken.",
  "Stripe n'a pas pu v\xE9rifier les pouvoirs de la personne qui autorise le compte. Remplacez-la par une personne d\xFBment enregistr\xE9e en tant que repr\xE9sentant autoris\xE9.": "Stripe kon de bevoegdheden van de persoon die het account autoriseert niet verifi\xEBren. Vervang deze persoon door iemand die correct is geregistreerd als gemachtigde vertegenwoordiger.",
  "Stripe n'a pas pu v\xE9rifier les pouvoirs du repr\xE9sentant du compte. Ajoutez une personne qui autorise le compte et fournissez une lettre d'autorisation sign\xE9e par elle.": "Stripe kon de bevoegdheden van de accountvertegenwoordiger niet verifi\xEBren. Voeg een persoon toe die het account autoriseert en lever een door die persoon ondertekende machtigingsbrief aan.",
  "Stripe n'a pas trouv\xE9 d'informations \xE0 partir des donn\xE9es saisies. V\xE9rifiez qu'elles sont correctes.": "Stripe heeft op basis van de ingevoerde gegevens geen informatie gevonden. Controleer of deze correct zijn.",
  "Stripe signale une erreur de v\xE9rification (code : {code}).": "Stripe meldt een verificatiefout (code: {code}).",
  "Stripe v\xE9rifie actuellement les informations du compte. Aucune action n'est requise.": "Stripe controleert momenteel de accountgegevens. Er is geen actie vereist.",
  "T\xE9l\xE9phone du service donateurs / support": "Telefoonnummer donateursservice / support",
  "Une information est manquante.": "Er ontbreekt een gegeven.",
  "Une information est manquante. D\xE9tail donn\xE9 par Stripe : \xAB {detail} \xBB": "Er ontbreekt een gegeven. Detail van Stripe: \u201E{detail}\u201D",
  "une pi\xE8ce d'identit\xE9": "een identiteitsbewijs",
  "une pi\xE8ce d'identit\xE9 ou un justificatif de domicile": "een identiteitsbewijs of een adresbewijs",
  "une v\xE9rification par selfie": "een selfieverificatie",
  "URL du service donateurs / support": "URL donateursservice / support",
  "V\xE9rification d'identit\xE9": "Identiteitsverificatie",
  "V\xE9rification de l'adresse": "Adresverificatie",
  "V\xE9rification des personnes politiquement expos\xE9es": "Verificatie van politiek prominente personen",
  "V\xE9rification du compte bancaire": "Verificatie van de bankrekening",
  "V\xE9rification du nom de domaine": "Verificatie van de domeinnaam",
  "V\xE9rification li\xE9e aux sanctions": "Verificatie in verband met sancties",
  "V\xE9rification Stripe en cours": "Stripe-verificatie lopende",
  "Verso de la pi\xE8ce d'identit\xE9": "Achterkant van het identiteitsbewijs",
  "Ville": "Plaats",
  "Ville de l'organisation": "Plaats van de organisatie"
};
var nl_default = textes5;

// src/textesStripe/index.ts
var CATALOGUES = { en: en_default, de: de_default, es: es_default, it: it_default, nl: nl_default };
function langueTextesStripe() {
  return langueDepuisLocale(localeAffichage());
}
function remplir(texte, variables) {
  if (!variables) return texte;
  return texte.replace(
    /\{(\w+)\}/g,
    (brut, nom) => Object.prototype.hasOwnProperty.call(variables, nom) ? String(variables[nom]) : brut
  );
}
function tS(francais, variables) {
  return traduireTexteStripe(francais, variables);
}
function aTraduire(francais) {
  return francais;
}
function traduireTexteStripe(francais, variables) {
  const langue = langueTextesStripe();
  const texte = langue === "fr" ? francais : CATALOGUES[langue][francais] ?? francais;
  return remplir(texte, variables);
}
function libelleEnMinuscules(libelle) {
  return langueTextesStripe() === "de" ? libelle : libelle.toLowerCase();
}

// src/stripe.ts
function normaliserExigencesStripe(req) {
  if (!req) return null;
  return {
    disabled_reason: req.disabled_reason ?? null,
    current_deadline: req.current_deadline ?? null,
    currently_due: [...req.currently_due ?? []],
    past_due: [...req.past_due ?? []],
    eventually_due: [...req.eventually_due ?? []],
    pending_verification: [...req.pending_verification ?? []],
    errors: (req.errors ?? []).map((e) => ({
      code: e.code ?? "",
      reason: e.reason ?? "",
      requirement: e.requirement ?? ""
    })),
    alternatives: (req.alternatives ?? []).map((a) => ({
      alternative_fields_due: [...a.alternative_fields_due ?? []],
      original_fields_due: [...a.original_fields_due ?? []]
    })).filter((a) => a.alternative_fields_due.length > 0 && a.original_fields_due.length > 0)
  };
}
function translateDisabledReason(reason) {
  if (!reason) return null;
  switch (reason) {
    case "action_required.requested_capabilities":
      return {
        title: tS("Fonctionnalit\xE9s \xE0 demander"),
        description: tS("Selon Stripe, certaines fonctionnalit\xE9s doivent encore \xEAtre demand\xE9es pour ce compte par la plateforme Donoor."),
        severity: "warning",
        userActionable: false
      };
    case "listed":
      return {
        title: tS("Compte en cours de v\xE9rification"),
        description: tS("Selon Stripe, le compte pourrait figurer sur une liste de personnes ou d'entreprises interdites. Stripe effectue une v\xE9rification et pourra refuser ou r\xE9tablir l'acc\xE8s au compte en cons\xE9quence."),
        severity: "info",
        userActionable: false
      };
    case "rejected.fraud":
      return {
        title: tS("Compte rejet\xE9"),
        description: tS("Stripe a rejet\xE9 le compte pour suspicion de fraude ou d'activit\xE9s ill\xE9gales."),
        severity: "blocking",
        userActionable: false
      };
    case "rejected.incomplete_verification":
      return {
        title: tS("Compte rejet\xE9 \u2014 v\xE9rification incompl\xE8te"),
        description: tS("Stripe a rejet\xE9 le compte, car les exigences de v\xE9rification n'ont pas \xE9t\xE9 satisfaites dans le d\xE9lai requis."),
        severity: "blocking",
        userActionable: false
      };
    case "rejected.listed":
      return {
        title: tS("Compte refus\xE9"),
        description: tS("Stripe a refus\xE9 le compte, car il figure sur une liste de personnes ou d'entreprises interdites \xE9tablie par un tiers (par exemple un fournisseur de services financiers ou un gouvernement)."),
        severity: "blocking",
        userActionable: false
      };
    case "rejected.other":
      return {
        title: tS("Compte rejet\xE9"),
        description: tS("Stripe a rejet\xE9 le compte pour un autre motif, sans le pr\xE9ciser."),
        severity: "blocking",
        userActionable: false
      };
    case "rejected.terms_of_service":
      return {
        title: tS("Compte rejet\xE9 \u2014 Conditions d'utilisation"),
        description: tS("Stripe a rejet\xE9 le compte pour suspicion d'infraction \xE0 ses conditions d'utilisation."),
        severity: "blocking",
        userActionable: false
      };
    case "requirements.past_due":
      return {
        title: tS("Informations \xE0 fournir"),
        description: tS("Selon Stripe, des informations suppl\xE9mentaires sont requises pour activer les fonctionnalit\xE9s de ce compte."),
        severity: "error",
        userActionable: true
      };
    case "requirements.pending_verification":
      return {
        title: tS("V\xE9rification Stripe en cours"),
        description: tS("Stripe v\xE9rifie actuellement les informations du compte. Aucune action n'est requise."),
        severity: "info",
        userActionable: false
      };
    case "under_review":
      return {
        title: tS("Compte en cours de v\xE9rification"),
        description: tS("Le compte est en cours de v\xE9rification par Stripe."),
        severity: "info",
        userActionable: false
      };
    case "platform_paused":
      return {
        title: tS("Compte suspendu par Donoor"),
        description: tS("La plateforme Donoor a suspendu ce compte. Contactez le support Donoor."),
        severity: "warning",
        userActionable: false
      };
    case "other":
      return {
        title: tS("Compte restreint par Stripe"),
        description: tS("Stripe a restreint le compte sans pr\xE9ciser de motif."),
        severity: "warning",
        userActionable: false
      };
    default:
      return {
        title: tS("Compte restreint par Stripe"),
        description: tS("Motif indiqu\xE9 par Stripe : {motif}.", { motif: reason }),
        severity: "warning",
        userActionable: false
      };
  }
}
var FICHIER_A_REFAIRE = aTraduire("Fournissez un nouveau fichier : image en couleur, 10 Mo maximum, pi\xE8ces d'identit\xE9 en JPG ou PNG, justificatifs d'adresse ou d'entit\xE9 juridique en JPG, PNG ou PDF, toutes les pages incluses, sans mot de passe.");
var PIECE_NE_CORRESPOND_PAS = aTraduire("Les informations figurant sur la pi\xE8ce d'identit\xE9 ne correspondent pas \xE0 celles fournies. V\xE9rifiez et corrigez les informations fournies.");
var DOCUMENT_NE_CORRESPOND_PAS = aTraduire("Les informations figurant sur le document ne correspondent pas \xE0 celles fournies. V\xE9rifiez-les, puis corrigez-les ou t\xE9l\xE9chargez un document correspondant.");
var DOCUMENT_INCOMPLET = aTraduire("Le document t\xE9l\xE9charg\xE9 ne contient pas toutes les informations requises. T\xE9l\xE9chargez un autre document contenant les informations manquantes.");
var ADRESSE_PHYSIQUE = aTraduire("L'adresse doit \xEAtre une adresse physique valide \xE0 partir de laquelle le compte exerce ses activit\xE9s.");
var CATEGORIES_RESTREINTES = {
  "charities that don't have a proper registrations status on file (such as a 501(c)(3) registration or an equivalent classification)": aTraduire("associations caritatives sans statut d'enregistrement au dossier (par exemple un enregistrement 501(c)(3) ou une classification \xE9quivalente)")
};
var MOTIF_SUPPORTABILITY = /^This account seems to offer products or services that fall into a prohibited or restricted category under Stripe's services agreement\.\s*Category:\s*(.+?)\.\s*For more information, see\s+(\S+?)\.?\s*$/;
function traduireSupportability(reason) {
  const m = reason?.match(MOTIF_SUPPORTABILITY);
  if (m) {
    const [, categorie, lien] = m;
    const connue = CATEGORIES_RESTREINTES[categorie];
    const cat = connue ? traduireTexteStripe(connue) : categorie;
    return tS(
      "Selon Stripe, ce compte semble proposer des produits ou services relevant d'une cat\xE9gorie interdite ou restreinte par le contrat de services Stripe. Cat\xE9gorie : {categorie}. Plus d'informations : {lien}",
      { categorie: cat, lien }
    );
  }
  return reason || tS("Stripe doit v\xE9rifier que l'activit\xE9 de ce compte peut \xEAtre prise en charge sur sa plateforme.");
}
function translateErrorCode(code, fallbackReason) {
  switch (code) {
    // ─── Informations manquantes / valeur ───────────────────────
    case "information_missing":
      return fallbackReason ? tS("Une information est manquante. D\xE9tail donn\xE9 par Stripe : \xAB {detail} \xBB", { detail: fallbackReason }) : tS("Une information est manquante.");
    case "invalid_value_other":
      return tS("La valeur fournie n'est pas valide.");
    // ─── Adresse ────────────────────────────────────────────────
    case "invalid_address_city_state_postal_code":
      return tS("Stripe n'a pas pu valider l'ensemble ville, r\xE9gion et code postal de l'adresse fournie.");
    case "invalid_address_highway_contract_box":
      return `${traduireTexteStripe(ADRESSE_PHYSIQUE)} ${tS("Elle ne peut pas \xEAtre un bo\xEEtier de contrat routier.")}`;
    case "invalid_address_private_mailbox":
      return `${traduireTexteStripe(ADRESSE_PHYSIQUE)} ${tS("Elle ne peut pas \xEAtre une bo\xEEte postale priv\xE9e.")}`;
    case "invalid_address_cmra_address":
      return tS("L'adresse fournie est celle d'un service de r\xE9ception de courrier (CMRA), qui n'est pas accept\xE9e.");
    case "invalid_address_registered_agent_address":
      return tS("L'adresse fournie est celle d'un agent enregistr\xE9, qui n'est pas accept\xE9e.");
    case "invalid_street_address":
      return tS("Stripe n'a pas pu valider le nom de la rue ni le num\xE9ro de l'adresse fournie.");
    // ─── Nom / profil ───────────────────────────────────────────
    case "invalid_business_profile_name":
      return tS("Le nom de l'organisation doit \xEAtre facile \xE0 comprendre et compos\xE9 de mots reconnaissables.");
    case "invalid_business_profile_name_denylisted":
      return tS("Le nom de l'organisation doit correspondre \xE0 l'activit\xE9 du compte et ne peut pas \xEAtre un nom g\xE9n\xE9rique ou bien connu.");
    case "invalid_company_name_denylisted":
      return tS("Le nom l\xE9gal de l'organisation ne peut pas \xEAtre un nom g\xE9n\xE9rique ou bien connu.");
    // ─── Identifiant fiscal ─────────────────────────────────────
    case "invalid_tax_id":
    case "invalid_tax_id_format":
      return tS("Le num\xE9ro fiscal doit \xEAtre compos\xE9 de 9 chiffres, sans tirets ni autres caract\xE8res sp\xE9ciaux.");
    // ─── Date de naissance ──────────────────────────────────────
    case "invalid_dob_age_over_maximum":
      return tS("La date de naissance doit se situer dans les 120 derni\xE8res ann\xE9es.");
    case "invalid_dob_age_under_18":
      return tS("La personne doit avoir au moins 18 ans.");
    case "invalid_dob_age_under_minimum":
      return tS("La personne doit avoir au moins 13 ans.");
    // ─── Téléphone ──────────────────────────────────────────────
    case "invalid_phone_number":
      return tS("Stripe n'a pas pu valider le num\xE9ro de t\xE9l\xE9phone. Son format doit correspondre \xE0 celui du pays de la personne concern\xE9e.");
    // ─── Libellé de relevé bancaire ─────────────────────────────
    case "invalid_statement_descriptor_length":
      return tS("Le libell\xE9 de relev\xE9 bancaire doit comporter au moins 5 caract\xE8res.");
    case "invalid_statement_descriptor_business_mismatch":
      return tS("Le libell\xE9 de relev\xE9 bancaire doit \xEAtre similaire au nom de l'organisation, \xE0 sa d\xE9nomination l\xE9gale ou \xE0 l'adresse de son site web.");
    case "invalid_statement_descriptor_denylisted":
    case "invalid_statement_descriptor_prefix_denylisted":
      return tS("Le libell\xE9 de relev\xE9 bancaire ne peut pas correspondre \xE0 un nom d'entreprise g\xE9n\xE9rique ou bien connu.");
    case "invalid_statement_descriptor_prefix_mismatch":
      return tS("Le pr\xE9fixe du libell\xE9 de relev\xE9 bancaire doit correspondre \xE0 votre libell\xE9, au nom de votre organisation, \xE0 sa d\xE9nomination l\xE9gale ou \xE0 votre site web.");
    // ─── Description ────────────────────────────────────────────
    case "invalid_product_description_length":
      return tS("La description doit comporter au moins 10 caract\xE8res.");
    case "invalid_product_description_url_match":
      return tS("La description doit \xEAtre diff\xE9rente de l'adresse du site web.");
    // ─── Représentant / CGU ─────────────────────────────────────
    case "invalid_representative_country":
      return tS("Le pays du repr\xE9sentant n'est pas accept\xE9 pour ce compte.");
    case "invalid_tos_acceptance":
      return tS("L'acceptation des conditions d'utilisation de Stripe n'est plus valide.");
    // ─── Site web (tableau « erreurs de vérification de l'URL ») ─
    case "invalid_url_denylisted":
      return tS("L'adresse du site correspond \xE0 un site g\xE9n\xE9rique que Stripe consid\xE8re comme sans lien avec le compte. Fournissez une adresse propre \xE0 votre organisation.");
    case "invalid_url_format":
      return tS("Le format de l'adresse du site est incorrect. Fournissez une adresse correctement format\xE9e, par exemple https://example.com.");
    case "invalid_url_web_presence_detected":
      return tS("Stripe a d\xE9tect\xE9 que ce compte utilise un site web, un profil sur les r\xE9seaux sociaux ou une application pour vendre ou promouvoir des produits ou services, mais aucune adresse n'a \xE9t\xE9 fournie. Fournissez une adresse.");
    case "invalid_url_website_business_information_mismatch":
      return tS("Les informations du site web ne correspondent pas \xE0 celles du compte Stripe.");
    case "invalid_url_website_empty":
      return tS("Stripe n'a pas pu v\xE9rifier le site web, car il ne pr\xE9sente aucun contenu.");
    case "invalid_url_website_inaccessible":
      return tS("Stripe n'a pas pu acc\xE9der au site web. Si vous bloquez l'acc\xE8s \xE0 votre site dans certaines r\xE9gions, d\xE9sactivez temporairement ce blocage jusqu'\xE0 la v\xE9rification.");
    case "invalid_url_website_inaccessible_geoblocked":
      return tS("Stripe n'a pas pu v\xE9rifier le site web, car son acc\xE8s est bloqu\xE9 dans certaines r\xE9gions. D\xE9sactivez temporairement ce blocage jusqu'\xE0 la v\xE9rification.");
    case "invalid_url_website_inaccessible_password_protected":
      return tS("Stripe n'a pas pu v\xE9rifier le site web, car il est prot\xE9g\xE9 par un mot de passe.");
    case "invalid_url_website_incomplete":
      return tS("Le site web ne comporte pas de nom d'organisation ni de description claire des biens et services propos\xE9s.");
    case "invalid_url_website_incomplete_cancellation_policy":
      return tS("Le site web ne contient pas de conditions d'annulation.");
    case "invalid_url_website_incomplete_customer_service_details":
      return tS("Le site web ne contient pas d'informations sur le service client.");
    case "invalid_url_website_incomplete_legal_restrictions":
      return tS("Le site web ne contient pas les mentions applicables aux produits et services soumis \xE0 des restrictions juridiques ou \xE0 l'exportation.");
    case "invalid_url_website_incomplete_refund_policy":
      return tS("Le site web ne contient pas de politique de remboursement.");
    case "invalid_url_website_incomplete_return_policy":
      return tS("Le site web ne contient pas de politique ni de processus de retour.");
    case "invalid_url_website_incomplete_terms_and_conditions":
      return tS("Le site web ne contient pas de conditions g\xE9n\xE9rales.");
    case "invalid_url_website_incomplete_under_construction":
      return tS("Stripe n'a pas pu v\xE9rifier le site web, car il est toujours en construction.");
    case "invalid_url_website_other":
      return tS("Stripe n'a pas pu v\xE9rifier l'activit\xE9 du compte \xE0 l'adresse fournie (site web, profil de r\xE9seaux sociaux ou application).");
    // ─── Fichier refusé (groupe « Télécharger ») ────────────────
    case "verification_document_corrupt":
      return `${tS("Le fichier est corrompu.")} ${traduireTexteStripe(FICHIER_A_REFAIRE)}`;
    case "verification_document_copy":
      return `${tS("Le document est une copie.")} ${traduireTexteStripe(FICHIER_A_REFAIRE)}`;
    case "verification_document_greyscale":
      return `${tS("Le document est en niveaux de gris.")} ${traduireTexteStripe(FICHIER_A_REFAIRE)}`;
    case "verification_document_incomplete":
      return `${tS("Le document est incomplet.")} ${traduireTexteStripe(FICHIER_A_REFAIRE)}`;
    case "verification_document_not_readable":
      return `${tS("Le document n'est pas lisible.")} ${traduireTexteStripe(FICHIER_A_REFAIRE)}`;
    case "verification_document_not_uploaded":
      return `${tS("Le fichier n'a pas \xE9t\xE9 t\xE9l\xE9charg\xE9.")} ${traduireTexteStripe(FICHIER_A_REFAIRE)}`;
    case "verification_document_not_signed":
      return `${tS("Le document n'est pas sign\xE9.")} ${traduireTexteStripe(FICHIER_A_REFAIRE)}`;
    case "verification_document_missing_back":
      return `${tS("Le verso du document est manquant.")} ${traduireTexteStripe(FICHIER_A_REFAIRE)}`;
    case "verification_document_missing_front":
      return `${tS("Le recto du document est manquant.")} ${traduireTexteStripe(FICHIER_A_REFAIRE)}`;
    case "verification_document_too_large":
      return `${tS("Le fichier est trop volumineux.")} ${traduireTexteStripe(FICHIER_A_REFAIRE)}`;
    case "verification_document_failed_copy":
      return tS("Le document a \xE9t\xE9 refus\xE9 : il s'agit d'une copie.");
    case "verification_document_failed_greyscale":
      return tS("Le document a \xE9t\xE9 refus\xE9 : il est en niveaux de gris.");
    case "verification_document_failed_test_mode":
      return tS("Document refus\xE9 (mode test).");
    case "verification_document_duplicate_type":
      return tS("Le m\xEAme type de document a \xE9t\xE9 fourni deux fois.");
    case "verification_document_country_not_supported":
    case "verification_document_invalid":
    case "verification_document_type_not_supported":
      return tS("Le fichier fourni n'est pas une pi\xE8ce accept\xE9e par Stripe (pays pris en charge, type de document attendu pour ce type d'organisation). Fournissez un nouveau fichier conforme.");
    case "verification_document_failed_other":
    case "verification_document_verification_failed_other":
      return tS("Contactez le service d'assistance de Stripe pour conna\xEEtre les raisons de l'\xE9chec de la v\xE9rification.");
    case "verification_document_expired":
    case "verification_document_issue_or_expiry_date_missing":
      return tS("Le document ne comporte pas de date de d\xE9livrance ou d'expiration, ou il est expir\xE9. Une pi\xE8ce d'identit\xE9 doit \xEAtre valide \xE0 la date d'envoi ; un justificatif de domicile doit dater de moins de six mois.");
    case "verification_document_fraudulent":
    case "verification_document_manipulated":
      return tS("Il se peut que le document ait \xE9t\xE9 modifi\xE9. Contactez le service d'assistance de Stripe pour conna\xEEtre les raisons de l'\xE9chec de la v\xE9rification.");
    // ─── Document ≠ informations saisies ────────────────────────
    case "verification_document_name_mismatch":
    case "verification_document_dob_mismatch":
    case "verification_document_address_mismatch":
    case "verification_document_id_number_mismatch":
    case "verification_document_photo_mismatch":
    case "verification_document_nationality_mismatch":
      return traduireTexteStripe(PIECE_NE_CORRESPOND_PAS);
    case "verification_document_address_missing":
    case "verification_document_id_number_missing":
    case "verification_document_name_missing":
      return traduireTexteStripe(DOCUMENT_INCOMPLET);
    // ─── Vérification de l'organisation ─────────────────────────
    case "verification_failed_keyed_match":
    case "verification_failed_document_match":
      return tS("Stripe n'a pas pu v\xE9rifier les informations du compte. Vous pouvez t\xE9l\xE9charger un document de v\xE9rification ou mettre \xE0 jour vos informations.");
    case "verification_failed_tax_id_not_issued":
    case "verification_failed_tax_id_match":
      return tS("L'administration fiscale n'a pas pu v\xE9rifier les informations fournies. Corrigez le nom de l'organisation ou le num\xE9ro fiscal, ou t\xE9l\xE9chargez un document justificatif.");
    case "verification_failed_id_number_match":
    case "verification_failed_name_match":
    case "verification_failed_address_match":
      return traduireTexteStripe(DOCUMENT_NE_CORRESPOND_PAS);
    case "verification_legal_entity_structure_mismatch":
      return tS("Le type ou la structure de l'organisation ne semble pas correct. Indiquez le type et la structure appropri\xE9s pour ce compte.");
    case "verification_failed_keyed_identity":
      return tS("Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Corrigez-les si elles sont fausses, ou prouvez l'identit\xE9 autrement.");
    case "verification_failed_other":
      return tS("Stripe n'a pas trouv\xE9 d'informations \xE0 partir des donn\xE9es saisies. V\xE9rifiez qu'elles sont correctes.");
    case "verification_failed_residential_address":
      return tS("L'adresse de r\xE9sidence n'a pas pu \xEAtre v\xE9rifi\xE9e.");
    case "verification_failed_business_iec_number":
      return tS("Le num\xE9ro IEC de l'organisation n'a pas pu \xEAtre v\xE9rifi\xE9.");
    case "unsupported_business_type":
      return tS("Ce type d'organisation n'est pas pris en charge par Stripe.");
    case "invalid_signator":
      return tS("Le signataire du document n'a pas pu \xEAtre v\xE9rifi\xE9.");
    // ─── Dirigeants / propriétaires / représentants ─────────────
    case "verification_failed_authorizer_authority":
      return tS("Stripe n'a pas pu v\xE9rifier les pouvoirs de la personne qui autorise le compte. Remplacez-la par une personne d\xFBment enregistr\xE9e en tant que repr\xE9sentant autoris\xE9.");
    case "verification_failed_representative_authority":
      return tS("Stripe n'a pas pu v\xE9rifier les pouvoirs du repr\xE9sentant du compte. Ajoutez une personne qui autorise le compte et fournissez une lettre d'autorisation sign\xE9e par elle.");
    case "verification_missing_owners":
      return tS("Aucun propri\xE9taire n'a \xE9t\xE9 indiqu\xE9. Fournissez des informations sur tous les propri\xE9taires.");
    case "verification_missing_directors":
      return tS("La liste des dirigeants n'a pas \xE9t\xE9 fournie. Mettez \xE0 jour le compte et t\xE9l\xE9chargez un document d'immatriculation avec la liste des dirigeants actuels.");
    case "verification_document_directors_mismatch":
      return tS("Les dirigeants mentionn\xE9s dans le document ne figurent pas dans le compte. Mettez \xE0 jour le compte et t\xE9l\xE9chargez un document d'immatriculation avec la liste des dirigeants actuels.");
    case "verification_directors_mismatch":
      return tS("Les dirigeants d\xE9clar\xE9s ne correspondent pas \xE0 ceux que Stripe a trouv\xE9s.");
    case "verification_extraneous_directors":
      return tS("Des dirigeants en trop ont \xE9t\xE9 d\xE9clar\xE9s.");
    case "verification_missing_executives":
      return tS("Des cadres dirigeants n'ont pas \xE9t\xE9 indiqu\xE9s.");
    case "verification_rejected_ownership_exemption_reason":
      return tS("Stripe a refus\xE9 le motif d'exemption relatif \xE0 la propri\xE9t\xE9. Choisissez un autre motif ou t\xE9l\xE9chargez un justificatif de b\xE9n\xE9ficiaire effectif.");
    case "verification_requires_additional_memorandum_of_associations":
      return tS("Stripe demande les statuts de soci\xE9t\xE9s qui d\xE9tiennent une participation importante.");
    case "verification_requires_additional_proof_of_registration":
      return tS("Stripe demande un justificatif d'immatriculation de soci\xE9t\xE9s qui d\xE9tiennent une participation importante.");
    // ─── Activité à vérifier ────────────────────────────────────
    case "verification_supportability":
      return traduireSupportability(fallbackReason);
    // ─── Code inconnu : le texte de Stripe tel quel ─────────────
    default:
      return fallbackReason || tS("Stripe signale une erreur de v\xE9rification (code : {code}).", { code });
  }
}
var DEMANDES_STRIPE = {
  business_model_verification: {
    sujet: aTraduire("Informations sur la nature de votre activit\xE9"),
    description: aTraduire("Stripe a besoin d'informations suppl\xE9mentaires sur la nature de l'activit\xE9 afin de v\xE9rifier qu'il peut prendre le compte en charge.")
  },
  restricted_or_prohibited_industry_diligence: {
    sujet: aTraduire("Activit\xE9 dans un secteur r\xE9glement\xE9"),
    description: aTraduire("Selon Stripe, l'organisation peut exercer ses activit\xE9s dans un secteur r\xE9glement\xE9. Stripe pourrait avoir besoin d'informations suppl\xE9mentaires sur la nature de l'activit\xE9 ou sur les licences requises afin de v\xE9rifier qu'il peut prendre le compte en charge.")
  },
  intellectual_property_usage: {
    sujet: aTraduire("Produits ou services prot\xE9g\xE9s par le droit d'auteur"),
    description: aTraduire("L'organisation pourrait vendre des produits ou des services prot\xE9g\xE9s par le droit d'auteur. Stripe a besoin d'informations suppl\xE9mentaires pour v\xE9rifier que le compte est autoris\xE9 \xE0 les vendre.")
  },
  supportability_rejection_appeal: {
    sujet: aTraduire("Recours contre un refus de prise en charge"),
    description: aTraduire("Les conditions d'utilisation de Stripe interdisent la prise en charge de l'activit\xE9. Le compte peut faire appel de cette d\xE9cision.")
  },
  other_supportability_inquiry: {
    sujet: aTraduire("Informations compl\xE9mentaires sur votre activit\xE9"),
    description: aTraduire("Stripe a besoin d'informations suppl\xE9mentaires pour v\xE9rifier qu'il peut prendre le compte en charge.")
  },
  credit_review: {
    sujet: aTraduire("Examen de la nature de votre activit\xE9"),
    description: aTraduire("Stripe a besoin d'informations suppl\xE9mentaires sur la nature de l'activit\xE9 afin de v\xE9rifier qu'il peut prendre le compte en charge.")
  },
  reserve_appeal: {
    sujet: aTraduire("Recours contre une r\xE9serve sur le compte"),
    description: aTraduire("Stripe a impos\xE9 une r\xE9serve au compte, ce qui n'affecte pas sa capacit\xE9 \xE0 accepter des paiements. Le titulaire du compte peut contester cette d\xE9cision.")
  },
  identity_verification: {
    sujet: aTraduire("V\xE9rification d'identit\xE9"),
    description: aTraduire("La personne responsable du compte doit v\xE9rifier son identit\xE9 en t\xE9l\xE9chargeant une pi\xE8ce d'identit\xE9 officielle et un selfie.")
  },
  url_inquiry: {
    sujet: aTraduire("Adresse du site web"),
    description: aTraduire("L'adresse du site de l'organisation doit faire r\xE9f\xE9rence aux produits et services qu'elle fournit. Stripe pourrait exiger une modification de cette adresse avant de pouvoir prendre le compte en charge.")
  },
  address_verification: {
    sujet: aTraduire("V\xE9rification de l'adresse"),
    description: aTraduire("Stripe doit v\xE9rifier l'adresse de l'organisation, avec les justificatifs appropri\xE9s.")
  },
  bank_account_verification: {
    sujet: aTraduire("V\xE9rification du compte bancaire"),
    description: aTraduire("Stripe doit v\xE9rifier les informations du compte bancaire associ\xE9 \xE0 l'organisation.")
  },
  customer_service_contact: {
    sujet: aTraduire("Coordonn\xE9es du service client"),
    description: aTraduire("Stripe doit v\xE9rifier les coordonn\xE9es du service client de l'organisation.")
  },
  domain_verification: {
    sujet: aTraduire("V\xE9rification du nom de domaine"),
    description: aTraduire("Stripe doit v\xE9rifier que le titulaire du compte contr\xF4le bien l'adresse ou le nom de domaine qu'il a fourni.")
  },
  fulfillment_policy: {
    sujet: aTraduire("Politique d'ex\xE9cution des commandes"),
    description: aTraduire("Stripe doit v\xE9rifier la politique d'ex\xE9cution des commandes de l'organisation.")
  },
  other_compliance_inquiry: {
    sujet: aTraduire("Demande de conformit\xE9"),
    description: aTraduire("Stripe a besoin d'informations suppl\xE9mentaires sur la conformit\xE9.")
  },
  other_business_inquiry: {
    sujet: aTraduire("Informations sur l'organisation"),
    description: aTraduire("Stripe a besoin d'informations suppl\xE9mentaires sur l'organisation.")
  },
  platform_concern: {
    sujet: aTraduire("Intervention de la plateforme"),
    description: aTraduire("La plateforme a initi\xE9 une intervention sur ce compte.")
  },
  product_description: {
    sujet: aTraduire("Description des produits ou services"),
    description: aTraduire("Le compte Stripe de l'organisation doit contenir une description exacte des produits ou services.")
  },
  rejection_appeal: {
    sujet: aTraduire("Recours contre un refus"),
    description: aTraduire("Les conditions d'utilisation de Stripe interdisent la prise en charge de cette organisation en raison du niveau de risque qu'elle pr\xE9sente. Le titulaire du compte peut contester cette d\xE9cision.")
  },
  statement_descriptor: {
    sujet: aTraduire("Libell\xE9 de relev\xE9 bancaire"),
    description: aTraduire("Stripe a besoin d'un libell\xE9 de relev\xE9 bancaire qui corresponde exactement \xE0 l'activit\xE9 de l'organisation.")
  },
  sanctions_review: {
    sujet: aTraduire("V\xE9rification li\xE9e aux sanctions"),
    description: aTraduire("Stripe doit v\xE9rifier que l'organisation n'est pas en relation avec une personne ou une juridiction sanctionn\xE9e.")
  },
  pep_review: {
    sujet: aTraduire("V\xE9rification des personnes politiquement expos\xE9es"),
    description: aTraduire("Stripe doit v\xE9rifier que l'organisation n'est pas li\xE9e \xE0 une personne d'int\xE9r\xEAt ou \xE0 une personne politiquement expos\xE9e.")
  },
  legal_hold: {
    sujet: aTraduire("Fonds retenus pour raison l\xE9gale"),
    description: aTraduire("Stripe est tenu de conserver des fonds pour une raison l\xE9gale. Il peut \xEAtre n\xE9cessaire de remettre des fonds \xE0 un tiers.")
  }
};
var CONSIGNES_CHEMIN = {
  challenge: aTraduire("Selon Stripe, vous devez r\xE9pondre vous-m\xEAme \xE0 cette demande de v\xE9rification."),
  form: aTraduire("Selon Stripe, cette demande se remplit dans un formulaire Stripe."),
  notice: aTraduire("Selon Stripe, cette demande se r\xE8gle par l'interm\xE9diaire d'un tiers."),
  support: aTraduire("Selon Stripe, cette demande ne se traite pas dans un formulaire : contactez le service d'assistance de Stripe."),
  underwriting_case: aTraduire("Stripe a demand\xE9 des informations suppl\xE9mentaires dans le cadre de son \xE9valuation des risques.")
};
var STRIPE_ASSISTANCE_URL = "https://support.stripe.com/contact";
var MOTIF_INTERVENTION = /^interv_[A-Za-z0-9]+\.(.+?)\.(challenge|form|notice|support|underwriting_case)$/;
function lireDemandeStripe(field) {
  const m = field.match(MOTIF_INTERVENTION);
  if (!m) {
    if (!field.startsWith("interv_")) return null;
    return { sujet: tS("Demande d'informations de Stripe"), description: null, chemin: null, consigne: null };
  }
  const [, nom, chemin] = m;
  const connue = DEMANDES_STRIPE[nom];
  return {
    sujet: connue ? traduireTexteStripe(connue.sujet) : tS("Demande d'informations de Stripe"),
    description: connue ? traduireTexteStripe(connue.description) : null,
    chemin,
    consigne: traduireTexteStripe(CONSIGNES_CHEMIN[chemin])
  };
}
function stripeDemandesViaAssistance(requirements) {
  if (!requirements) return false;
  const aFaire = /* @__PURE__ */ new Set([
    ...requirements.currently_due ?? [],
    ...requirements.past_due ?? [],
    ...(requirements.errors ?? []).map((e) => e.requirement)
  ]);
  if (aFaire.size === 0) return false;
  return [...aFaire].every((f2) => lireDemandeStripe(f2)?.chemin === "support");
}
function stripeExigencesACompleter(account) {
  if (!account?.details_submitted || !account.requirements) return null;
  const r = account.requirements;
  const enVerification = new Set(r.pending_verification ?? []);
  const aFaire = /* @__PURE__ */ new Set([
    ...(r.errors ?? []).map((e) => e.requirement),
    ...r.past_due ?? [],
    ...r.currently_due ?? []
  ]);
  for (const demande of stripeDemandesAuChoix(r)) {
    for (const piste of demande.pistes) aFaire.add(piste);
  }
  const cibles = [...aFaire].filter(
    (f2) => !enVerification.has(f2) && lireDemandeStripe(f2)?.chemin !== "support"
  );
  return cibles.length > 0 ? cibles : null;
}
var PISTE_IDENTITE = /^(?:person_[A-Za-z0-9]+|individual|representative|directors|executives|owners)\.verification\.(document|additional_document|proof_of_liveness)$/;
function libellePiste(champ) {
  switch (champ.match(PISTE_IDENTITE)?.[1]) {
    case "document":
      return tS("une pi\xE8ce d'identit\xE9");
    case "additional_document":
      return tS("une pi\xE8ce d'identit\xE9 ou un justificatif de domicile");
    case "proof_of_liveness":
      return tS("une v\xE9rification par selfie");
    default:
      return libelleEnMinuscules(translateRequirementField(champ));
  }
}
function listeAuChoix(libelles) {
  return libelles.reduce((liste, libelle, i) => i === 0 ? libelle : tS("{liste}, ou {piste}", { liste, piste: libelle }), "");
}
function stripeDemandesAuChoix(requirements) {
  const alternatives = requirements?.alternatives ?? [];
  if (alternatives.length === 0) return [];
  const aFaire = /* @__PURE__ */ new Set([
    ...requirements?.currently_due ?? [],
    ...requirements?.past_due ?? [],
    ...(requirements?.errors ?? []).map((e) => e.requirement)
  ]);
  const enVerification = new Set(requirements?.pending_verification ?? []);
  const groupes = /* @__PURE__ */ new Map();
  for (const a of alternatives) {
    const originaux = (a.original_fields_due ?? []).filter((f2) => aFaire.has(f2));
    if (originaux.length === 0 || (a.alternative_fields_due ?? []).length === 0) continue;
    const cle = [...a.original_fields_due].sort().join("|");
    const groupe = groupes.get(cle) ?? { originaux: [...new Set(originaux)], pistes: [] };
    for (const piste of a.alternative_fields_due) {
      if (!groupe.pistes.includes(piste)) groupe.pistes.push(piste);
    }
    groupes.set(cle, groupe);
  }
  return [...groupes.entries()].map(([cle, { originaux, pistes }]) => {
    const identite = pistes.every((p) => PISTE_IDENTITE.test(p));
    const libellesPistes = [...new Set(pistes.map(libellePiste))];
    const pistesTexte = listeAuChoix(libellesPistes);
    const plusieurs = libellesPistes.length > 1;
    let consigne;
    if (identite) {
      consigne = plusieurs ? tS("Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Fournissez {pistes} : une seule suffit. Si une information est fausse, vous pouvez aussi la corriger.", { pistes: pistesTexte }) : tS("Stripe n'a pas pu confirmer l'identit\xE9 \xE0 partir des informations saisies. Fournissez {pistes}. Si une information est fausse, vous pouvez aussi la corriger.", { pistes: pistesTexte });
    } else {
      const champs = [...new Set(originaux.map((f2) => libelleEnMinuscules(translateRequirementField(f2))))].join(", ");
      consigne = tS("Stripe n'a pas pu confirmer ces informations : {champs}. Corrigez-les si elles sont fausses, ou fournissez \xE0 la place {pistes}.", { champs, pistes: pistesTexte });
    }
    return {
      cle,
      originaux,
      pistes,
      libelle: identite ? tS("V\xE9rification d'identit\xE9") : tS("Informations \xE0 confirmer"),
      consigne,
      enVerification: pistes.some((p) => enVerification.has(p))
    };
  });
}
var ETAPES_ONBOARDING_STRIPE = {
  stripe_user_authentication: "Authentification Stripe",
  risk_intervention: "Exigences li\xE9es aux risques",
  legal_entity_sharing: "R\xE9utilisation des informations d'un autre compte",
  business_type: "Type d'entreprise",
  networked_onboarding_with_link: "R\xE9utilisation des informations Link",
  business_details: "Informations sur l'entreprise",
  additional_business_details: "Informations compl\xE9mentaires sur l'entreprise",
  additional_persons: "Personnes associ\xE9es au compte",
  business_verification: "Justificatif de l'entit\xE9",
  business_bank_account_ownership_verification: "Justificatif du compte bancaire",
  business_documents: "Documents de l'entreprise",
  representative_details: "Repr\xE9sentant du compte",
  representative_document: "Pi\xE8ce d'identit\xE9 du repr\xE9sentant",
  representative_additional_document: "Document compl\xE9mentaire du repr\xE9sentant",
  legal_guardian_details: "Consentement du tuteur l\xE9gal",
  owners: "B\xE9n\xE9ficiaires effectifs",
  directors: "Directeurs",
  executives: "Dirigeants",
  person_identity_verification: "V\xE9rification d'identit\xE9 d'une personne",
  rep_identity_verification: "V\xE9rification d'identit\xE9 du repr\xE9sentant (pi\xE8ce + selfie)",
  rep_verification_method_selection: "Choix de la m\xE9thode de v\xE9rification d'identit\xE9",
  proof_of_ownership_document: "Justificatif des b\xE9n\xE9ficiaires effectifs",
  proof_of_authorization: "Justificatif du pouvoir du repr\xE9sentant",
  designate_authorizer: "D\xE9signation d'une personne habilit\xE9e",
  confirm_owners: "Attestation des b\xE9n\xE9ficiaires effectifs",
  relationship_verification: "V\xE9rification des propri\xE9taires ou directeurs",
  owner_verification: "V\xE9rification des propri\xE9taires",
  directors_verification: "V\xE9rification des directeurs",
  payment_method: "Compte bancaire pour les frais de plateforme",
  external_account: "Compte bancaire de versement",
  support_details: "Coordonn\xE9es visibles par les donateurs",
  fraud_protection: "Protection contre la fraude (Radar)",
  climate: "Stripe Climate",
  tax: "Stripe Tax",
  summary: "R\xE9capitulatif final",
  summary_risk: "R\xE9capitulatif \u2014 exigences li\xE9es aux risques",
  summary_business_type: "R\xE9capitulatif \u2014 type d'entreprise",
  summary_business: "R\xE9capitulatif \u2014 entreprise",
  summary_support: "R\xE9capitulatif \u2014 informations publiques",
  summary_persons: "R\xE9capitulatif \u2014 personnes",
  summary_payment_method: "R\xE9capitulatif \u2014 compte bancaire des frais",
  summary_external_account: "R\xE9capitulatif \u2014 compte bancaire de versement",
  summary_tax: "R\xE9capitulatif \u2014 Stripe Tax",
  summary_tax_identification_form: "R\xE9capitulatif \u2014 informations fiscales",
  summary_climate: "R\xE9capitulatif \u2014 Stripe Climate",
  summary_fraud_protection: "R\xE9capitulatif \u2014 protection contre la fraude",
  terms_of_service: "Conditions d'utilisation du service"
};
function translateOnboardingStep(step) {
  return ETAPES_ONBOARDING_STRIPE[step] ?? step;
}
var MOTIF_ETAPE_ONBOARDING_STRIPE = /^[a-z][a-z0-9_]{0,63}$/;
function translateRequirementField(field) {
  const demande = lireDemandeStripe(field);
  if (demande) return demande.sujet;
  const normalized = field.replace(/^(person_[A-Za-z0-9]+|directors|representative|executives|owners)\./, "individual.");
  if (field.startsWith("directors.")) {
    const suffix = field.replace(/^directors\./, "individual.");
    return tS("Dirigeant \u2014 {champ}", { champ: libelleEnMinuscules(translateRequirementField(suffix)) });
  }
  if (field.startsWith("representative.")) {
    const suffix = field.replace(/^representative\./, "individual.");
    return tS("Repr\xE9sentant l\xE9gal \u2014 {champ}", { champ: libelleEnMinuscules(translateRequirementField(suffix)) });
  }
  if (field.startsWith("executives.")) {
    const suffix = field.replace(/^executives\./, "individual.");
    return tS("Cadre dirigeant \u2014 {champ}", { champ: libelleEnMinuscules(translateRequirementField(suffix)) });
  }
  if (field.startsWith("owners.")) {
    const suffix = field.replace(/^owners\./, "individual.");
    return tS("Propri\xE9taire \u2014 {champ}", { champ: libelleEnMinuscules(translateRequirementField(suffix)) });
  }
  switch (normalized) {
    // Acceptation des CGU
    case "tos_acceptance.date":
      return tS("Acceptation des conditions d'utilisation Stripe");
    case "tos_acceptance.ip":
      return tS("Adresse IP d'acceptation des CGU");
    // Compte bancaire de versement
    case "external_account":
      return tS("Compte bancaire pour les versements (IBAN)");
    // Organisation — informations
    case "company.name":
      return tS("Nom l\xE9gal de l'organisation");
    case "company.tax_id":
      return tS("Num\xE9ro SIRET / identifiant fiscal");
    case "company.vat_id":
      return tS("Num\xE9ro de TVA intracommunautaire");
    case "company.registration_number":
      return tS("Num\xE9ro d'enregistrement de l'organisation");
    case "company.phone":
      return tS("Num\xE9ro de t\xE9l\xE9phone de l'organisation");
    case "company.structure":
      return tS("Forme juridique de l'organisation");
    case "company.directors_provided":
      return tS("Confirmation que tous les dirigeants ont \xE9t\xE9 d\xE9clar\xE9s");
    case "company.executives_provided":
      return tS("Confirmation que tous les cadres dirigeants ont \xE9t\xE9 d\xE9clar\xE9s");
    case "company.owners_provided":
      return tS("Confirmation que tous les propri\xE9taires ont \xE9t\xE9 d\xE9clar\xE9s");
    // Adresse de l'organisation
    case "company.address.line1":
      return tS("Adresse de l'organisation (rue)");
    case "company.address.line2":
      return tS("Adresse de l'organisation (compl\xE9ment)");
    case "company.address.city":
      return tS("Ville de l'organisation");
    case "company.address.state":
      return tS("D\xE9partement / r\xE9gion de l'organisation");
    case "company.address.postal_code":
      return tS("Code postal de l'organisation");
    case "company.address.country":
      return tS("Pays de l'organisation");
    // Documents d'entreprise
    case "company.verification.document":
      return tS("Document officiel de l'organisation");
    case "documents.proof_of_registration.files":
      return tS("Justificatif d'enregistrement de l'organisation");
    case "documents.company_tax_id_verification.files":
      return tS("Justificatif du num\xE9ro fiscal (SIRET)");
    case "documents.company_ministerial_decree.files":
      return tS("D\xE9cret minist\xE9riel (le cas \xE9ch\xE9ant)");
    case "documents.company_license.files":
      return tS("Licence ou autorisation d'exercer");
    case "documents.company_memorandum_of_association.files":
      return tS("Statuts de l'organisation");
    // Personne (individual / dirigeant / représentant)
    case "individual.first_name":
      return tS("Pr\xE9nom");
    case "individual.last_name":
      return tS("Nom de famille");
    case "individual.dob":
    case "individual.dob.day":
    case "individual.dob.month":
    case "individual.dob.year":
      return tS("Date de naissance");
    case "individual.email":
      return tS("Adresse email");
    case "individual.phone":
      return tS("Num\xE9ro de t\xE9l\xE9phone");
    case "individual.id_number":
      return tS("Num\xE9ro d'identification (\xE9quivalent du num\xE9ro de s\xE9curit\xE9 sociale)");
    case "individual.ssn_last_4":
      return tS("4 derniers chiffres du num\xE9ro de s\xE9curit\xE9 sociale");
    case "individual.gender":
      return tS("Genre");
    case "individual.nationality":
      return tS("Nationalit\xE9");
    case "individual.political_exposure":
      return tS("Statut d'exposition politique");
    case "individual.relationship.title":
      return tS("Fonction au sein de l'organisation");
    case "individual.relationship.percent_ownership":
      return tS("Pourcentage de propri\xE9t\xE9");
    // Adresse personnelle
    case "individual.address.line1":
      return tS("Adresse personnelle (rue)");
    case "individual.address.line2":
      return tS("Adresse personnelle (compl\xE9ment)");
    case "individual.address.city":
      return tS("Ville");
    case "individual.address.state":
      return tS("D\xE9partement / r\xE9gion");
    case "individual.address.postal_code":
      return tS("Code postal");
    case "individual.address.country":
      return tS("Pays");
    // Documents personnels
    case "individual.verification.document":
      return tS("Pi\xE8ce d'identit\xE9 (passeport, carte nationale d'identit\xE9 ou permis de conduire)");
    case "individual.verification.document.front":
      return tS("Recto de la pi\xE8ce d'identit\xE9");
    case "individual.verification.document.back":
      return tS("Verso de la pi\xE8ce d'identit\xE9");
    case "individual.verification.additional_document":
      return tS("Justificatif de domicile (facture d'\xE9nergie, quittance de loyer, etc.)");
    case "individual.verification.proof_of_liveness":
      return tS("Preuve de pr\xE9sence (selfie)");
    // Profil business
    case "business_profile.name":
      return tS("Nom commercial / d'affichage de l'organisation");
    case "business_profile.url":
      return tS("Site web de l'organisation");
    case "business_profile.product_description":
      return tS("Description de votre activit\xE9");
    case "business_profile.mcc":
      return tS("Cat\xE9gorie d'activit\xE9 (code MCC)");
    case "business_profile.support_email":
      return tS("Email du service donateurs / support");
    case "business_profile.support_phone":
      return tS("T\xE9l\xE9phone du service donateurs / support");
    case "business_profile.support_url":
      return tS("URL du service donateurs / support");
    case "business_profile.support_address.line1":
      return tS("Adresse du service donateurs / support");
    // Configuration de paiement
    case "settings.payments.statement_descriptor":
      return tS("Libell\xE9 qui appara\xEEtra sur le relev\xE9 bancaire des donateurs");
    case "settings.card_payments.statement_descriptor_prefix":
      return tS("Pr\xE9fixe du libell\xE9 sur le relev\xE9 bancaire");
    // Cas générique : on essaie d'avoir un fallback lisible plutôt que `foo.bar.baz`
    default: {
      const human = field.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      return human;
    }
  }
}
function stripePayoutsSuspended(account) {
  if (!account || account.charges_enabled !== true) return false;
  if (account.payouts_enabled !== false) return false;
  const r = account.requirements;
  if (r?.disabled_reason === "platform_paused") return false;
  return !!r?.disabled_reason || (r?.past_due?.length ?? 0) > 0 || (r?.errors?.length ?? 0) > 0 || (r?.currently_due?.length ?? 0) > 0;
}
function classifyStripeAccount(account) {
  if (!account) return "unknown";
  if (account.charges_enabled) {
    return stripePayoutsSuspended(account) ? "requires_action" : "ready";
  }
  const reason = account.requirements?.disabled_reason;
  if (reason && reason.startsWith("rejected.")) return "rejected";
  if (reason === "requirements.pending_verification") return "pending_verification";
  const hasPendingVerif = (account.requirements?.pending_verification?.length ?? 0) > 0;
  const hasErrors = (account.requirements?.errors?.length ?? 0) > 0;
  const hasCurrentlyDue = (account.requirements?.currently_due?.length ?? 0) > 0;
  const hasPastDue = (account.requirements?.past_due?.length ?? 0) > 0;
  if (hasPastDue || hasErrors) return "requires_action";
  if (hasCurrentlyDue && account.details_submitted) return "requires_action";
  if (hasPendingVerif && !hasCurrentlyDue) return "pending_verification";
  if (!account.details_submitted) return "not_submitted";
  return "pending_verification";
}
function isStripeVerificationInProgress(account) {
  if (!account || account.charges_enabled) return false;
  return classifyStripeAccount(account) === "pending_verification" || !!account.payouts_enabled;
}

// src/tarifTapToPay.ts
var STRIPE_TAP_TO_PAY_AUTORISATION_CENTIMES = 10;
var STRIPE_SUR_PLACE = {
  tauxPourcentage: 1.4,
  /** 0,10 € par paiement réussi + 0,10 € par autorisation Tap to Pay. */
  fixeCentimes: 10 + STRIPE_TAP_TO_PAY_AUTORISATION_CENTIMES
};
var STRIPE_EN_LIGNE = {
  tauxPourcentage: STRIPE_EU_RATE * 100,
  fixeCentimes: Math.round(STRIPE_EU_FIXED_FEE * 100)
};
var FRAIS_FIXES_TAP_TO_PAY_MAX_CENTIMES = 100;
function tarifTapToPayAvec(commissionPourcentage, fraisFixesDonoorCentimes, memePrixQuEnLigne = false) {
  return {
    commission_pourcentage: commissionPourcentage,
    frais_fixes_donoor_centimes: fraisFixesDonoorCentimes,
    stripe_taux_pourcentage: STRIPE_SUR_PLACE.tauxPourcentage,
    stripe_fixe_centimes: STRIPE_SUR_PLACE.fixeCentimes,
    meme_prix_qu_en_ligne: memePrixQuEnLigne,
    en_ligne_taux_pourcentage: STRIPE_EN_LIGNE.tauxPourcentage,
    en_ligne_fixe_centimes: STRIPE_EN_LIGNE.fixeCentimes
  };
}
function totalEnLigneCentimes(netCentimes, commissionPourcentage, tarif = {}) {
  if (!Number.isFinite(netCentimes) || netCentimes <= 0) return 0;
  const net = netCentimes / 100;
  const commission = net * (commissionPourcentage / 100);
  const taux = (tarif.en_ligne_taux_pourcentage ?? STRIPE_EN_LIGNE.tauxPourcentage) / 100;
  const fixe = (tarif.en_ligne_fixe_centimes ?? STRIPE_EN_LIGNE.fixeCentimes) / 100;
  const brut = (net + commission + fixe) / (1 - taux);
  return Math.ceil(brut * 100);
}
function fraisStripeEnLigneCentimes(montantCentimes, tarif = {}) {
  return Math.floor((montantCentimes * pointsDeBase(tarif.en_ligne_taux_pourcentage ?? STRIPE_EN_LIGNE.tauxPourcentage) + 5e3) / 1e4) + (tarif.en_ligne_fixe_centimes ?? STRIPE_EN_LIGNE.fixeCentimes);
}
function pointsDeBase(pourcentage) {
  return Math.round(pourcentage * 100);
}
function commissionTapToPayCentimes(montantCentimes, commissionPourcentage, fraisFixesCentimes) {
  const commission = Math.floor((montantCentimes * pointsDeBase(commissionPourcentage) + 9999) / 1e4);
  const total = commission + Math.max(0, Math.round(fraisFixesCentimes));
  return Math.max(0, Math.min(total, montantCentimes));
}
function fraisStripeSurPlaceCentimes(montantCentimes, tarif) {
  return Math.floor((montantCentimes * pointsDeBase(tarif.stripe_taux_pourcentage) + 5e3) / 1e4) + tarif.stripe_fixe_centimes;
}
function partDonoorTapToPayCentimes(montantCentimes, tarif, netViseCentimes) {
  if (tarif.meme_prix_qu_en_ligne) {
    const stripe = fraisStripeSurPlaceCentimes(montantCentimes, tarif);
    if (netViseCentimes !== null && netViseCentimes !== void 0 && netViseCentimes > 0) {
      return Math.max(0, Math.min(montantCentimes - stripe - netViseCentimes, montantCentimes));
    }
    const commission = commissionTapToPayCentimes(montantCentimes, tarif.commission_pourcentage, 0);
    const ecart = Math.max(0, fraisStripeEnLigneCentimes(montantCentimes, tarif) - stripe);
    return Math.max(0, Math.min(commission + ecart, montantCentimes - stripe));
  }
  const part = commissionTapToPayCentimes(
    montantCentimes,
    tarif.commission_pourcentage,
    tarif.frais_fixes_donoor_centimes
  );
  if (netViseCentimes === null || netViseCentimes === void 0 || !(netViseCentimes > 0)) {
    return part;
  }
  const marge = montantCentimes - fraisStripeSurPlaceCentimes(montantCentimes, tarif) - netViseCentimes;
  return Math.max(0, Math.min(part, marge));
}
function recuParAssociationCentimes(montantCentimes, tarif) {
  if (tarif.meme_prix_qu_en_ligne) {
    return montantCentimes - partDonoorTapToPayCentimes(montantCentimes, tarif) - fraisStripeSurPlaceCentimes(montantCentimes, tarif);
  }
  return montantCentimes - commissionTapToPayCentimes(
    montantCentimes,
    tarif.commission_pourcentage,
    tarif.frais_fixes_donoor_centimes
  ) - fraisStripeSurPlaceCentimes(montantCentimes, tarif);
}
function totalAvecFraisCentimes(netCentimes, tarif) {
  if (!Number.isFinite(netCentimes) || netCentimes <= 0) return 0;
  if (tarif.meme_prix_qu_en_ligne) return totalEnLigneCentimes(netCentimes, tarif.commission_pourcentage, tarif);
  const taux = (tarif.commission_pourcentage + tarif.stripe_taux_pourcentage) / 100;
  const fixes = tarif.frais_fixes_donoor_centimes + tarif.stripe_fixe_centimes;
  let total = Math.max(netCentimes, Math.floor((netCentimes + fixes) / (1 - taux)) - 2);
  while (recuParAssociationCentimes(total, tarif) < netCentimes) total += 1;
  while (total > netCentimes && recuParAssociationCentimes(total - 1, tarif) >= netCentimes) {
    total -= 1;
  }
  return total;
}

// src/etatOptionsPaypal.ts
var OPTIONS_PAYPAL = ["PAYPAL", "PPCP_CUSTOM", "APPLE_PAY", "GOOGLE_PAY"];
var ETATS_OPTION_PAYPAL = [
  "active",
  "examen",
  "infos_demandees",
  "accordee",
  "refusee",
  "pays_non_eligible",
  "non_activee"
];
var ETATS_PAYPAL_EN_SUSPENS = /* @__PURE__ */ new Set([
  "examen",
  "infos_demandees",
  "accordee",
  "non_activee"
]);
var PAYS_OPTIONS_AVANCEES_PAYPAL = /* @__PURE__ */ new Set([
  "AU",
  "AT",
  "BE",
  "BG",
  "CA",
  "CN",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HK",
  "HU",
  "IE",
  "IT",
  "JP",
  "LV",
  "LI",
  "LT",
  "LU",
  "MT",
  "MX",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "SG",
  "SK",
  "SI",
  "ES",
  "SE",
  "GB",
  "US"
]);
var PAYS_PAYPAL_ENVOI_SEULEMENT = /* @__PURE__ */ new Set([
  "AO",
  "AI",
  "AM",
  "AW",
  "AZ",
  "BY",
  "BJ",
  "BT",
  "BO",
  "VG",
  "BN",
  "BF",
  "BI",
  "CI",
  "KH",
  "CM",
  "CV",
  "TD",
  "KM",
  "CG",
  "CK",
  "DJ",
  "ER",
  "ET",
  "FK",
  "GA",
  "GM",
  "GN",
  "GW",
  "GY",
  "KI",
  "KG",
  "LA",
  "MK",
  "MG",
  "MV",
  "ML",
  "MH",
  "MR",
  "FM",
  "MN",
  "ME",
  "MS",
  "NA",
  "NR",
  "NP",
  "NE",
  "NG",
  "NU",
  "NF",
  "PG",
  "PY",
  "PN",
  "RW",
  "ST",
  "WS",
  "SL",
  "SB",
  "SO",
  "SH",
  "PM",
  "VC",
  "SR",
  "SJ",
  "TJ",
  "TZ",
  "TG",
  "TO",
  "TN",
  "TM",
  "TV",
  "UG",
  "UA",
  "VA",
  "WF",
  "YE",
  "ZM",
  "ZW"
]);
function paypalPaysEnvoiSeulement(pays) {
  return PAYS_PAYPAL_ENVOI_SEULEMENT.has((pays ?? "").trim().toUpperCase());
}
var PRODUITS_DE_L_OPTION = {
  PAYPAL: ["PPCP_STANDARD"],
  PPCP_CUSTOM: ["PPCP_CUSTOM"],
  APPLE_PAY: ["PAYMENT_METHODS"],
  GOOGLE_PAY: ["PAYMENT_METHODS"]
};
function depuisStatutPaypal(statut) {
  switch ((statut ?? "").toUpperCase()) {
    case "ACTIVE":
      return "active";
    case "APPROVED":
      return "accordee";
    case "IN_REVIEW":
    case "PENDING":
    case "REVIEW":
      return "examen";
    case "NEED_DATA":
    case "NEED_MORE_DATA":
      return "infos_demandees";
    case "DENIED":
    case "REVOKED":
    case "SUSPENDED":
      return "refusee";
    default:
      return null;
  }
}
function lireProduits(products) {
  let liste = products;
  if (typeof liste === "string") {
    try {
      liste = JSON.parse(liste);
    } catch {
      liste = [];
    }
  }
  if (!Array.isArray(liste)) return [];
  return liste.filter((p) => typeof p?.name === "string").map((p) => ({ name: p.name, vetting_status: p.vetting_status ?? null }));
}
function etatOptionPaypal(option, compte) {
  if (paypalPaysEnvoiSeulement(compte.country)) return "pays_non_eligible";
  let capabilities = compte.capabilities;
  if (typeof capabilities === "string") {
    try {
      capabilities = JSON.parse(capabilities);
    } catch {
      capabilities = null;
    }
  }
  const statut = capabilities?.[option];
  const parAutorisation = depuisStatutPaypal(statut);
  if (parAutorisation === "active") return "active";
  const pays = (compte.country ?? "").trim().toUpperCase();
  if (option !== "PAYPAL" && pays && !PAYS_OPTIONS_AVANCEES_PAYPAL.has(pays)) {
    return "pays_non_eligible";
  }
  if (parAutorisation) return parAutorisation;
  const produits = lireProduits(compte.products);
  for (const nom of PRODUITS_DE_L_OPTION[option]) {
    const produit = produits.find((p) => p.name === nom);
    const parExamen = depuisStatutPaypal(produit?.vetting_status);
    if (parExamen && parExamen !== "active") return parExamen;
  }
  return "non_activee";
}
function etatsOptionsPaypal(compte) {
  return {
    PAYPAL: etatOptionPaypal("PAYPAL", compte),
    PPCP_CUSTOM: etatOptionPaypal("PPCP_CUSTOM", compte),
    APPLE_PAY: etatOptionPaypal("APPLE_PAY", compte),
    GOOGLE_PAY: etatOptionPaypal("GOOGLE_PAY", compte)
  };
}

// src/random.ts
function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
function randomFrom(array) {
  return array[Math.floor(Math.random() * array.length)];
}

// src/attribution.ts
var ATTRIBUTION_PARAM_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "igshid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term"
];
function guessPlacement(params, refHost) {
  const medium = params.utm_medium?.toLowerCase();
  if (medium && PAID_MEDIUMS.has(medium)) return "ad";
  if (params.igshid) return "share";
  if (params.fbclid || /l\.instagram\.|l\.facebook\.|lm\.facebook\./.test(refHost)) {
    return "in_app_link";
  }
  return void 0;
}
var AI_ENGINES = {
  "chatgpt.com": "chatgpt",
  "chat.openai.com": "chatgpt",
  "openai.com": "chatgpt",
  "perplexity.ai": "perplexity",
  "claude.ai": "claude",
  "gemini.google.com": "gemini",
  "bard.google.com": "gemini",
  "copilot.microsoft.com": "copilot",
  // Domaine actuel de Copilot : il signe ses liens `utm_source=copilot.com`
  // (21 sessions en prod au 01/11, toutes rangées en « Référent »).
  "copilot.com": "copilot",
  "deepseek.com": "deepseek",
  "chat.deepseek.com": "deepseek",
  "mistral.ai": "mistral",
  "chat.mistral.ai": "mistral",
  "grok.com": "grok",
  "x.ai": "grok",
  "poe.com": "poe",
  "you.com": "you",
  "phind.com": "phind"
};
var WEBMAILS = {
  "mail.google.com": "gmail",
  "mail.yahoo.com": "yahoo_mail",
  // pas « yahoo » : c'est le canal du moteur
  "outlook.live.com": "outlook",
  "outlook.office.com": "outlook",
  "outlook.office365.com": "outlook",
  "mail.proton.me": "proton"
};
var SEARCH_ENGINES = {
  "google.": "google",
  "bing.": "bing",
  "yahoo.": "yahoo",
  "duckduckgo.": "duckduckgo",
  "ecosia.": "ecosia",
  "qwant.": "qwant",
  "yandex.": "yandex",
  "baidu.": "baidu",
  "brave.": "brave"
};
var SOCIAL_NETWORKS = {
  "instagram.": "instagram",
  "l.instagram.": "instagram",
  ig: "instagram",
  "facebook.": "facebook",
  "l.facebook.": "facebook",
  "fb.": "facebook",
  fb: "facebook",
  "m.facebook.": "facebook",
  "t.co": "twitter",
  "twitter.": "twitter",
  "x.com": "twitter",
  "linkedin.": "linkedin",
  "lnkd.in": "linkedin",
  "tiktok.": "tiktok",
  "youtube.": "youtube",
  "youtu.be": "youtube",
  "pinterest.": "pinterest",
  "snapchat.": "snapchat",
  "reddit.": "reddit",
  "whatsapp.": "whatsapp",
  "wa.me": "whatsapp",
  "telegram.": "telegram",
  "t.me": "telegram"
};
var PAID_MEDIUMS = /* @__PURE__ */ new Set([
  "cpc",
  "ppc",
  "paid",
  "paidsearch",
  "paid-search",
  "paid_social",
  "paidsocial",
  "display",
  "cpm",
  "banner"
]);
var EMAIL_MEDIUMS = /* @__PURE__ */ new Set(["email", "e-mail", "newsletter", "mail"]);
function hostFromUrl(u) {
  if (!u) return "";
  try {
    return new URL(u).hostname.toLowerCase();
  } catch {
    return "";
  }
}
function matchMap(host, map) {
  if (!host) return null;
  for (const [needle, label] of Object.entries(map)) {
    if (needle.endsWith(".")) {
      if (host === needle.slice(0, -1) || host.startsWith(needle) || host.includes(`.${needle}`)) {
        return label;
      }
      continue;
    }
    if (host === needle || host.endsWith(`.${needle}`)) return label;
  }
  return null;
}
var SOCIAL_UTM_ALIASES = {
  instagram: "instagram",
  ig: "instagram",
  insta: "instagram",
  facebook: "facebook",
  fb: "facebook",
  meta: "facebook",
  twitter: "twitter",
  x: "twitter",
  linkedin: "linkedin",
  tiktok: "tiktok",
  youtube: "youtube",
  yt: "youtube",
  pinterest: "pinterest",
  snapchat: "snapchat",
  reddit: "reddit",
  whatsapp: "whatsapp",
  telegram: "telegram"
};
function socialFromUtmSource(src) {
  if (!src) return null;
  return SOCIAL_UTM_ALIASES[src.toLowerCase().trim()] || null;
}
var AI_UTM_ALIASES = {
  chatgpt: "chatgpt",
  openai: "chatgpt",
  perplexity: "perplexity",
  claude: "claude",
  gemini: "gemini",
  bard: "gemini",
  copilot: "copilot",
  deepseek: "deepseek",
  mistral: "mistral",
  grok: "grok",
  poe: "poe",
  phind: "phind"
};
function assistantIA(valeur) {
  const v = (valeur ?? "").toLowerCase().trim();
  if (!v) return null;
  return AI_UTM_ALIASES[v] ?? matchMap(v, AI_ENGINES);
}
function designeEmail(valeur) {
  return EMAIL_MEDIUMS.has((valeur ?? "").toLowerCase().trim());
}
function classifyAttribution(input) {
  const params = {};
  if (input.params) {
    for (const k of ATTRIBUTION_PARAM_KEYS) {
      const v = input.params[k];
      if (typeof v === "string" && v) params[k] = v;
    }
  }
  if (input.url) {
    try {
      const sp = new URL(input.url).searchParams;
      for (const k of ATTRIBUTION_PARAM_KEYS) {
        if (!params[k]) {
          const v = sp.get(k);
          if (v) params[k] = v;
        }
      }
    } catch {
    }
  }
  const gclid = params.gclid || params.gbraid || params.wbraid;
  const fbclid = params.fbclid;
  const utm_source = params.utm_source;
  const utm_medium = params.utm_medium?.toLowerCase();
  const refHost = hostFromUrl(input.referrer);
  const base = {
    source: "direct",
    channel: "direct",
    placement: guessPlacement(params, refHost),
    gclid,
    fbclid,
    igshid: params.igshid,
    utm_source: params.utm_source,
    utm_medium: params.utm_medium,
    utm_campaign: params.utm_campaign,
    utm_content: params.utm_content,
    utm_term: params.utm_term
  };
  if (gclid) return { ...base, source: "paid", channel: "google" };
  if (utm_medium && PAID_MEDIUMS.has(utm_medium)) {
    const channel = socialFromUtmSource(utm_source) || utm_source?.toLowerCase() || matchMap(refHost, SOCIAL_NETWORKS) || "unknown";
    return { ...base, source: "paid", channel };
  }
  if (designeEmail(utm_medium) || designeEmail(utm_source)) {
    return { ...base, source: "email", channel: utm_source?.toLowerCase() || "email" };
  }
  const socialUtm = socialFromUtmSource(utm_source);
  if (socialUtm) return { ...base, source: "social", channel: socialUtm };
  const socialRef = matchMap(refHost, SOCIAL_NETWORKS);
  if (socialRef) return { ...base, source: "social", channel: socialRef };
  if (fbclid) return { ...base, source: "social", channel: "facebook" };
  const aiUtm = assistantIA(utm_source);
  if (aiUtm) return { ...base, source: "ai", channel: aiUtm };
  if (utm_source) {
    return { ...base, source: "referral", channel: utm_source.toLowerCase() };
  }
  const ai = matchMap(refHost, AI_ENGINES);
  if (ai) return { ...base, source: "ai", channel: ai };
  const webmail = matchMap(refHost, WEBMAILS);
  if (webmail) return { ...base, source: "email", channel: webmail };
  const engine = matchMap(refHost, SEARCH_ENGINES);
  if (engine) return { ...base, source: "organic", channel: engine };
  if (refHost) return { ...base, source: "referral", channel: refHost };
  return base;
}

// src/origineAcquisition.ts
var COULEURS_ORIGINE = {
  publicite: "#C2410C",
  // orange-700 · 5,2:1
  recherche: "#1D4ED8",
  // blue-700 · 6,7:1
  ia: "#7C3AED",
  // violet-600 · 5,7:1
  social: "#BE185D",
  // pink-700 · 6,0:1
  email: "#0F766E",
  // teal-700 · 5,5:1
  site: "#334155",
  // slate-700 · 10,4:1
  direct: "#6B7280"
  // gray-500 · 4,8:1
};
var PLATEFORMES = {
  // Moteurs de recherche
  google: { nom: "Google", icone: "i-simple-icons-google", famille: "recherche" },
  bing: { nom: "Bing", icone: "i-simple-icons-microsoftbing", famille: "recherche" },
  yahoo: { nom: "Yahoo", icone: "i-simple-icons-yahoo", famille: "recherche" },
  duckduckgo: { nom: "DuckDuckGo", icone: "i-simple-icons-duckduckgo", famille: "recherche" },
  ecosia: { nom: "Ecosia", icone: "i-simple-icons-ecosia", famille: "recherche" },
  qwant: { nom: "Qwant", icone: "i-simple-icons-qwant", famille: "recherche" },
  brave: { nom: "Brave", icone: "i-simple-icons-brave", famille: "recherche" },
  baidu: { nom: "Baidu", icone: "i-simple-icons-baidu", famille: "recherche" },
  yandex: { nom: "Yandex", icone: "i-lucide-search", famille: "recherche" },
  // Assistants IA. Copilot, Grok, Poe, Phind, You.com : pas de logo fidèle
  // dans simple-icons (celui de GitHub Copilot n'est pas celui de Microsoft).
  chatgpt: { nom: "ChatGPT", icone: "i-simple-icons-openai", famille: "ia" },
  perplexity: { nom: "Perplexity", icone: "i-simple-icons-perplexity", famille: "ia" },
  claude: { nom: "Claude", icone: "i-simple-icons-claude", famille: "ia" },
  gemini: { nom: "Gemini", icone: "i-simple-icons-googlegemini", famille: "ia" },
  copilot: { nom: "Copilot", icone: "i-lucide-sparkles", famille: "ia" },
  deepseek: { nom: "DeepSeek", icone: "i-simple-icons-deepseek", famille: "ia" },
  mistral: { nom: "Mistral", icone: "i-simple-icons-mistralai", famille: "ia" },
  grok: { nom: "Grok", icone: "i-lucide-sparkles", famille: "ia" },
  poe: { nom: "Poe", icone: "i-lucide-sparkles", famille: "ia" },
  phind: { nom: "Phind", icone: "i-lucide-sparkles", famille: "ia" },
  you: { nom: "You.com", icone: "i-lucide-sparkles", famille: "ia" },
  // Réseaux sociaux
  facebook: { nom: "Facebook", icone: "i-simple-icons-facebook", famille: "social" },
  instagram: { nom: "Instagram", icone: "i-simple-icons-instagram", famille: "social" },
  meta: { nom: "Meta", icone: "i-simple-icons-meta", famille: "social" },
  linkedin: { nom: "LinkedIn", icone: "i-simple-icons-linkedin", famille: "social" },
  tiktok: { nom: "TikTok", icone: "i-simple-icons-tiktok", famille: "social" },
  youtube: { nom: "YouTube", icone: "i-simple-icons-youtube", famille: "social" },
  twitter: { nom: "X", icone: "i-simple-icons-x", famille: "social" },
  pinterest: { nom: "Pinterest", icone: "i-simple-icons-pinterest", famille: "social" },
  snapchat: { nom: "Snapchat", icone: "i-simple-icons-snapchat", famille: "social" },
  reddit: { nom: "Reddit", icone: "i-simple-icons-reddit", famille: "social" },
  whatsapp: { nom: "WhatsApp", icone: "i-simple-icons-whatsapp", famille: "social" },
  telegram: { nom: "Telegram", icone: "i-simple-icons-telegram", famille: "social" }
};
var PICTOS = {
  googleAds: "i-simple-icons-googleads",
  publicite: "i-lucide-megaphone",
  demarchage: "i-lucide-send",
  email: "i-lucide-mail",
  ia: "i-lucide-sparkles",
  recherche: "i-lucide-search",
  social: "i-lucide-share-2",
  siteDonoor: "i-lucide-house",
  site: "i-lucide-link",
  direct: "i-lucide-mouse-pointer-click"
};
var ICONES_ORIGINE_ACQUISITION = [
  .../* @__PURE__ */ new Set([...Object.values(PICTOS), ...Object.values(PLATEFORMES).map((p) => p.icone)])
];
var CAMPAGNES_DEMARCHAGE = {
  helloasso_dormant: "Assos HelloAsso",
  joafe_creation: "Nouvelles assos"
};
function origine(famille, libelle, detail, icone) {
  return { famille, libelle, detail, couleur: COULEURS_ORIGINE[famille], icone };
}
function origineAcquisition(a) {
  if (!a) return null;
  const source = (a.source ?? "").toLowerCase().trim();
  const canal = (a.channel ?? "").toLowerCase().trim();
  const campagne = a.utmCampaign?.trim() || null;
  if (a.viaAds || source === "paid" && canal === "google") {
    return origine("publicite", "Google Ads", campagne, PICTOS.googleAds);
  }
  if (source === "paid") {
    const p = PLATEFORMES[canal];
    return p ? origine("publicite", `${p.nom} Ads`, campagne, p.icone) : origine("publicite", "Publicit\xE9", campagne ?? (canal || null), PICTOS.publicite);
  }
  if (source === "email" || designeEmail(canal) || designeEmail(a.utmSource) || designeEmail(a.utmMedium)) {
    const demarchage = campagne ? CAMPAGNES_DEMARCHAGE[campagne] : void 0;
    return demarchage ? origine("email", "D\xE9marchage e-mail", demarchage, PICTOS.demarchage) : origine("email", "E-mail", campagne, PICTOS.email);
  }
  const plateforme = PLATEFORMES[canal] ?? PLATEFORMES[assistantIA(canal) ?? ""];
  if (plateforme) {
    const libelle = plateforme.famille === "recherche" ? `Recherche ${plateforme.nom}` : plateforme.nom;
    return origine(plateforme.famille, libelle, null, plateforme.icone);
  }
  if (source === "ai") return origine("ia", "Assistant IA", canal || null, PICTOS.ia);
  if (source === "organic") return origine("recherche", "Moteur de recherche", canal || null, PICTOS.recherche);
  if (source === "social") return origine("social", "R\xE9seau social", canal || null, PICTOS.social);
  if (source === "referral" && canal && canal !== "direct") {
    const domaine = canal.replace(/^www\./, "");
    return domaine === "donoor.org" ? origine("site", "Site donoor.org", null, PICTOS.siteDonoor) : origine("site", domaine, null, PICTOS.site);
  }
  return origine("direct", "Direct", null, PICTOS.direct);
}

// src/string.ts
function truncate(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}
function capitalize(text) {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}
function truncateForMeta(text, maxLength = 160) {
  if (!text) return "";
  const codePoints = [...text];
  if (codePoints.length <= maxLength) return text;
  const budget = Math.max(0, maxLength - 1);
  let sliced = codePoints.slice(0, budget).join("");
  const lastSpace = sliced.lastIndexOf(" ");
  if (lastSpace > 0) sliced = sliced.slice(0, lastSpace);
  return `${sliced.trimEnd()}\u2026`;
}
function stripHtml(html) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/\s+/g, " ").trim();
}
function slugify(text, maxLength = 50) {
  if (!text) return "";
  const COMBINING_MARKS = /[̀-ͯ]/g;
  return text.toLowerCase().normalize("NFD").replace(COMBINING_MARKS, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, maxLength);
}
function plural(count, singular, pluralForm) {
  const n = Math.abs(count);
  if (n < 2) return singular;
  return pluralForm ?? `${singular}s`;
}
function pluralCount(count, singular, pluralForm) {
  return `${count} ${plural(count, singular, pluralForm)}`;
}
var RNA_ENCODING_FIXES = [
  // Formes doublement encodées d'abord : elles contiennent « Ã », que les
  // règles suivantes ne doivent pas voir.
  [/ÃŽ/g, "\xF4"],
  [/Ã©/g, "\xE9"],
  [/Ã¨/g, "\xE8"],
  [/Ãª/g, "\xEA"],
  [/Ã´/g, "\xF4"],
  [/Ã§/g, "\xE7"],
  [/Ã®/g, "\xEE"],
  [/Ã¹/g, "\xF9"],
  [/Ã»/g, "\xFB"],
  [/Ã\s{1,2}/g, "\xE0 "],
  [/â€™/g, "\u2019"],
  // Apostrophe : « là’orgue » → « l'orgue ». Seulement après une lettre, pour
  // ne pas toucher un « à » en fin de mot suivi d'une vraie apostrophe.
  [/([a-zA-ZÀ-ÿ])à’/g, "$1'"],
  [/àœ/g, "\u0153"],
  // « è » : entre deux majuscules on reste en capitales (« DÉCÈS »).
  [/(?<=[A-ZÀ-Ý])Ú(?=[A-ZÀ-Ý])/g, "\xC8"],
  [/Ú/g, "\xE8"],
  // « ô » : uniquement en milieu de mot minuscule — « Île », « Îlot » restent.
  [/(?<=[a-zà-ÿ])Î/g, "\xF4"],
  // « à » suivi de deux espaces ou plus.
  [/à {2,}/g, "\xE0 "]
];
var RNA_ENCODING_MARKERS = /Ú|Î|Ã|à’|à {2}|àœ|â€/;
function fixRnaEncoding(text) {
  if (!text || !RNA_ENCODING_MARKERS.test(text)) return text;
  let out = text;
  for (const [pattern, replacement] of RNA_ENCODING_FIXES) {
    out = out.replace(pattern, replacement);
  }
  return out;
}
function replierPourRecherche(text) {
  if (!text) return "";
  const COMBINING_MARKS = /[\u0300-\u036f]/g;
  return text.toLowerCase().normalize("NFD").replace(COMBINING_MARKS, "");
}
function correspondALaRecherche(recherche, ...champs) {
  const terme = replierPourRecherche(recherche?.trim());
  if (!terme) return true;
  return champs.some((c) => replierPourRecherche(c).includes(terme));
}
var EXTENSIONS_NUES = "com|org|net|fr|eu|be|ch|lu|ma|dz|tn|sn|ci|re|gp|mq|gf|yt|pm|nc|pf|io|app|info|co|me|uk|de|es|it|nl";
var MOTIF_LIENS = new RegExp(
  `(https?://[^\\s<>"]+)|([A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\\.[A-Za-z0-9-]+)*\\.[A-Za-z]{2,})|(www\\.[^\\s<>"]+)|((?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\\.)+(?:${EXTENSIONS_NUES})(?![A-Za-z0-9-])(?:/[^\\s<>"]*)?)`,
  "gi"
);
var FIN_PARASITE = `.,;:!?'"\xBB)]}\u2026`;
function sansPonctuationFinale(lien) {
  let l = lien;
  while (l.length > 0 && FIN_PARASITE.includes(l[l.length - 1])) {
    if (l.endsWith(")") && l.split("(").length > l.split(")").length - 1) break;
    l = l.slice(0, -1);
  }
  return l;
}
function decouperLiens(texte) {
  if (!texte) return [];
  const morceaux = [];
  let curseur = 0;
  for (const m of texte.matchAll(MOTIF_LIENS)) {
    const debut = m.index ?? 0;
    const avant = debut > 0 ? texte[debut - 1] : "";
    if (avant && /[A-Za-z0-9@./-]/.test(avant)) continue;
    const brut = sansPonctuationFinale(m[0]);
    if (!brut) continue;
    const url = m[1] ? brut : m[2] ? `mailto:${brut}` : `https://${brut}`;
    if (debut > curseur) morceaux.push({ texte: texte.slice(curseur, debut) });
    morceaux.push({ texte: brut, url });
    curseur = debut + brut.length;
  }
  if (curseur < texte.length) morceaux.push({ texte: texte.slice(curseur) });
  return morceaux;
}

// src/socials.ts
var INSTAGRAM_HANDLE = "donoor_org";
var FACEBOOK_HANDLE = "donoor.0rg";
var TIKTOK_HANDLE = "donoor.org";
var DONOOR_SOCIALS = {
  instagram: `https://www.instagram.com/${INSTAGRAM_HANDLE}`,
  facebook: `https://www.facebook.com/${FACEBOOK_HANDLE}`,
  tiktok: `https://www.tiktok.com/@${TIKTOK_HANDLE}`
};
var DONOOR_SOCIAL_URLS = [
  DONOOR_SOCIALS.instagram,
  DONOOR_SOCIALS.facebook,
  DONOOR_SOCIALS.tiktok
];

// src/eventCustomFields.ts
function documentDeLaReponse(brut) {
  if (!brut || typeof brut !== "object" || Array.isArray(brut)) return null;
  const obj = brut;
  if (typeof obj.file_id !== "string" || !obj.file_id) return null;
  const nom = [obj.file_name, obj.filename].find(
    (n) => typeof n === "string" && n.trim() !== ""
  );
  return { id: obj.file_id, nom: nom?.trim() || tS("Document") };
}
function rendreLisible(brut) {
  if (brut === null || brut === void 0) return "";
  if (typeof brut === "boolean") return brut ? tS("Oui") : tS("Non");
  if (Array.isArray(brut)) {
    return brut.map((v) => rendreLisible(v)).filter(Boolean).join(", ");
  }
  if (typeof brut === "object") {
    const document2 = documentDeLaReponse(brut);
    if (document2) return document2.nom;
    return "";
  }
  return String(brut).trim();
}
function apparierReponsesFormulaire(definitions, donnees) {
  if (!definitions?.length || !donnees) return [];
  const out = [];
  for (const def of definitions) {
    if (!def?.key) continue;
    const brut = donnees[def.key] !== void 0 ? donnees[def.key] : def.label ? donnees[def.label] : void 0;
    const value = rendreLisible(brut);
    if (!value) continue;
    const document2 = documentDeLaReponse(brut);
    out.push({
      key: def.key,
      label: def.label || def.key,
      value,
      ...document2 ? { fichier: document2 } : {}
    });
  }
  return out;
}

// src/porteeTarifs.ts
function concerneTarif(element, tarifId) {
  if (element.applies_to !== "custom") return true;
  if (!tarifId) return false;
  return (element.applies_to_ids ?? []).map(String).includes(String(tarifId));
}
function erreurPrixLibreBillet(prix, min, max, formater) {
  if (prix === null || prix === void 0 || !Number.isFinite(prix) || prix < 0) {
    return "Indiquez le montant r\xE9gl\xE9.";
  }
  const minimum = Number(min ?? 0);
  if (minimum > 0 && prix < minimum) return `Le montant minimum est de ${formater(minimum)}.`;
  if (max !== null && max !== void 0 && Number.isFinite(Number(max)) && prix > Number(max)) {
    return `Le montant maximum est de ${formater(Number(max))}.`;
  }
  return null;
}
var INDICATIF_PAR_PREFIXE = {
  "590": "590",
  "690": "590",
  "691": "590",
  "596": "596",
  "696": "596",
  "697": "596",
  "594": "594",
  "694": "594",
  "262": "262",
  "263": "262",
  "692": "262",
  "693": "262",
  "269": "262",
  "639": "262"
};
var INDICATIFS_PLAN_FRANCAIS = ["33", "590", "594", "596", "262"];
function indicatifTelephone(national9) {
  return INDICATIF_PAR_PREFIXE[national9.slice(0, 3)] ?? "33";
}
function paysInterpretation(paysAsso) {
  const code = (paysAsso ?? "").trim().toUpperCase();
  return code && isSupportedCountry(code) ? code : "FR";
}
function nettoyer(brut) {
  let t = brut.trim().replace(/[\s.\-·()/]/g, "");
  if (t.startsWith("00")) t = "+" + t.slice(2);
  return t;
}
function planFrancais(national9) {
  if (!/^[1-9]\d{8}$/.test(national9)) return null;
  return `+${indicatifTelephone(national9)}${national9}`;
}
function telephoneInternational(brut, paysAsso = "FR") {
  if (!brut) return null;
  const t = nettoyer(brut);
  if (!t) return null;
  if (t.startsWith("+")) {
    const chiffres = t.slice(1);
    if (!/^\d+$/.test(chiffres)) return null;
    const indicatif = INDICATIFS_PLAN_FRANCAIS.find((i) => chiffres.startsWith(i));
    if (indicatif) return planFrancais(chiffres.slice(indicatif.length).replace(/^0/, ""));
    const numero2 = parsePhoneNumberFromString(t);
    return numero2?.isPossible() ? numero2.number : null;
  }
  if (!/^\d+$/.test(t)) return null;
  const pays = paysInterpretation(paysAsso);
  if (pays === "FR") {
    if (/^0\d{9}$/.test(t)) return planFrancais(t.slice(1));
    if (/^\d{9}$/.test(t) && !t.startsWith("33")) return planFrancais(t);
    return null;
  }
  const numero = parsePhoneNumberFromString(t, pays);
  if (numero && INDICATIFS_PLAN_FRANCAIS.includes(numero.countryCallingCode)) {
    return planFrancais(numero.nationalNumber);
  }
  return numero?.isValid() ? numero.number : null;
}
function telephoneValide(brut, paysAsso = "FR") {
  return telephoneInternational(brut, paysAsso) !== null;
}
function telephoneAEnregistrer(brut, paysAsso = "FR") {
  const t = brut?.trim() ?? "";
  if (!t) return "";
  return telephoneInternational(t, paysAsso) ?? t.replace(/[\s.\-·()/]/g, "");
}
function nationalFrancais(international) {
  const indicatif = INDICATIFS_PLAN_FRANCAIS.find((i) => international.startsWith(`+${i}`));
  if (!indicatif) return null;
  return `0${international.slice(indicatif.length + 1)}`.replace(/(\d{2})(?=\d)/g, "$1 ");
}
function telephoneAffiche(brut, paysAsso = "FR") {
  const t = brut?.trim() ?? "";
  if (!t) return "";
  const international = telephoneInternational(t, paysAsso);
  if (!international) return t;
  const pays = paysInterpretation(paysAsso);
  const francais = nationalFrancais(international);
  if (francais && pays === "FR") return francais;
  const numero = parsePhoneNumberFromString(international);
  if (!numero) return t;
  if (numero.countryCallingCode === getCountryCallingCode(pays)) return numero.formatNational();
  return numero.formatInternational();
}
function paysDuTelephone(brut, paysAsso = "FR") {
  const international = telephoneInternational(brut, paysAsso);
  if (!international) return null;
  const numero = parsePhoneNumberFromString(international);
  if (numero?.country) return numero.country;
  const indicatif = INDICATIFS_PLAN_FRANCAIS.find((i) => international.startsWith(`+${i}`));
  if (!indicatif) return null;
  const national9 = international.slice(indicatif.length + 1);
  if (indicatif === "262") return ["269", "639"].includes(national9.slice(0, 3)) ? "YT" : "RE";
  return { "33": "FR", "590": "GP", "594": "GF", "596": "MQ" }[indicatif] ?? null;
}
var ADJECTIF_PAYS = {
  DE: "allemand",
  AD: "andorran",
  AT: "autrichien",
  BE: "belge",
  BG: "bulgare",
  CY: "chypriote",
  HR: "croate",
  DK: "danois",
  ES: "espagnol",
  EE: "estonien",
  FI: "finlandais",
  GR: "grec",
  HU: "hongrois",
  IE: "irlandais",
  IS: "islandais",
  IT: "italien",
  LV: "letton",
  LI: "liechtensteinois",
  LT: "lituanien",
  LU: "luxembourgeois",
  MT: "maltais",
  MC: "mon\xE9gasque",
  NO: "norv\xE9gien",
  NL: "n\xE9erlandais",
  PL: "polonais",
  PT: "portugais",
  CZ: "tch\xE8que",
  RO: "roumain",
  GB: "britannique",
  SK: "slovaque",
  SI: "slov\xE8ne",
  SE: "su\xE9dois",
  CH: "suisse"
};
function indicatifDuPays(pays) {
  const code = (pays ?? "").trim().toUpperCase();
  return code && isSupportedCountry(code) ? `+${getCountryCallingCode(code)}` : null;
}
function exempleTelephone(paysAsso = "FR") {
  const pays = paysInterpretation(paysAsso);
  return getExampleNumber(pays, exemples)?.formatNational() ?? "";
}
function messageTelephoneInvalide(paysAsso = "FR") {
  const pays = paysInterpretation(paysAsso);
  const adjectif = ADJECTIF_PAYS[pays];
  const exemple = exempleTelephone(pays);
  const debut = adjectif ? `Num\xE9ro de t\xE9l\xE9phone ${adjectif} invalide.` : "Num\xE9ro de t\xE9l\xE9phone invalide.";
  return exemple ? `${debut} Exemple : ${exemple}` : debut;
}
function lienTelephone(brut, paysAsso = "FR") {
  return `tel:${numeroPourLien(brut, paysAsso)}`;
}
function lienSms(brut, paysAsso = "FR") {
  return `sms:${numeroPourLien(brut, paysAsso)}`;
}
function lienWhatsApp(brut, paysAsso = "FR") {
  const international = telephoneInternational(brut?.trim() ?? "", paysAsso);
  return international ? `https://wa.me/${international.slice(1)}` : null;
}
function numeroPourLien(brut, paysAsso) {
  const t = brut?.trim() ?? "";
  return telephoneInternational(t, paysAsso) ?? t.replace(/[\s.]/g, "");
}

// src/communesHomonymesPays.ts
var COMMUNES_HOMONYMES_PAYS = [
  "Argentine",
  // AR — 73220 (73019)
  "Grenade",
  // GD — 31330 (31232)
  "Sainte-H\xE9l\xE8ne",
  // SH — 33480 (33417), 48190 (48157), 56700 (56220), 71390 (71426), 88700 (88418)
  "Soudan"
  // SD — 44110 (44199), 79800 (79316)
];
var COMMUNES_TERMINEES_PAR_UN_PAYS = [
  "Baillet-en-France",
  // « france » — 95560 (95042)
  "Beauchery-Saint-Martin",
  // « saint-martin » — 77560 (77026)
  "Beaumerie-Saint-Martin",
  // « saint-martin » — 62170 (62094)
  "Belloy-en-France",
  // « france » — 95270 (95056)
  "Bernay-Saint-Martin",
  // « saint-martin » — 17330 (17043)
  "B\xE9thisy-Saint-Martin",
  // « saint-martin » — 60320 (60067)
  "Boiry-Saint-Martin",
  // « saint-martin » — 62175 (62146)
  "Bonneuil-en-France",
  // « france » — 95500 (95088)
  "Bourg-Saint-Maurice",
  // « maurice » — 73700 (73054)
  "Bouy-Luxembourg",
  // « luxembourg » — 10220 (10056)
  "Bussy-Saint-Martin",
  // « saint-martin » — 77600 (77059)
  "Candes-Saint-Martin",
  // « saint-martin » — 37500 (37042)
  "Cavron-Saint-Martin",
  // « saint-martin » — 62140 (62220)
  "Ch\xE2tenay-en-France",
  // « france » — 95190 (95144)
  "Cour-Saint-Maurice",
  // « maurice » — 25380 (25173)
  "Fontaines-Saint-Martin",
  // « saint-martin » — 69270 (69087)
  "Fort-de-France",
  // « france » — 97200 (97209)
  "Hannogne-Saint-Martin",
  // « saint-martin » — 08160 (08209)
  "L'Hospitalet-pr\xE8s-l'Andorre",
  // « andorre » — 09390 (09139)
  "La Chapelle-Blanche-Saint-Martin",
  // « saint-martin » — 37240 (37057)
  "La Chapelle-du-Mont-de-France",
  // « france » — 71520 (71091)
  "La Chapelle-Saint-Martin",
  // « saint-martin » — 73170 (73078)
  "La Chapelle-Saint-Maurice",
  // « maurice » — 74410 (74060)
  "La Fontaine-Saint-Martin",
  // « saint-martin » — 72330 (72135)
  "La Jonch\xE8re-Saint-Maurice",
  // « maurice » — 87340 (87079)
  "La Motte-Saint-Martin",
  // « saint-martin » — 38770 (38266)
  "La R\xE9sie-Saint-Martin",
  // « saint-martin » — 70140 (70444)
  "La R\xE9union",
  // « reunion » — 47700 (47222)
  "La Roche-Maurice",
  // « maurice » — 29800 (29237)
  "La Rochebeaucourt-et-Argentine",
  // « argentine » — 24340 (24353)
  "Lafresguimont-Saint-Martin",
  // « saint-martin » — 80430 (80456)
  "Lamonzie-Saint-Martin",
  // « saint-martin » — 24680 (24225)
  "Latour-de-France",
  // « france » — 66720 (66096)
  "Le Ban-Saint-Martin",
  // « saint-martin » — 57050 (57049)
  "Luzy-Saint-Martin",
  // « saint-martin » — 55700 (55310)
  "Mareil-en-France",
  // « france » — 95850 (95365)
  "Mont-Saint-Martin",
  // « saint-martin » — 02220 (02523), 08400 (08308), 38120 (38258), 54350 (54382)
  "Montbonnot-Saint-Martin",
  // « saint-martin » — 38330 (38249)
  "Montigny-Saint-Barth\xE9lemy",
  // « saint-barthelemy » — 21390 (21430)
  "Montjoie-Saint-Martin",
  // « saint-martin » — 50240 (50347)
  "Mouzeuil-Saint-Martin",
  // « saint-martin » — 85370 (85158)
  "Neaufles-Saint-Martin",
  // « saint-martin » — 27830 (27426)
  "Noyers-Saint-Martin",
  // « saint-martin » — 60480 (60470)
  "Pont-Saint-Martin",
  // « saint-martin » — 44860 (44130)
  "Pouligny-Saint-Martin",
  // « saint-martin » — 36160 (36164)
  "Pr\xE9-Saint-Martin",
  // « saint-martin » — 28800 (28306)
  "Pr\xE9cy-Saint-Martin",
  // « saint-martin » — 10500 (10304)
  "Puiseux-en-France",
  // « france » — 95380 (95509)
  "Puy-Saint-Martin",
  // « saint-martin » — 26450 (26258)
  "Revest-Saint-Martin",
  // « saint-martin » — 04230 (04164)
  "Rigny-Saint-Martin",
  // « saint-martin » — 55140 (55434)
  "Robert-Espagne",
  // « espagne » — 55000 (55435)
  "Rocourt-Saint-Martin",
  // « saint-martin » — 02210 (02649)
  "Roissy-en-France",
  // « france » — 95700 (95527)
  "Sacierges-Saint-Martin",
  // « saint-martin » — 36170 (36177)
  "Saint-Benin",
  // « benin » — 59360 (59531)
  "Saint-Maurice",
  // « maurice » — 52200 (52453), 58330 (58257), 63270 (63378), 67220 (67427), 94410 (94069)
  "Sorcy-Saint-Martin",
  // « saint-martin » — 55190 (55496)
  "Th\xE9zey-Saint-Martin",
  // « saint-martin » — 54610 (54517)
  "Thun-Saint-Martin",
  // « saint-martin » — 59141 (59595)
  "Tournon-Saint-Martin",
  // « saint-martin » — 36220 (36224)
  "Tremblay-en-France",
  // « france » — 93290 (93073)
  "Tug\xE9ras-Saint-Maurice",
  // « maurice » — 17130 (17454)
  "Villers-Saint-Barth\xE9lemy",
  // « saint-barthelemy » — 60650 (60681)
  "Villers-Saint-Martin"
  // « saint-martin » — 25110 (25626)
];
var COMMUNES_HOMONYMES_TERRITOIRES = [
  "Arue",
  // métropole, PF
  "Bellefontaine",
  // métropole, MQ
  "La Trinit\xE9",
  // métropole, MQ
  "Le Port",
  // métropole, RE
  "Port-Louis",
  // GP, métropole
  "Saint-Andr\xE9",
  // métropole, RE
  "Saint-Barth\xE9lemy",
  // BL, métropole
  "Saint-Beno\xEEt",
  // métropole, RE
  "Saint-Claude",
  // GP, métropole
  "Saint-Denis",
  // métropole, RE
  "Saint-Georges",
  // GF, métropole
  "Saint-Joseph",
  // métropole, MQ, RE
  "Saint-Louis",
  // GP, métropole, RE
  "Saint-Martin",
  // métropole, MF
  "Saint-Paul",
  // métropole, RE
  "Saint-Pierre",
  // métropole, MQ, PM, RE
  "Sainte-Anne",
  // GP, métropole, MQ
  "Sainte-Luce",
  // métropole, MQ
  "Sainte-Marie",
  // métropole, MQ, RE
  "Sainte-Rose",
  // GP, RE
  "Sainte-Suzanne",
  // métropole, RE
  "Trois-Rivi\xE8res"
  // GP, métropole
];

// src/nomsPays.ts
var NOMS_PAYS_FR = {
  AD: "Andorre",
  AE: "\xC9mirats arabes unis",
  AF: "Afghanistan",
  AG: "Antigua-et-Barbuda",
  AI: "Anguilla",
  AL: "Albanie",
  AM: "Arm\xE9nie",
  AO: "Angola",
  AQ: "Antarctique",
  AR: "Argentine",
  AS: "Samoa am\xE9ricaines",
  AT: "Autriche",
  AU: "Australie",
  AW: "Aruba",
  AX: "\xCEles \xC5land",
  AZ: "Azerba\xEFdjan",
  BA: "Bosnie-Herz\xE9govine",
  BB: "Barbade",
  BD: "Bangladesh",
  BE: "Belgique",
  BF: "Burkina Faso",
  BG: "Bulgarie",
  BH: "Bahre\xEFn",
  BI: "Burundi",
  BJ: "B\xE9nin",
  BL: "Saint-Barth\xE9lemy",
  BM: "Bermudes",
  BN: "Brunei",
  BO: "Bolivie",
  BQ: "Pays-Bas carib\xE9ens",
  BR: "Br\xE9sil",
  BS: "Bahamas",
  BT: "Bhoutan",
  BV: "\xCEle Bouvet",
  BW: "Botswana",
  BY: "Bi\xE9lorussie",
  BZ: "Belize",
  CA: "Canada",
  CC: "\xCEles Cocos",
  CD: "Congo-Kinshasa",
  CF: "R\xE9publique centrafricaine",
  CG: "Congo-Brazzaville",
  CH: "Suisse",
  CI: "C\xF4te d\u2019Ivoire",
  CK: "\xCEles Cook",
  CL: "Chili",
  CM: "Cameroun",
  CN: "Chine",
  CO: "Colombie",
  CR: "Costa Rica",
  CU: "Cuba",
  CV: "Cap-Vert",
  CW: "Cura\xE7ao",
  CX: "\xCEle Christmas",
  CY: "Chypre",
  CZ: "Tch\xE9quie",
  DE: "Allemagne",
  DJ: "Djibouti",
  DK: "Danemark",
  DM: "Dominique",
  DO: "R\xE9publique dominicaine",
  DZ: "Alg\xE9rie",
  EC: "\xC9quateur",
  EE: "Estonie",
  EG: "\xC9gypte",
  EH: "Sahara occidental",
  ER: "\xC9rythr\xE9e",
  ES: "Espagne",
  ET: "\xC9thiopie",
  FI: "Finlande",
  FJ: "Fidji",
  FK: "\xCEles Malouines",
  FM: "Micron\xE9sie",
  FO: "\xCEles F\xE9ro\xE9",
  FR: "France",
  GA: "Gabon",
  GB: "Royaume-Uni",
  GD: "Grenade",
  GE: "G\xE9orgie",
  GF: "Guyane fran\xE7aise",
  GG: "Guernesey",
  GH: "Ghana",
  GI: "Gibraltar",
  GL: "Groenland",
  GM: "Gambie",
  GN: "Guin\xE9e",
  GP: "Guadeloupe",
  GQ: "Guin\xE9e \xE9quatoriale",
  GR: "Gr\xE8ce",
  GS: "G\xE9orgie du Sud-et-les \xCEles Sandwich du Sud",
  GT: "Guatemala",
  GU: "Guam",
  GW: "Guin\xE9e-Bissau",
  GY: "Guyana",
  HK: "R.A.S. chinoise de Hong Kong",
  HM: "\xCEles Heard-et-MacDonald",
  HN: "Honduras",
  HR: "Croatie",
  HT: "Ha\xEFti",
  HU: "Hongrie",
  ID: "Indon\xE9sie",
  IE: "Irlande",
  IL: "Isra\xEBl",
  IM: "\xCEle de Man",
  IN: "Inde",
  IO: "Territoire britannique de l\u2019oc\xE9an Indien",
  IQ: "Irak",
  IR: "Iran",
  IS: "Islande",
  IT: "Italie",
  JE: "Jersey",
  JM: "Jama\xEFque",
  JO: "Jordanie",
  JP: "Japon",
  KE: "Kenya",
  KG: "Kirghizstan",
  KH: "Cambodge",
  KI: "Kiribati",
  KM: "Comores",
  KN: "Saint-Christophe-et-Ni\xE9v\xE8s",
  KP: "Cor\xE9e du Nord",
  KR: "Cor\xE9e du Sud",
  KW: "Kowe\xEFt",
  KY: "\xCEles Ca\xEFmans",
  KZ: "Kazakhstan",
  LA: "Laos",
  LB: "Liban",
  LC: "Sainte-Lucie",
  LI: "Liechtenstein",
  LK: "Sri Lanka",
  LR: "Liberia",
  LS: "Lesotho",
  LT: "Lituanie",
  LU: "Luxembourg",
  LV: "Lettonie",
  LY: "Libye",
  MA: "Maroc",
  MC: "Monaco",
  MD: "Moldavie",
  ME: "Mont\xE9n\xE9gro",
  MF: "Saint-Martin",
  MG: "Madagascar",
  MH: "\xCEles Marshall",
  MK: "Mac\xE9doine du Nord",
  ML: "Mali",
  MM: "Myanmar (Birmanie)",
  MN: "Mongolie",
  MO: "R.A.S. chinoise de Macao",
  MP: "\xCEles Mariannes du Nord",
  MQ: "Martinique",
  MR: "Mauritanie",
  MS: "Montserrat",
  MT: "Malte",
  MU: "Maurice",
  MV: "Maldives",
  MW: "Malawi",
  MX: "Mexique",
  MY: "Malaisie",
  MZ: "Mozambique",
  NA: "Namibie",
  NC: "Nouvelle-Cal\xE9donie",
  NE: "Niger",
  NF: "\xCEle Norfolk",
  NG: "Nigeria",
  NI: "Nicaragua",
  NL: "Pays-Bas",
  NO: "Norv\xE8ge",
  NP: "N\xE9pal",
  NR: "Nauru",
  NU: "Niue",
  NZ: "Nouvelle-Z\xE9lande",
  OM: "Oman",
  PA: "Panama",
  PE: "P\xE9rou",
  PF: "Polyn\xE9sie fran\xE7aise",
  PG: "Papouasie-Nouvelle-Guin\xE9e",
  PH: "Philippines",
  PK: "Pakistan",
  PL: "Pologne",
  PM: "Saint-Pierre-et-Miquelon",
  PN: "\xCEles Pitcairn",
  PR: "Porto Rico",
  PS: "Territoires palestiniens",
  PT: "Portugal",
  PW: "Palaos",
  PY: "Paraguay",
  QA: "Qatar",
  RE: "La R\xE9union",
  RO: "Roumanie",
  RS: "Serbie",
  RU: "Russie",
  RW: "Rwanda",
  SA: "Arabie saoudite",
  SB: "\xCEles Salomon",
  SC: "Seychelles",
  SD: "Soudan",
  SE: "Su\xE8de",
  SG: "Singapour",
  SH: "Sainte-H\xE9l\xE8ne",
  SI: "Slov\xE9nie",
  SJ: "Svalbard et Jan Mayen",
  SK: "Slovaquie",
  SL: "Sierra Leone",
  SM: "Saint-Marin",
  SN: "S\xE9n\xE9gal",
  SO: "Somalie",
  SR: "Suriname",
  SS: "Soudan du Sud",
  ST: "Sao Tom\xE9-et-Principe",
  SV: "Salvador",
  SX: "Saint-Martin (partie n\xE9erlandaise)",
  SY: "Syrie",
  SZ: "Eswatini",
  TC: "\xCEles Turques-et-Ca\xEFques",
  TD: "Tchad",
  TF: "Terres australes fran\xE7aises",
  TG: "Togo",
  TH: "Tha\xEFlande",
  TJ: "Tadjikistan",
  TK: "Tokelau",
  TL: "Timor oriental",
  TM: "Turkm\xE9nistan",
  TN: "Tunisie",
  TO: "Tonga",
  TR: "Turquie",
  TT: "Trinit\xE9-et-Tobago",
  TV: "Tuvalu",
  TW: "Ta\xEFwan",
  TZ: "Tanzanie",
  UA: "Ukraine",
  UG: "Ouganda",
  UM: "\xCEles mineures \xE9loign\xE9es des \xC9tats-Unis",
  US: "\xC9tats-Unis",
  UY: "Uruguay",
  UZ: "Ouzb\xE9kistan",
  VA: "\xC9tat de la Cit\xE9 du Vatican",
  VC: "Saint-Vincent-et-les Grenadines",
  VE: "Venezuela",
  VG: "\xCEles Vierges britanniques",
  VI: "\xCEles Vierges des \xC9tats-Unis",
  VN: "Vi\xEAt Nam",
  VU: "Vanuatu",
  WF: "Wallis-et-Futuna",
  WS: "Samoa",
  YE: "Y\xE9men",
  YT: "Mayotte",
  ZA: "Afrique du Sud",
  ZM: "Zambie",
  ZW: "Zimbabwe"
};

// src/adresse.ts
var BAN_URL_RECHERCHE = "https://data.geopf.fr/geocodage/search";
var LONGUEUR_MAX_ADRESSE = 255;
var LONGUEUR_MAX_VILLE = 120;
var RECHERCHE_MIN_CARACTERES = 3;
var RECHERCHE_MAX_CARACTERES = 200;
var SUGGESTIONS_LIMITE_MAX = 8;
var DELAI_FRAPPE_MS = 250;
var SCORE_BAN_SUGGESTION = 0.4;
var SCORE_BAN_GEOCODAGE = 0.6;
var COORDONNEES_DECIMALES = 6;
var TERRITOIRES_OUTRE_MER = [
  { code: "GP", departement: "971", nom: "Guadeloupe", preposition: "en Guadeloupe", slug: "guadeloupe", centre: { latitude: 16.2412, longitude: -61.5331 }, fournisseur: "ban", couvertureBan: "complete" },
  { code: "MQ", departement: "972", nom: "Martinique", preposition: "en Martinique", slug: "martinique", centre: { latitude: 14.6161, longitude: -61.0588 }, fournisseur: "ban", couvertureBan: "complete" },
  { code: "GF", departement: "973", nom: "Guyane", preposition: "en Guyane", slug: "guyane", centre: { latitude: 4.9224, longitude: -52.3135 }, fournisseur: "ban", couvertureBan: "complete" },
  { code: "RE", departement: "974", nom: "La R\xE9union", preposition: "\xE0 La R\xE9union", slug: "la-reunion", centre: { latitude: -20.8821, longitude: 55.4507 }, fournisseur: "ban", couvertureBan: "complete" },
  { code: "YT", departement: "976", nom: "Mayotte", preposition: "\xE0 Mayotte", slug: "mayotte", centre: { latitude: -12.7806, longitude: 45.2279 }, fournisseur: "ban", couvertureBan: "complete" },
  { code: "PM", departement: "975", nom: "Saint-Pierre-et-Miquelon", preposition: "\xE0 Saint-Pierre-et-Miquelon", slug: "saint-pierre-et-miquelon", centre: { latitude: 46.7811, longitude: -56.1764 }, fournisseur: "ban", couvertureBan: "complete" },
  { code: "BL", departement: "977", nom: "Saint-Barth\xE9lemy", preposition: "\xE0 Saint-Barth\xE9lemy", slug: "saint-barthelemy", centre: { latitude: 17.8962, longitude: -62.8498 }, fournisseur: "ban", couvertureBan: "complete" },
  { code: "MF", departement: "978", nom: "Saint-Martin", preposition: "\xE0 Saint-Martin", slug: "saint-martin", centre: { latitude: 18.0667, longitude: -63.0833 }, fournisseur: "ban", couvertureBan: "complete" },
  { code: "WF", departement: "986", nom: "Wallis-et-Futuna", preposition: "\xE0 Wallis-et-Futuna", slug: "wallis-et-futuna", centre: { latitude: -13.2825, longitude: -176.1736 }, fournisseur: "osm", couvertureBan: "partielle" },
  { code: "PF", departement: "987", nom: "Polyn\xE9sie fran\xE7aise", preposition: "en Polyn\xE9sie fran\xE7aise", slug: "polynesie-francaise", centre: { latitude: -17.535, longitude: -149.5696 }, fournisseur: "osm", couvertureBan: "partielle" },
  { code: "NC", departement: "988", nom: "Nouvelle-Cal\xE9donie", preposition: "en Nouvelle-Cal\xE9donie", slug: "nouvelle-caledonie", centre: { latitude: -22.2758, longitude: 166.458 }, fournisseur: "ban", couvertureBan: "partielle" }
];
var TERRITOIRES_BAN_PARTIELLE = TERRITOIRES_OUTRE_MER.filter((t) => t.couvertureBan === "partielle").map((t) => t.code);
var ALIAS_PAYS = [
  { alias: "France m\xE9tropolitaine", code: "FR" },
  { alias: "R\xE9publique fran\xE7aise", code: "FR" },
  { alias: "R\xE9union", code: "RE" },
  { alias: "\xCEle de la R\xE9union", code: "RE" },
  { alias: "Guyane", code: "GF" },
  { alias: "Tahiti", code: "PF" },
  // Formes courtes usuelles du Pacifique (« Papeete, Polynésie », « Nouméa,
  // Calédonie ») : sans elles, ce mot devenait la VILLE, le territoire était
  // perdu, la BAN (qui ne couvre pas ces territoires) choisie, et le fuseau
  // automatique repassait à Europe/Paris.
  { alias: "Polyn\xE9sie", code: "PF" },
  { alias: "Cal\xE9donie", code: "NC" },
  { alias: "Wallis", code: "WF" },
  { alias: "Futuna", code: "WF" },
  { alias: "Saint-Pierre et Miquelon", code: "PM" },
  { alias: "Belgium", code: "BE" },
  { alias: "Belgi\xEB", code: "BE" },
  { alias: "Belgien", code: "BE" },
  { alias: "Schweiz", code: "CH" },
  { alias: "Svizzera", code: "CH" },
  { alias: "Switzerland", code: "CH" },
  { alias: "Deutschland", code: "DE" },
  { alias: "Germany", code: "DE" },
  { alias: "Luxemburg", code: "LU" },
  { alias: "L\xEBtzebuerg", code: "LU" },
  { alias: "Italia", code: "IT" },
  { alias: "Italy", code: "IT" },
  { alias: "Espa\xF1a", code: "ES" },
  { alias: "Spain", code: "ES" },
  { alias: "Nederland", code: "NL" },
  { alias: "Netherlands", code: "NL" },
  { alias: "Hollande", code: "NL" },
  { alias: "United Kingdom", code: "GB" },
  { alias: "Grande-Bretagne", code: "GB" },
  { alias: "Angleterre", code: "GB" },
  { alias: "UK", code: "GB" },
  { alias: "\xD6sterreich", code: "AT" },
  { alias: "Austria", code: "AT" },
  { alias: "Ireland", code: "IE" },
  { alias: "Polska", code: "PL" },
  { alias: "Poland", code: "PL" },
  { alias: "Danmark", code: "DK" },
  { alias: "Denmark", code: "DK" },
  { alias: "Sverige", code: "SE" },
  { alias: "Sweden", code: "SE" },
  { alias: "Norge", code: "NO" },
  { alias: "Norway", code: "NO" },
  { alias: "Suomi", code: "FI" },
  { alias: "Finland", code: "FI" },
  { alias: "R\xE9publique tch\xE8que", code: "CZ" },
  { alias: "Czechia", code: "CZ" },
  { alias: "Romania", code: "RO" },
  { alias: "Principaut\xE9 de Monaco", code: "MC" },
  { alias: "USA", code: "US" },
  { alias: "United States", code: "US" },
  { alias: "Hong Kong", code: "HK" },
  { alias: "Macao", code: "MO" },
  { alias: "Birmanie", code: "MM" },
  { alias: "Vatican", code: "VA" },
  { alias: "\xCEle Maurice", code: "MU" }
];
var PAYS_CODE_POSTAL_5_CHIFFRES = [
  "AS",
  "AX",
  "BA",
  "BR",
  "BT",
  "CR",
  "CU",
  "CZ",
  "DE",
  "DO",
  "DZ",
  "EE",
  "EG",
  "ES",
  "FI",
  "GR",
  "GT",
  "GU",
  "HN",
  "HR",
  "ID",
  "IQ",
  "IT",
  "JO",
  "KE",
  "KH",
  "KR",
  "KW",
  "LA",
  "LK",
  "LT",
  "ME",
  "MM",
  "MN",
  "MP",
  "MA",
  "MV",
  "MX",
  "MY",
  "NP",
  "PE",
  "PK",
  "PR",
  "RS",
  "SA",
  "SD",
  "SE",
  "SK",
  "SM",
  "SN",
  "TH",
  "TR",
  "TZ",
  "UA",
  "US",
  "UY",
  "VA",
  "VI",
  "XK",
  "ZM"
];
var FORMATS_CODE_POSTAL = [
  { pays: "AL", format: "4" },
  { pays: "AM", format: "4" },
  { pays: "AT", format: "4" },
  { pays: "BE", format: "4" },
  { pays: "BG", format: "4" },
  { pays: "CH", format: "4" },
  { pays: "CY", format: "4" },
  { pays: "DK", format: "4" },
  { pays: "GE", format: "4" },
  { pays: "HU", format: "4" },
  { pays: "LI", format: "4" },
  { pays: "LU", format: "4" },
  { pays: "LV", format: "4" },
  { pays: "MK", format: "4" },
  { pays: "NO", format: "4" },
  { pays: "SI", format: "4" },
  { pays: "TN", format: "4" },
  { pays: "FO", format: "3" },
  { pays: "IS", format: "3" },
  { pays: "NL", format: "4L" },
  { pays: "PT", format: "4-3" },
  { pays: "PL", format: "2-3" },
  { pays: "CZ", format: "3+2" },
  { pays: "GR", format: "3+2" },
  { pays: "SE", format: "3+2" },
  { pays: "SK", format: "3+2" },
  { pays: "GB", format: "GB" },
  { pays: "GG", format: "GB" },
  { pays: "IM", format: "GB" },
  { pays: "JE", format: "GB" },
  { pays: "IE", format: "IE" }
];
var PAYS_HOMONYMES_VILLE = [
  "DJ",
  "GI",
  "GT",
  "HK",
  "KW",
  "LU",
  "MC",
  "MO",
  "PA",
  "SG",
  "SM",
  "VA"
];
var TYPES_VOIE = [
  "rue",
  "r",
  "avenue",
  "av",
  "ave",
  "chemin",
  "ch",
  "che",
  "allee",
  "allees",
  "place",
  "pl",
  "boulevard",
  "bd",
  "bld",
  "blvd",
  "impasse",
  "imp",
  "route",
  "rte",
  "quai",
  "cours",
  "crs",
  "bp",
  "cs",
  "tsa",
  "lieu-dit",
  "lieudit",
  "ld",
  "square",
  "sq",
  "passage",
  "residence",
  "res",
  "lotissement",
  "lot",
  "hameau",
  "cite",
  "voie",
  "sentier",
  "rond-point",
  "parvis",
  "esplanade",
  "promenade",
  "faubourg",
  "fbg",
  "zone",
  "za",
  "zi",
  "zac",
  "zae",
  "batiment",
  "bat",
  "immeuble",
  "ruelle",
  "traverse",
  "montee",
  "venelle",
  "villa",
  "domaine",
  "mail",
  "carrefour",
  "parc"
];
var BLANCS = /* @__PURE__ */ new Set([
  9,
  10,
  11,
  12,
  13,
  32,
  160,
  5760,
  8192,
  8193,
  8194,
  8195,
  8196,
  8197,
  8198,
  8199,
  8200,
  8201,
  8202,
  8232,
  8233,
  8239,
  8287,
  12288,
  65279
]);
function estBlanc(c) {
  return BLANCS.has(c.codePointAt(0));
}
function replierBlancs(texte) {
  let sortie = "";
  let enBlanc = false;
  for (const c of texte ?? "") {
    if (estBlanc(c)) {
      enBlanc = true;
    } else {
      if (enBlanc && sortie.length > 0) sortie += " ";
      enBlanc = false;
      sortie += c;
    }
  }
  return sortie;
}
function estChiffre(c) {
  return c >= "0" && c <= "9";
}
function queChiffres(t) {
  if (t.length === 0) return false;
  for (const c of t) if (!estChiffre(c)) return false;
  return true;
}
function contientChiffre(t) {
  for (const c of t) if (estChiffre(c)) return true;
  return false;
}
function mots(texte) {
  return texte.split(" ").filter((m) => m.length > 0);
}
function tronquer(texte, max) {
  return texte.length <= max ? texte : texte.slice(0, max);
}
var PLIAGE_DE = "\xC0\xC1\xC2\xC3\xC4\xC5\xE0\xE1\xE2\xE3\xE4\xE5\xC8\xC9\xCA\xCB\xE8\xE9\xEA\xEB\xCC\xCD\xCE\xCF\xEC\xED\xEE\xEF\xD2\xD3\xD4\xD5\xD6\xD8\xF2\xF3\xF4\xF5\xF6\xF8\xD9\xDA\xDB\xDC\xF9\xFA\xFB\xFC\xDD\xFD\xFF\xD1\xF1\xC7\xE7";
var PLIAGE_VERS = "AAAAAAaaaaaaEEEEeeeeIIIIiiiiOOOOOOooooooUUUUuuuuYyyNnCc";
var PLIAGE = new Map([...PLIAGE_DE].map((c, i) => [c, PLIAGE_VERS[i]]));
var REMPLACEMENTS_SLUG = [
  ["\u0153", "oe"],
  ["\u0152", "OE"],
  ["\xE6", "ae"],
  ["\xC6", "AE"],
  ["\xDF", "ss"],
  ["\u1E9E", "SS"],
  ["\u0130", "I"],
  ["\u212A", "K"]
  // signe Kelvin
];
function slugTexte(texte) {
  let t = texte ?? "";
  for (const [de, vers] of REMPLACEMENTS_SLUG) t = t.split(de).join(vers);
  let sortie = "";
  let tiret = false;
  for (const brut of t) {
    let c = PLIAGE.get(brut) ?? brut;
    if (c >= "A" && c <= "Z") c = String.fromCharCode(c.charCodeAt(0) + 32);
    const garde = c >= "a" && c <= "z" || c >= "0" && c <= "9";
    if (garde) {
      if (tiret && sortie.length > 0) sortie += "-";
      tiret = false;
      sortie += c;
    } else {
      tiret = true;
    }
  }
  return sortie;
}
function slugContient(botte, aiguille) {
  if (!aiguille || !botte) return false;
  return `-${botte}-`.includes(`-${aiguille}-`);
}
function normaliserRecherche(q) {
  return slugTexte(q);
}
function memeLibelle(a, b) {
  const sa = slugTexte(a);
  return sa.length > 0 && sa === slugTexte(b);
}
var RE_INSEE = /^[0-9][0-9AB][0-9]{3}$/;
var RE_CP5 = /^[0-9]{5}$/;
var TERRITOIRE_PAR_DEPARTEMENT = {
  "971": "GP",
  "972": "MQ",
  "973": "GF",
  "974": "RE",
  "975": "PM",
  "976": "YT",
  "977": "BL",
  "978": "MF",
  "986": "WF",
  "987": "PF",
  "988": "NC"
};
function paysEstFrance(pays) {
  return (pays ?? "FR").toUpperCase() === "FR";
}
function territoireIndice({ pays, codePostal, codeInsee, indice }) {
  if (!paysEstFrance(pays) || codePostal != null || codeInsee != null || indice == null) return null;
  return territoireParCode(indice.toUpperCase());
}
function territoireDepuisCodes(codes) {
  return territoireDesCodes(codes) ?? territoireIndice(codes)?.code ?? null;
}
function territoireDesCodes({ pays, codePostal, codeInsee }) {
  if (!paysEstFrance(pays)) return null;
  if (codeInsee != null && RE_INSEE.test(codeInsee)) {
    return TERRITOIRE_PAR_DEPARTEMENT[codeInsee.slice(0, 3)] ?? null;
  }
  if (codePostal === "97133") return "BL";
  if (codePostal === "97150") return "MF";
  if (codePostal != null && RE_CP5.test(codePostal)) {
    const p = codePostal.slice(0, 3);
    if (p === "977" || p === "978") return null;
    return TERRITOIRE_PAR_DEPARTEMENT[p] ?? null;
  }
  return null;
}
var DEPARTEMENTS_OUTRE_MER_INSEE = /* @__PURE__ */ new Set(["971", "972", "973", "974", "975", "976", "977", "978", "986", "987", "988"]);
var DEPARTEMENTS_OUTRE_MER_CP = /* @__PURE__ */ new Set(["971", "972", "973", "974", "975", "976", "986", "987", "988"]);
function departementDepuisCodes(codes) {
  return departementDesCodes(codes) ?? territoireIndice(codes)?.departement ?? null;
}
function departementDesCodes({ pays, codePostal, codeInsee }) {
  if (!paysEstFrance(pays)) return null;
  if (codeInsee != null && RE_INSEE.test(codeInsee)) {
    const d2 = codeInsee.slice(0, 2);
    if (d2 === "97" || d2 === "98") {
      const d3 = codeInsee.slice(0, 3);
      return DEPARTEMENTS_OUTRE_MER_INSEE.has(d3) ? d3 : null;
    }
    return d2;
  }
  if (codePostal === "97133") return "977";
  if (codePostal === "97150") return "978";
  if (codePostal != null && RE_CP5.test(codePostal)) {
    const d2 = codePostal.slice(0, 2);
    if (d2 === "97" || d2 === "98") {
      const d3 = codePostal.slice(0, 3);
      return DEPARTEMENTS_OUTRE_MER_CP.has(d3) ? d3 : null;
    }
    if (d2 === "20") return codePostal < "20200" ? "2A" : "2B";
    if (d2 === "00") return null;
    return d2;
  }
  return null;
}
function slugVille({
  pays,
  ville,
  departement
}) {
  const s = slugTexte(ville);
  if (!s) return null;
  if (!paysEstFrance(pays)) return `${s}-${(pays ?? "").toLowerCase()}`;
  if (departement != null && /^9[78][0-9]$/.test(departement)) return `${s}-${departement}`;
  return s;
}
function paysEtTerritoireDepuisIso(code) {
  const c = (code ?? "").trim().toUpperCase();
  const territoire = TERRITOIRES_OUTRE_MER.find((t) => t.code === c);
  if (territoire) return { pays: "FR", territoire: territoire.code };
  return { pays: c, territoire: null };
}
function territoireParCode(code) {
  return code ? TERRITOIRES_OUTRE_MER.find((t) => t.code === code) ?? null : null;
}
var _codeParSlugPays = null;
function codeParSlugPays() {
  if (_codeParSlugPays) return _codeParSlugPays;
  const m = /* @__PURE__ */ new Map();
  for (const [code, nom] of Object.entries(NOMS_PAYS_FR)) {
    const s = slugTexte(nom);
    if (s && !m.has(s)) m.set(s, code);
  }
  for (const { alias, code } of ALIAS_PAYS) {
    const s = slugTexte(alias);
    if (s && !m.has(s)) m.set(s, code);
  }
  _codeParSlugPays = m;
  return m;
}
function codePaysDuNom(texte) {
  const s = slugTexte(texte);
  if (!s) return null;
  const direct = codeParSlugPays().get(s);
  if (direct !== void 0) return direct;
  const tiret = s.lastIndexOf("-");
  if (tiret <= 0) return null;
  const code = codeParSlugPays().get(s.slice(0, tiret)) ?? null;
  return territoireParCode(code)?.departement === s.slice(tiret + 1) ? code : null;
}
var MOTS_AVANT_NOM_COMMUN = /* @__PURE__ */ new Set([
  "de",
  "d",
  "du",
  "des",
  "la",
  "le",
  "les",
  "l",
  "en",
  "au",
  "aux",
  "saint",
  "sainte",
  "st",
  "ste",
  "chez",
  "c-o"
]);
var MOTS_COMPLEMENT = /* @__PURE__ */ new Set(["chez", "c-o"]);
var _communesTerminees = null;
function finitParUneCommune(ms, debutNom) {
  _communesTerminees ??= new Set(COMMUNES_TERMINEES_PAR_UN_PAYS.map((n) => slugTexte(n)));
  for (let k = 0; k < debutNom; k++) {
    if (_communesTerminees.has(slugTexte(ms.slice(k).join(" ")))) return true;
  }
  return false;
}
var TYPES_VOIE_ENSEMBLE = new Set(TYPES_VOIE);
function prefixeCodePostal(mot) {
  const tiret = mot.indexOf("-");
  if (tiret < 1 || tiret > 2) return null;
  const prefixe = mot.slice(0, tiret);
  const chiffres = mot.slice(tiret + 1);
  for (const c of prefixe) if (!majusculeAscii(c)) return null;
  if (chiffres.length < 3 || chiffres.length > 5 || !queChiffres(chiffres)) return null;
  return prefixe;
}
function motAutoriseAvantPays(mot, code) {
  const s = slugTexte(mot);
  if (!s) return false;
  const dernier = mot.slice(-1);
  if (dernier === "'" || dernier === "\u2019") return false;
  if (MOTS_AVANT_NOM_COMMUN.has(s) || TYPES_VOIE_ENSEMBLE.has(s)) return false;
  if (queChiffres(mot)) {
    if (mot.length === 4) return true;
    return mot.length === 5 && departementDepuisCodes({ pays: "FR", codePostal: mot }) === null;
  }
  const prefixe = prefixeCodePostal(mot);
  if (prefixe !== null) return prefixe === code || prefixe.length === 1 && code.startsWith(prefixe);
  return !contientChiffre(mot);
}
var SAUTS_DE_LIGNE = /* @__PURE__ */ new Set([10, 11, 12, 13, 8232, 8233]);
var TIRETS_SEPARATEURS = /* @__PURE__ */ new Set(["-", "\u2013", "\u2014"]);
function lignesEnSegments(texte) {
  const cs = [...texte ?? ""];
  let sortie = "";
  let dernier = "";
  let apresSeparateur = false;
  for (let i = 0; i < cs.length; i++) {
    const c = cs[i];
    const tiretIsole = TIRETS_SEPARATEURS.has(c) && (i === 0 || estBlanc(cs[i - 1])) && (i === cs.length - 1 || estBlanc(cs[i + 1]));
    if (SAUTS_DE_LIGNE.has(c.codePointAt(0)) || c === ";" || tiretIsole) {
      sortie = sansBlancsFinaux(sortie) + (dernier === "," ? " " : ", ");
      dernier = ",";
      apresSeparateur = true;
      continue;
    }
    if (apresSeparateur && estBlanc(c)) continue;
    apresSeparateur = false;
    sortie += c;
    if (!estBlanc(c)) dernier = c;
  }
  return sortie;
}
function sansBlancsFinaux(texte) {
  const cs = [...texte];
  let fin = cs.length;
  while (fin > 0 && estBlanc(cs[fin - 1])) fin--;
  return cs.slice(0, fin).join("");
}
function segments(texte) {
  return replierBlancs(lignesEnSegments(texte)).split(",").map((s) => replierBlancs(s)).filter((s) => s.length > 0);
}
function paysEnFinDeSegment(segment) {
  const ms = mots(segment);
  if (ms.length > 0 && MOTS_COMPLEMENT.has(slugTexte(ms[0]))) return null;
  for (let n = Math.min(6, ms.length - 1); n >= 1; n--) {
    const debutNom = ms.length - n;
    const nom = ms.slice(debutNom).join(" ");
    const code = codePaysDuNom(nom);
    if (code === null) continue;
    if (motAutoriseAvantPays(ms[debutNom - 1], code) && !finitParUneCommune(ms, debutNom)) {
      return { code, nom, reste: ms.slice(0, debutNom).join(" ") };
    }
  }
  return null;
}
var COLLECTIVITES_COMMUNES = /* @__PURE__ */ new Set(["BL", "MF"]);
var _communesHomonymes = null;
function estCommuneHomonymePays(nom) {
  _communesHomonymes ??= new Set(COMMUNES_HOMONYMES_PAYS.map((n) => slugTexte(n)));
  return _communesHomonymes.has(slugTexte(nom));
}
function paysDuDefaut(paysParDefaut) {
  return paysEtTerritoireDepuisIso((paysParDefaut ?? "").trim().toUpperCase() || "FR").pays;
}
function paysNommeRetenu(code, nom, segmentEntier, { paysParDefaut, territoireParDefaut }) {
  if (COLLECTIVITES_COMMUNES.has(code)) {
    if (!segmentEntier) return null;
    const connu = (territoireParDefaut ?? paysEtTerritoireDepuisIso(paysParDefaut).territoire ?? "").trim().toUpperCase();
    return connu === code ? code : null;
  }
  if (paysDuDefaut(paysParDefaut) === "FR" && estCommuneHomonymePays(nom)) return null;
  return code;
}
function paysNommeEnFin(texte, options = {}) {
  const segs = segments(texte ?? "");
  if (segs.length === 0) return null;
  const dernier = segs[segs.length - 1];
  const entier = codePaysDuNom(dernier);
  if (entier !== null) return paysNommeRetenu(entier, dernier, true, options);
  const fin = paysEnFinDeSegment(dernier);
  return fin ? paysNommeRetenu(fin.code, fin.nom, false, options) : null;
}
var PREFIXES_NON_POSTAUX = /* @__PURE__ */ new Set(["BP", "CS", "TSA", "CEDEX"]);
function lettresAscii(mot) {
  const estLettre = (c) => c >= "A" && c <= "Z" || c >= "a" && c <= "z";
  const estAlnum = (c) => estLettre(c) || estChiffre(c);
  const cs = [...mot];
  let debut = 0;
  let fin = cs.length;
  while (debut < fin && !estAlnum(cs[debut])) debut++;
  while (fin > debut && !estAlnum(cs[fin - 1])) fin--;
  const coeur = cs.slice(debut, fin);
  if (coeur.length === 0 || !coeur.every(estLettre)) return "";
  return coeur.join("").toUpperCase();
}
function trouverCodePostal(t) {
  for (let i = 0; i + 5 <= t.length; i++) {
    if (!estChiffre(t[i])) continue;
    if (i > 0 && estChiffre(t[i - 1])) continue;
    let ok = true;
    for (let k = 1; k < 5; k++) if (!estChiffre(t[i + k])) ok = false;
    if (!ok) continue;
    if (i + 5 < t.length && estChiffre(t[i + 5])) continue;
    const avant = mots(t.slice(0, i));
    const precedent = lettresAscii(avant[avant.length - 1] ?? "");
    if (PREFIXES_NON_POSTAUX.has(precedent)) continue;
    return { code: t.slice(i, i + 5), debut: i, fin: i + 5, etranger: false, villeAvant: false };
  }
  return null;
}
var _formats = null;
function formatCodePostal(pays) {
  _formats ??= new Map(FORMATS_CODE_POSTAL.map((f2) => [f2.pays, f2.format]));
  return _formats.get((pays ?? "").trim().toUpperCase()) ?? null;
}
function codePostalSansPrefixe(pays, saisie) {
  const cp = replierBlancs(saisie).toUpperCase();
  const code = (pays ?? "").trim().toUpperCase();
  const m = /^([A-Z]{1,2}) ?- ?(.+)$/.exec(cp);
  if (!m || !code) return cp;
  const prefixe = m[1];
  return prefixe === code || prefixe.length === 1 && code.startsWith(prefixe) ? m[2] : cp;
}
function codePostalSaisiValide(pays, saisie) {
  const cp = codePostalSansPrefixe(pays, saisie);
  switch (formatCodePostal(pays)) {
    case "4":
      return /^[0-9]{4}$/.test(cp);
    case "3":
      return /^[0-9]{3}$/.test(cp);
    case "4L":
      return /^[0-9]{4} ?[A-Z]{2}$/.test(cp);
    case "4-3":
      return /^[0-9]{4}-[0-9]{3}$/.test(cp);
    case "2-3":
      return /^[0-9]{2}-[0-9]{3}$/.test(cp);
    case "3+2":
      return /^[0-9]{3} ?[0-9]{2}$/.test(cp);
    case "GB":
      return /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/.test(cp);
    case "IE":
      return /^(?:[A-Z][0-9]{2}|D6W) ?[A-Z0-9]{4}$/.test(cp);
    default:
      return /^[0-9]{5}$/.test(cp);
  }
}
var EXEMPLES_CODE_POSTAL_5_CHIFFRES = {
  DE: "10115",
  EE: "10111",
  ES: "28001",
  FI: "00100",
  HR: "10000",
  IT: "00184",
  LT: "01100",
  MC: "98000"
};
function exempleCodePostal(pays) {
  switch (formatCodePostal(pays)) {
    case "4":
      return "1000";
    case "3":
      return "101";
    case "4L":
      return "1012 AB";
    case "4-3":
      return "1100-148";
    case "2-3":
      return "00-950";
    case "3+2":
      return "114 55";
    case "GB":
      return "SW1A 2AA";
    case "IE":
      return "D02 X285";
    default:
      return EXEMPLES_CODE_POSTAL_5_CHIFFRES[(pays ?? "").trim().toUpperCase()] ?? "75001";
  }
}
function codePostalEnChiffres(pays) {
  const format = formatCodePostal(pays);
  return format === null || format === "4" || format === "3" || format === "3+2";
}
function lettreAscii(c) {
  return c !== void 0 && (c >= "A" && c <= "Z" || c >= "a" && c <= "z");
}
function majusculeAscii(c) {
  return c !== void 0 && c >= "A" && c <= "Z";
}
function chiffreAscii(c) {
  return c !== void 0 && c >= "0" && c <= "9";
}
function alnumAscii(c) {
  return lettreAscii(c) || chiffreAscii(c);
}
function nChiffres(t, i, n) {
  for (let k = 0; k < n; k++) if (!chiffreAscii(t[i + k])) return -1;
  return i + n;
}
function nLettres(t, i, n) {
  for (let k = 0; k < n; k++) if (!lettreAscii(t[i + k])) return -1;
  return i + n;
}
function finDeMot(t, i) {
  return i >= 0 && !alnumAscii(t[i]);
}
function lireFormat(t, i, format) {
  switch (format) {
    case "4": {
      let j = i;
      let n = 0;
      while (n < 2 && majusculeAscii(t[j])) {
        j++;
        n++;
      }
      const debutChiffres = n > 0 && t[j] === "-" ? j + 1 : i;
      const f2 = nChiffres(t, debutChiffres, 4);
      if (f2 < 0 || !finDeMot(t, f2)) return null;
      return { debut: i, fin: f2, code: t.slice(debutChiffres, f2), numerique: true };
    }
    case "3": {
      const f2 = nChiffres(t, i, 3);
      return f2 >= 0 && finDeMot(t, f2) ? { debut: i, fin: f2, code: t.slice(i, f2), numerique: true } : null;
    }
    case "4L": {
      const d = nChiffres(t, i, 4);
      if (d < 0) return null;
      const l = t[d] === " " ? d + 1 : d;
      const f2 = nLettres(t, l, 2);
      if (f2 >= 0 && finDeMot(t, f2)) {
        return { debut: i, fin: f2, code: `${t.slice(i, d)} ${t.slice(l, f2).toUpperCase()}`, numerique: false };
      }
      return finDeMot(t, d) ? { debut: i, fin: d, code: t.slice(i, d), numerique: true } : null;
    }
    case "4-3":
    case "2-3": {
      const n1 = format === "4-3" ? 4 : 2;
      const d = nChiffres(t, i, n1);
      if (d < 0 || t[d] !== "-") return null;
      const f2 = nChiffres(t, d + 1, 3);
      return f2 >= 0 && finDeMot(t, f2) ? { debut: i, fin: f2, code: t.slice(i, f2), numerique: false } : null;
    }
    case "3+2": {
      const d = nChiffres(t, i, 3);
      if (d < 0 || t[d] !== " ") return null;
      const f2 = nChiffres(t, d + 1, 2);
      return f2 >= 0 && finDeMot(t, f2) ? { debut: i, fin: f2, code: t.slice(i, f2), numerique: true } : null;
    }
    case "GB": {
      for (const nL of [2, 1]) {
        if (nLettres(t, i, nL) < 0 || !chiffreAscii(t[i + nL])) continue;
        const base = i + nL + 1;
        for (const opt of [1, 0]) {
          if (opt === 1 && !alnumAscii(t[base])) continue;
          const o = base + opt;
          for (const sp of [1, 0]) {
            if (sp === 1 && t[o] !== " ") continue;
            const s = o + sp;
            if (chiffreAscii(t[s]) && nLettres(t, s + 1, 2) >= 0 && finDeMot(t, s + 3)) {
              const code = `${t.slice(i, o)} ${t.slice(s, s + 3)}`.toUpperCase();
              return { debut: i, fin: s + 3, code, numerique: false };
            }
          }
        }
      }
      return null;
    }
    case "IE": {
      if (!lettreAscii(t[i]) || !chiffreAscii(t[i + 1])) return null;
      const c3 = t[i + 2];
      if (!chiffreAscii(c3) && c3 !== "W" && c3 !== "w") return null;
      const s = t[i + 3] === " " ? i + 4 : i + 3;
      for (let k = 0; k < 4; k++) if (!alnumAscii(t[s + k])) return null;
      if (!finDeMot(t, s + 4)) return null;
      return { debut: i, fin: s + 4, code: `${t.slice(i, i + 3)} ${t.slice(s, s + 4)}`.toUpperCase(), numerique: false };
    }
  }
}
function villeSuit(t, fin) {
  let j = fin;
  while (j < t.length && t[j] === " ") j++;
  return j < t.length && t[j] !== "," && !chiffreAscii(t[j]);
}
function seulDansSonSegment(t, debut, fin) {
  let a = debut - 1;
  while (a >= 0 && t[a] === " ") a--;
  let b = fin;
  while (b < t.length && t[b] === " ") b++;
  return (a < 0 || t[a] === ",") && (b >= t.length || t[b] === ",");
}
function trouverCodePostalEtranger(t, format) {
  let retenu = null;
  let i = 0;
  while (i < t.length && retenu === null) {
    const debutDeMot = i === 0 || !alnumAscii(t[i - 1]) && t[i - 1] !== "-";
    const m = debutDeMot ? lireFormat(t, i, format) : null;
    if (m === null) {
      i++;
      continue;
    }
    if (!m.numerique || villeSuit(t, m.fin) || seulDansSonSegment(t, m.debut, m.fin)) retenu = m;
    i = m.fin;
  }
  if (retenu === null) return null;
  const villeAvant = format === "GB" || format === "IE";
  return { code: retenu.code, debut: retenu.debut, fin: retenu.fin, etranger: true, villeAvant };
}
function sansCedex(segment) {
  const ms = mots(segment);
  const garde = [];
  for (let i = 0; i < ms.length; i++) {
    if (slugTexte(ms[i]) === "cedex") {
      if (i + 1 < ms.length && queChiffres(ms[i + 1]) && ms[i + 1].length <= 3) i++;
      continue;
    }
    garde.push(ms[i]);
  }
  return garde.join(" ");
}
function commenceParTypeVoie(segment) {
  const premier = mots(segment)[0];
  return premier !== void 0 && TYPES_VOIE_ENSEMBLE.has(slugTexte(premier));
}
function sansPaysFinal(segment, estCommune) {
  const p = paysEnFinDeSegment(segment);
  return p && p.reste && !estCommune(p.nom) ? p.reste : segment;
}
function mentionCedex(segment) {
  const ms = mots(segment);
  for (let i = 0; i < ms.length; i++) {
    if (slugTexte(ms[i]) !== "cedex") continue;
    const numero = i + 1 < ms.length && queChiffres(ms[i + 1]) && ms[i + 1].length <= 3 ? ms[i + 1] : null;
    return numero ? `CEDEX ${numero}` : "CEDEX";
  }
  return null;
}
var DECOUPAGE_VIDE = { codePostal: null, rue: null, ville: null, cedex: null, complement: null };
var _homonymes = null;
function segmentEstLePays(code, cpFrancais) {
  if (code === null || COLLECTIVITES_COMMUNES.has(code)) return false;
  if (code === "FR" || territoireParCode(code) !== null) return true;
  _homonymes ??= new Set(PAYS_HOMONYMES_VILLE);
  return !cpFrancais && !_homonymes.has(code);
}
function villeNette(ville) {
  return ville ? replierBlancs(tronquer(casseVille(ville), LONGUEUR_MAX_VILLE)) || null : null;
}
function complementDe(segs, cedexLu) {
  let cedex = cedexLu;
  const reste = [];
  for (const brut of segs) {
    const seg = replierBlancs(brut);
    cedex = cedex ?? mentionCedex(seg);
    const net = sansCedex(seg);
    if (net) reste.push(net);
  }
  while (reste.length > 0 && codePaysDuNom(reste[reste.length - 1]) !== null) reste.pop();
  const complement = reste.join(", ");
  return { cedex, complement: complement ? tronquer(complement, LONGUEUR_MAX_ADRESSE) : null };
}
function segmentVille(segment, estCommune) {
  return !contientChiffre(segment) && !commenceParTypeVoie(segment) && (codePaysDuNom(segment) === null || estCommune(segment));
}
function decouper(t, cp, cpFrancais, contexteFrancais) {
  if (!t) return DECOUPAGE_VIDE;
  const estCommune = (s) => COLLECTIVITES_COMMUNES.has(codePaysDuNom(s) ?? "") || contexteFrancais && estCommuneHomonymePays(s);
  if (cp && cp.villeAvant) {
    const avant = t.slice(0, cp.debut).split(",").map((s) => replierBlancs(s));
    const memeSegment = avant.pop() ?? "";
    const rueSegs = avant.filter((s) => s.length > 0);
    let ville = null;
    let rue = null;
    if (memeSegment) {
      if (rueSegs.length > 0) ville = sansPaysFinal(memeSegment, estCommune);
      else rue = memeSegment;
    } else if (rueSegs.length > 0 && segmentVille(rueSegs[rueSegs.length - 1], estCommune)) {
      ville = rueSegs.pop();
    }
    rue ??= rueSegs.join(", ") || null;
    const { cedex, complement } = complementDe(t.slice(cp.fin).split(","), null);
    return { codePostal: cp.code, rue: rue ? nettoyerBord(rue) || null : null, ville: villeNette(ville), cedex, complement };
  }
  if (cp) {
    const rue = nettoyerBord(t.slice(0, cp.debut));
    const brut = t.slice(cp.debut, cp.fin);
    const suite = t.slice(cp.fin).split(",");
    let ville = null;
    let cedex = null;
    let k = 0;
    for (; k < suite.length; k++) {
      let seg = replierBlancs(suite[k]);
      if (seg.length >= 5 && queChiffres(seg.slice(0, 5)) && (seg.length === 5 || seg[5] === " ")) {
        seg = replierBlancs(seg.slice(5));
      } else if (seg === brut || seg.startsWith(`${brut} `)) {
        seg = replierBlancs(seg.slice(brut.length));
      }
      cedex = cedex ?? mentionCedex(seg);
      seg = sansCedex(seg);
      if (!seg) continue;
      if (segmentEstLePays(codePaysDuNom(seg), cpFrancais)) {
        k = suite.length;
        break;
      }
      seg = sansPaysFinal(seg, estCommune);
      ville = seg || null;
      k++;
      break;
    }
    const details = complementDe(suite.slice(k), cedex);
    return { codePostal: cp.code, rue: rue || null, ville: villeNette(ville), ...details };
  }
  const segs = segments(t).map(sansCedex).filter((s) => s.length > 0);
  while (segs.length > 0 && codePaysDuNom(segs[segs.length - 1]) !== null && !estCommune(segs[segs.length - 1])) {
    segs.pop();
  }
  if (segs.length === 0) return DECOUPAGE_VIDE;
  const dernier = sansPaysFinal(segs[segs.length - 1], estCommune);
  const estVille = segmentVille(dernier, estCommune);
  if (segs.length === 1) {
    return estVille ? { ...DECOUPAGE_VIDE, ville: villeNette(dernier) } : { ...DECOUPAGE_VIDE, rue: segs[0] };
  }
  if (estVille) {
    return { ...DECOUPAGE_VIDE, rue: segs.slice(0, -1).join(", "), ville: villeNette(dernier) };
  }
  return { ...DECOUPAGE_VIDE, rue: segs.join(", ") };
}
function nettoyerBord(texte) {
  let t = replierBlancs(texte);
  while (t.length > 0 && (t.endsWith(",") || t.endsWith(";"))) t = replierBlancs(t.slice(0, -1));
  while (t.length > 0 && (t.startsWith(",") || t.startsWith(";"))) t = replierBlancs(t.slice(1));
  return t;
}
var _cinqChiffres = null;
function codePostalEtrangerA5Chiffres(pays) {
  _cinqChiffres ??= new Set(PAYS_CODE_POSTAL_5_CHIFFRES);
  return _cinqChiffres.has(pays);
}
function lire(texte, options = {}) {
  const t = replierBlancs(lignesEnSegments(texte));
  const nomme = paysNommeEnFin(texte, options);
  const paysNomme = nomme ? paysEtTerritoireDepuisIso(nomme) : null;
  const etranger = paysNomme !== null && paysNomme.pays !== "FR";
  const defaut = paysDuDefaut(options.paysParDefaut);
  const cinqChiffresDuDefaut = paysNomme === null && codePostalEtrangerA5Chiffres(defaut);
  const formatNomme = etranger ? formatCodePostal(paysNomme.pays) : null;
  const formatDefaut = paysNomme === null && defaut !== "FR" ? formatCodePostal(defaut) : null;
  let cp = formatNomme ? trouverCodePostalEtranger(t, formatNomme) : null;
  cp ??= trouverCodePostal(t);
  if (!cp && formatDefaut) cp = trouverCodePostalEtranger(t, formatDefaut);
  const cpFrancais = cp !== null && !cp.etranger && !etranger && !cinqChiffresDuDefaut && departementDepuisCodes({ pays: "FR", codePostal: cp.code }) !== null;
  const contexteFrancais = (paysNomme?.pays ?? defaut) === "FR";
  return { decoupage: decouper(t, cp, cpFrancais, contexteFrancais), nomme, paysNomme, defaut, cpFrancais };
}
function villeDepuisTexte(texte, options = {}) {
  return lire(texte, options).decoupage.ville;
}
function detailsPostaux(texte, options = {}) {
  const d = lire(texte, options).decoupage;
  return { cedex: d.cedex, complement: d.complement };
}
function analyserAdresseLibre(texte, options = {}) {
  const libelle = tronquer(replierBlancs(texte), LONGUEUR_MAX_ADRESSE);
  const { decoupage: d, nomme, paysNomme, defaut, cpFrancais } = lire(texte, options);
  const pays = cpFrancais ? "FR" : paysNomme ? paysNomme.pays : defaut;
  const codes = { pays, codePostal: d.codePostal, codeInsee: null, indice: paysNomme?.territoire ?? null };
  return {
    libelle,
    rue: d.rue ? tronquer(d.rue, LONGUEUR_MAX_ADRESSE) : null,
    codePostal: d.codePostal,
    ville: d.ville,
    codeInsee: null,
    pays,
    territoire: territoireDepuisCodes(codes),
    departement: departementDepuisCodes(codes),
    latitude: null,
    longitude: null,
    precision: null,
    source: "analyse",
    placeId: null,
    idBan: null,
    idOsm: null,
    paysExplicite: nomme !== null
  };
}
function codePostalFrancaisDuTexte(texte, { paysParDefaut = "FR" } = {}) {
  const a = analyserAdresseLibre(texte, { paysParDefaut });
  if (a.pays !== "FR" || a.codePostal === null) return null;
  return departementDepuisCodes({ pays: "FR", codePostal: a.codePostal }) !== null ? a.codePostal : null;
}
function zoneDuTerritoire(t, explicite) {
  return { fournisseur: t.fournisseur, pays: "FR", territoire: t.code, explicite };
}
function territoireDeLIndice(pays, territoire) {
  return territoireParCode(territoire) ?? territoireParCode(paysEtTerritoireDepuisIso(pays).territoire);
}
function zoneDeRecherche({
  pays,
  territoire,
  texte
}) {
  const nomme = paysNommeEnFin(texte, { paysParDefaut: pays, territoireParDefaut: territoire });
  const paysNomme = nomme !== null ? paysEtTerritoireDepuisIso(nomme) : null;
  if (paysNomme !== null && paysNomme.pays !== "FR") {
    return { fournisseur: "osm", pays: paysNomme.pays, territoire: null, explicite: true };
  }
  const cp = codePostalFrancaisDuTexte(texte, { paysParDefaut: pays });
  if (cp !== null) {
    const t = territoireParCode(territoireDepuisCodes({ pays: "FR", codePostal: cp }));
    return t ? zoneDuTerritoire(t, false) : { fournisseur: "ban", pays: "FR", territoire: null, explicite: false };
  }
  const territoireNomme = territoireParCode(paysNomme?.territoire);
  if (territoireNomme) return zoneDuTerritoire(territoireNomme, true);
  const tIndice = territoireDeLIndice(pays, territoire);
  if (paysNomme !== null) {
    return tIndice ? zoneDuTerritoire(tIndice, false) : { fournisseur: "ban", pays: "FR", territoire: null, explicite: false };
  }
  if (tIndice) return zoneDuTerritoire(tIndice, false);
  const indice = (pays ?? "").trim().toUpperCase();
  if (indice && indice !== "FR") return { fournisseur: "osm", pays: indice, territoire: null, explicite: false };
  return { fournisseur: "ban", pays: "FR", territoire: null, explicite: false };
}
function fournisseurAdresse(entree) {
  return zoneDeRecherche(entree).fournisseur;
}
var MAJUSCULES = "ABCDEFGHIJKLMNOPQRSTUVWXYZ\xC0\xC2\xC4\xC7\xC9\xC8\xCA\xCB\xCE\xCF\xD4\xD6\xD9\xDB\xDC\u0178\u0152\xC6";
var MINUSCULES = "abcdefghijklmnopqrstuvwxyz\xE0\xE2\xE4\xE7\xE9\xE8\xEA\xEB\xEE\xEF\xF4\xF6\xF9\xFB\xFC\xFF\u0153\xE6";
var VERS_MINUSCULE = new Map([...MAJUSCULES].map((c, i) => [c, [...MINUSCULES][i]]));
var EST_MINUSCULE = /* @__PURE__ */ new Set([...MINUSCULES]);
var SEPARATEURS_CASSE = /* @__PURE__ */ new Set([" ", "-", "'", "\u2019", "(", ")", "/", ".", ","]);
var PARTICULES = /* @__PURE__ */ new Set(["de", "du", "des", "la", "le", "les", "sur", "sous", "en", "et", "aux", "l\xE8s"]);
function casseVille(ville) {
  const t = ville ?? "";
  const caracteres = [...t];
  let sortie = "";
  let indexMot = 0;
  let i = 0;
  while (i < caracteres.length) {
    const c = caracteres[i];
    if (SEPARATEURS_CASSE.has(c)) {
      sortie += c;
      i++;
      continue;
    }
    let j = i;
    while (j < caracteres.length && !SEPARATEURS_CASSE.has(caracteres[j])) j++;
    const mot = caracteres.slice(i, j);
    const elide = j < caracteres.length && (caracteres[j] === "'" || caracteres[j] === "\u2019");
    sortie += casserMot(mot, indexMot, elide);
    indexMot++;
    i = j;
  }
  return sortie;
}
function casserMot(mot, index, elide) {
  if (mot.some((c) => EST_MINUSCULE.has(c))) return mot.join("");
  const bas = mot.map((c) => VERS_MINUSCULE.get(c) ?? c).join("");
  if (index > 0 && (PARTICULES.has(bas) || elide && (bas === "d" || bas === "l"))) return bas;
  return mot[0] + [...bas].slice(1).join("");
}
function nomTerritoireOuPays(adresse) {
  if (!adresse) return null;
  const t = territoireParCode(adresse.territoire);
  if (t) return t.nom;
  const pays = (adresse.pays ?? "FR").trim().toUpperCase() || "FR";
  if (pays === "FR") return null;
  return NOMS_PAYS_FR[pays] ?? pays;
}
function libelleLieuCourt(adresse) {
  if (!adresse) return null;
  const ville = replierBlancs(adresse.ville);
  const lieu = nomTerritoireOuPays(adresse);
  if (!ville) return lieu;
  if (!lieu || slugTexte(lieu) === slugTexte(ville)) return ville;
  return `${ville}, ${lieu}`;
}
function ligneCodePostalVille(pays, codePostal, ville) {
  const cp = replierBlancs(codePostal);
  const v = replierBlancs(ville);
  const format = formatCodePostal(pays);
  const ordre = format === "GB" || format === "IE" ? [v, cp] : [cp, v];
  return ordre.filter(Boolean).join(" ");
}
function lignesAdresse(adresse) {
  if (!adresse) return [];
  const cpVille = ligneCodePostalVille(adresse.pays, adresse.codePostal, adresse.ville);
  const cedex = replierBlancs(adresse.cedex);
  const candidates = [
    replierBlancs(adresse.rue),
    cpVille && cedex ? `${cpVille} ${cedex}` : cpVille,
    replierBlancs(adresse.complement),
    nomTerritoireOuPays(adresse) ?? ""
  ];
  const villeSlug = slugTexte(adresse.ville);
  const lignes = [];
  const vus = /* @__PURE__ */ new Set();
  candidates.forEach((ligne, i) => {
    const s = slugTexte(ligne);
    if (!s || vus.has(s)) return;
    if (i >= 2 && s === villeSlug) return;
    vus.add(s);
    lignes.push(ligne);
  });
  return lignes;
}
function formaterLieu({
  nom,
  adresse,
  ville,
  structure
}) {
  const adresseNette = replierBlancs(adresse);
  const slugAdresse = slugTexte(adresseNette);
  const elements = [
    { texte: replierBlancs(nom), estAdresse: false },
    { texte: adresseNette, estAdresse: true },
    { texte: replierBlancs(ville), estAdresse: false },
    { texte: nomTerritoireOuPays(structure) ?? "", estAdresse: false }
  ];
  const retenus = [];
  const slugs = [];
  for (const { texte, estAdresse } of elements) {
    const s = slugTexte(texte);
    if (!s || slugs.includes(s)) continue;
    if (!estAdresse && slugContient(slugAdresse, s)) continue;
    retenus.push(texte);
    slugs.push(s);
  }
  return retenus.join(", ");
}
function texteLieu(adresse, ville, options = {}) {
  const a = replierBlancs(adresse);
  const v = replierBlancs(ville);
  if (!a) return v;
  if (!slugTexte(v)) return a;
  const { decoupage: d, nomme } = lire(a, options);
  if (d.ville && !villesContradictoires(d.ville, v)) return a;
  if (d.codePostal && d.ville) return a;
  if (!d.codePostal && d.ville && d.rue && nomme !== null) return a;
  return `${a}, ${v}`;
}
function villesContradictoires(a, b) {
  if (memeVilleSaisie(a, b)) return false;
  const sa = slugTexte(a);
  const sb = slugTexte(b);
  return !slugContient(sa, sb) && !slugContient(sb, sa);
}
function memeVilleSaisie(a, b) {
  const sansNombres = (t) => slugTexte(t).split("-").filter((m) => m.length > 0 && !queChiffres(m)).join("-");
  const sa = sansNombres(a);
  return sa.length > 0 && sa === sansNombres(b);
}
function villeLieuAEcrire({
  adresse,
  villeSaisie,
  villeTrouvee,
  paysParDefaut
}) {
  const trouvee = replierBlancs(villeTrouvee);
  if (!trouvee) return null;
  const saisie = replierBlancs(villeSaisie);
  if (!saisie || memeVilleSaisie(saisie, trouvee)) return trouvee;
  if (!villesContradictoires(saisie, trouvee)) return null;
  if (lire(adresse, { paysParDefaut }).decoupage.codePostal) return trouvee;
  const sa = slugTexte(adresse);
  return slugContient(sa, slugTexte(trouvee)) && !slugContient(sa, slugTexte(saisie)) ? trouvee : null;
}
function texteItineraireLieu(adresse, ville, repli) {
  return texteLieu(adresse, ville) || replierBlancs(repli);
}
function rueDepuisSuggestion(suggestion, retenue) {
  if (suggestion?.fournisseur === "google") {
    const details = retenue?.adresse ?? null;
    if (!details) return "";
    const rue = replierBlancs(details.rue);
    if (rue) return rue;
    const libelle = replierBlancs(retenue?.libelle) || replierBlancs(details.libelle);
    const lue = analyserAdresseLibre(libelle, { paysParDefaut: details.pays, territoireParDefaut: details.territoire });
    return replierBlancs(lue.rue);
  }
  const adresse = retenue?.adresse ?? suggestion?.adresse ?? null;
  return replierBlancs(adresse?.rue) || replierBlancs(suggestion?.principal) || replierBlancs(retenue?.libelle) || replierBlancs(suggestion?.libelle);
}
function requeteRecherche(q) {
  const t = replierBlancs(q);
  if (t.length < RECHERCHE_MIN_CARACTERES || t.length > RECHERCHE_MAX_CARACTERES) return null;
  return t;
}
function tronquerAuMot(texte, max) {
  if (texte.length <= max) return texte;
  const coupe = texte.slice(0, max);
  const espace = coupe.lastIndexOf(" ");
  return replierBlancs(espace > max / 2 ? coupe.slice(0, espace) : coupe);
}
function texteRechercheBan(texte) {
  const net = replierBlancs(texte);
  if (net.length <= RECHERCHE_MAX_CARACTERES) return net;
  const a = analyserAdresseLibre(texte, { paysParDefaut: "FR" });
  if (a.codePostal) {
    const segmentRue = (a.rue ?? "").split(",").map((x) => replierBlancs(x)).filter(Boolean).pop() ?? "";
    const cpVille = replierBlancs(`${a.codePostal} ${a.ville ?? ""}`);
    const reste = RECHERCHE_MAX_CARACTERES - cpVille.length - 1;
    const rue = reste > 0 ? tronquerAuMot(segmentRue, reste) : "";
    return tronquerAuMot(replierBlancs(`${rue} ${cpVille}`), RECHERCHE_MAX_CARACTERES);
  }
  return tronquerAuMot(net, RECHERCHE_MAX_CARACTERES);
}
function aDesMorceauxAdresse(m) {
  return replierBlancs(m?.address_country).length > 0;
}
function morceauxConcordants(texte, morceaux) {
  if (!morceaux || !aDesMorceauxAdresse(morceaux)) return null;
  const lu = morceaux.full_address;
  if (lu === void 0 || lu === null) return morceaux;
  return replierBlancs(lu) === replierBlancs(texte) ? morceaux : null;
}
function adresseDeclareeOrganisation(texte, morceaux) {
  const declare = replierBlancs(texte);
  if (!declare) return null;
  const m = morceauxConcordants(declare, morceaux);
  const paysAnalyse = m ? replierBlancs(m.address_country).toUpperCase() : morceaux?.country ?? "FR";
  const a = analyserAdresseLibre(texte, { paysParDefaut: paysAnalyse });
  const details = detailsPostaux(texte, { paysParDefaut: paysAnalyse });
  let adresse;
  if (m) {
    const rueEnregistree = replierBlancs(m.address_street);
    const rue = rueEnregistree && slugContient(slugTexte(declare), slugTexte(rueEnregistree)) ? rueEnregistree : a.rue;
    const codePostal = replierBlancs(m.address_postcode) || a.codePostal;
    const ville = replierBlancs(m.address_city) || a.ville;
    const codeInsee = replierBlancs(m.address_citycode) || null;
    const pays = paysAnalyse;
    const codes = { pays, codePostal, codeInsee };
    adresse = {
      rue,
      codePostal,
      ville,
      codeInsee,
      pays,
      territoire: territoireParCode(replierBlancs(m.address_territory).toUpperCase())?.code ?? territoireDepuisCodes(codes),
      departement: replierBlancs(m.address_department) || departementDepuisCodes(codes)
    };
  } else {
    adresse = {
      rue: a.rue,
      codePostal: a.codePostal,
      ville: a.ville,
      codeInsee: null,
      pays: a.pays,
      territoire: a.territoire,
      departement: a.departement
    };
  }
  const lignes = lignesAdresse({ ...adresse, ...details });
  return { ...adresse, ...details, lignes: lignes.length > 0 ? lignes : [declare], depuisMorceaux: m !== null };
}
function ligneAdresseDeclaree(texte) {
  return adresseDeclareeOrganisation(texte, null)?.lignes.join(", ") ?? "";
}
function villeCourteDeclaree(adresse, ville) {
  const declaree = adresseDeclareeOrganisation(adresse, null);
  return libelleLieuCourt({
    ville: declaree?.ville ?? ville ?? null,
    pays: declaree?.pays ?? "FR",
    territoire: declaree?.territoire ?? null
  }) ?? (ville ?? "").trim();
}
function codePostalEtVilleDuRepertoire(adresse, ville) {
  const texte = [adresse, ville].map((s) => (s ?? "").trim()).filter(Boolean).join(", ");
  const declaree = adresseDeclareeOrganisation(texte, null);
  return { codePostal: declaree?.codePostal ?? null, ville: declaree?.ville ?? null };
}
function villeAnnuaireOrganisation(org) {
  if (!org) return null;
  if (aDesMorceauxAdresse(org)) {
    const ville = replierBlancs(org.address_city);
    if (!ville) return null;
    const pays = replierBlancs(org.address_country).toUpperCase() || "FR";
    const departement = replierBlancs(org.address_department) || null;
    const cle2 = replierBlancs(org.address_city_slug) || slugVille({ pays, ville, departement });
    if (!cle2) return null;
    return { cle: cle2, ville, departement, pays, territoire: replierBlancs(org.address_territory).toUpperCase() || null };
  }
  const a = analyserAdresseLibre(org.full_address, { paysParDefaut: org.country });
  if (!a.ville) return null;
  const cle = slugVille({ pays: a.pays, ville: a.ville, departement: a.departement });
  return cle ? { cle, ville: a.ville, departement: a.departement, pays: a.pays, territoire: a.territoire } : null;
}
function departementAnnuaireOrganisation(org) {
  if (!org) return null;
  if (aDesMorceauxAdresse(org)) return replierBlancs(org.address_department) || null;
  return analyserAdresseLibre(org.full_address, { paysParDefaut: org.country }).departement;
}
function formaterCoordonnee(valeur) {
  let s = valeur.toFixed(COORDONNEES_DECIMALES);
  if (s.includes(".")) {
    while (s.endsWith("0")) s = s.slice(0, -1);
    if (s.endsWith(".")) s = s.slice(0, -1);
  }
  return s === "-0" ? "0" : s;
}
var NON_RESERVES = /^[A-Za-z0-9\-_.~]$/;
var UTF8 = new TextEncoder();
function encoderComposantUrl(texte) {
  let sortie = "";
  for (const c of texte) {
    if (NON_RESERVES.test(c)) {
      sortie += c;
      continue;
    }
    for (const octet of UTF8.encode(c)) {
      sortie += "%" + octet.toString(16).toUpperCase().padStart(2, "0");
    }
  }
  return sortie;
}
function lienItineraire(adresse, { plateforme }) {
  if (!adresse) return null;
  const { latitude, longitude } = adresse;
  const coords = typeof latitude === "number" && typeof longitude === "number" && Number.isFinite(latitude) && Number.isFinite(longitude) ? `${formaterCoordonnee(latitude)},${formaterCoordonnee(longitude)}` : null;
  const texte = replierBlancs(adresse.libelle);
  if (!coords && !texte) return null;
  const valeur = encoderComposantUrl(coords ?? texte);
  switch (plateforme) {
    case "google":
      return `https://www.google.com/maps/dir/?api=1&destination=${valeur}`;
    case "apple":
      return `https://maps.apple.com/?daddr=${valeur}`;
    case "waze":
      return coords ? `https://waze.com/ul?ll=${valeur}&navigate=yes` : `https://waze.com/ul?q=${valeur}&navigate=yes`;
  }
}
var ZOOM_RECHERCHE_MAPS = 17;
function zoomCarteDepuisPrecision(precision) {
  if (precision === "ville") return 12;
  if (precision === "pays") return 5;
  return 16;
}
function lienRechercheMaps({
  nom,
  latitude,
  longitude,
  ville
}) {
  const nomNet = replierBlancs(nom);
  if (typeof latitude === "number" && typeof longitude === "number" && Number.isFinite(latitude) && Number.isFinite(longitude)) {
    const point = `${formaterCoordonnee(latitude)},${formaterCoordonnee(longitude)}`;
    return `https://www.google.com/maps/search/${encoderComposantUrl(nomNet || point)}/@${point},${ZOOM_RECHERCHE_MAPS}z`;
  }
  const texte = replierBlancs([nomNet, replierBlancs(ville)].filter(Boolean).join(" "));
  return `https://www.google.com/maps/search/${encoderComposantUrl(texte)}`;
}
function texteOuNull(v) {
  if (typeof v !== "string") return null;
  const t = replierBlancs(v);
  return t ? t : null;
}
function nombreFini(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function adresseDepuisFeatureBan(feature) {
  const p = feature?.properties;
  const libelle = texteOuNull(p?.label);
  if (!p || !libelle) return null;
  const type = p.type ?? null;
  const codePostal = texteOuNull(p.postcode);
  const codeInsee = texteOuNull(p.citycode);
  const codes = { pays: "FR", codePostal, codeInsee };
  const coords = Array.isArray(feature?.geometry?.coordinates) ? feature.geometry.coordinates : [];
  const longitude = nombreFini(coords[0]);
  const latitude = nombreFini(coords[1]);
  const aPoint = latitude !== null && longitude !== null;
  return {
    libelle: tronquer(libelle, LONGUEUR_MAX_ADRESSE),
    rue: type === "housenumber" || type === "street" ? texteOuNull(p.name) : null,
    codePostal,
    ville: texteOuNull(p.city),
    codeInsee,
    pays: "FR",
    territoire: territoireDepuisCodes(codes),
    departement: departementDepuisCodes(codes),
    latitude: aPoint ? latitude : null,
    longitude: aPoint ? longitude : null,
    precision: type === "housenumber" ? "adresse" : type === "street" ? "rue" : "ville",
    source: "ban",
    placeId: null,
    idBan: texteOuNull(p.id),
    idOsm: null
  };
}
function territoireConnu(morceaux) {
  if (!morceaux) return null;
  if (morceaux.address_territory) return { territoire: morceaux.address_territory };
  return morceaux.address_department ? { territoire: null } : null;
}
function communeDeFeatureBan(f2) {
  const p = f2.properties ?? {};
  const codes = { pays: "FR", codePostal: texteOuNull(p.postcode), codeInsee: texteOuNull(p.citycode) };
  return {
    territoire: territoireDepuisCodes(codes),
    cle: `${departementDepuisCodes(codes) ?? "?"}|${slugTexte(p.city)}`
  };
}
function resultatBanRetenu(features, {
  texte,
  codePostal,
  territoireAttendu,
  territoirePrefere = null,
  villeLieu = null
}) {
  if (!Array.isArray(features)) return null;
  const slugAnalyse = slugTexte(texte);
  const slugVilleLieu = slugTexte(villeLieu);
  const candidats = features.filter((f2) => {
    const p = f2?.properties;
    if (!p || typeof p.score !== "number" || p.score < SCORE_BAN_GEOCODAGE) return false;
    if (codePostal) return p.postcode === codePostal;
    const slugVilleBan = slugTexte(p.city);
    if (!slugVilleBan) return false;
    if (territoireAttendu && communeDeFeatureBan(f2).territoire !== territoireAttendu.territoire) return false;
    if (slugVilleLieu && slugVilleBan === slugVilleLieu) return true;
    return slugContient(slugAnalyse, slugVilleBan);
  });
  if (candidats.length === 0) return null;
  if (codePostal) return candidats[0];
  const uneSeuleCommune = (liste) => new Set(liste.map((f2) => communeDeFeatureBan(f2).cle)).size === 1;
  const dansLePrefere = (f2) => communeDeFeatureBan(f2).territoire === territoirePrefere?.territoire;
  if (uneSeuleCommune(candidats)) {
    const premier = candidats[0];
    if (!territoirePrefere || dansLePrefere(premier)) return premier;
    return estCommuneHomonymeTerritoires(premier.properties?.city) ? null : premier;
  }
  if (!territoirePrefere) return null;
  const preferes = candidats.filter(dansLePrefere);
  return preferes.length > 0 && uneSeuleCommune(preferes) ? preferes[0] : null;
}
var _homonymesTerritoires = null;
function estCommuneHomonymeTerritoires(ville) {
  _homonymesTerritoires ??= new Set(COMMUNES_HOMONYMES_TERRITOIRES.map((n) => slugTexte(n)));
  return typeof ville === "string" && _homonymesTerritoires.has(slugTexte(ville));
}
function suggestionsDepuisReponseBan(json, { scoreMin = SCORE_BAN_SUGGESTION, limite = SUGGESTIONS_LIMITE_MAX } = {}) {
  const features = json?.features;
  if (!Array.isArray(features)) return [];
  const sortie = [];
  const vus = /* @__PURE__ */ new Set();
  for (const f2 of features) {
    const score = nombreFini(f2?.properties?.score) ?? 0;
    if (score < scoreMin) continue;
    const adresse = adresseDepuisFeatureBan(f2);
    if (!adresse) continue;
    const cle = slugTexte(adresse.libelle);
    if (vus.has(cle)) continue;
    vus.add(cle);
    const cpVille = [adresse.codePostal, adresse.ville].filter(Boolean).join(" ");
    const lieu = nomTerritoireOuPays(adresse);
    sortie.push({
      id: adresse.idBan ?? adresse.libelle,
      libelle: adresse.libelle,
      principal: texteOuNull(f2.properties?.name) ?? adresse.libelle,
      secondaire: lieu && cpVille ? `${cpVille}, ${lieu}` : cpVille || lieu || "",
      fournisseur: "ban",
      adresse
    });
    if (sortie.length >= limite) break;
  }
  return sortie;
}
var COUCHES_PHOTON = ["house", "street", "city"];
var COUCHES_PHOTON_VERIFICATION = ["house", "street"];
var RE_ID_OSM = /^[NWR][1-9][0-9]{0,19}$/;
function estIdOsm(id) {
  return typeof id === "string" && RE_ID_OSM.test(id);
}
var PAYS_NUMERO_AVANT_VOIE = [
  "FR",
  "MC",
  "LU",
  "GB",
  "IE",
  "MT",
  "US",
  "CA",
  "AU",
  "NZ",
  "ZA",
  "IN",
  "SG",
  "MY",
  "PH",
  "HK",
  "NG",
  "GH",
  "KE",
  "MA",
  "DZ",
  "TN",
  "SN",
  "CI",
  "CM",
  "ML",
  "BF",
  "NE",
  "TD",
  "GN",
  "BJ",
  "TG",
  "GA",
  "CG",
  "CD",
  "CF",
  "MG",
  "MU",
  "KM",
  "SC",
  "DJ",
  "HT",
  "RW",
  "BI"
];
var _numeroAvant = null;
function rueNumeroEtVoie(pays, numero, voie) {
  const v = replierBlancs(voie);
  if (!v) return null;
  const n = replierBlancs(numero);
  if (!n) return v;
  _numeroAvant ??= new Set(PAYS_NUMERO_AVANT_VOIE);
  const code = (pays ?? "FR").trim().toUpperCase() || "FR";
  return _numeroAvant.has(code) ? `${n} ${v}` : `${v} ${n}`;
}
function idOsmDeFeature(feature) {
  const p = feature?.properties;
  const type = typeof p?.osm_type === "string" ? p.osm_type : "";
  const brut = p?.osm_id;
  let numero = "";
  if (typeof brut === "number") numero = Number.isSafeInteger(brut) && brut > 0 ? String(brut) : "";
  else if (typeof brut === "string") numero = brut;
  const id = `${type}${numero}`;
  return estIdOsm(id) ? id : null;
}
function codePostalPhoton(v) {
  const cp = texteOuNull(v);
  if (!cp || cp.length > 16 || cp.includes(";") || cp.includes(",")) return null;
  return cp;
}
function numeroPhoton(v) {
  const n = texteOuNull(v);
  if (!n || n.includes(";") || n.includes(",")) return null;
  return n;
}
function territoireParSlug(nom) {
  const s = typeof nom === "string" ? slugTexte(nom) : "";
  return s ? TERRITOIRES_OUTRE_MER.find((t) => t.slug === s)?.code ?? null : null;
}
function pointGeoJson(coordinates) {
  const c = Array.isArray(coordinates) ? coordinates : [];
  const longitude = nombreFini(c[0]);
  const latitude = nombreFini(c[1]);
  if (latitude === null || longitude === null) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  return { latitude, longitude };
}
function libelleOsmConstruit(a) {
  const zone = nomTerritoireOuPays({ pays: a.pays, territoire: a.territoire });
  const zoneAffichee = zone && slugTexte(zone) !== slugTexte(a.ville) ? zone : null;
  const libelle = [a.rue, ligneCodePostalVille(a.pays, a.codePostal, a.ville), zoneAffichee].filter(Boolean).join(", ");
  return tronquer(libelle, LONGUEUR_MAX_ADRESSE);
}
function adresseDepuisFeaturePhoton(feature) {
  const p = feature?.properties;
  if (!p) return null;
  const couche = p.type;
  if (couche !== "house" && couche !== "street" && couche !== "city") return null;
  const idOsm = idOsmDeFeature(feature);
  if (!idOsm) return null;
  const cc = texteOuNull(p.countrycode);
  if (!cc || cc.length !== 2 || !lettreAscii(cc[0]) || !lettreAscii(cc[1])) return null;
  const iso = paysEtTerritoireDepuisIso(cc);
  const pays = iso.pays;
  const codePostal = codePostalPhoton(p.postcode);
  const codes = { pays, codePostal, codeInsee: null, indice: iso.territoire ?? territoireParSlug(p.state) };
  const territoire = pays === "FR" ? iso.territoire ?? territoireDepuisCodes(codes) : null;
  const nom = texteOuNull(p.name);
  let rue = null;
  let villeBrute;
  if (couche === "house") {
    const numero = numeroPhoton(p.housenumber);
    const voie = texteOuNull(p.street);
    if (!numero || !voie) return null;
    rue = rueNumeroEtVoie(pays, numero, voie);
    villeBrute = texteOuNull(p.city);
  } else if (couche === "street") {
    if (!nom) return null;
    rue = nom;
    villeBrute = texteOuNull(p.city);
  } else {
    if (!nom) return null;
    villeBrute = nom;
  }
  const ville = villeBrute ? tronquer(villeBrute, LONGUEUR_MAX_VILLE) : null;
  if (!ville && !codePostal) return null;
  const point = pointGeoJson(feature?.geometry?.coordinates);
  return {
    libelle: libelleOsmConstruit({ rue, pays, territoire, codePostal, ville }),
    rue: rue ? tronquer(rue, LONGUEUR_MAX_ADRESSE) : null,
    codePostal,
    ville,
    codeInsee: null,
    pays,
    territoire,
    departement: pays === "FR" ? departementDepuisCodes({ ...codes, indice: territoire }) : null,
    latitude: point?.latitude ?? null,
    longitude: point?.longitude ?? null,
    precision: couche === "house" ? "adresse" : couche === "street" ? "rue" : "ville",
    source: "osm",
    placeId: null,
    idBan: null,
    idOsm
  };
}
function zonePhotonDepuisZone(zone) {
  const t = territoireParCode(zone.territoire);
  if (t && t.couvertureBan === "partielle") return { territoire: t.code };
  if (zone.fournisseur === "ban") return { horsBan: true };
  if (zone.territoire) return { territoire: zone.territoire };
  return { pays: zone.pays };
}
function fournisseurComplementaire(zone) {
  const t = territoireParCode(zone.territoire);
  if (!t || t.couvertureBan !== "partielle") return null;
  return zone.fournisseur === "ban" ? "osm" : "ban";
}
function serviParLaBan(a) {
  if (a.pays !== "FR") return false;
  return a.territoire === null || territoireParCode(a.territoire)?.couvertureBan === "complete";
}
function adresseDansZonePhoton(a, zone) {
  if (serviParLaBan(a)) return false;
  if ("territoire" in zone) return a.territoire === zone.territoire;
  if ("pays" in zone) return a.pays === zone.pays.trim().toUpperCase() && a.territoire === null;
  return true;
}
function nombrePhotonPourSuggestions(limite) {
  const l = Number.isInteger(limite) && limite > 0 ? Math.min(limite, SUGGESTIONS_LIMITE_MAX) : SUGGESTIONS_LIMITE_MAX;
  return 2 * l;
}
function parametresRecherchePhoton({
  q,
  zone,
  nombre,
  couches = COUCHES_PHOTON
}) {
  const params = [
    ["q", q],
    ["lang", "fr"],
    ["limit", String(nombre)]
  ];
  for (const c of couches) params.push(["layer", c]);
  if ("territoire" in zone) {
    const t = territoireParCode(zone.territoire);
    params.push(["countrycode", "FR"]);
    if (t) {
      params.push(["lat", formaterCoordonnee(t.centre.latitude)], ["lon", formaterCoordonnee(t.centre.longitude)], ["zoom", "9"]);
    }
  } else if ("pays" in zone) {
    params.push(["countrycode", zone.pays.trim().toUpperCase()]);
  }
  return params;
}
function suggestionsDepuisReponsePhoton(json, { zone, limite = SUGGESTIONS_LIMITE_MAX }) {
  const features = json?.features;
  if (!Array.isArray(features)) return [];
  const sortie = [];
  const libelles = /* @__PURE__ */ new Set();
  const ids = /* @__PURE__ */ new Set();
  for (const f2 of features) {
    const adresse = adresseDepuisFeaturePhoton(f2);
    if (!adresse || !adresseDansZonePhoton(adresse, zone)) continue;
    const cle = slugTexte(adresse.libelle);
    const id = adresse.idOsm;
    if (libelles.has(cle) || ids.has(id)) continue;
    libelles.add(cle);
    ids.add(id);
    const cpVille = ligneCodePostalVille(adresse.pays, adresse.codePostal, adresse.ville);
    const nomZone = nomTerritoireOuPays(adresse);
    const lieu = nomZone && slugTexte(nomZone) !== slugTexte(adresse.ville) ? nomZone : null;
    sortie.push({
      id,
      libelle: adresse.libelle,
      principal: adresse.rue ?? adresse.ville ?? adresse.libelle,
      secondaire: lieu && cpVille ? `${cpVille}, ${lieu}` : cpVille || lieu || "",
      fournisseur: "osm",
      adresse
    });
    if (sortie.length >= limite) break;
  }
  return sortie;
}
var ATTRIBUTION_OSM_TEXTE = "\xA9 OpenStreetMap";
var ATTRIBUTION_OSM_URL = "https://www.openstreetmap.org/copyright";
var ATTRIBUTION_GOOGLE_TEXTE = "Google Maps";
function attributionDesSuggestions(attributionServeur, suggestions) {
  if (attributionServeur === "google" || attributionServeur === "osm") return attributionServeur;
  const liste = Array.isArray(suggestions) ? suggestions : [];
  if (liste.some((s) => s?.fournisseur === "google")) return "google";
  if (liste.some((s) => s?.fournisseur === "osm")) return "osm";
  return null;
}
function attributionAffichee(attribution) {
  if (attribution === "osm") return { texte: ATTRIBUTION_OSM_TEXTE, url: ATTRIBUTION_OSM_URL };
  if (attribution === "google") return { texte: ATTRIBUTION_GOOGLE_TEXTE, url: null };
  return null;
}
function adresseChoisieDepuisSuggestion(suggestion, details) {
  if (!suggestion) return null;
  const libelle = suggestion.libelle ?? "";
  switch (suggestion.fournisseur) {
    case "ban":
      if (!replierBlancs(libelle)) return null;
      return { libelle, fournisseur: "ban", idBan: suggestion.adresse?.idBan ?? null };
    case "osm": {
      if (!replierBlancs(libelle)) return null;
      const id = estIdOsm(suggestion.adresse?.idOsm) ? suggestion.adresse.idOsm : estIdOsm(suggestion.id) ? suggestion.id : null;
      return id ? { libelle, fournisseur: "osm", idOsm: id } : null;
    }
    case "google": {
      const libelleDetails = details?.libelle ?? "";
      if (!details || !replierBlancs(libelleDetails)) return null;
      const placeId = texteOuNull(details.placeId) ?? texteOuNull(suggestion.id);
      return placeId ? { libelle: libelleDetails, fournisseur: "google", placeId } : null;
    }
    default:
      return null;
  }
}
function texteChoixSansDetails(suggestion, saisie) {
  if (suggestion.fournisseur !== "google") return suggestion.libelle ?? "";
  if (saisie != null && replierBlancs(saisie)) return saisie;
  return replierBlancs(suggestion.secondaire);
}
function lirePlaceGoogle(place) {
  const composants = Array.isArray(place.addressComponents) ? place.addressComponents : [];
  const composant = (type, court = false) => {
    const c = composants.find((x) => Array.isArray(x?.types) && x.types.includes(type));
    return c ? texteOuNull(court ? c.shortText : c.longText) : null;
  };
  const numero = composant("street_number");
  const voie = composant("route");
  const codePostal = composant("postal_code");
  const ville = composant("locality") ?? composant("postal_town") ?? composant("administrative_area_level_2");
  const iso = paysEtTerritoireDepuisIso(composant("country", true));
  const pays = iso.pays || "FR";
  const codes = { pays, codePostal, codeInsee: null };
  const territoire = iso.territoire ?? territoireDepuisCodes(codes);
  return { numero, voie, codePostal, ville, pays, territoire, codes, nomPays: composant("country") };
}
function elementsVerificationGoogle(place) {
  if (!place) return null;
  const l = lirePlaceGoogle(place);
  const rue = rueNumeroEtVoie(l.pays, l.numero, l.voie);
  const cpVille = ligneCodePostalVille(l.pays, l.codePostal, l.ville);
  let texte;
  if (rue && cpVille) {
    texte = `${rue}, ${cpVille}`;
  } else {
    const morceauxLibelle = replierBlancs(place.formattedAddress).split(",").map((m) => replierBlancs(m)).filter(Boolean);
    const dernier = morceauxLibelle[morceauxLibelle.length - 1];
    const sPays = [slugTexte(l.nomPays), slugTexte(NOMS_PAYS_FR[l.pays] ?? "")];
    if (dernier && morceauxLibelle.length > 1 && sPays.includes(slugTexte(dernier))) morceauxLibelle.pop();
    texte = morceauxLibelle.join(", ");
  }
  if (!texte) return null;
  return {
    texte,
    numero: l.numero,
    voie: l.voie,
    codePostal: l.codePostal,
    ville: l.ville,
    pays: l.pays,
    territoire: l.territoire
  };
}
function texteVerificationGoogle(place) {
  return elementsVerificationGoogle(place)?.texte ?? "";
}
var SEPARATEUR_VOIE_BILINGUE = /\s+[-–—]\s+|\s*\/\s*/;
function voieComparable(voie) {
  const mots2 = slugTexte(voie).split("-").filter(Boolean);
  let i = 0;
  if (i < mots2.length && /^[0-9]+[a-z]?$/.test(mots2[i])) {
    i++;
    if (i < mots2.length && Object.prototype.hasOwnProperty.call(INDICES_REPETITION, mots2[i])) i++;
  } else if (i < mots2.length && /^[0-9]+(bis|ter|quater|quinquies)$/.test(mots2[i])) {
    i++;
  }
  return (i > 0 && i < mots2.length ? mots2.slice(i) : mots2).join("-");
}
function memeVoie(a, b) {
  const sa = voieComparable(a ?? "");
  const sb = voieComparable(b ?? "");
  if (!sa || !sb) return false;
  if (sa === sb) return true;
  const morceaux = (voie) => voie.split(SEPARATEUR_VOIE_BILINGUE).map((m) => voieComparable(m)).filter(Boolean);
  const ma = morceaux(a ?? "");
  const mb = morceaux(b ?? "");
  return ma.length > 1 && ma.includes(sb) || mb.length > 1 && mb.includes(sa);
}
var INDICES_REPETITION = { bis: "b", ter: "t", quater: "q", quinquies: "c" };
function numeroComparable(n) {
  const s = slugTexte(n).split("-").join("");
  let i = 0;
  while (i < s.length && s[i] >= "0" && s[i] <= "9") i++;
  if (i === 0) return s;
  const indice = s.slice(i);
  return s.slice(0, i) + (INDICES_REPETITION[indice] ?? indice);
}
function memeNumero(a, b) {
  const sa = numeroComparable(a);
  return sa.length > 0 && sa === numeroComparable(b);
}
function memeCodePostal(a, b) {
  const sa = slugTexte(a).split("-").join("");
  return sa.length > 0 && sa === slugTexte(b).split("-").join("");
}
function lieuNommeGoogle(types) {
  const t = Array.isArray(types) ? types : [];
  if (t.includes("street_address") || t.includes("premise") || t.includes("subpremise")) return false;
  return t.includes("establishment") || t.includes("point_of_interest");
}
function featureBanConfirmeGoogle(feature, google) {
  const p = feature?.properties;
  if (!p || !google.voie) return false;
  const voieBan = texteOuNull(p.street) ?? (p.type === "street" ? texteOuNull(p.name) : null);
  if (!memeVoie(voieBan, google.voie)) return false;
  if (!google.numero) return true;
  return p.type === "housenumber" && memeNumero(p.housenumber, google.numero);
}
function memeVilleOsm(a, b) {
  if (memeVilleSaisie(a, b)) return true;
  const morceaux = (v) => replierBlancs(v).split(SEPARATEUR_VOIE_BILINGUE).map((m) => replierBlancs(m)).filter(Boolean);
  const ma = morceaux(a);
  const mb = morceaux(b);
  return ma.length > 1 && ma.some((m) => memeVilleSaisie(m, b)) || mb.length > 1 && mb.some((m) => memeVilleSaisie(m, a));
}
function premiereAdresseOsmConfirmantGoogle(features, google) {
  if (!google.voie) return null;
  for (const f2 of features) {
    const a = adresseDepuisFeaturePhoton(f2);
    if (!a || a.pays !== google.pays || a.territoire !== google.territoire) continue;
    const p = f2.properties;
    const voieOsm = p.type === "house" ? texteOuNull(p.street) : p.type === "street" ? texteOuNull(p.name) : null;
    if (!memeVoie(voieOsm, google.voie)) continue;
    if (google.numero && !(p.type === "house" && memeNumero(p.housenumber, google.numero))) continue;
    const memeLocalite = !!google.codePostal && memeCodePostal(a.codePostal, google.codePostal) || memeVilleOsm(a.ville, google.ville);
    if (!memeLocalite) continue;
    return a;
  }
  return null;
}
function adresseOsmSousVilleGoogle(a, villeGoogle) {
  const v = replierBlancs(villeGoogle);
  if (a.source !== "osm" || !v || memeVilleOsm(a.ville, v)) return a;
  const ville = tronquer(v, LONGUEUR_MAX_VILLE);
  return { ...a, ville, libelle: libelleOsmConstruit({ ...a, ville }) };
}
function adressePhotonConfirmantGoogle(json, google) {
  const features = json?.features;
  if (!Array.isArray(features)) return null;
  return premiereAdresseOsmConfirmantGoogle(features, google);
}
function texteVerificationOsm(el) {
  if (!el) return null;
  const rue = rueNumeroEtVoie(el.pays, el.numero, el.voie);
  const localite = replierBlancs(el.ville) || replierBlancs(el.codePostal);
  return rue && localite ? `${rue}, ${localite}` : null;
}
var NOMBRE_VERIFICATION_NOMINATIM = 5;
function parametresRechercheNominatim(el) {
  const voie = replierBlancs(el?.voie);
  if (!el || !voie) return null;
  const numero = replierBlancs(el.numero);
  const ville = replierBlancs(el.ville);
  const codePostal = replierBlancs(el.codePostal);
  if (!ville && !codePostal) return null;
  const params = [["street", numero ? `${numero} ${voie}` : voie]];
  if (ville) params.push(["city", ville]);
  else params.push(["postalcode", codePostal]);
  params.push(
    ["countrycodes", (el.pays || "FR").trim().toLowerCase()],
    ["format", "geocodejson"],
    ["addressdetails", "1"],
    ["limit", String(NOMBRE_VERIFICATION_NOMINATIM)],
    ["accept-language", "fr"]
  );
  return params;
}
var TYPES_OSM_NOMINATIM = { node: "N", way: "W", relation: "R" };
function featurePhotonDepuisNominatim(feature) {
  const g = feature?.properties?.geocoding;
  if (!g) return null;
  const osmType = typeof g.osm_type === "string" ? TYPES_OSM_NOMINATIM[g.osm_type.toLowerCase()] ?? null : null;
  if (!osmType) return null;
  const cc = texteOuNull(g.country_code);
  return {
    type: "Feature",
    geometry: feature?.geometry ?? null,
    properties: {
      osm_type: osmType,
      osm_id: g.osm_id ?? null,
      osm_key: g.osm_key ?? null,
      osm_value: g.osm_value ?? null,
      type: g.type ?? null,
      name: g.name ?? null,
      housenumber: g.housenumber ?? null,
      street: g.street ?? null,
      postcode: g.postcode ?? null,
      city: g.city ?? null,
      locality: g.locality ?? null,
      district: g.district ?? null,
      county: g.county ?? null,
      state: g.state ?? null,
      country: g.country ?? null,
      countrycode: cc ? cc.toUpperCase() : null
    }
  };
}
function adresseNominatimConfirmantGoogle(json, google) {
  const features = json?.features;
  if (!Array.isArray(features)) return null;
  return premiereAdresseOsmConfirmantGoogle(
    features.map((f2) => featurePhotonDepuisNominatim(f2)),
    google
  );
}
function adresseDepuisPlaceGoogle(place, placeId) {
  const libelle = texteOuNull(place?.formattedAddress);
  if (!place || !libelle) return null;
  const l = lirePlaceGoogle(place);
  const rue = rueNumeroEtVoie(l.pays, l.numero, l.voie);
  return {
    libelle: tronquer(libelle, LONGUEUR_MAX_ADRESSE),
    rue: rue ? tronquer(rue, LONGUEUR_MAX_ADRESSE) : null,
    codePostal: l.codePostal,
    ville: l.ville,
    codeInsee: null,
    pays: l.pays,
    territoire: l.territoire,
    departement: l.pays === "FR" ? departementDepuisCodes(l.codes) : null,
    latitude: null,
    longitude: null,
    precision: l.numero && l.voie ? "adresse" : l.voie ? "rue" : "ville",
    source: "google",
    placeId: texteOuNull(placeId) ?? texteOuNull(place.id),
    idBan: null,
    idOsm: null
  };
}

// src/pays.ts
var f = (id, libelle) => ({ id, libelle });
var FUSEAUX_FRANCE = [
  f("Europe/Paris", "Paris (m\xE9tropole)"),
  f("America/Guadeloupe", "Guadeloupe"),
  f("America/Cayenne", "Guyane"),
  f("Indian/Reunion", "La R\xE9union"),
  f("America/Martinique", "Martinique"),
  f("Indian/Mayotte", "Mayotte"),
  f("Pacific/Noumea", "Nouvelle-Cal\xE9donie"),
  f("Pacific/Tahiti", "Polyn\xE9sie fran\xE7aise"),
  f("America/St_Barthelemy", "Saint-Barth\xE9lemy"),
  f("America/Marigot", "Saint-Martin"),
  f("America/Miquelon", "Saint-Pierre-et-Miquelon"),
  f("Pacific/Wallis", "Wallis-et-Futuna")
];
var PAYS = [
  { code: "FR", nom: "France", devise: "EUR", indicatif: "33", langue: "fr-FR", fuseaux: FUSEAUX_FRANCE },
  { code: "DE", nom: "Allemagne", devise: "EUR", indicatif: "49", langue: "de-DE", fuseaux: [f("Europe/Berlin", "Berlin")] },
  { code: "AD", nom: "Andorre", devise: "EUR", indicatif: "376", langue: "ca-AD", fuseaux: [f("Europe/Andorra", "Andorre")] },
  { code: "AT", nom: "Autriche", devise: "EUR", indicatif: "43", langue: "de-AT", fuseaux: [f("Europe/Vienna", "Vienne")] },
  { code: "BE", nom: "Belgique", devise: "EUR", indicatif: "32", langue: "fr-BE", fuseaux: [f("Europe/Brussels", "Bruxelles")] },
  { code: "BG", nom: "Bulgarie", devise: "EUR", indicatif: "359", langue: "bg-BG", fuseaux: [f("Europe/Sofia", "Sofia")] },
  { code: "CY", nom: "Chypre", devise: "EUR", indicatif: "357", langue: "el-CY", fuseaux: [f("Asia/Nicosia", "Nicosie")] },
  { code: "HR", nom: "Croatie", devise: "EUR", indicatif: "385", langue: "hr-HR", fuseaux: [f("Europe/Zagreb", "Zagreb")] },
  { code: "DK", nom: "Danemark", devise: "DKK", indicatif: "45", langue: "da-DK", fuseaux: [f("Europe/Copenhagen", "Copenhague")] },
  {
    code: "ES",
    nom: "Espagne",
    devise: "EUR",
    indicatif: "34",
    langue: "es-ES",
    fuseaux: [f("Europe/Madrid", "Madrid (p\xE9ninsule)"), f("Atlantic/Canary", "\xCEles Canaries")]
  },
  { code: "EE", nom: "Estonie", devise: "EUR", indicatif: "372", langue: "et-EE", fuseaux: [f("Europe/Tallinn", "Tallinn")] },
  { code: "FI", nom: "Finlande", devise: "EUR", indicatif: "358", langue: "fi-FI", fuseaux: [f("Europe/Helsinki", "Helsinki")] },
  { code: "GR", nom: "Gr\xE8ce", devise: "EUR", indicatif: "30", langue: "el-GR", fuseaux: [f("Europe/Athens", "Ath\xE8nes")] },
  { code: "HU", nom: "Hongrie", devise: "HUF", indicatif: "36", langue: "hu-HU", fuseaux: [f("Europe/Budapest", "Budapest")] },
  { code: "IE", nom: "Irlande", devise: "EUR", indicatif: "353", langue: "en-IE", fuseaux: [f("Europe/Dublin", "Dublin")] },
  { code: "IS", nom: "Islande", devise: "ISK", indicatif: "354", langue: "is-IS", fuseaux: [f("Atlantic/Reykjavik", "Reykjavik")] },
  { code: "IT", nom: "Italie", devise: "EUR", indicatif: "39", langue: "it-IT", fuseaux: [f("Europe/Rome", "Rome")] },
  { code: "LV", nom: "Lettonie", devise: "EUR", indicatif: "371", langue: "lv-LV", fuseaux: [f("Europe/Riga", "Riga")] },
  { code: "LI", nom: "Liechtenstein", devise: "CHF", indicatif: "423", langue: "de-LI", fuseaux: [f("Europe/Vaduz", "Vaduz")] },
  { code: "LT", nom: "Lituanie", devise: "EUR", indicatif: "370", langue: "lt-LT", fuseaux: [f("Europe/Vilnius", "Vilnius")] },
  { code: "LU", nom: "Luxembourg", devise: "EUR", indicatif: "352", langue: "fr-LU", fuseaux: [f("Europe/Luxembourg", "Luxembourg")] },
  { code: "MT", nom: "Malte", devise: "EUR", indicatif: "356", langue: "en-MT", fuseaux: [f("Europe/Malta", "La Valette")] },
  { code: "MC", nom: "Monaco", devise: "EUR", indicatif: "377", langue: "fr-MC", fuseaux: [f("Europe/Monaco", "Monaco")] },
  { code: "NO", nom: "Norv\xE8ge", devise: "NOK", indicatif: "47", langue: "nb-NO", fuseaux: [f("Europe/Oslo", "Oslo")] },
  { code: "NL", nom: "Pays-Bas", devise: "EUR", indicatif: "31", langue: "nl-NL", fuseaux: [f("Europe/Amsterdam", "Amsterdam")] },
  { code: "PL", nom: "Pologne", devise: "PLN", indicatif: "48", langue: "pl-PL", fuseaux: [f("Europe/Warsaw", "Varsovie")] },
  {
    code: "PT",
    nom: "Portugal",
    devise: "EUR",
    indicatif: "351",
    langue: "pt-PT",
    fuseaux: [f("Europe/Lisbon", "Lisbonne (continent)"), f("Atlantic/Madeira", "Mad\xE8re"), f("Atlantic/Azores", "A\xE7ores")]
  },
  { code: "RO", nom: "Roumanie", devise: "RON", indicatif: "40", langue: "ro-RO", fuseaux: [f("Europe/Bucharest", "Bucarest")] },
  { code: "GB", nom: "Royaume-Uni", devise: "GBP", indicatif: "44", langue: "en-GB", fuseaux: [f("Europe/London", "Londres")] },
  { code: "SK", nom: "Slovaquie", devise: "EUR", indicatif: "421", langue: "sk-SK", fuseaux: [f("Europe/Bratislava", "Bratislava")] },
  { code: "SI", nom: "Slov\xE9nie", devise: "EUR", indicatif: "386", langue: "sl-SI", fuseaux: [f("Europe/Ljubljana", "Ljubljana")] },
  { code: "SE", nom: "Su\xE8de", devise: "SEK", indicatif: "46", langue: "sv-SE", fuseaux: [f("Europe/Stockholm", "Stockholm")] },
  { code: "CH", nom: "Suisse", devise: "CHF", indicatif: "41", langue: "fr-CH", fuseaux: [f("Europe/Zurich", "Zurich")] },
  // Nom court officiel (et celui de NOMS_PAYS_FR, affiché avec l'adresse) : un seul nom partout.
  { code: "CZ", nom: "Tch\xE9quie", devise: "CZK", indicatif: "420", langue: "cs-CZ", fuseaux: [f("Europe/Prague", "Prague")] }
];
var PAYS_PAR_DEFAUT = "FR";
var FUSEAU_PAR_DEFAUT = "Europe/Paris";
var DEVISE_PAR_DEFAUT = "EUR";
var LANGUE_PAR_DEFAUT = "fr-FR";
function paysParCode(code) {
  if (!code) return null;
  const c = code.trim().toUpperCase();
  return PAYS.find((p) => p.code === c) ?? null;
}
var EXEMPLES_IBAN = {
  FR: "FR76 3000 1007 9412 3456 7890 185",
  AT: "AT61 1904 3002 3457 3201",
  BE: "BE68 5390 0754 7034",
  DE: "DE89 3704 0044 0532 0130 00",
  ES: "ES91 2100 0418 4502 0005 1332",
  FI: "FI21 1234 5600 0007 85",
  IE: "IE29 AIBK 9311 5212 3456 78",
  IT: "IT60 X054 2811 1010 0000 0123 456",
  LU: "LU28 0019 4006 4475 0000",
  NL: "NL91 ABNA 0417 1643 00",
  PT: "PT50 0002 0123 1234 5678 9015 4"
};
function exempleIban(pays) {
  const c = (pays ?? "").trim().toUpperCase() || PAYS_PAR_DEFAUT;
  return EXEMPLES_IBAN[c] ?? `${c}00 0000 0000 0000 0000`;
}
var AFFICHEURS_PAYS = /* @__PURE__ */ new Map();
function afficheurPays(langue) {
  let d = AFFICHEURS_PAYS.get(langue);
  if (d !== void 0) return d;
  try {
    d = new Intl.DisplayNames([LOCALE_INTL_PAR_LANGUE[langue]], { type: "region" });
  } catch {
    d = void 0;
  }
  AFFICHEURS_PAYS.set(langue, d);
  return d ?? null;
}
function nomPaysDansLangue(pays, langue = "fr") {
  if (langue === "fr") return pays.nom;
  const nom = afficheurPays(langue)?.of(pays.code);
  return nom && nom !== pays.code ? nom : pays.nom;
}
function nomPaysDepuisCode(code, langue = "fr") {
  const c = (code ?? "").trim().toUpperCase();
  if (!c) return "";
  const connu = paysParCode(c);
  if (connu) return nomPaysDansLangue(connu, langue);
  const nom = afficheurPays(langue)?.of(c);
  return nom && nom !== c ? nom : c;
}
var FUSEAU_PAYS_MONDE = {
  AD: "Europe/Andorra",
  AE: "Asia/Dubai",
  AF: "Asia/Kabul",
  AG: "America/Antigua",
  AI: "America/Anguilla",
  AL: "Europe/Tirane",
  AM: "Asia/Yerevan",
  AO: "Africa/Luanda",
  AR: "America/Argentina/Buenos_Aires",
  AS: "Pacific/Pago_Pago",
  AT: "Europe/Vienna",
  AU: "Australia/Sydney",
  AW: "America/Aruba",
  AX: "Europe/Mariehamn",
  AZ: "Asia/Baku",
  BA: "Europe/Sarajevo",
  BB: "America/Barbados",
  BD: "Asia/Dhaka",
  BE: "Europe/Brussels",
  BF: "Africa/Ouagadougou",
  BG: "Europe/Sofia",
  BH: "Asia/Bahrain",
  BI: "Africa/Bujumbura",
  BJ: "Africa/Porto-Novo",
  BM: "Atlantic/Bermuda",
  BN: "Asia/Brunei",
  BO: "America/La_Paz",
  BQ: "America/Kralendijk",
  BR: "America/Sao_Paulo",
  BS: "America/Nassau",
  BT: "Asia/Thimphu",
  BW: "Africa/Gaborone",
  BY: "Europe/Minsk",
  BZ: "America/Belize",
  CA: "America/Toronto",
  CC: "Indian/Cocos",
  CD: "Africa/Kinshasa",
  CF: "Africa/Bangui",
  CG: "Africa/Brazzaville",
  CH: "Europe/Zurich",
  CI: "Africa/Abidjan",
  CK: "Pacific/Rarotonga",
  CL: "America/Santiago",
  CM: "Africa/Douala",
  CN: "Asia/Shanghai",
  CO: "America/Bogota",
  CR: "America/Costa_Rica",
  CU: "America/Havana",
  CV: "Atlantic/Cape_Verde",
  CW: "America/Curacao",
  CX: "Indian/Christmas",
  CY: "Asia/Nicosia",
  CZ: "Europe/Prague",
  DE: "Europe/Berlin",
  DJ: "Africa/Djibouti",
  DK: "Europe/Copenhagen",
  DM: "America/Dominica",
  DO: "America/Santo_Domingo",
  DZ: "Africa/Algiers",
  EC: "America/Guayaquil",
  EE: "Europe/Tallinn",
  EG: "Africa/Cairo",
  EH: "Africa/El_Aaiun",
  ER: "Africa/Asmara",
  ES: "Europe/Madrid",
  ET: "Africa/Addis_Ababa",
  FI: "Europe/Helsinki",
  FJ: "Pacific/Fiji",
  FK: "Atlantic/Stanley",
  FM: "Pacific/Pohnpei",
  FO: "Atlantic/Faroe",
  GA: "Africa/Libreville",
  GB: "Europe/London",
  GD: "America/Grenada",
  GE: "Asia/Tbilisi",
  GG: "Europe/Guernsey",
  GH: "Africa/Accra",
  GI: "Europe/Gibraltar",
  GL: "America/Nuuk",
  GM: "Africa/Banjul",
  GN: "Africa/Conakry",
  GQ: "Africa/Malabo",
  GR: "Europe/Athens",
  GS: "Atlantic/South_Georgia",
  GT: "America/Guatemala",
  GU: "Pacific/Guam",
  GW: "Africa/Bissau",
  GY: "America/Guyana",
  HK: "Asia/Hong_Kong",
  HN: "America/Tegucigalpa",
  HR: "Europe/Zagreb",
  HT: "America/Port-au-Prince",
  HU: "Europe/Budapest",
  ID: "Asia/Jakarta",
  IE: "Europe/Dublin",
  IL: "Asia/Jerusalem",
  IM: "Europe/Isle_of_Man",
  IN: "Asia/Kolkata",
  IO: "Indian/Chagos",
  IQ: "Asia/Baghdad",
  IR: "Asia/Tehran",
  IS: "Atlantic/Reykjavik",
  IT: "Europe/Rome",
  JE: "Europe/Jersey",
  JM: "America/Jamaica",
  JO: "Asia/Amman",
  JP: "Asia/Tokyo",
  KE: "Africa/Nairobi",
  KG: "Asia/Bishkek",
  KH: "Asia/Phnom_Penh",
  KI: "Pacific/Tarawa",
  KM: "Indian/Comoro",
  KN: "America/St_Kitts",
  KP: "Asia/Pyongyang",
  KR: "Asia/Seoul",
  KW: "Asia/Kuwait",
  KY: "America/Cayman",
  KZ: "Asia/Almaty",
  LA: "Asia/Vientiane",
  LB: "Asia/Beirut",
  LC: "America/St_Lucia",
  LI: "Europe/Vaduz",
  LK: "Asia/Colombo",
  LR: "Africa/Monrovia",
  LS: "Africa/Maseru",
  LT: "Europe/Vilnius",
  LU: "Europe/Luxembourg",
  LV: "Europe/Riga",
  LY: "Africa/Tripoli",
  MA: "Africa/Casablanca",
  MC: "Europe/Monaco",
  MD: "Europe/Chisinau",
  ME: "Europe/Podgorica",
  MG: "Indian/Antananarivo",
  MH: "Pacific/Majuro",
  MK: "Europe/Skopje",
  ML: "Africa/Bamako",
  MM: "Asia/Yangon",
  MN: "Asia/Ulaanbaatar",
  MO: "Asia/Macau",
  MP: "Pacific/Saipan",
  MR: "Africa/Nouakchott",
  MS: "America/Montserrat",
  MT: "Europe/Malta",
  MU: "Indian/Mauritius",
  MV: "Indian/Maldives",
  MW: "Africa/Blantyre",
  MX: "America/Mexico_City",
  MY: "Asia/Kuala_Lumpur",
  MZ: "Africa/Maputo",
  NA: "Africa/Windhoek",
  NE: "Africa/Niamey",
  NF: "Pacific/Norfolk",
  NG: "Africa/Lagos",
  NI: "America/Managua",
  NL: "Europe/Amsterdam",
  NO: "Europe/Oslo",
  NP: "Asia/Kathmandu",
  NR: "Pacific/Nauru",
  NU: "Pacific/Niue",
  NZ: "Pacific/Auckland",
  OM: "Asia/Muscat",
  PA: "America/Panama",
  PE: "America/Lima",
  PG: "Pacific/Port_Moresby",
  PH: "Asia/Manila",
  PK: "Asia/Karachi",
  PL: "Europe/Warsaw",
  PN: "Pacific/Pitcairn",
  PR: "America/Puerto_Rico",
  PS: "Asia/Hebron",
  PT: "Europe/Lisbon",
  PW: "Pacific/Palau",
  PY: "America/Asuncion",
  QA: "Asia/Qatar",
  RO: "Europe/Bucharest",
  RS: "Europe/Belgrade",
  RU: "Europe/Moscow",
  RW: "Africa/Kigali",
  SA: "Asia/Riyadh",
  SB: "Pacific/Guadalcanal",
  SC: "Indian/Mahe",
  SD: "Africa/Khartoum",
  SE: "Europe/Stockholm",
  SG: "Asia/Singapore",
  SH: "Atlantic/St_Helena",
  SI: "Europe/Ljubljana",
  SJ: "Arctic/Longyearbyen",
  SK: "Europe/Bratislava",
  SL: "Africa/Freetown",
  SM: "Europe/San_Marino",
  SN: "Africa/Dakar",
  SO: "Africa/Mogadishu",
  SR: "America/Paramaribo",
  SS: "Africa/Juba",
  ST: "Africa/Sao_Tome",
  SV: "America/El_Salvador",
  SX: "America/Lower_Princes",
  SY: "Asia/Damascus",
  SZ: "Africa/Mbabane",
  TC: "America/Grand_Turk",
  TD: "Africa/Ndjamena",
  TG: "Africa/Lome",
  TH: "Asia/Bangkok",
  TJ: "Asia/Dushanbe",
  TK: "Pacific/Fakaofo",
  TL: "Asia/Dili",
  TM: "Asia/Ashgabat",
  TN: "Africa/Tunis",
  TO: "Pacific/Tongatapu",
  TR: "Europe/Istanbul",
  TT: "America/Port_of_Spain",
  TV: "Pacific/Funafuti",
  TW: "Asia/Taipei",
  TZ: "Africa/Dar_es_Salaam",
  UA: "Europe/Kyiv",
  UG: "Africa/Kampala",
  US: "America/New_York",
  UY: "America/Montevideo",
  UZ: "Asia/Tashkent",
  VA: "Europe/Vatican",
  VC: "America/St_Vincent",
  VE: "America/Caracas",
  VG: "America/Tortola",
  VI: "America/St_Thomas",
  VN: "Asia/Ho_Chi_Minh",
  VU: "Pacific/Efate",
  WS: "Pacific/Apia",
  YE: "Asia/Aden",
  ZA: "Africa/Johannesburg",
  ZM: "Africa/Lusaka",
  ZW: "Africa/Harare"
};
function codePaysMonde(code) {
  const c = (code ?? "").trim().toUpperCase();
  if (c === PAYS_PAR_DEFAUT) return c;
  return FUSEAU_PAYS_MONDE[c] ? c : null;
}
function drapeauPays(code) {
  const c = (code ?? "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(c)) return "";
  return String.fromCodePoint(...[...c].map((l) => 127462 + l.charCodeAt(0) - 65));
}
function listePaysMonde(langue = "fr") {
  const tri = new Intl.Collator(LOCALE_INTL_PAR_LANGUE[langue], { sensitivity: "base" });
  const autres = Object.keys(FUSEAU_PAYS_MONDE).map((code) => ({ code, nom: nomPaysDepuisCode(code, langue), drapeau: drapeauPays(code) })).sort((a, b) => tri.compare(a.nom, b.nom));
  const france = { code: PAYS_PAR_DEFAUT, nom: nomPaysDepuisCode(PAYS_PAR_DEFAUT, langue), drapeau: drapeauPays(PAYS_PAR_DEFAUT) };
  return [france, ...autres];
}
function fuseauParDefaut(code) {
  return paysParCode(code)?.fuseaux[0]?.id ?? FUSEAU_PAYS_MONDE[(code ?? "").trim().toUpperCase()] ?? FUSEAU_PAR_DEFAUT;
}
function fuseauValidePourPays(pays, fuseau) {
  if (!fuseau) return false;
  const connu = paysParCode(pays);
  if (connu) return connu.fuseaux.some((z) => z.id === fuseau);
  return FUSEAU_PAYS_MONDE[(pays ?? "").trim().toUpperCase()] === fuseau;
}
function libelleFuseau(fuseau) {
  if (!fuseau) return "";
  for (const p of PAYS) {
    const z = p.fuseaux.find((x) => x.id === fuseau);
    if (z) return z.libelle;
  }
  const ville = fuseau.split("/").pop() ?? fuseau;
  return ville.replace(/_/g, " ");
}
function fuseauDepuisCodePostal(codePostal) {
  const cp = (codePostal ?? "").trim();
  if (!/^\d{5}$/.test(cp)) return FUSEAU_PAR_DEFAUT;
  if (cp === "97133") return "America/St_Barthelemy";
  if (cp === "97150") return "America/Marigot";
  const PAR_PREFIXE = {
    "971": "America/Guadeloupe",
    "972": "America/Martinique",
    "973": "America/Cayenne",
    "974": "Indian/Reunion",
    "975": "America/Miquelon",
    "976": "Indian/Mayotte",
    "986": "Pacific/Wallis",
    "987": "Pacific/Tahiti",
    "988": "Pacific/Noumea"
  };
  return PAR_PREFIXE[cp.slice(0, 3)] ?? FUSEAU_PAR_DEFAUT;
}
var FUSEAU_PAR_TERRITOIRE = {
  GP: "America/Guadeloupe",
  MQ: "America/Martinique",
  GF: "America/Cayenne",
  RE: "Indian/Reunion",
  YT: "Indian/Mayotte",
  PM: "America/Miquelon",
  BL: "America/St_Barthelemy",
  MF: "America/Marigot",
  WF: "Pacific/Wallis",
  PF: "Pacific/Tahiti",
  NC: "Pacific/Noumea"
};
function codePostalDepuisAdresse(adresse) {
  const m = (adresse ?? "").match(/(?:^|\D)(\d{5})(?!\d)/);
  return m ? m[1] : null;
}
function fuseauDeduit(pays, adresse, codePostal, territoire) {
  const source = codePostal != null && /^[0-9]{5}$/.test(codePostal) ? codePostal : adresse;
  const code = codePaysMonde(pays) ?? PAYS_PAR_DEFAUT;
  if (code !== "FR") return fuseauParDefaut(code);
  const cp = codePostalDepuisAdresse(source);
  if (cp !== null) return fuseauDepuisCodePostal(cp);
  const t = territoire !== void 0 ? territoire : analyserAdresseLibre(adresse, { paysParDefaut: "FR" }).territoire;
  return FUSEAU_PAR_TERRITOIRE[(t ?? "").trim().toUpperCase()] ?? FUSEAU_PAR_DEFAUT;
}
function libellesRegionOrganisation(org) {
  const pays = paysParCode(org.country ?? PAYS_PAR_DEFAUT);
  const origine2 = org.timezone_source === "manuel" ? "choisi" : "automatique";
  return {
    pays: pays?.nom ?? org.country ?? "France",
    fuseau: `${libelleFuseau(org.timezone || FUSEAU_PAR_DEFAUT)} (${origine2})`
  };
}
var SIGNE_MOINS_UTC = "\u2212";
var PREFIXE_HEURE_LOCALE = "Heure locale : ";
var SUFFIXE_FUSEAU_MANUEL = " \xB7 choisi \xE0 la main";
var formateursDecalage = /* @__PURE__ */ new Map();
function formateurDecalage(fuseau) {
  if (formateursDecalage.has(fuseau)) return formateursDecalage.get(fuseau);
  let f2;
  try {
    f2 = new Intl.DateTimeFormat("en-US", {
      timeZone: fuseau,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  } catch {
    f2 = null;
  }
  formateursDecalage.set(fuseau, f2);
  return f2;
}
function decalageUtcMinutes(fuseau, instant2) {
  const id = (fuseau ?? "").trim();
  const t = instant2.getTime();
  if (!id || !Number.isFinite(t)) return null;
  const f2 = formateurDecalage(id);
  if (!f2) return null;
  const parties = {};
  for (const p of f2.formatToParts(new Date(t))) {
    if (p.type !== "literal") parties[p.type] = Number(p.value);
  }
  const murale = Date.UTC(
    parties.year,
    parties.month - 1,
    parties.day,
    parties.hour % 24,
    parties.minute,
    parties.second
  );
  const aLaSeconde = Math.floor(t / 1e3) * 1e3;
  return Math.round((murale - aLaSeconde) / 6e4);
}
function libelleDecalageUtc(minutes) {
  if (!Number.isFinite(minutes) || minutes === 0) return "UTC";
  const signe = minutes < 0 ? SIGNE_MOINS_UTC : "+";
  const abs = Math.abs(Math.trunc(minutes));
  const heures = Math.floor(abs / 60);
  const reste = abs % 60;
  return `UTC${signe}${heures}${reste ? `:${String(reste).padStart(2, "0")}` : ""}`;
}
function libelleHeureLocale(fuseau, instant2) {
  const id = (fuseau ?? "").trim() || FUSEAU_PAR_DEFAUT;
  const decalage = decalageUtcMinutes(id, instant2);
  const nom = libelleFuseau(id);
  return decalage === null ? nom : `${nom} (${libelleDecalageUtc(decalage)})`;
}
function ligneHeureLocale({ fuseau, manuel }, instant2) {
  return `${PREFIXE_HEURE_LOCALE}${libelleHeureLocale(fuseau, instant2)}${manuel ? SUFFIXE_FUSEAU_MANUEL : ""}`;
}

// src/numerosOfficiels.json
var numerosOfficiels_default = {
  types: {
    BE_BCE: {
      pays: "BE",
      saisie: {
        prefixesPays: [
          "BE"
        ],
        chiffresSeulement: true,
        completerAGauche: {
          depuis: 9,
          jusqua: 10,
          avec: "0"
        },
        longueurMax: 15
      },
      format: "^[01][0-9]{9}$",
      cle: "MOD97_BE",
      masque: "####.###.###",
      exemple: "0123.456.789",
      ressemble: "^(BE)?[\\s.0-9]+$",
      urlRecherche: "https://kbopub.economie.fgov.be/kbopub/zoeknaamfonetischform.html",
      urlRegistreNumero: "https://kbopub.economie.fgov.be/kbopub/toonondernemingps.html?ondernemingsnummer={numero}",
      registre: "BE",
      formesNonLucratives: {
        "017": "Association sans but lucratif (ASBL)",
        "018": "\xC9tablissement d'utilit\xE9 publique (code clos en 2003, entit\xE9s existantes)",
        "026": "Fondation priv\xE9e",
        "028": "Institution sans but lucratif (ISBL)",
        "029": "Fondation d'utilit\xE9 publique",
        "117": "Association sans but lucratif de droit public",
        "125": "Association internationale sans but lucratif (AISBL)",
        "128": "Temporel des cultes (code clos en 2003, entit\xE9s existantes)",
        "325": "Association internationale sans but lucratif de droit public"
      },
      libelles: {
        nom: {
          fr: "Num\xE9ro d'entreprise (BCE)",
          en: "Enterprise number (CBE)",
          de: "Unternehmensnummer (ZDU)",
          es: "N\xFAmero de empresa (BCE)",
          it: "Numero d'impresa (BCE)",
          nl: "Ondernemingsnummer (KBO)"
        },
        ligne: {
          fr: "N\xB0 d'entreprise {numero}",
          en: "Enterprise no. {numero}",
          de: "Unternehmensnr. {numero}",
          es: "N.\xBA de empresa {numero}",
          it: "N. d'impresa {numero}",
          nl: "Ondernemingsnr. {numero}"
        },
        votreNumero: {
          fr: "votre num\xE9ro d'entreprise (BCE)",
          en: "your enterprise number (CBE)",
          de: "Ihre Unternehmensnummer (ZDU)",
          es: "su n\xFAmero de empresa (BCE)",
          it: "il vostro numero d'impresa (BCE)",
          nl: "uw ondernemingsnummer (KBO)"
        },
        aide: {
          fr: "Le num\xE9ro de votre association \xE0 la Banque-Carrefour des Entreprises, sur 10 chiffres. Nous le v\xE9rifions dans le registre officiel belge.",
          en: "Your organisation's 10-digit number at the Crossroads Bank for Enterprises. We check it in the official Belgian register.",
          de: "Die 10-stellige Nummer Ihres Vereins in der Zentralen Datenbank der Unternehmen. Wir pr\xFCfen sie im offiziellen belgischen Register.",
          es: "El n\xFAmero de 10 cifras de su asociaci\xF3n en el Banco-Cruce de Empresas. Lo comprobamos en el registro oficial belga.",
          it: "Il numero a 10 cifre della vostra associazione presso la Banca-Crocevia delle Imprese. Lo verifichiamo nel registro ufficiale belga.",
          nl: "Het 10-cijferige nummer van uw vereniging in de Kruispuntbank van Ondernemingen. Wij controleren het in het offici\xEBle Belgische register."
        },
        invalide: {
          fr: "Num\xE9ro invalide : 10 chiffres, par exemple 0123.456.789 (v\xE9rifiez les deux derniers chiffres).",
          en: "Invalid number: 10 digits, for example 0123.456.789 (check the last two digits).",
          de: "Ung\xFCltige Nummer: 10 Ziffern, zum Beispiel 0123.456.789 (pr\xFCfen Sie die letzten beiden Ziffern).",
          es: "N\xFAmero no v\xE1lido: 10 cifras, por ejemplo 0123.456.789 (compruebe las dos \xFAltimas cifras).",
          it: "Numero non valido: 10 cifre, ad esempio 0123.456.789 (verificate le ultime due cifre).",
          nl: "Ongeldig nummer: 10 cijfers, bijvoorbeeld 0123.456.789 (controleer de laatste twee cijfers)."
        },
        trouver: {
          fr: "Trouver mon num\xE9ro d'entreprise",
          en: "Find my enterprise number",
          de: "Meine Unternehmensnummer finden",
          es: "Encontrar mi n\xFAmero de empresa",
          it: "Trovare il mio numero d'impresa",
          nl: "Mijn ondernemingsnummer vinden"
        },
        verifie: {
          fr: "V\xE9rifi\xE9 dans le registre officiel belge",
          en: "Verified in the official Belgian register",
          de: "Im offiziellen belgischen Register gepr\xFCft",
          es: "Verificado en el registro oficial belga",
          it: "Verificato nel registro ufficiale belga",
          nl: "Gecontroleerd in het offici\xEBle Belgische register"
        },
        enAttente: {
          fr: "Enregistr\xE9 : la v\xE9rification dans le registre officiel belge est en cours.",
          en: "Saved: verification in the official Belgian register is in progress.",
          de: "Gespeichert: Die Pr\xFCfung im offiziellen belgischen Register l\xE4uft.",
          es: "Guardado: la verificaci\xF3n en el registro oficial belga est\xE1 en curso.",
          it: "Salvato: la verifica nel registro ufficiale belga \xE8 in corso.",
          nl: "Opgeslagen: de controle in het offici\xEBle Belgische register loopt."
        },
        pageEnAttente: {
          fr: "Votre num\xE9ro d'entreprise est enregistr\xE9 : votre page publique s'ouvrira d\xE8s sa v\xE9rification dans le registre officiel belge.",
          en: "Your enterprise number is saved: your public page will open as soon as it is verified in the official Belgian register.",
          de: "Ihre Unternehmensnummer ist gespeichert: Ihre \xF6ffentliche Seite wird freigeschaltet, sobald sie im offiziellen belgischen Register gepr\xFCft ist.",
          es: "Su n\xFAmero de empresa est\xE1 guardado: su p\xE1gina p\xFAblica se abrir\xE1 en cuanto se verifique en el registro oficial belga.",
          it: "Il vostro numero d'impresa \xE8 salvato: la vostra pagina pubblica si aprir\xE0 non appena sar\xE0 verificato nel registro ufficiale belga.",
          nl: "Uw ondernemingsnummer is opgeslagen: uw openbare pagina gaat open zodra het in het offici\xEBle Belgische register is gecontroleerd."
        },
        zeroCommission: {
          fr: "Les associations et fondations belges (ASBL, AISBL, fondations, \xE9tablissements de culte reconnus) ne paient aucune commission : indiquez votre num\xE9ro d'entreprise (BCE).",
          en: "Belgian associations and foundations (ASBL/VZW, AISBL, foundations, recognised places of worship) pay no commission: enter your enterprise number (CBE).",
          de: "Belgische Vereine und Stiftungen (VoG, IVoG, Stiftungen, anerkannte Kultst\xE4tten) zahlen keine Provision: Geben Sie Ihre Unternehmensnummer (ZDU) ein.",
          es: "Las asociaciones y fundaciones belgas (ASBL, AISBL, fundaciones, lugares de culto reconocidos) no pagan ninguna comisi\xF3n: indique su n\xFAmero de empresa (BCE).",
          it: "Le associazioni e fondazioni belghe (ASBL, AISBL, fondazioni, luoghi di culto riconosciuti) non pagano alcuna commissione: indicate il vostro numero d'impresa (BCE).",
          nl: "Belgische verenigingen en stichtingen (vzw, ivzw, stichtingen, erkende gebedshuizen) betalen geen commissie: vul uw ondernemingsnummer (KBO) in."
        },
        avantages: {
          fr: "0% de commission Donoor pour les ASBL, AISBL et fondations",
          en: "0% Donoor commission for ASBL/VZW, AISBL and foundations",
          de: "0 % Donoor-Provision f\xFCr VoG, IVoG und Stiftungen",
          es: "0 % de comisi\xF3n de Donoor para ASBL, AISBL y fundaciones",
          it: "0% di commissione Donoor per ASBL, AISBL e fondazioni",
          nl: "0% commissie van Donoor voor vzw's, ivzw's en stichtingen"
        },
        consigneRecherche: {
          fr: "Commencez \xE0 taper le nom de votre association ou son num\xE9ro d'entreprise (BCE), puis choisissez-la dans les propositions.",
          en: "Start typing your organisation's name or its enterprise number (CBE), then choose it from the suggestions.",
          de: "Geben Sie den Namen Ihres Vereins oder seine Unternehmensnummer (ZDU) ein und w\xE4hlen Sie ihn dann in den Vorschl\xE4gen aus.",
          es: "Empiece a escribir el nombre de su asociaci\xF3n o su n\xFAmero de empresa (BCE) y el\xEDjala en las propuestas.",
          it: "Iniziate a digitare il nome della vostra associazione o il suo numero d'impresa (BCE), poi sceglietela tra le proposte.",
          nl: "Typ de naam van uw vereniging of haar ondernemingsnummer (KBO) en kies haar daarna in de voorstellen."
        },
        placeholderRecherche: {
          fr: "Nom ou num\xE9ro d'entreprise (BCE)",
          en: "Name or enterprise number (CBE)",
          de: "Name oder Unternehmensnummer (ZDU)",
          es: "Nombre o n\xFAmero de empresa (BCE)",
          it: "Nome o numero d'impresa (BCE)",
          nl: "Naam of ondernemingsnummer (KBO)"
        }
      }
    },
    LU_RCS: {
      pays: "LU",
      saisie: {
        prefixesPays: [
          "RCS",
          "LU"
        ],
        chiffresSeulement: false,
        longueurMax: 12
      },
      format: "^[A-O][0-9]{1,6}$",
      cle: "AUCUNE",
      masque: "",
      exemple: "F12345",
      ressemble: "^(RCS|LU)?\\s*[A-O]?[\\s0-9]*$",
      urlRecherche: "https://www.lbr.lu/mjrcs-web-front/",
      urlRegistreNumero: "https://www.lbr.lu/mjrcs-web-front/consult-search-list?search={numero}",
      registre: null,
      formesNonLucratives: {
        F: "Association sans but lucratif (ASBL)",
        G: "Fondation"
      },
      libelles: {
        nom: {
          fr: "Num\xE9ro RCS (Registre de commerce et des soci\xE9t\xE9s)",
          en: "RCS number (Trade and Companies Register)",
          de: "RCS-Nummer (Handels- und Firmenregister)",
          es: "N\xFAmero RCS (Registro Mercantil y de Sociedades)",
          it: "Numero RCS (Registro di commercio e delle societ\xE0)",
          nl: "RCS-nummer (Handels- en Vennootschapsregister)"
        },
        ligne: {
          fr: "RCS {numero}",
          en: "RCS {numero}",
          de: "RCS {numero}",
          es: "RCS {numero}",
          it: "RCS {numero}",
          nl: "RCS {numero}"
        },
        votreNumero: {
          fr: "votre num\xE9ro RCS",
          en: "your RCS number",
          de: "Ihre RCS-Nummer",
          es: "su n\xFAmero RCS",
          it: "il vostro numero RCS",
          nl: "uw RCS-nummer"
        },
        aide: {
          fr: "Le num\xE9ro de votre organisation au Registre de commerce et des soci\xE9t\xE9s (LBR) : une lettre suivie de chiffres (F pour une ASBL, G pour une fondation, B pour une soci\xE9t\xE9). Notre \xE9quipe le v\xE9rifie avant d'ouvrir votre page.",
          en: "Your organisation's number at the Trade and Companies Register (LBR): a letter followed by digits (F for an ASBL, G for a foundation, B for a company). Our team checks it before opening your page.",
          de: "Die Nummer Ihrer Organisation im Handels- und Firmenregister (LBR): ein Buchstabe gefolgt von Ziffern (F f\xFCr eine ASBL, G f\xFCr eine Stiftung, B f\xFCr eine Gesellschaft). Unser Team pr\xFCft sie, bevor Ihre Seite freigeschaltet wird.",
          es: "El n\xFAmero de su organizaci\xF3n en el Registro Mercantil y de Sociedades (LBR): una letra seguida de cifras (F para una ASBL, G para una fundaci\xF3n, B para una sociedad). Nuestro equipo lo comprueba antes de abrir su p\xE1gina.",
          it: "Il numero della vostra organizzazione nel Registro di commercio e delle societ\xE0 (LBR): una lettera seguita da cifre (F per una ASBL, G per una fondazione, B per una societ\xE0). Il nostro team lo verifica prima di aprire la vostra pagina.",
          nl: "Het nummer van uw organisatie in het Handels- en Vennootschapsregister (LBR): een letter gevolgd door cijfers (F voor een vzw (ASBL), G voor een stichting, B voor een vennootschap). Ons team controleert het voordat uw pagina opengaat."
        },
        invalide: {
          fr: "Num\xE9ro invalide : une lettre suivie de chiffres, par exemple F12345 (ASBL) ou B123456 (soci\xE9t\xE9).",
          en: "Invalid number: a letter followed by digits, for example F12345 (ASBL) or B123456 (company).",
          de: "Ung\xFCltige Nummer: ein Buchstabe gefolgt von Ziffern, zum Beispiel F12345 (ASBL) oder B123456 (Gesellschaft).",
          es: "N\xFAmero no v\xE1lido: una letra seguida de cifras, por ejemplo F12345 (ASBL) o B123456 (sociedad).",
          it: "Numero non valido: una lettera seguita da cifre, ad esempio F12345 (ASBL) o B123456 (societ\xE0).",
          nl: "Ongeldig nummer: een letter gevolgd door cijfers, bijvoorbeeld F12345 (ASBL) of B123456 (vennootschap)."
        },
        trouver: {
          fr: "Trouver mon num\xE9ro RCS",
          en: "Find my RCS number",
          de: "Meine RCS-Nummer finden",
          es: "Encontrar mi n\xFAmero RCS",
          it: "Trovare il mio numero RCS",
          nl: "Mijn RCS-nummer vinden"
        },
        verifie: {
          fr: "V\xE9rifi\xE9 au Registre de commerce et des soci\xE9t\xE9s",
          en: "Verified at the Trade and Companies Register",
          de: "Im Handels- und Firmenregister gepr\xFCft",
          es: "Verificado en el Registro Mercantil y de Sociedades",
          it: "Verificato nel Registro di commercio e delle societ\xE0",
          nl: "Gecontroleerd in het Handels- en Vennootschapsregister"
        },
        enAttente: {
          fr: "Enregistr\xE9 : notre \xE9quipe le v\xE9rifie au Registre de commerce et des soci\xE9t\xE9s.",
          en: "Saved: our team is checking it at the Trade and Companies Register.",
          de: "Gespeichert: Unser Team pr\xFCft sie im Handels- und Firmenregister.",
          es: "Guardado: nuestro equipo lo est\xE1 comprobando en el Registro Mercantil y de Sociedades.",
          it: "Salvato: il nostro team lo sta verificando nel Registro di commercio e delle societ\xE0.",
          nl: "Opgeslagen: ons team controleert het in het Handels- en Vennootschapsregister."
        },
        pageEnAttente: {
          fr: "Votre num\xE9ro RCS est enregistr\xE9 : votre page publique s'ouvrira d\xE8s que notre \xE9quipe l'aura v\xE9rifi\xE9.",
          en: "Your RCS number is saved: your public page will open as soon as our team has checked it.",
          de: "Ihre RCS-Nummer ist gespeichert: Ihre \xF6ffentliche Seite wird freigeschaltet, sobald unser Team sie gepr\xFCft hat.",
          es: "Su n\xFAmero RCS est\xE1 guardado: su p\xE1gina p\xFAblica se abrir\xE1 en cuanto nuestro equipo lo haya comprobado.",
          it: "Il vostro numero RCS \xE8 salvato: la vostra pagina pubblica si aprir\xE0 non appena il nostro team lo avr\xE0 verificato.",
          nl: "Uw RCS-nummer is opgeslagen: uw openbare pagina gaat open zodra ons team het heeft gecontroleerd."
        },
        zeroCommission: {
          fr: "Les ASBL et fondations luxembourgeoises ne paient aucune commission : indiquez votre num\xE9ro RCS.",
          en: "Luxembourg ASBLs and foundations pay no commission: enter your RCS number.",
          de: "Luxemburgische ASBL und Stiftungen zahlen keine Provision: Geben Sie Ihre RCS-Nummer ein.",
          es: "Las ASBL y fundaciones luxemburguesas no pagan ninguna comisi\xF3n: indique su n\xFAmero RCS.",
          it: "Le ASBL e le fondazioni lussemburghesi non pagano alcuna commissione: indicate il vostro numero RCS.",
          nl: "Luxemburgse vzw's (ASBL) en stichtingen betalen geen commissie: vul uw RCS-nummer in."
        },
        avantages: {
          fr: "0% de commission Donoor pour les ASBL et fondations",
          en: "0% Donoor commission for ASBLs and foundations",
          de: "0 % Donoor-Provision f\xFCr ASBL und Stiftungen",
          es: "0 % de comisi\xF3n de Donoor para ASBL y fundaciones",
          it: "0% di commissione Donoor per ASBL e fondazioni",
          nl: "0% commissie van Donoor voor vzw's (ASBL) en stichtingen"
        },
        consigneRecherche: {
          fr: "Tapez le nom de votre association, puis indiquez son num\xE9ro RCS.",
          en: "Type your organisation's name, then enter its RCS number.",
          de: "Geben Sie den Namen Ihres Vereins ein und dann seine RCS-Nummer.",
          es: "Escriba el nombre de su asociaci\xF3n y luego indique su n\xFAmero RCS.",
          it: "Digitate il nome della vostra associazione, poi indicate il suo numero RCS.",
          nl: "Typ de naam van uw vereniging en vul daarna haar RCS-nummer in."
        },
        placeholderRecherche: {
          fr: "Nom de votre association",
          en: "Your organisation's name",
          de: "Name Ihres Vereins",
          es: "Nombre de su asociaci\xF3n",
          it: "Nome della vostra associazione",
          nl: "Naam van uw vereniging"
        }
      }
    },
    NL_KVK: {
      pays: "NL",
      saisie: {
        prefixesPays: [
          "KVK-NUMMER",
          "KVK",
          "NL"
        ],
        chiffresSeulement: true,
        longueurMax: 16
      },
      format: "^[0-9]{8}$",
      cle: "AUCUNE",
      masque: "",
      exemple: "12345678",
      ressemble: "^(KVK-NUMMER|KVK|NL)?[\\s:]*[0-9][\\s0-9.]*$",
      urlRecherche: "https://www.kvk.nl/zoeken/",
      urlRegistreNumero: "https://www.kvk.nl/zoeken/?source=all&q={numero}",
      registre: null,
      formesNonLucratives: {
        vereniging: "Vereniging (association \xE0 pleine capacit\xE9 juridique)",
        vereniging_beperkt: "Vereniging met beperkte rechtsbevoegdheid (association sans statuts notari\xE9s)",
        stichting: "Stichting (fondation)",
        kerkgenootschap: "Kerkgenootschap (communaut\xE9 religieuse)"
      },
      libelles: {
        nom: {
          fr: "Num\xE9ro KvK (Chambre de commerce n\xE9erlandaise)",
          en: "KvK number (Dutch Chamber of Commerce)",
          de: "KvK-Nummer (niederl\xE4ndische Handelskammer)",
          es: "N\xFAmero KvK (C\xE1mara de Comercio neerlandesa)",
          it: "Numero KvK (Camera di Commercio olandese)",
          nl: "KvK-nummer (Kamer van Koophandel)"
        },
        ligne: {
          fr: "KvK {numero}",
          en: "KvK {numero}",
          de: "KvK {numero}",
          es: "KvK {numero}",
          it: "KvK {numero}",
          nl: "KvK {numero}"
        },
        votreNumero: {
          fr: "votre num\xE9ro KvK",
          en: "your KvK number",
          de: "Ihre KvK-Nummer",
          es: "su n\xFAmero KvK",
          it: "il vostro numero KvK",
          nl: "uw KvK-nummer"
        },
        aide: {
          fr: "Le num\xE9ro \xE0 8 chiffres de votre organisation \xE0 la Chambre de commerce (KvK), indiqu\xE9 sur l'extrait du registre. Notre \xE9quipe le v\xE9rifie avant d'ouvrir votre page.",
          en: "Your organisation's 8-digit number at the Chamber of Commerce (KvK), shown on your register extract. Our team checks it before opening your page.",
          de: "Die 8-stellige Nummer Ihrer Organisation bei der Handelskammer (KvK), angegeben auf dem Registerauszug. Unser Team pr\xFCft sie, bevor Ihre Seite freigeschaltet wird.",
          es: "El n\xFAmero de 8 cifras de su organizaci\xF3n en la C\xE1mara de Comercio (KvK), que figura en el extracto del registro. Nuestro equipo lo comprueba antes de abrir su p\xE1gina.",
          it: "Il numero di 8 cifre della vostra organizzazione presso la Camera di Commercio (KvK), indicato nell'estratto del registro. Il nostro team lo verifica prima di aprire la vostra pagina.",
          nl: "Het 8-cijferige nummer van uw organisatie bij de Kamer van Koophandel (KvK), zoals op uw uittreksel staat. Ons team controleert het voordat uw pagina opengaat."
        },
        invalide: {
          fr: "Num\xE9ro invalide : 8 chiffres, par exemple 12345678.",
          en: "Invalid number: 8 digits, for example 12345678.",
          de: "Ung\xFCltige Nummer: 8 Ziffern, zum Beispiel 12345678.",
          es: "N\xFAmero no v\xE1lido: 8 cifras, por ejemplo 12345678.",
          it: "Numero non valido: 8 cifre, ad esempio 12345678.",
          nl: "Ongeldig nummer: 8 cijfers, bijvoorbeeld 12345678."
        },
        trouver: {
          fr: "Trouver mon num\xE9ro KvK",
          en: "Find my KvK number",
          de: "Meine KvK-Nummer finden",
          es: "Encontrar mi n\xFAmero KvK",
          it: "Trovare il mio numero KvK",
          nl: "Mijn KvK-nummer vinden"
        },
        verifie: {
          fr: "V\xE9rifi\xE9 au registre de la Chambre de commerce (KvK)",
          en: "Checked in the Chamber of Commerce register (KvK)",
          de: "Im Handelsregister der Handelskammer (KvK) gepr\xFCft",
          es: "Comprobado en el registro de la C\xE1mara de Comercio (KvK)",
          it: "Verificato nel registro della Camera di Commercio (KvK)",
          nl: "Gecontroleerd in het Handelsregister van de KvK"
        },
        enAttente: {
          fr: "Enregistr\xE9 : notre \xE9quipe le v\xE9rifie au registre de la Chambre de commerce (KvK).",
          en: "Saved: our team is checking it in the Chamber of Commerce register (KvK).",
          de: "Gespeichert: Unser Team pr\xFCft sie im Handelsregister der Handelskammer (KvK).",
          es: "Guardado: nuestro equipo lo comprueba en el registro de la C\xE1mara de Comercio (KvK).",
          it: "Salvato: il nostro team lo verifica nel registro della Camera di Commercio (KvK).",
          nl: "Opgeslagen: ons team controleert het in het Handelsregister van de KvK."
        },
        pageEnAttente: {
          fr: "Votre num\xE9ro KvK est enregistr\xE9 : votre page publique s'ouvrira d\xE8s que notre \xE9quipe l'aura v\xE9rifi\xE9.",
          en: "Your KvK number is saved: your public page will open as soon as our team has checked it.",
          de: "Ihre KvK-Nummer ist gespeichert: Ihre \xF6ffentliche Seite wird freigeschaltet, sobald unser Team sie gepr\xFCft hat.",
          es: "Su n\xFAmero KvK est\xE1 guardado: su p\xE1gina p\xFAblica se abrir\xE1 en cuanto nuestro equipo lo haya comprobado.",
          it: "Il vostro numero KvK \xE8 salvato: la vostra pagina pubblica si aprir\xE0 non appena il nostro team l'avr\xE0 verificato.",
          nl: "Uw KvK-nummer is opgeslagen: uw openbare pagina gaat open zodra ons team het heeft gecontroleerd."
        },
        zeroCommission: {
          fr: "Les associations, fondations et communaut\xE9s religieuses n\xE9erlandaises ne paient aucune commission : indiquez votre num\xE9ro KvK.",
          en: "Dutch associations, foundations and religious communities pay no commission: enter your KvK number.",
          de: "Niederl\xE4ndische Vereine, Stiftungen und Religionsgemeinschaften zahlen keine Provision: Geben Sie Ihre KvK-Nummer an.",
          es: "Las asociaciones, fundaciones y comunidades religiosas neerlandesas no pagan comisi\xF3n: indique su n\xFAmero KvK.",
          it: "Le associazioni, fondazioni e comunit\xE0 religiose olandesi non pagano commissioni: indicate il vostro numero KvK.",
          nl: "Nederlandse verenigingen, stichtingen en kerkgenootschappen betalen geen commissie: vul uw KvK-nummer in."
        },
        avantages: {
          fr: "0% de commission Donoor pour les associations, fondations et communaut\xE9s religieuses",
          en: "0% Donoor commission for associations, foundations and religious communities",
          de: "0% Donoor-Provision f\xFCr Vereine, Stiftungen und Religionsgemeinschaften",
          es: "0% de comisi\xF3n de Donoor para asociaciones, fundaciones y comunidades religiosas",
          it: "0% di commissione Donoor per associazioni, fondazioni e comunit\xE0 religiose",
          nl: "0% commissie van Donoor voor verenigingen, stichtingen en kerkgenootschappen"
        },
        consigneRecherche: {
          fr: "Tapez le nom de votre association, puis indiquez son num\xE9ro KvK.",
          en: "Type your organisation's name, then enter its KvK number.",
          de: "Geben Sie den Namen Ihres Vereins ein und dann seine KvK-Nummer.",
          es: "Escriba el nombre de su asociaci\xF3n y despu\xE9s indique su n\xFAmero KvK.",
          it: "Digitate il nome della vostra associazione, poi indicate il suo numero KvK.",
          nl: "Typ de naam van uw vereniging of stichting en vul daarna het KvK-nummer in."
        },
        placeholderRecherche: {
          fr: "Nom de votre association",
          en: "Your organisation's name",
          de: "Name Ihres Vereins",
          es: "Nombre de su asociaci\xF3n",
          it: "Nome della vostra associazione",
          nl: "Naam van uw vereniging of stichting"
        }
      }
    },
    DE_VR: {
      pays: "DE",
      saisie: {
        prefixesPays: [],
        chiffresSeulement: false,
        longueurMax: 18
      },
      format: "^(VR|HRB|HRA|GNR|PR)[0-9]{1,7}[A-Z]{0,2}$",
      cle: "AUCUNE",
      masque: "",
      affichage: {
        motif: "^([A-Z]+)([0-9]+)([A-Z]*)$",
        remplacement: "$1 $2 $3"
      },
      exemple: "VR 12345",
      ressemble: "^(VR|HRB|HRA|GNR|PR)?[\\s.0-9]*[A-Z]{0,2}$",
      urlRecherche: "https://www.handelsregister.de/rp_web/normalesuche/welcome.xhtml",
      urlRegistreNumero: null,
      registre: null,
      tribunaux: {
        liste: [
          "Aachen",
          "Altenburg",
          "Amberg",
          "Ansbach",
          "Apolda",
          "Arnsberg",
          "Arnstadt",
          "Arnstadt Zweigstelle Ilmenau",
          "Aschaffenburg",
          "Augsburg",
          "Aurich",
          "Bad Hersfeld",
          "Bad Homburg v.d.H.",
          "Bad Kreuznach",
          "Bad Oeynhausen",
          "Bad Salzungen",
          "Bamberg",
          "Bayreuth",
          "Berlin (Charlottenburg)",
          "Bielefeld",
          "Bochum",
          "Bonn",
          "Braunschweig",
          "Bremen",
          "Chemnitz",
          "Coburg",
          "Coesfeld",
          "Cottbus",
          "Darmstadt",
          "Deggendorf",
          "Dortmund",
          "Dresden",
          "Duisburg",
          "D\xFCren",
          "D\xFCsseldorf",
          "Eisenach",
          "Erfurt",
          "Eschwege",
          "Essen",
          "Flensburg",
          "Frankfurt am Main",
          "Frankfurt (Oder)",
          "Freiburg",
          "Friedberg",
          "Fritzlar",
          "Fulda",
          "F\xFCrth",
          "Gelsenkirchen",
          "Gera",
          "Gie\xDFen",
          "Gotha",
          "G\xF6ttingen",
          "Greiz",
          "G\xFCtersloh",
          "Hagen",
          "Hamburg",
          "Hamm",
          "Hanau",
          "Hannover",
          "Heilbad Heiligenstadt",
          "Hildburghausen",
          "Hildesheim",
          "Hof",
          "Homburg",
          "Ingolstadt",
          "Iserlohn",
          "Jena",
          "Kaiserslautern",
          "Kassel",
          "Kempten (Allg\xE4u)",
          "Kiel",
          "Kleve",
          "Koblenz",
          "K\xF6ln",
          "K\xF6nigstein",
          "Korbach",
          "Krefeld",
          "Landau",
          "Landshut",
          "Langenfeld",
          "Lebach",
          "Leipzig",
          "Lemgo",
          "Limburg",
          "L\xFCbeck",
          "Ludwigshafen a.Rhein (Ludwigshafen)",
          "L\xFCneburg",
          "Mainz",
          "Mannheim",
          "Marburg",
          "Meiningen",
          "Memmingen",
          "Merzig",
          "M\xF6nchengladbach",
          "Montabaur",
          "M\xFChlhausen",
          "M\xFCnchen",
          "M\xFCnster",
          "Neubrandenburg",
          "Neunkirchen",
          "Neuruppin",
          "Neuss",
          "Nordhausen",
          "N\xFCrnberg",
          "Offenbach am Main",
          "Oldenburg (Oldenburg)",
          "Osnabr\xFCck",
          "Ottweiler",
          "Paderborn",
          "Passau",
          "Pinneberg",
          "P\xF6\xDFneck",
          "P\xF6\xDFneck Zweigstelle Bad Lobenstein",
          "Potsdam",
          "Recklinghausen",
          "Regensburg",
          "Rostock",
          "Rudolstadt",
          "Saarbr\xFCcken",
          "Saarlouis",
          "Schweinfurt",
          "Schwerin",
          "Siegburg",
          "Siegen",
          "S\xF6mmerda",
          "Sondershausen",
          "Sonneberg",
          "Stadthagen",
          "Stadtroda",
          "Steinfurt",
          "Stendal",
          "St. Ingbert (St Ingbert)",
          "Stralsund",
          "Straubing",
          "Stuttgart",
          "St. Wendel (St Wendel)",
          "Suhl",
          "Tostedt",
          "Traunstein",
          "Ulm",
          "V\xF6lklingen",
          "Walsrode",
          "Weiden i. d. OPf.",
          "Weimar",
          "Wetzlar",
          "Wiesbaden",
          "Wittlich",
          "Wuppertal",
          "W\xFCrzburg",
          "Zweibr\xFCcken"
        ],
        orthographeStripe: {
          "Frankfurt (Oder)": "Frankfurt/Oder"
        }
      },
      formesNonLucratives: {
        EV: "Eingetragener Verein (e.V., association inscrite)",
        GGMBH: "Gemeinn\xFCtzige GmbH (gGmbH, soci\xE9t\xE9 d'int\xE9r\xEAt g\xE9n\xE9ral)",
        STIFTUNG: "Stiftung (fondation)",
        KDOER: "K\xF6rperschaft des \xF6ffentlichen Rechts (corporation de droit public)"
      },
      libelles: {
        nom: {
          fr: "Num\xE9ro de registre (Vereinsregister ou Handelsregister)",
          en: "Register number (Vereinsregister or Handelsregister)",
          de: "Registernummer (Vereinsregister oder Handelsregister)",
          es: "N\xFAmero de registro (Vereinsregister o Handelsregister)",
          it: "Numero di registro (Vereinsregister o Handelsregister)",
          nl: "Registernummer (Vereinsregister of Handelsregister)"
        },
        ligne: {
          fr: "{numero}",
          en: "{numero}",
          de: "{numero}",
          es: "{numero}",
          it: "{numero}",
          nl: "{numero}"
        },
        votreNumero: {
          fr: "votre num\xE9ro de registre",
          en: "your register number",
          de: "Ihre Registernummer",
          es: "su n\xFAmero de registro",
          it: "il vostro numero di registro",
          nl: "uw registernummer"
        },
        aide: {
          fr: "Le num\xE9ro de votre association au registre de son tribunal (Amtsgericht) : \xAB VR \xBB suivi de chiffres pour une association inscrite (e.V.), \xAB HRB \xBB pour une gGmbH, par exemple VR 12345 B. Il figure sur votre extrait de registre. Notre \xE9quipe le v\xE9rifie avant d'ouvrir votre page.",
          en: "Your association's number in its court's register (Amtsgericht): \u201CVR\u201D followed by digits for a registered association (e.V.), \u201CHRB\u201D for a gGmbH, for example VR 12345 B. It is shown on your register extract. Our team checks it before opening your page.",
          de: "Die Nummer Ihres Vereins im Register seines Amtsgerichts: \u201EVR\u201C gefolgt von Ziffern f\xFCr einen eingetragenen Verein (e.V.), \u201EHRB\u201C f\xFCr eine gGmbH, zum Beispiel VR 12345 B. Sie steht auf Ihrem Registerauszug. Unser Team pr\xFCft sie, bevor Ihre Seite freigeschaltet wird.",
          es: "El n\xFAmero de su asociaci\xF3n en el registro de su tribunal (Amtsgericht): \xABVR\xBB seguido de cifras para una asociaci\xF3n inscrita (e.V.), \xABHRB\xBB para una gGmbH, por ejemplo VR 12345 B. Figura en su extracto del registro. Nuestro equipo lo comprueba antes de abrir su p\xE1gina.",
          it: "Il numero della vostra associazione nel registro del suo tribunale (Amtsgericht): \xABVR\xBB seguito da cifre per un'associazione registrata (e.V.), \xABHRB\xBB per una gGmbH, ad esempio VR 12345 B. Figura nel vostro estratto del registro. Il nostro team lo verifica prima di aprire la vostra pagina.",
          nl: "Het nummer van uw vereniging in het register van haar rechtbank (Amtsgericht): \u201EVR\u201D gevolgd door cijfers voor een ingeschreven vereniging (e.V.), \u201EHRB\u201D voor een gGmbH, bijvoorbeeld VR 12345 B. Het staat op uw registeruittreksel. Ons team controleert het voordat uw pagina opengaat."
        },
        invalide: {
          fr: "Num\xE9ro invalide : le type de registre puis des chiffres, par exemple VR 12345 ou VR 12345 B (association), HRB 1234 (gGmbH).",
          en: "Invalid number: the register type then digits, for example VR 12345 or VR 12345 B (association), HRB 1234 (gGmbH).",
          de: "Ung\xFCltige Nummer: die Registerart und dann Ziffern, zum Beispiel VR 12345 oder VR 12345 B (Verein), HRB 1234 (gGmbH).",
          es: "N\xFAmero no v\xE1lido: el tipo de registro y luego cifras, por ejemplo VR 12345 o VR 12345 B (asociaci\xF3n), HRB 1234 (gGmbH).",
          it: "Numero non valido: il tipo di registro e poi cifre, ad esempio VR 12345 o VR 12345 B (associazione), HRB 1234 (gGmbH).",
          nl: "Ongeldig nummer: het registertype en dan cijfers, bijvoorbeeld VR 12345 of VR 12345 B (vereniging), HRB 1234 (gGmbH)."
        },
        trouver: {
          fr: "Trouver mon num\xE9ro de registre",
          en: "Find my register number",
          de: "Meine Registernummer finden",
          es: "Encontrar mi n\xFAmero de registro",
          it: "Trovare il mio numero di registro",
          nl: "Mijn registernummer vinden"
        },
        verifie: {
          fr: "V\xE9rifi\xE9 au registre du tribunal (Amtsgericht)",
          en: "Checked in the court register (Amtsgericht)",
          de: "Im Register des Amtsgerichts gepr\xFCft",
          es: "Comprobado en el registro del tribunal (Amtsgericht)",
          it: "Verificato nel registro del tribunale (Amtsgericht)",
          nl: "Gecontroleerd in het register van de rechtbank (Amtsgericht)"
        },
        enAttente: {
          fr: "Enregistr\xE9 : notre \xE9quipe le v\xE9rifie au registre du tribunal.",
          en: "Saved: our team is checking it in the court register.",
          de: "Gespeichert: Unser Team pr\xFCft sie im Register des Amtsgerichts.",
          es: "Registrado: nuestro equipo lo est\xE1 comprobando en el registro del tribunal.",
          it: "Registrato: il nostro team lo sta verificando nel registro del tribunale.",
          nl: "Opgeslagen: ons team controleert het in het register van de rechtbank."
        },
        pageEnAttente: {
          fr: "Votre num\xE9ro de registre est enregistr\xE9 : votre page publique s'ouvrira d\xE8s que notre \xE9quipe l'aura v\xE9rifi\xE9.",
          en: "Your register number is saved: your public page will open as soon as our team has checked it.",
          de: "Ihre Registernummer ist gespeichert: Ihre \xF6ffentliche Seite wird freigeschaltet, sobald unser Team sie gepr\xFCft hat.",
          es: "Su n\xFAmero de registro est\xE1 registrado: su p\xE1gina p\xFAblica se abrir\xE1 en cuanto nuestro equipo lo haya comprobado.",
          it: "Il vostro numero di registro \xE8 registrato: la vostra pagina pubblica si aprir\xE0 appena il nostro team l'avr\xE0 verificato.",
          nl: "Uw registernummer is opgeslagen: uw openbare pagina gaat open zodra ons team het heeft gecontroleerd."
        },
        zeroCommission: {
          fr: "Les associations allemandes (e.V.) et fondations ne paient aucune commission : indiquez votre num\xE9ro de registre.",
          en: "German associations (e.V.) and foundations pay no commission: enter your register number.",
          de: "Deutsche Vereine (e.V.) und Stiftungen zahlen keine Provision: Geben Sie Ihre Registernummer ein.",
          es: "Las asociaciones alemanas (e.V.) y las fundaciones no pagan ninguna comisi\xF3n: indique su n\xFAmero de registro.",
          it: "Le associazioni tedesche (e.V.) e le fondazioni non pagano alcuna commissione: indicate il vostro numero di registro.",
          nl: "Duitse verenigingen (e.V.) en stichtingen betalen geen commissie: vul uw registernummer in."
        },
        avantages: {
          fr: "0% de commission Donoor pour les associations (e.V.) et fondations",
          en: "0% Donoor commission for associations (e.V.) and foundations",
          de: "0 % Donoor-Provision f\xFCr Vereine (e.V.) und Stiftungen",
          es: "0 % de comisi\xF3n Donoor para asociaciones (e.V.) y fundaciones",
          it: "0% di commissione Donoor per associazioni (e.V.) e fondazioni",
          nl: "0% Donoor-commissie voor verenigingen (e.V.) en stichtingen"
        },
        consigneRecherche: {
          fr: "Tapez le nom de votre association, puis indiquez son num\xE9ro de registre et son tribunal.",
          en: "Type your association's name, then enter its register number and its court.",
          de: "Geben Sie den Namen Ihres Vereins ein und dann seine Registernummer und sein Amtsgericht.",
          es: "Escriba el nombre de su asociaci\xF3n y luego indique su n\xFAmero de registro y su tribunal.",
          it: "Digitate il nome della vostra associazione, poi indicate il suo numero di registro e il suo tribunale.",
          nl: "Typ de naam van uw vereniging en vul daarna haar registernummer en rechtbank in."
        },
        placeholderRecherche: {
          fr: "Nom de votre association",
          en: "Your association's name",
          de: "Name Ihres Vereins",
          es: "Nombre de su asociaci\xF3n",
          it: "Nome della vostra associazione",
          nl: "Naam van uw vereniging"
        },
        tribunal: {
          fr: "Tribunal du registre (Amtsgericht)",
          en: "Register court (Amtsgericht)",
          de: "Registergericht (Amtsgericht)",
          es: "Tribunal del registro (Amtsgericht)",
          it: "Tribunale del registro (Amtsgericht)",
          nl: "Registerrechtbank (Amtsgericht)"
        },
        tribunalAide: {
          fr: "Le tribunal qui tient votre registre, indiqu\xE9 sur votre extrait (par exemple \xAB Berlin (Charlottenburg) \xBB).",
          en: "The court that keeps your register, shown on your extract (for example \u201CBerlin (Charlottenburg)\u201D).",
          de: "Das Amtsgericht, das Ihr Register f\xFChrt, wie auf Ihrem Auszug angegeben (zum Beispiel \u201EBerlin (Charlottenburg)\u201C).",
          es: "El tribunal que lleva su registro, indicado en su extracto (por ejemplo \xABBerlin (Charlottenburg)\xBB).",
          it: "Il tribunale che tiene il vostro registro, indicato nel vostro estratto (ad esempio \xABBerlin (Charlottenburg)\xBB).",
          nl: "De rechtbank die uw register bijhoudt, zoals op uw uittreksel staat (bijvoorbeeld \u201EBerlin (Charlottenburg)\u201D)."
        },
        tribunalInvalide: {
          fr: "Choisissez le tribunal de votre registre dans la liste.",
          en: "Choose your register's court from the list.",
          de: "W\xE4hlen Sie das Registergericht aus der Liste.",
          es: "Elija el tribunal de su registro en la lista.",
          it: "Scegliete il tribunale del vostro registro dall'elenco.",
          nl: "Kies de rechtbank van uw register uit de lijst."
        },
        tribunalRecherche: {
          fr: "Rechercher un tribunal",
          en: "Search for a court",
          de: "Amtsgericht suchen",
          es: "Buscar un tribunal",
          it: "Cerca un tribunale",
          nl: "Rechtbank zoeken"
        },
        ligneTribunal: {
          fr: "Amtsgericht {tribunal}",
          en: "Amtsgericht {tribunal}",
          de: "Amtsgericht {tribunal}",
          es: "Amtsgericht {tribunal}",
          it: "Amtsgericht {tribunal}",
          nl: "Amtsgericht {tribunal}"
        }
      }
    }
  }
};

// src/numerosOfficiels.ts
var TYPES = numerosOfficiels_default.types;
var TYPES_NUMERO_OFFICIEL = Object.keys(TYPES);
function estTypeNumeroOfficiel(valeur) {
  return typeof valeur === "string" && Object.prototype.hasOwnProperty.call(TYPES, valeur);
}
function descripteur(type) {
  return estTypeNumeroOfficiel(type) ? TYPES[type] : null;
}
function typeNumeroOfficielDuPays(pays) {
  const c = (pays ?? "").trim().toUpperCase();
  if (!c) return null;
  const trouve = TYPES_NUMERO_OFFICIEL.find((t) => TYPES[t].pays === c);
  return trouve ?? null;
}
function registreDuNumeroOfficiel(type) {
  return descripteur(type)?.registre ?? null;
}
var CLES = {
  /** Belgique : les deux derniers chiffres valent 97 − (les huit premiers modulo 97). */
  MOD97_BE: (n) => 97 - Number(n.slice(0, 8)) % 97 === Number(n.slice(8)),
  AUCUNE: () => true
};
function normaliserNumeroOfficiel(type, saisie) {
  const d = descripteur(type);
  let s = (saisie ?? "").toUpperCase().trim();
  if (!d) return s;
  for (const prefixe of d.saisie.prefixesPays) {
    if (s.startsWith(prefixe)) {
      s = s.slice(prefixe.length);
      break;
    }
  }
  s = d.saisie.chiffresSeulement ? s.replace(/[^0-9]/g, "") : s.replace(/[\s.\-/]/g, "");
  const c = d.saisie.completerAGauche;
  if (c && s.length === c.depuis) s = s.padStart(c.jusqua, c.avec);
  return s;
}
function estNumeroOfficielValide(type, saisie) {
  const d = descripteur(type);
  if (!d) return false;
  const n = normaliserNumeroOfficiel(type, saisie);
  if (!new RegExp(d.format).test(n)) return false;
  const cle = CLES[d.cle];
  return cle ? cle(n) : false;
}
function formaterNumeroOfficiel(type, saisie) {
  const d = descripteur(type);
  if (!d) return (saisie ?? "").trim();
  const n = normaliserNumeroOfficiel(type, saisie);
  if (!d.masque) {
    if (!estNumeroOfficielValide(type, saisie)) return (saisie ?? "").trim();
    if (!d.affichage) return n;
    return n.replace(new RegExp(d.affichage.motif), d.affichage.remplacement).replace(/\s+/g, " ").trim();
  }
  const places = [...d.masque].filter((c) => c === "#").length;
  if (n.length !== places || d.saisie.chiffresSeulement && !/^[0-9]+$/.test(n)) return (saisie ?? "").trim();
  let i = 0;
  return [...d.masque].map((c) => c === "#" ? n[i++] : c).join("");
}
function ressembleNumeroOfficiel(type, saisie) {
  const d = descripteur(type);
  const s = (saisie ?? "").trim().toUpperCase();
  return !!d && s.length > 0 && new RegExp(d.ressemble).test(s) && /[0-9]/.test(s);
}
function codeForme(d, code) {
  const c = (code ?? "").trim();
  const longueur = Object.keys(d.formesNonLucratives)[0]?.length ?? 0;
  return /^[0-9]+$/.test(c) && c.length <= longueur ? c.padStart(longueur, "0") : c;
}
function estFormeNonLucrative(type, code) {
  const d = descripteur(type);
  if (!d || !(code ?? "").trim()) return false;
  return Object.prototype.hasOwnProperty.call(d.formesNonLucratives, codeForme(d, code));
}
function libelleFormeNonLucrative(type, code) {
  const d = descripteur(type);
  if (!d) return null;
  return d.formesNonLucratives[codeForme(d, code)] ?? null;
}
function formesNonLucratives(type) {
  return descripteur(type)?.formesNonLucratives ?? {};
}
function libelleNumeroOfficiel(type, cle, langue = "fr", valeurs = {}) {
  const textes6 = descripteur(type)?.libelles[cle];
  if (!textes6) return "";
  let texte = textes6[langue] ?? textes6.fr ?? "";
  if (valeurs.numero !== void 0) texte = texte.replace("{numero}", valeurs.numero);
  if (valeurs.tribunal !== void 0) texte = texte.replace("{tribunal}", valeurs.tribunal);
  return texte;
}
function exempleNumeroOfficiel(type) {
  return descripteur(type)?.exemple ?? "";
}
function urlRechercheNumeroOfficiel(type) {
  return descripteur(type)?.urlRecherche ?? null;
}
function urlRegistrePourNumero(type, numero) {
  const d = descripteur(type);
  if (!d) return null;
  const n = type ? normaliserNumeroOfficiel(type, numero) : "";
  if (!d.urlRegistreNumero || !n) return d.urlRecherche ?? null;
  return d.urlRegistreNumero.replace("{numero}", encodeURIComponent(n));
}
function tribunalRequisNumeroOfficiel(type) {
  return (descripteur(type)?.tribunaux?.liste.length ?? 0) > 0;
}
function tribunauxNumeroOfficiel(type) {
  return descripteur(type)?.tribunaux?.liste ?? [];
}
function estTribunalNumeroOfficiel(type, tribunal) {
  return !!tribunal && tribunauxNumeroOfficiel(type).includes(tribunal);
}
function tribunalPourStripe(type, tribunal) {
  if (!estTribunalNumeroOfficiel(type, tribunal)) return null;
  return descripteur(type)?.tribunaux?.orthographeStripe[tribunal] ?? tribunal;
}
function longueurMaxNumeroOfficiel(type) {
  return descripteur(type)?.saisie.longueurMax ?? 40;
}
function saisieNumeriqueNumeroOfficiel(type) {
  return descripteur(type)?.saisie.chiffresSeulement ?? false;
}

// src/profilPays.ts
var PROFILS = {
  FR: { code: "FR", paiements: true, paypal: true, recuFiscal: "cerfa", reductionImpotDons: 66 },
  // Pilote : identité belge en place, paiements pas encore ouverts.
  // Reçus fiscaux (30/10) : attestation 281.71 annuelle, Belcotax déposé par
  // l'association (recusFiscauxPays.ts).
  BE: { code: "BE", paiements: false, paypal: false, recuFiscal: "be_281_71", reductionImpotDons: 30 },
  // Luxembourg (25/10) : identité en place (numéro RCS, vérifié par l'équipe),
  // paiements ouverts depuis backup-manager → Pays. Les dons y sont DÉDUITS du
  // revenu imposable (dépenses spéciales, art. 112 L.I.R.) : pas de taux de
  // réduction d'impôt à annoncer.
  // Reçus fiscaux (30/10) : reçu de don après chaque don (art. 112 L.I.R.).
  LU: { code: "LU", paiements: false, paypal: false, recuFiscal: "lu_art112", reductionImpotDons: null },
  // Pays-Bas (01/11) : numéro KvK vérifié par l'équipe (pas d'accès à l'API
  // KvK pour une entreprise étrangère), iDEAL comme moyen local. Dons DÉDUITS
  // du revenu (giftenaftrek, au-delà de 1 % du revenu et 60 €) : pas de taux.
  // Reçus (01/11) : justificatif après chaque don à une ANBI (statut vérifié
  // dans la liste publique du fisc par le RSIN) — recusFiscauxPays.ts.
  NL: { code: "NL", paiements: false, paypal: false, recuFiscal: "nl_anbi", reductionImpotDons: null },
  // Allemagne (02/11) : numéro de registre + tribunal, vérifiés par l'équipe
  // (pas d'API gratuite). Dons DÉDUITS du revenu (Sonderausgaben, plafond
  // 20 %) : pas de taux. Reçus (03/11) : Zuwendungsbestätigung après chaque
  // don, au modèle officiel du BMF — recusFiscauxPays.ts.
  DE: { code: "DE", paiements: false, paypal: false, recuFiscal: "de_zuwendung", reductionImpotDons: null }
};
var PAYS_AVEC_INTERRUPTEURS = Object.keys(PROFILS);
function profilPays(code) {
  const c = (code ?? "").trim().toUpperCase() || PAYS_PAR_DEFAUT;
  const base = PROFILS[c] ?? { code: c, paiements: false, paypal: false, recuFiscal: null, reductionImpotDons: null };
  return { ...base, numeroOfficiel: typeNumeroOfficielDuPays(c) };
}
function paiementsOuvertsDansPays(code) {
  return profilPays(code).paiements;
}
function disponibilitePaiementsPays(code) {
  if (paiementsOuvertsDansPays(code)) return "ouverts";
  const c = (code ?? "").trim().toUpperCase() || PAYS_PAR_DEFAUT;
  return paysParCode(c) || PROFILS[c] ? "bientot" : "indisponibles";
}
function recusFiscauxDansPays(code) {
  return profilPays(code).recuFiscal !== null;
}
function paypalDansPays(code) {
  return profilPays(code).paypal;
}
function numeroOfficielDuPays(code) {
  return profilPays(code).numeroOfficiel;
}
var PAYS_AVEC_REGISTRE = TYPES_NUMERO_OFFICIEL.map((t) => registreDuNumeroOfficiel(t)).filter(
  (r) => r !== null
);
function registreBrancheDansPays(code) {
  return registreDuNumeroOfficiel(typeNumeroOfficielDuPays(code)) !== null;
}
var PAYS_OUVERTS_PAIEMENTS = Object.values(PROFILS).filter((p) => p.paiements).map((p) => p.code);

// src/legal-form.ts
var CATEGORIES_JURIDIQUES_ELIGIBLES = /* @__PURE__ */ new Set([
  "9240",
  // Congrégation
  "9300",
  // Fondation (recouvre aussi les fonds de dotation)
  "7430"
  // Établissement public des cultes d'Alsace-Lorraine
]);
var CATEGORIES_JURIDIQUES_RECHERCHE = [
  "9210",
  // Association non déclarée
  "9220",
  // Association déclarée
  "9221",
  // Association déclarée « entreprises d'insertion par l'économique »
  "9222",
  // Association intermédiaire
  "9223",
  // Groupement d'employeurs
  "9230",
  // Association déclarée reconnue d'utilité publique
  "9240",
  // Congrégation
  "9260",
  // Association de droit local (Alsace-Moselle)
  "9300",
  // Fondation, fonds de dotation
  "7430"
  // Établissement public des cultes d'Alsace-Lorraine
];
function estFormeJuridiqueEligible(natureJuridique) {
  if (!natureJuridique) return false;
  const code = natureJuridique.trim();
  if (code.startsWith("92")) return true;
  return CATEGORIES_JURIDIQUES_ELIGIBLES.has(code);
}
function estEligibleSansRnaPossible(natureJuridique) {
  if (!natureJuridique) return false;
  const code = natureJuridique.trim();
  return code === "9260" || code === "9300" || code === "7430";
}
function aUneIdentiteOfficielle(rna, siret) {
  return typeof rna === "string" && rna.trim().length > 0 || typeof siret === "string" && siret.trim().length > 0;
}
var LIBELLES_FORME_JURIDIQUE = {
  fr: {
    "9260": "association de droit local (Alsace-Moselle)",
    "9300": "fondation ou fonds de dotation",
    "7430": "\xE9tablissement public des cultes d'Alsace-Lorraine",
    "9240": "congr\xE9gation",
    defaut: "association"
  },
  en: {
    "9260": "local-law organisation (Alsace-Moselle)",
    "9300": "foundation or endowment fund",
    "7430": "public religious institution (Alsace-Lorraine)",
    "9240": "congregation",
    defaut: "organisation"
  },
  de: {
    "9260": "Organisation nach Ortsrecht (Elsass-Mosel)",
    "9300": "Stiftung oder Dotationsfonds",
    "7430": "\xF6ffentliche Kultuseinrichtung (Elsass-Lothringen)",
    "9240": "Kongregation",
    defaut: "Organisation"
  },
  es: {
    "9260": "organizaci\xF3n de derecho local (Alsacia-Mosela)",
    "9300": "fundaci\xF3n o fondo de dotaci\xF3n",
    "7430": "entidad p\xFAblica de culto (Alsacia-Lorena)",
    "9240": "congregaci\xF3n",
    defaut: "organizaci\xF3n"
  },
  it: {
    "9260": "organizzazione di diritto locale (Alsazia-Mosella)",
    "9300": "fondazione o fondo di dotazione",
    "7430": "ente pubblico di culto (Alsazia-Lorena)",
    "9240": "congregazione",
    defaut: "organizzazione"
  },
  nl: {
    "9260": "organisatie naar plaatselijk recht (Alsace-Moselle)",
    "9300": "stichting of schenkingsfonds",
    "7430": "publieke religieuze instelling (Alsace-Lorraine)",
    "9240": "congregatie",
    defaut: "organisatie"
  }
};
function libelleFormeJuridique(natureJuridique, langue = "fr") {
  const code = (natureJuridique ?? "").trim();
  const table = LIBELLES_FORME_JURIDIQUE[langue] ?? LIBELLES_FORME_JURIDIQUE.fr;
  switch (code) {
    case "9260":
      return table["9260"];
    case "9300":
      return table["9300"];
    case "7430":
      return table["7430"];
    case "9240":
      return table["9240"];
    default:
      return code.startsWith("92") ? table.defaut : null;
  }
}
function familleJuridiquePaiement(natureJuridique) {
  const code = (natureJuridique ?? "").trim();
  if (!/^\d{4}$/.test(code)) return null;
  if (code.startsWith("92")) return "association";
  if (code === "9300" || code === "7430" || code.startsWith("83") || code.startsWith("84")) {
    return "non_lucratif";
  }
  if (code.startsWith("5") || code.startsWith("62") || code.startsWith("65")) {
    return "societe";
  }
  return null;
}
function typeStripeDepuisFormeJuridique(natureJuridique) {
  const famille = familleJuridiquePaiement(natureJuridique);
  if (famille === "association" || famille === "non_lucratif") return "non_profit";
  if (famille === "societe") return "company";
  return null;
}
function typePaypalDepuisFormeJuridique(natureJuridique) {
  const famille = familleJuridiquePaiement(natureJuridique);
  if (famille === "association") {
    return {
      type: "NONPROFIT",
      subtype: (natureJuridique ?? "").trim() === "9210" ? "ASSO_TYPE_NON_INCORPORATED" : "ASSO_TYPE_INCORPORATED"
    };
  }
  if (famille === "non_lucratif") return { type: "NONPROFIT" };
  if (famille === "societe") return { type: "CORPORATION" };
  return null;
}

// src/registresPays.ts
var TERRITOIRES_FRANCAIS = /* @__PURE__ */ new Set(["GP", "MQ", "GF", "RE", "YT", "PM", "BL", "MF", "WF", "PF", "NC"]);
function paysSuggereDepuisConnexion(code) {
  const c = (code ?? "").trim().toUpperCase();
  if (TERRITOIRES_FRANCAIS.has(c)) return "FR";
  return /^[A-Z]{2}$/.test(c) && c !== "XX" && c !== "T1" ? c : "FR";
}
function aUnNumeroOfficielVerifie(org) {
  return !!org?.numero_officiel && !!org.numero_officiel_type && !!org.numero_officiel_verifie_at;
}
function exonereParNumeroOfficiel(org) {
  if (!aUnNumeroOfficielVerifie(org)) return false;
  return estFormeNonLucrative(org?.numero_officiel_type, org?.numero_officiel_forme);
}
function numeroOfficielAffiche(org) {
  if (!org?.numero_officiel) return "";
  const type = org.numero_officiel_type;
  const numero = type ? formaterNumeroOfficiel(type, org.numero_officiel) : org.numero_officiel;
  if (!type || !tribunalRequisNumeroOfficiel(type) || !org.numero_officiel_tribunal) return numero;
  return `${numero} \xB7 ${libelleNumeroOfficiel(type, "ligneTribunal", "fr", { tribunal: org.numero_officiel_tribunal })}`;
}
function aUneIdentiteOfficielleConnue(org) {
  return aUneIdentiteOfficielle(org?.rna, org?.siret) || aUnNumeroOfficielVerifie(org);
}

// src/recusFiscauxPays.ts
var REGLES = {
  cerfa: {
    regime: "cerfa",
    pays: "FR",
    frequence: "par_don",
    avantage: "reduction",
    tauxReduction: 66,
    economieMaxPourcent: null,
    seuilAnnuelParAssociation: null,
    seuilAnnuelTousOrganismes: null,
    billetsEtAdhesionsDeductibles: true,
    identifiantDonateur: null,
    agrement: "rescrit_facultatif",
    depotAdministration: null
  },
  // Art. 145/33 CIR 92 : 30 % depuis les dons du 01/01/2025 (45 % avant),
  // 40 € par an et par institution ; attestation 281.71 annuelle déposée via
  // Belcotax-on-web au plus tard le 28 février (brochure Belcotax 2025).
  be_281_71: {
    regime: "be_281_71",
    pays: "BE",
    frequence: "annuel",
    avantage: "reduction",
    tauxReduction: 30,
    economieMaxPourcent: null,
    seuilAnnuelParAssociation: 40,
    seuilAnnuelTousOrganismes: null,
    billetsEtAdhesionsDeductibles: false,
    identifiantDonateur: "numero_national_be",
    agrement: "agrement_spf",
    depotAdministration: { service: "belcotax", moisLimite: 2, jourLimite: 28 }
  },
  // Art. 112 L.I.R. : dépenses spéciales, 120 € par an tous organismes
  // confondus (circulaire L.I.R. n° 112/1 du 08/11/2021, ACD). Économie au
  // mieux : tranche la plus haute du barème (42 %) majorée de 9 % pour le
  // fonds pour l'emploi (impotsdirects.public.lu, « Fonds pour l'emploi »)
  // = 45,78 %.
  lu_art112: {
    regime: "lu_art112",
    pays: "LU",
    frequence: "par_don",
    avantage: "deduction",
    tauxReduction: null,
    economieMaxPourcent: 45.78,
    seuilAnnuelParAssociation: null,
    seuilAnnuelTousOrganismes: 120,
    billetsEtAdhesionsDeductibles: false,
    identifiantDonateur: null,
    agrement: "reconnaissance_lu",
    depotAdministration: null
  },
  // Pays-Bas (01/11/2026, belastingdienst.nl « Gift aftrekken ») : un don à
  // une ANBI est déduit du revenu au-delà de 1 % du revenu de référence, avec
  // un MINIMUM de 60 € (seuil propre à chaque donateur : seulement annoncé),
  // plafond 10 %. Aucun reçu officiel exigé, rien à déposer au fisc : le
  // justificatif après chaque don sert de preuve écrite. Dons en espèces,
  // billets et cotisations : jamais (seul le don ajouté). Économie au mieux :
  // 37,56 % en 2026 — les déductions sont plafonnées au taux de la 2e tranche,
  // même pour les hauts revenus (belastingdienst.nl, « Afbouw tarief
  // aftrekposten als u een hoog inkomen hebt »).
  nl_anbi: {
    regime: "nl_anbi",
    pays: "NL",
    frequence: "par_don",
    avantage: "deduction",
    tauxReduction: null,
    economieMaxPourcent: 37.56,
    seuilAnnuelParAssociation: null,
    seuilAnnuelTousOrganismes: 60,
    billetsEtAdhesionsDeductibles: false,
    identifiantDonateur: null,
    agrement: "anbi",
    depotAdministration: null
  },
  // Allemagne (03/11/2026) : un don à une organisation exonérée (§ 5 Abs. 1
  // Nr. 9 KStG) est DÉDUIT du revenu (Sonderausgaben, § 10b EStG, plafond
  // 20 % du revenu), sur présentation d'une Zuwendungsbestätigung au modèle
  // officiel du BMF (07/11/2013, toujours en vigueur : formulaire 034122).
  // Aucun seuil propre aux dons (sous 300 €, un relevé bancaire suffit au
  // donateur ; le reçu reste utile). Billets : jamais (contrepartie).
  // Cotisations : déductibles pour une mosquée, mais Donoor ne certifie que
  // les dons (un reçu de cotisation suit d'autres règles selon les buts).
  // Économie au mieux : taux marginal le plus haut (45 %) majoré de la
  // contribution de solidarité (5,5 % de l'impôt) = 47,475 % ; l'impôt
  // d'Église n'est pas compté (les mosquées n'en perçoivent pas).
  de_zuwendung: {
    regime: "de_zuwendung",
    pays: "DE",
    frequence: "par_don",
    avantage: "deduction",
    tauxReduction: null,
    economieMaxPourcent: 47.48,
    seuilAnnuelParAssociation: null,
    seuilAnnuelTousOrganismes: null,
    billetsEtAdhesionsDeductibles: false,
    identifiantDonateur: null,
    agrement: "freistellung_de",
    depotAdministration: null
  }
};
function regleRecuFiscal(regime) {
  return REGLES[regime];
}
function regleRecuFiscalPays(code) {
  const regime = profilPays(code).recuFiscal;
  return regime ? REGLES[regime] : null;
}
var REGIMES_RECU_FISCAL = Object.keys(REGLES);
function billetsEtAdhesionsDeductiblesDansPays(code) {
  return regleRecuFiscalPays(code)?.billetsEtAdhesionsDeductibles ?? false;
}
function tauxReductionAnnonce(code, taux75 = false) {
  const r = regleRecuFiscalPays(code);
  if (!r || r.avantage !== "reduction" || r.tauxReduction === null) return null;
  return r.regime === "cerfa" && taux75 ? 75 : r.tauxReduction;
}
var CATEGORIES_AGREMENT_BE = {
  "01": "art. 145/33, 1\xB0, a \u2014 universit\xE9s et h\xF4pitaux universitaires",
  "02": "art. 145/33, 1\xB0, b \u2014 recherche scientifique",
  "03": "art. 145/33, 1\xB0, c \u2014 centres publics d'action sociale (CPAS)",
  "04": "art. 145/33, 1\xB0, d \u2014 institutions culturelles",
  "05": "art. 145/33, 1\xB0, e \u2014 aide aux victimes de guerre, personnes handicap\xE9es, personnes \xE2g\xE9es, mineurs prot\xE9g\xE9s, personnes d\xE9munies",
  "06": "art. 145/33, 1\xB0, f \u2014 Croix-Rouge, Fondation Roi Baudouin, Child Focus\u2026",
  "07": "art. 145/33, 1\xB0, g \u2014 aide aux victimes de calamit\xE9s",
  "08": "art. 145/33, 1\xB0, h \u2014 entreprises de travail adapt\xE9",
  "09": "art. 145/33, 1\xB0, i \u2014 protection de la nature et de l'environnement",
  "10": "art. 145/33, 1\xB0, j \u2014 monuments et sites",
  "11": "art. 145/33, 1\xB0, k \u2014 refuges pour animaux",
  "12": "art. 145/33, 1\xB0, l \u2014 d\xE9veloppement durable",
  "13": "art. 145/33, 2\xB0 \u2014 aide aux pays en d\xE9veloppement",
  "14": "art. 145/33, 3\xB0 \u2014 aide aux victimes d'accidents industriels majeurs",
  "16": "art. 145/33, 4\xB0, a \u2014 mus\xE9es (dons en argent)",
  "17": "art. 145/33, 4\xB0, b \u2014 mus\xE9es (\u0153uvres d'art)",
  "18": "plusieurs articles"
};
var CODES_AGREMENT_BE = Object.keys(CATEGORIES_AGREMENT_BE).sort();
function estCategorieAgrementBe(valeur) {
  return typeof valeur === "string" && Object.prototype.hasOwnProperty.call(CATEGORIES_AGREMENT_BE, valeur);
}
var RECONNAISSANCES_LU = {
  asbl_utilite_publique: "ASBL reconnue d'utilit\xE9 publique (art. 26-2 de la loi du 21 avril 1928)",
  fondation: "Fondation (art. 27 et suivants de la loi du 21 avril 1928)",
  loi_speciale: "Organisme reconnu d'utilit\xE9 publique par une loi sp\xE9ciale",
  ongd_agreee: "ONGD agr\xE9\xE9e par le minist\xE8re des Affaires \xE9trang\xE8res et europ\xE9ennes",
  sis: "Soci\xE9t\xE9 d'impact soci\xE9tal (SIS) agr\xE9\xE9e"
};
function estReconnaissanceLu(valeur) {
  return typeof valeur === "string" && Object.prototype.hasOwnProperty.call(RECONNAISSANCES_LU, valeur);
}
function normaliserRsin(saisie) {
  return (saisie ?? "").replace(/[^0-9]/g, "");
}
function estRsinValide(saisie) {
  const n = normaliserRsin(saisie);
  if (!/^[0-9]{9}$/.test(n) || /^0+$/.test(n)) return false;
  let total = 0;
  for (let i = 0; i < 8; i++) total += Number(n[i]) * (9 - i);
  total -= Number(n[8]);
  return total % 11 === 0;
}
function normaliserNumeroNational(saisie) {
  return (saisie ?? "").replace(/[^0-9]/g, "");
}
function joursDansMois(annee, mois) {
  return new Date(Date.UTC(annee, mois, 0)).getUTCDate();
}
function estNumeroNationalBelgeValide(saisie) {
  const n = normaliserNumeroNational(saisie);
  if (!/^[0-9]{11}$/.test(n)) return false;
  const neuf = n.slice(0, 9);
  const cle = Number(n.slice(9));
  const compteur = Number(n.slice(6, 9));
  if (compteur === 0 || compteur === 999) return false;
  const avant2000 = 97 - Number(neuf) % 97 === cle;
  const depuis2000 = 97 - Number("2" + neuf) % 97 === cle;
  if (!avant2000 && !depuis2000) return false;
  let mois = Number(n.slice(2, 4));
  const jour = Number(n.slice(4, 6));
  if (mois >= 40) mois -= 40;
  else if (mois >= 20) mois -= 20;
  if (mois > 12 || jour > 31) return false;
  if (mois > 0 && jour > 0) {
    const annee = (depuis2000 && !avant2000 ? 2e3 : 1900) + Number(n.slice(0, 2));
    if (jour > joursDansMois(annee, mois)) return false;
  }
  return true;
}
function formaterNumeroNational(saisie) {
  const n = normaliserNumeroNational(saisie);
  if (n.length !== 11) return (saisie ?? "").trim();
  return `${n.slice(0, 2)}.${n.slice(2, 4)}.${n.slice(4, 6)}-${n.slice(6, 9)}.${n.slice(9)}`;
}
function masquerNumeroNational(saisie) {
  const n = normaliserNumeroNational(saisie);
  if (n.length !== 11) return "";
  return `${n.slice(0, 2)}.${n.slice(2, 4)}.${n.slice(4, 6)}-\u2022\u2022\u2022.\u2022\u2022`;
}
function lireIdentifiantDonateurBe(saisie, nature) {
  if (nature === "personne_morale") {
    return estNumeroOfficielValide("BE_BCE", saisie) ? { nature, numero: normaliserNumeroOfficiel("BE_BCE", saisie) } : null;
  }
  return estNumeroNationalBelgeValide(saisie) ? { nature, numero: normaliserNumeroNational(saisie) } : null;
}
function masquerIdentifiantDonateurBe(id) {
  return id.nature === "personne_morale" ? formaterNumeroOfficiel("BE_BCE", id.numero) : masquerNumeroNational(id.numero);
}
var ZWECKE_DE = {
  "52_01": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 1 AO", objet: "von Wissenschaft und Forschung" },
  "52_02": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 2 AO", objet: "der Religion" },
  "52_03": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 3 AO", objet: "des \xF6ffentlichen Gesundheitswesens und der \xF6ffentlichen Gesundheitspflege" },
  "52_04": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 4 AO", objet: "der Jugend- und Altenhilfe" },
  "52_05": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 5 AO", objet: "von Kunst und Kultur" },
  "52_06": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 6 AO", objet: "des Denkmalschutzes und der Denkmalpflege" },
  "52_07": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 7 AO", objet: "der Erziehung, Volks- und Berufsbildung einschlie\xDFlich der Studentenhilfe" },
  "52_08": {
    loi: "\xA7 52 Abs. 2 Satz 1 Nr. 8 AO",
    objet: "des Naturschutzes und der Landschaftspflege, des Umweltschutzes, einschlie\xDFlich des Klimaschutzes, des K\xFCstenschutzes und des Hochwasserschutzes"
  },
  "52_09": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 9 AO", objet: "des Wohlfahrtswesens" },
  "52_10": {
    loi: "\xA7 52 Abs. 2 Satz 1 Nr. 10 AO",
    objet: "der Hilfe f\xFCr politisch, rassistisch oder religi\xF6s Verfolgte, f\xFCr Fl\xFCchtlinge, Vertriebene, Aussiedler, Sp\xE4taussiedler, Kriegsopfer, Kriegshinterbliebene, Kriegsbesch\xE4digte und Kriegsgefangene, Zivilbesch\xE4digte und Behinderte sowie der Hilfe f\xFCr Opfer von Straftaten"
  },
  "52_11": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 11 AO", objet: "der Rettung aus Lebensgefahr" },
  "52_12": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 12 AO", objet: "des Feuer-, Arbeits-, Katastrophen- und Zivilschutzes sowie der Unfallverh\xFCtung" },
  "52_13": {
    loi: "\xA7 52 Abs. 2 Satz 1 Nr. 13 AO",
    objet: "internationaler Gesinnung, der Toleranz auf allen Gebieten der Kultur und des V\xF6lkerverst\xE4ndigungsgedankens"
  },
  "52_14": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 14 AO", objet: "des Tierschutzes" },
  "52_15": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 15 AO", objet: "der Entwicklungszusammenarbeit" },
  "52_16": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 16 AO", objet: "von Verbraucherberatung und Verbraucherschutz" },
  "52_17": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 17 AO", objet: "der F\xFCrsorge f\xFCr Strafgefangene und ehemalige Strafgefangene" },
  "52_18": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 18 AO", objet: "der Gleichberechtigung von Frauen und M\xE4nnern" },
  "52_19": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 19 AO", objet: "des Schutzes von Ehe und Familie" },
  "52_20": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 20 AO", objet: "der Kriminalpr\xE4vention" },
  "52_21": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 21 AO", objet: "des Sports", cotisationsNonDeductibles: true },
  "52_22": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 22 AO", objet: "der Heimatpflege, Heimatkunde und der Ortsversch\xF6nerung", cotisationsNonDeductibles: true },
  "52_23": {
    loi: "\xA7 52 Abs. 2 Satz 1 Nr. 23 AO",
    objet: "der Tierzucht, der Pflanzenzucht, der Kleing\xE4rtnerei, des traditionellen Brauchtums einschlie\xDFlich des Karnevals, der Fastnacht und des Faschings, der Soldaten- und Reservistenbetreuung, des Amateurfunkens, des Freifunks, des Modellflugs und des Hundesports",
    cotisationsNonDeductibles: true
  },
  "52_24": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 24 AO", objet: "des demokratischen Staatswesens" },
  "52_25": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 25 AO", objet: "des b\xFCrgerschaftlichen Engagements zugunsten gemeinn\xFCtziger, mildt\xE4tiger und kirchlicher Zwecke" },
  "52_26": {
    loi: "\xA7 52 Abs. 2 Satz 1 Nr. 26 AO",
    objet: "der Unterhaltung und Pflege von Friedh\xF6fen und der Unterhaltung von Gedenkst\xE4tten f\xFCr nichtbestattungspflichtige Kinder und F\xF6ten"
  },
  "52_27": { loi: "\xA7 52 Abs. 2 Satz 1 Nr. 27 AO", objet: "wohngemeinn\xFCtziger Zwecke" },
  "53": { loi: "\xA7 53 AO", objet: "mildt\xE4tiger Zwecke" },
  "54": { loi: "\xA7 54 AO", objet: "kirchlicher Zwecke" }
};
var CODES_ZWECKE_DE = Object.keys(ZWECKE_DE).sort();
function estZweckDe(valeur) {
  return typeof valeur === "string" && Object.prototype.hasOwnProperty.call(ZWECKE_DE, valeur);
}
function libelleZweckDe(code) {
  return `F\xF6rderung ${ZWECKE_DE[code].objet}`;
}
function objetsZweckeDe(codes) {
  const objets = CODES_ZWECKE_DE.filter((c) => codes.includes(c)).map((c) => ZWECKE_DE[c].objet);
  if (objets.length <= 1) return objets[0] ?? "";
  return `${objets.slice(0, -1).join(", ")} und ${objets[objets.length - 1]}`;
}
function mentionCotisationDe(codes) {
  return CODES_ZWECKE_DE.some(
    (c) => codes.includes(c) && "cotisationsNonDeductibles" in ZWECKE_DE[c] && ZWECKE_DE[c].cotisationsNonDeductibles === true
  );
}
var BESCHEID_ARTEN_DE = ["freistellung", "feststellung_60a", "oeffentliches_recht"];
function estBescheidArtDe(valeur) {
  return typeof valeur === "string" && BESCHEID_ARTEN_DE.includes(valeur);
}
function dureeValiditeBescheidDe(art) {
  return art === "freistellung" ? 5 : art === "feststellung_60a" ? 3 : null;
}
function finValiditeBescheidDe(art, dateBescheid) {
  const ans = dureeValiditeBescheidDe(art);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateBescheid ?? "");
  if (ans === null || !m) return null;
  const annee = Number(m[1]) + ans;
  const mois = Number(m[2]);
  const jour = Math.min(Number(m[3]), joursDansMois(annee, mois));
  return `${annee}-${String(mois).padStart(2, "0")}-${String(jour).padStart(2, "0")}`;
}
function normaliserSteuernummer(saisie) {
  return (saisie ?? "").trim().replace(/\s+/g, " ").replace(/\s*\/\s*/g, "/");
}
function estSteuernummerValide(saisie) {
  const n = normaliserSteuernummer(saisie);
  if (!/^[0-9][0-9 /]*[0-9]$/.test(n)) return false;
  const chiffres = n.replace(/[^0-9]/g, "").length;
  return chiffres === 10 || chiffres === 11 || chiffres === 13;
}
var UNITES_DE = [
  "",
  "ein",
  "zwei",
  "drei",
  "vier",
  "f\xFCnf",
  "sechs",
  "sieben",
  "acht",
  "neun",
  "zehn",
  "elf",
  "zw\xF6lf",
  "dreizehn",
  "vierzehn",
  "f\xFCnfzehn",
  "sechzehn",
  "siebzehn",
  "achtzehn",
  "neunzehn"
];
var DIZAINES_DE = ["", "", "zwanzig", "drei\xDFig", "vierzig", "f\xFCnfzig", "sechzig", "siebzig", "achtzig", "neunzig"];
function sousCentDe(n) {
  if (n < 20) return UNITES_DE[n] ?? "";
  const u = n % 10;
  const d = DIZAINES_DE[Math.floor(n / 10)] ?? "";
  return u === 0 ? d : `${UNITES_DE[u]}und${d}`;
}
function sousMilleDe(n) {
  const c = Math.floor(n / 100);
  return `${c > 0 ? `${UNITES_DE[c]}hundert` : ""}${sousCentDe(n % 100)}`;
}
function nombreEnLettresAllemand(n) {
  if (!Number.isInteger(n) || n < 0 || n >= 1e9) throw new RangeError(`nombre hors limites : ${n}`);
  if (n === 0) return "null";
  const millions = Math.floor(n / 1e6);
  const milliers = Math.floor(n % 1e6 / 1e3);
  const reste = n % 1e3;
  const parties = [];
  if (millions > 0) parties.push(millions === 1 ? "eine Million" : `${sousMilleDe(millions)} Millionen`);
  let fin = `${milliers > 0 ? `${sousMilleDe(milliers)}tausend` : ""}${sousMilleDe(reste)}`;
  if (reste % 100 === 1) fin = `${fin.slice(0, -3)}eins`;
  if (fin) parties.push(fin);
  return parties.join(" ");
}
function montantEnLettresAllemand(centimes) {
  if (!Number.isInteger(centimes) || centimes < 0) throw new RangeError(`montant invalide : ${centimes}`);
  const euros = Math.floor(centimes / 100);
  const cents = centimes % 100;
  const motEuros = euros === 1 ? "ein" : nombreEnLettresAllemand(euros);
  const partieEuros = `${motEuros} Euro`;
  if (cents === 0) return partieEuros;
  return `${partieEuros} und ${cents === 1 ? "ein" : nombreEnLettresAllemand(cents)} Cent`;
}

// src/moyensPaiementLocaux.ts
var PAYS_BANCONTACT_SEPA = [
  "AT",
  "AU",
  "BE",
  "BG",
  "CA",
  "CH",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GB",
  "GI",
  "GR",
  "HK",
  "HR",
  "HU",
  "IE",
  "IT",
  "JP",
  "LI",
  "LT",
  "LU",
  "LV",
  "MT",
  "MX",
  "NL",
  "NO",
  "NZ",
  "PL",
  "PT",
  "RO",
  "SE",
  "SG",
  "SI",
  "SK",
  "US"
];
var PAYS_REVOLUT_PAY = [
  "AT",
  "BE",
  "BG",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GB",
  "GR",
  "HR",
  "HU",
  "IE",
  "IT",
  "LI",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK"
];
var PAYS_WERO = [
  "AT",
  "BE",
  "BG",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GR",
  "HR",
  "HU",
  "IE",
  "IS",
  "IT",
  "LI",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK"
];
var MOYENS_PAIEMENT_LOCAUX = {
  bancontact: {
    /** Pays dont les associations peuvent le proposer (là où Stripe l'accepte). */
    pays: PAYS_BANCONTACT_SEPA,
    /** Pays où il est ALLUMÉ tant que l'association ne l'éteint pas (le moyen n° 1 en Belgique). */
    paysParDefaut: ["BE"],
    /** Capacité Stripe à demander au compte de l'association. */
    capaciteStripe: "bancontact_payments",
    /** Nom affiché (marque, identique dans toutes les langues). */
    nom: "Bancontact",
    /**
     * Frais Stripe par pays du compte (France et Allemagne : tarif en
     * pourcentage ; stripe.com/de/pricing/local-payment-methods, relevé le
     * 03/11/2026).
     */
    fraisStripe: {
      BE: { pourcentage: 0, fixeCentimes: 35 },
      LU: { pourcentage: 0, fixeCentimes: 35 },
      NL: { pourcentage: 0, fixeCentimes: 35 },
      FR: { pourcentage: 1.4, fixeCentimes: 25 },
      DE: { pourcentage: 1.4, fixeCentimes: 25 }
    },
    /** Numéro officiel de l'association affiché sur la page de don ? Non exigé. */
    identifiantOfficielAffiche: false,
    /** Le nom demandé est celui du titulaire de la CARTE Bancontact. */
    titulaire: "carte",
    parcours: "redirection",
    /** Dons ponctuels seulement (la carte reste là pour le mensuel). */
    mensuel: false
  },
  /**
   * iDEAL | Wero (Pays-Bas, 01/11/2026) : moyen de paiement de la plupart des
   * Néerlandais. Nom imposé par Stripe depuis le 1er trimestre 2026 (iDEAL
   * racheté par Wero, bascule vers Wero en 2026-2027 gérée par Stripe).
   * Règles iDEAL (docs.stripe.com/payments/ideal) : le numéro KvK de
   * l'association est affiché sur la page de don, et le nom du compte
   * connecté est celui de l'association. La catégorie interdite « financement participatif
   * par dons » vise le MÉTIER du commerçant : ici le commerçant est
   * l'association elle-même (charges directes, MCC caritatif / religieux /
   * associatif), jamais une plateforme de cagnottes pour particuliers.
   */
  ideal: {
    /**
     * Associations NÉERLANDAISES seulement (Karim, 02/11/2026) : iDEAL ne sert
     * qu'aux donateurs qui ont une banque aux Pays-Bas ; ailleurs, Wero.
     */
    pays: ["NL"],
    paysParDefaut: ["NL"],
    capaciteStripe: "ideal_payments",
    nom: "iDEAL | Wero",
    fraisStripe: {
      NL: { pourcentage: 0, fixeCentimes: 29 }
    },
    /** Exigé par iDEAL : le numéro KvK de l'association est affiché sur la page de don. */
    identifiantOfficielAffiche: true,
    /** iDEAL passe par le COMPTE bancaire : nom du titulaire du compte. */
    titulaire: "compte",
    parcours: "redirection",
    mensuel: false
  },
  /**
   * Wero (01/11/2026) : l'application de paiement des banques européennes
   * (donateurs en Belgique, France, Allemagne, Luxembourg, Pays-Bas). Stripe
   * l'accepte avec notre version de l'API (2025-09-30.clover), testé le
   * 01/11/2026 : capacité `wero_payments`, paiement par redirection
   * (`stripe.confirmWeroPayment`). Pas de don mensuel, 0,50 € minimum, pas
   * de contestation possible.
   * ÉTEINT par défaut partout : le paiement en ligne avec Wero démarre à
   * peine ; l'association l'allume si elle le souhaite, et Donoor demande
   * alors la capacité à Stripe (jamais à la création du compte).
   */
  wero: {
    pays: PAYS_WERO,
    paysParDefaut: [],
    capaciteStripe: "wero_payments",
    nom: "Wero",
    /**
     * Stripe ne publie pas de tarif pour Wero seul (relevé le 01/11/2026 :
     * seul « iDEAL | Wero » figure) : aucun montant n'est annoncé, et la
     * part de Donoor reste la part normale.
     */
    fraisStripe: {},
    identifiantOfficielAffiche: false,
    /** Wero passe par le COMPTE bancaire du donateur. */
    titulaire: "compte",
    parcours: "redirection",
    mensuel: false
  },
  /**
   * Prélèvement SEPA (Karim, 02/11/2026) : le donateur donne son IBAN et
   * accepte le mandat (formulaire de Stripe, texte du mandat compris) ; le
   * don est confirmé quelques jours plus tard (≈ 6 jours ouvrés). Sert aux
   * dons ponctuels ET mensuels — le gros intérêt : 0,35 € par don au lieu
   * de 1,5 % + 0,25 € par carte, et plus de carte expirée qui arrête un don
   * mensuel. Le donateur peut contester sans motif pendant 8 semaines.
   * Éteint par défaut ; ouvert à toutes les associations (décision du
   * 02/11/2026), frais affichés clairement dans les réglages.
   */
  sepa_debit: {
    pays: PAYS_BANCONTACT_SEPA,
    paysParDefaut: [],
    capaciteStripe: "sepa_debit_payments",
    /** Nom en français ; les écrans le traduisent (« SEPA Direct Debit », « SEPA-Lastschrift »…). */
    nom: "Pr\xE9l\xE8vement SEPA",
    /**
     * stripe.com/{fr,fr-be,fr-lu,nl}/pricing, relevé le 02/11/2026, et
     * stripe.com/de/pricing le 03/11/2026 : mêmes tarifs dans les 5 pays.
     */
    fraisStripe: {
      FR: { pourcentage: 0, fixeCentimes: 35, contestationCentimes: 1500, echecCentimes: 350 },
      BE: { pourcentage: 0, fixeCentimes: 35, contestationCentimes: 1500, echecCentimes: 350 },
      LU: { pourcentage: 0, fixeCentimes: 35, contestationCentimes: 1500, echecCentimes: 350 },
      NL: { pourcentage: 0, fixeCentimes: 35, contestationCentimes: 1500, echecCentimes: 350 },
      DE: { pourcentage: 0, fixeCentimes: 35, contestationCentimes: 1500, echecCentimes: 350 }
    },
    identifiantOfficielAffiche: false,
    /** Le titulaire du COMPTE prélevé. */
    titulaire: "compte",
    parcours: "prelevement",
    /** Dons ponctuels ET mensuels. */
    mensuel: true
  },
  /**
   * Revolut Pay (Karim, 02/11/2026) : l'application Revolut, partout en
   * Europe. Paiement par redirection (application ou QR code), comme Wero.
   * Éteint par défaut.
   */
  revolut_pay: {
    pays: PAYS_REVOLUT_PAY,
    paysParDefaut: [],
    capaciteStripe: "revolut_pay_payments",
    nom: "Revolut Pay",
    /** stripe.com/{fr,fr-be,fr-lu,nl}/pricing, relevé le 02/11/2026 ; stripe.com/de/pricing le 03/11/2026. */
    fraisStripe: {
      FR: { pourcentage: 1.5, fixeCentimes: 25 },
      BE: { pourcentage: 1.5, fixeCentimes: 25 },
      LU: { pourcentage: 1.5, fixeCentimes: 25 },
      NL: { pourcentage: 1.5, fixeCentimes: 25 },
      DE: { pourcentage: 1.5, fixeCentimes: 25 }
    },
    identifiantOfficielAffiche: false,
    titulaire: "compte",
    parcours: "redirection",
    mensuel: false
  }
};
var MOYENS = Object.keys(MOYENS_PAIEMENT_LOCAUX);
function estMoyenPaiementLocal(valeur) {
  return typeof valeur === "string" && MOYENS.includes(valeur);
}
var codePays = (pays) => (pays ?? "").trim().toUpperCase() || PAYS_PAR_DEFAUT;
function moyensPaiementLocauxDuPays(pays) {
  const code = codePays(pays);
  return MOYENS.filter((m) => MOYENS_PAIEMENT_LOCAUX[m].pays.includes(code));
}
function moyenLocalActifParDefaut(moyen, pays) {
  return MOYENS_PAIEMENT_LOCAUX[moyen].paysParDefaut.includes(codePays(pays));
}
function fraisStripeMoyenLocal(moyen, pays) {
  return MOYENS_PAIEMENT_LOCAUX[moyen].fraisStripe[codePays(pays)] ?? null;
}
function moyenLocalMensuel(moyen) {
  return MOYENS_PAIEMENT_LOCAUX[moyen].mensuel;
}
function parcoursMoyenLocal(moyen) {
  return MOYENS_PAIEMENT_LOCAUX[moyen].parcours;
}
function identifiantOfficielExige(moyens) {
  return moyens.some((m) => MOYENS_PAIEMENT_LOCAUX[m].identifiantOfficielAffiche);
}
function capacitesStripeLocales(pays) {
  return moyensPaiementLocauxDuPays(pays).filter((m) => moyenLocalActifParDefaut(m, pays)).map((m) => MOYENS_PAIEMENT_LOCAUX[m].capaciteStripe);
}
function lireReglagePaiementComplet(valeur) {
  let brut = valeur;
  if (typeof brut === "string") {
    try {
      brut = JSON.parse(brut);
    } catch {
      return {};
    }
  }
  if (!brut || typeof brut !== "object" || Array.isArray(brut)) return {};
  const reglage = {};
  for (const [cle, actif] of Object.entries(brut)) {
    if (typeof actif === "boolean") reglage[cle] = actif;
  }
  return reglage;
}
function lireReglageMoyensLocaux(valeur) {
  const reglage = {};
  for (const [cle, actif] of Object.entries(lireReglagePaiementComplet(valeur))) {
    if (estMoyenPaiementLocal(cle)) reglage[cle] = actif;
  }
  return reglage;
}
var CLE_LINK = "link";
function linkAllume(valeur) {
  return lireReglagePaiementComplet(valeur)[CLE_LINK] !== false;
}
function moyenLocalAllume(moyen, reglage, pays) {
  return reglage?.[moyen] ?? moyenLocalActifParDefaut(moyen, pays);
}
function moyensLocauxAllumes(pays, reglage) {
  return moyensPaiementLocauxDuPays(pays).filter((m) => moyenLocalAllume(m, reglage, pays));
}
function moyensPaiementLocauxActifs(pays, capacites) {
  return moyensPaiementLocauxDuPays(pays).filter((m) => capacites?.[MOYENS_PAIEMENT_LOCAUX[m].capaciteStripe] === "active");
}
function partDonoorMoyenLocalCentimes(moyen, pays, montantCentimes, netViseCentimes, partNormaleCentimes) {
  const frais = fraisStripeMoyenLocal(moyen, pays);
  if (frais === null) return partNormaleCentimes;
  const fraisCentimes = Math.round(montantCentimes * frais.pourcentage / 100) + frais.fixeCentimes;
  const reste = montantCentimes - fraisCentimes - netViseCentimes;
  return Math.max(partNormaleCentimes, Math.min(reste, montantCentimes));
}

// src/documentsDemandes.ts
var DOCUMENTS_FRANCE_SEULE = [
  "rna_certificate",
  "journal_officiel",
  "siret_certificate",
  "tax_exemption_certificate"
];
var DOCUMENTS_HORS_FRANCE_SEULS = ["registre_extrait"];
function documentDemandablePourPays(type, pays) {
  const france = ((pays ?? "").trim().toUpperCase() || PAYS_PAR_DEFAUT) === PAYS_PAR_DEFAUT;
  if (DOCUMENTS_FRANCE_SEULE.includes(type)) return france;
  if (DOCUMENTS_HORS_FRANCE_SEULS.includes(type)) return !france;
  return true;
}
function documentsImpossiblesPourPays(types, pays) {
  return (types ?? []).filter((t) => !documentDemandablePourPays(t, pays));
}

// src/conditionsVente.ts
function enFrance(pays) {
  return ((pays ?? "").trim().toUpperCase() || PAYS_PAR_DEFAUT) === PAYS_PAR_DEFAUT;
}
function modeleCgvDonoorDansPays(pays) {
  return enFrance(pays);
}
function mediateurObligatoireDansPays(pays) {
  return enFrance(pays);
}

// src/adressesPubliques.ts
var ACTION_TURNSTILE_ADRESSES = "adresse";
var DUREE_SESSION_ADRESSES_PUBLIQUES_S = 1800;
var RE_JETON_ADRESSES_PUBLIQUES = /^[A-Za-z0-9_-]{43}$/;
var URL_SCRIPT_TURNSTILE = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
var ATTENTE_JETON_ADRESSES_MS = 1500;
var REESSAI_TURNSTILE_MIN_MS = 6e4;
var RENOUVELLEMENT_SESSION_ADRESSES_MS = 6e4;
var DELAI_TURNSTILE_MS = 4e3;
function estJetonAdressesPubliques(x) {
  return typeof x === "string" && RE_JETON_ADRESSES_PUBLIQUES.test(x);
}
function jetonSessionRecherche() {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") {
    try {
      return c.randomUUID();
    } catch {
    }
  }
  const octets = new Uint8Array(16);
  if (c && typeof c.getRandomValues === "function") c.getRandomValues(octets);
  else for (let i = 0; i < 16; i++) octets[i] = Math.floor(Math.random() * 256);
  octets[6] = octets[6] & 15 | 64;
  octets[8] = octets[8] & 63 | 128;
  const hex = Array.from(octets, (o) => o.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
var ID_CONTENEUR_TURNSTILE = "donoor-turnstile-adresses";
function creerClientTurnstile(environnement = environnementNavigateur) {
  let script = null;
  let widget = null;
  let enCours = null;
  let attente = null;
  const conclure = (jeton) => {
    const a = attente;
    attente = null;
    a?.(jeton);
  };
  const chargerScript = (env) => {
    if (env.global.turnstile) return Promise.resolve(true);
    script ??= new Promise((resolve) => {
      const el = env.document.createElement("script");
      el.src = URL_SCRIPT_TURNSTILE;
      el.async = true;
      el.defer = true;
      el.onload = () => resolve(!!env.global.turnstile);
      el.onerror = () => {
        script = null;
        resolve(false);
      };
      env.document.head.appendChild(el);
    });
    return script;
  };
  const conteneur = (env) => {
    const el = env.document.createElement("div");
    el.id = ID_CONTENEUR_TURNSTILE;
    el.setAttribute("aria-hidden", "true");
    el.style.cssText = "position:fixed;left:-10000px;top:0;width:1px;height:1px;overflow:hidden;";
    env.document.body.appendChild(el);
    return el;
  };
  const resoudre = async (options) => {
    const env = environnement();
    const sitekey = (options.sitekey ?? "").trim();
    if (!env || !sitekey) return null;
    const action = options.action ?? ACTION_TURNSTILE_ADRESSES;
    const delaiMs = options.delaiMs ?? DELAI_TURNSTILE_MS;
    let minuterie;
    const delai = new Promise((resolve) => {
      minuterie = setTimeout(() => resolve(null), delaiMs);
    });
    let finie = false;
    const travail = (async () => {
      if (!await chargerScript(env) || finie) return null;
      const ts = env.global.turnstile;
      if (!ts) return null;
      const jeton = new Promise((resolve) => {
        attente = resolve;
      });
      if (!widget || widget.sitekey !== sitekey || widget.action !== action) {
        const id = ts.render(conteneur(env), {
          sitekey,
          action,
          execution: "execute",
          // Aucun nouvel essai ni rafraîchissement automatique : la session
          // décide (au plus un essai par minute), et le jeton est échangé
          // aussitôt reçu.
          retry: "never",
          "refresh-expired": "manual",
          callback: (t) => conclure(typeof t === "string" && t ? t : null),
          "error-callback": () => conclure(null),
          "expired-callback": () => conclure(null),
          "timeout-callback": () => conclure(null)
        });
        if (typeof id !== "string" || !id) {
          conclure(null);
          return jeton;
        }
        widget = { sitekey, action, id };
      }
      ts.execute(widget.id);
      return jeton;
    })().catch(() => null);
    try {
      return await Promise.race([travail, delai]);
    } finally {
      finie = true;
      clearTimeout(minuterie);
      conclure(null);
      if (widget) {
        try {
          env.global.turnstile?.reset(widget.id);
        } catch {
          widget = null;
        }
      }
    }
  };
  return {
    obtenir(options) {
      enCours ??= resoudre(options).finally(() => {
        enCours = null;
      });
      return enCours;
    }
  };
}
function environnementNavigateur() {
  const g = globalThis;
  if (typeof g.document === "undefined" || !g.document?.body) return null;
  return { document: g.document, global: g };
}
var clientParDefaut = null;
function obtenirJetonTurnstile(options) {
  clientParDefaut ??= creerClientTurnstile();
  return clientParDefaut.obtenir(options);
}
function creerSessionAdressesPubliques({
  obtenirTurnstile,
  ouvrir,
  maintenant = Date.now
}) {
  let jeton = null;
  let expireA = 0;
  let enCours = null;
  let derniereTentativeA = null;
  const actuel = () => jeton && expireA > maintenant() ? jeton : null;
  const aRenouveler = () => !jeton || expireA - maintenant() <= RENOUVELLEMENT_SESSION_ADRESSES_MS;
  const essaiPermis = () => derniereTentativeA === null || maintenant() - derniereTentativeA >= REESSAI_TURNSTILE_MIN_MS;
  const echanger = async () => {
    try {
      const turnstile = await obtenirTurnstile();
      if (!turnstile) return null;
      const r = await ouvrir(turnstile);
      if (!r || !estJetonAdressesPubliques(r.jeton)) return null;
      const duree = Number(r.expireDans);
      if (!Number.isFinite(duree) || duree <= 0) return null;
      jeton = r.jeton;
      expireA = maintenant() + duree * 1e3;
      return jeton;
    } catch {
      return null;
    } finally {
      derniereTentativeA = maintenant();
    }
  };
  const lancer = () => {
    if (enCours) return enCours;
    if (!essaiPermis()) return null;
    enCours = echanger().finally(() => {
      enCours = null;
    });
    return enCours;
  };
  return {
    async jeton({ attendreMs = ATTENTE_JETON_ADRESSES_MS } = {}) {
      if (!aRenouveler()) return actuel();
      const echange = lancer();
      const courant = actuel();
      if (courant || !echange) return courant;
      let minuterie;
      const delai = new Promise((resolve) => {
        minuterie = setTimeout(() => resolve(null), Math.max(0, attendreMs));
      });
      try {
        return await Promise.race([echange, delai]);
      } finally {
        clearTimeout(minuterie);
      }
    },
    prechauffer() {
      if (aRenouveler()) void lancer();
    },
    invalider() {
      jeton = null;
      expireA = 0;
    },
    actuel
  };
}

// src/listeFlottante.ts
var HAUTEUR_LISTE_SUGGESTIONS_MAX = 280;
var ECART_LISTE_SUGGESTIONS = 4;
var MARGE_LISTE_SUGGESTIONS = 8;
var PLANCHER_LISTE_SUGGESTIONS = 96;
function placerListeSuggestions(champ, zone, options = {}) {
  const hauteurMax = options.hauteurMax ?? HAUTEUR_LISTE_SUGGESTIONS_MAX;
  const ecart = options.ecart ?? ECART_LISTE_SUGGESTIONS;
  const marge = options.marge ?? MARGE_LISTE_SUGGESTIONS;
  const plancher = options.plancher ?? PLANCHER_LISTE_SUGGESTIONS;
  const placeBas = zone.bas - champ.bottom - ecart - marge;
  const placeHaut = champ.top - zone.haut - ecart - marge;
  let cote;
  if (placeBas >= hauteurMax) cote = "bas";
  else if (placeHaut >= hauteurMax) cote = "haut";
  else cote = placeBas >= placeHaut ? "bas" : "haut";
  const place = cote === "bas" ? placeBas : placeHaut;
  const hauteur = Math.min(hauteurMax, Math.max(plancher, Math.floor(place)));
  return {
    cote,
    top: cote === "bas" ? champ.bottom + ecart : null,
    bottom: cote === "haut" ? zone.hauteurFenetre - champ.top + ecart : null,
    left: champ.left,
    width: champ.width,
    hauteurMax: hauteur
  };
}
function placementListeInitial() {
  return { cote: "bas", top: 0, bottom: null, left: 0, width: 0, hauteurMax: HAUTEUR_LISTE_SUGGESTIONS_MAX };
}
function zoneVisibleFenetre(fenetre) {
  const hauteurFenetre = fenetre.innerHeight;
  const visuelle = fenetre.visualViewport;
  if (!visuelle || !(visuelle.height > 0)) {
    return { haut: 0, bas: hauteurFenetre, hauteurFenetre };
  }
  const haut = Math.max(0, visuelle.offsetTop);
  const bas = Math.min(hauteurFenetre, haut + visuelle.height);
  return { haut, bas, hauteurFenetre };
}
function styleListeSuggestions(p) {
  const style = {
    left: `${p.left}px`,
    width: `${p.width}px`,
    maxHeight: `${p.hauteurMax}px`
  };
  if (p.top !== null) style.top = `${p.top}px`;
  if (p.bottom !== null) style.bottom = `${p.bottom}px`;
  return style;
}

// src/nomOrganisation.ts
function estNomProvisoire(nom) {
  const n = (nom ?? "").trim();
  return n === "" || /^Organisation de\s/i.test(n);
}

// src/rna.ts
var RNA_PATTERN = /^W[0-9A-Z]{9}$/;
function normalizeRna(value) {
  return (value ?? "").replace(/[\s-]/g, "").toUpperCase();
}
function isValidRnaFormat(value) {
  return RNA_PATTERN.test(normalizeRna(value));
}
function looksLikeRnaPrefix(value) {
  const normalized = normalizeRna(value);
  return /^W[0-9A-Z]{0,9}$/.test(normalized) && normalized.length > 0;
}

// src/themes.ts
var THEMES = [
  { key: "animaux", label: "Animaux", icon: "i-lucide-paw-print" },
  { key: "arts_culture", label: "Arts & Culture", icon: "i-lucide-palette" },
  { key: "artisanat_cuisine", label: "Artisanat & Cuisine", icon: "i-lucide-chef-hat" },
  { key: "bien_etre", label: "Bien-\xEAtre", icon: "i-lucide-flower-2" },
  { key: "citoyennete", label: "Citoyennet\xE9 & Droits", icon: "i-lucide-scale" },
  { key: "ecologie", label: "\xC9cologie & Environnement", icon: "i-lucide-leaf" },
  { key: "education", label: "\xC9ducation", icon: "i-lucide-graduation-cap" },
  { key: "enfance_jeunesse", label: "Enfance & Jeunesse", icon: "i-lucide-baby" },
  { key: "humanitaire", label: "Humanitaire", icon: "i-lucide-globe" },
  { key: "loisirs", label: "Loisirs", icon: "i-lucide-dice-5" },
  { key: "musique", label: "Musique", icon: "i-lucide-music" },
  { key: "patrimoine", label: "Patrimoine", icon: "i-lucide-landmark" },
  { key: "reseaux_pros", label: "R\xE9seaux pros & \xE9tudiants", icon: "i-lucide-briefcase" },
  { key: "sante", label: "Sant\xE9", icon: "i-lucide-stethoscope" },
  { key: "sciences", label: "Sciences & Technologie", icon: "i-lucide-flask-conical" },
  { key: "solidarite", label: "Solidarit\xE9 & Action sociale", icon: "i-lucide-heart-handshake" },
  { key: "spiritualite", label: "Spiritualit\xE9 & Culte", icon: "i-lucide-moon-star" },
  { key: "sport", label: "Sport", icon: "i-lucide-trophy" },
  { key: "autre", label: "Autre", icon: "i-lucide-sparkles" }
];
var THEME_PAR_DEFAUT = "autre";
var THEME_KEYS = new Set(THEMES.map((t) => t.key));
function estTheme(v) {
  return typeof v === "string" && THEME_KEYS.has(v);
}
function libelleTheme(v) {
  return THEMES.find((t) => t.key === v)?.label ?? "Autre";
}
var ANCIENNES_CATEGORIES = {
  // Associations
  cultuelle: "spiritualite",
  religieuse: "spiritualite",
  sportive: "sport",
  culturelle: "arts_culture",
  educative: "education",
  environnement: "ecologie",
  // Cagnottes
  aide_humanitaire: "humanitaire",
  orphelins: "humanitaire",
  eau_potable: "humanitaire",
  ramadan: "spiritualite",
  sadaqa: "spiritualite",
  // Anciennes listes (formulaires d'adhésion, premières associations)
  culture: "arts_culture",
  ong: "humanitaire",
  // Événements / adhésions
  culturel: "arts_culture",
  religieux: "spiritualite",
  educatif: "education",
  social: "solidarite"
};
var LIBELLE_ASSOCIATION = {
  animaux: "Association de protection animale",
  arts_culture: "Association culturelle",
  artisanat_cuisine: "Association d'artisanat et de cuisine",
  bien_etre: "Association de bien-\xEAtre",
  citoyennete: "Association citoyenne",
  ecologie: "Association environnementale",
  education: "Association \xE9ducative",
  enfance_jeunesse: "Association enfance et jeunesse",
  humanitaire: "Association humanitaire",
  loisirs: "Association de loisirs",
  musique: "Association musicale",
  patrimoine: "Association de patrimoine",
  reseaux_pros: "R\xE9seau professionnel et \xE9tudiant",
  sante: "Association de sant\xE9",
  sciences: "Association scientifique",
  solidarite: "Association solidaire",
  spiritualite: "Association cultuelle",
  sport: "Association sportive",
  autre: "Association"
};
function libelleAssociation(v) {
  return LIBELLE_ASSOCIATION[themeDepuisCategorie(v)];
}
var LIBELLE_ASSOCIATIONS = {
  animaux: "Associations de protection animale",
  arts_culture: "Associations culturelles",
  artisanat_cuisine: "Associations d'artisanat et de cuisine",
  bien_etre: "Associations de bien-\xEAtre",
  citoyennete: "Associations citoyennes",
  ecologie: "Associations environnementales",
  education: "Associations \xE9ducatives",
  enfance_jeunesse: "Associations enfance et jeunesse",
  humanitaire: "Associations humanitaires",
  loisirs: "Associations de loisirs",
  musique: "Associations musicales",
  patrimoine: "Associations de patrimoine",
  reseaux_pros: "R\xE9seaux professionnels et \xE9tudiants",
  sante: "Associations de sant\xE9",
  sciences: "Associations scientifiques",
  solidarite: "Associations solidaires",
  spiritualite: "Associations cultuelles",
  sport: "Associations sportives",
  autre: "Associations"
};
function libelleAssociations(v) {
  return LIBELLE_ASSOCIATIONS[themeDepuisCategorie(v)];
}
function slugTheme(theme) {
  return theme.replace(/_/g, "-");
}
function themeDepuisSlug(slug) {
  if (!slug) return null;
  const cle = slug.toLowerCase().replace(/-/g, "_");
  if (estTheme(cle)) return cle;
  return ANCIENNES_CATEGORIES[cle] ?? ANCIENNES_CATEGORIES[slug.toLowerCase()] ?? null;
}
function themeDepuisCategorie(v) {
  if (!v) return THEME_PAR_DEFAUT;
  if (estTheme(v)) return v;
  return ANCIENNES_CATEGORIES[v] ?? THEME_PAR_DEFAUT;
}
var TYPES_ACTIVITE = [
  { key: "ateliers", label: "Ateliers", image: "/recherche/activite-ateliers.webp" },
  { key: "competitions", label: "Comp\xE9titions", image: "/recherche/activite-competitions.webp" },
  { key: "concerts", label: "Concerts", image: "/recherche/activite-concerts.webp" },
  { key: "conferences", label: "Conf\xE9rences", image: "/recherche/activite-conferences.webp" },
  { key: "cours", label: "Cours", image: "/recherche/activite-cours.webp" },
  { key: "diners_galas", label: "D\xEEners & Galas", image: "/recherche/activite-diners-galas.webp" },
  { key: "fetes", label: "F\xEAtes", image: "/recherche/activite-fetes.webp" },
  { key: "formations", label: "Formations", image: "/recherche/activite-formations.webp" },
  { key: "sorties", label: "Sorties", image: "/recherche/activite-sorties.webp" },
  { key: "spectacles", label: "Spectacles", image: "/recherche/activite-spectacles.webp" },
  { key: "autre", label: "Autre", image: "" }
];
var TYPE_KEYS = new Set(TYPES_ACTIVITE.map((t) => t.key));
function estTypeActivite(v) {
  return typeof v === "string" && TYPE_KEYS.has(v);
}
function libelleTypeActivite(v) {
  return TYPES_ACTIVITE.find((t) => t.key === v)?.label ?? "Autre";
}
var ANCIENNES_CATEGORIES_EVENEMENT = {
  conference: { theme: null, type: "conferences" },
  gala: { theme: null, type: "diners_galas" },
  diner: { theme: null, type: "diners_galas" },
  festival: { theme: null, type: "fetes" },
  kermesse: { theme: null, type: "fetes" },
  formation: { theme: "education", type: "formations" },
  sport: { theme: "sport", type: "competitions" },
  culturel: { theme: "arts_culture", type: "autre" },
  religieux: { theme: "spiritualite", type: "autre" },
  autre: { theme: null, type: "autre" }
};
var THEME_PAR_CODE = {
  // Musique, artisanat, sciences et langues, au sein de la culture
  "006030": "musique",
  "006090": "artisanat_cuisine",
  "006105": "sciences",
  "006110": "education",
  // Loisirs : animaux, cuisine, bien-être
  "007045": "animaux",
  "007050": "animaux",
  "007060": "artisanat_cuisine",
  "007085": "bien_etre",
  "007095": "sciences",
  // Action socioculturelle : jeunesse, échanges culturels
  "009005": "enfance_jeunesse",
  "009007": "arts_culture",
  "009010": "solidarite",
  "009015": "enfance_jeunesse",
  "009020": "enfance_jeunesse",
  "009025": "enfance_jeunesse",
  "009050": "arts_culture",
  // Droits de l'enfant
  "003012": "enfance_jeunesse",
  // Amicales : professionnels et étudiants
  "014025": "reseaux_pros",
  "014030": "reseaux_pros",
  // Éducation : étudiants et anciens élèves
  "015065": "reseaux_pros",
  "015070": "reseaux_pros",
  // Recherche : environnement
  "016025": "ecologie",
  // Santé : bien-être, vétérinaire
  "017085": "bien_etre",
  "017135": "bien_etre",
  "017300": "animaux",
  // Médico-social : petite enfance, adolescents
  "018005": "enfance_jeunesse",
  "018010": "enfance_jeunesse",
  // Interventions sociales : jeunes, catastrophes naturelles
  "019016": "enfance_jeunesse",
  "019030": "humanitaire",
  // Services familiaux : crèches
  "021005": "enfance_jeunesse",
  // Environnement : animaux
  "024025": "animaux",
  "024026": "animaux",
  // Emploi / vie locale : entrepreneuriat, développement durable
  "030015": "reseaux_pros",
  "030020": "reseaux_pros",
  "030050": "ecologie"
};
var THEME_PAR_FAMILLE = {
  "001": "citoyennete",
  // activités politiques
  "002": "citoyennete",
  // clubs, cercles de réflexion
  "003": "citoyennete",
  // défense de droits fondamentaux
  "004": "citoyennete",
  // justice
  "005": "arts_culture",
  // information, communication
  "006": "arts_culture",
  // culture, pratiques artistiques
  "007": "loisirs",
  // clubs de loisirs
  "009": "loisirs",
  // action socioculturelle
  "010": "patrimoine",
  // préservation du patrimoine
  "011": "sport",
  // sports, plein air
  "013": "loisirs",
  // chasse, pêche
  "014": "solidarite",
  // amicales, entraide
  "015": "education",
  // éducation, formation
  "016": "sciences",
  // recherche
  "017": "sante",
  // santé
  "018": "sante",
  // médico-social
  "019": "solidarite",
  // interventions sociales
  "020": "humanitaire",
  // caritatif, humanitaire
  "021": "solidarite",
  // services familiaux, personnes âgées
  "022": "reseaux_pros",
  // activités économiques
  "023": "reseaux_pros",
  // défense d'intérêts économiques
  "024": "ecologie",
  // environnement, cadre de vie
  "030": "solidarite",
  // emploi, développement local
  "032": "solidarite",
  // logement
  "034": "loisirs",
  // tourisme
  "036": "solidarite",
  // sécurité, protection civile
  "038": "patrimoine",
  // anciens combattants
  "040": "spiritualite"
  // religieux, spirituel, philosophique
};
function themeDepuisCodesRna(codes) {
  for (const brut of codes) {
    const code = (brut ?? "").trim();
    if (!/^\d{6}$/.test(code)) continue;
    const theme = THEME_PAR_CODE[code] ?? THEME_PAR_FAMILLE[code.slice(0, 3)];
    if (theme) return theme;
  }
  return null;
}
var MOTS_SPIRITUALITE = /\b(mosquee|mosque|masjid|masdjid|madrassa|madrasa|cultuel|cultuelle|paroisse|eglise|evangeli\w*|synagogue|diocese|chapelle|pagode)\b/;
var MOTS_PAR_THEME = [
  [/\b(football|futsal|basket|handball|rugby|tennis|judo|karate|aikido|boxe|natation|athletisme|escrime|equitation|petanque|cyclisme|sportive?s?)\b/, "sport"],
  [/\b(musique|musical|chorale|fanfare|orchestre)\b/, "musique"],
  [/\b(animal|animaux|refuge|felin|canin)\b/, "animaux"],
  [/\b(theatre|cinema)\b/, "arts_culture"],
  [/\b(humanitaire|ong)\b/, "humanitaire"],
  [/\b(entraide|solidarite|secours)\b/, "solidarite"],
  [/\b(enfant|enfants|enfance|jeunesse|lyceens)\b/, "enfance_jeunesse"]
];
function replier(texte) {
  return texte.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}
function estReligieuxParNom(...textes6) {
  return textes6.some((t) => !!t && MOTS_SPIRITUALITE.test(replier(t)));
}
function themeDepuisNom(...textes6) {
  if (estReligieuxParNom(...textes6)) return "spiritualite";
  const plie = replier(textes6.filter(Boolean).join(" "));
  if (!plie) return null;
  for (const [motif, theme] of MOTS_PAR_THEME) {
    if (motif.test(plie)) return theme;
  }
  return null;
}
function themeAssociation(opts) {
  if (estReligieuxParNom(opts.nom, opts.objetSocial)) return "spiritualite";
  const rna = (opts.themeRna && estTheme(opts.themeRna) && opts.themeRna !== "autre" ? opts.themeRna : null) ?? (opts.codesRna ? themeDepuisCodesRna(opts.codesRna) : null);
  if (rna) return rna;
  return themeDepuisNom(opts.nom, opts.objetSocial);
}

// src/error.ts
var DEFAULT_TECHNICAL_FALLBACK = "Une erreur technique est survenue. Veuillez r\xE9essayer ou contacter le support si le probl\xE8me persiste.";
var DEFAULT_NETWORK_FALLBACK = "Connexion au serveur impossible. V\xE9rifiez votre connexion internet et r\xE9essayez.";
function asError(err) {
  if (!err || typeof err !== "object") {
    return { message: typeof err === "string" ? err : void 0 };
  }
  return err;
}
function getErrorMessage(err, fallback = "Une erreur est survenue") {
  const e = asError(err);
  const messageFromError = (value) => typeof value === "string" ? value : value?.message;
  return e.data?.message || messageFromError(e.data?.error) || e.response?._data?.message || messageFromError(e.response?._data?.error) || e.message || fallback;
}
function extractApiError(e, options) {
  const TECHNICAL_FALLBACK = options?.technicalFallback ?? DEFAULT_TECHNICAL_FALLBACK;
  const NETWORK_FALLBACK = options?.networkFallback ?? DEFAULT_NETWORK_FALLBACK;
  if (!e) {
    return { message: TECHNICAL_FALLBACK, isTechnical: true };
  }
  const err = asError(e);
  const status = err.response?.status ?? err.statusCode ?? err.status;
  const messageFromError = (value) => typeof value === "string" ? value : value?.message;
  const backendMessage = err.data?.message || messageFromError(err.data?.error) || err.response?._data?.message || messageFromError(err.response?._data?.error);
  const isNetworkError = err.name === "FetchError" && !status && !backendMessage;
  if (isNetworkError) {
    return { message: NETWORK_FALLBACK, isTechnical: true };
  }
  if (status && status >= 500) {
    return {
      message: backendMessage || TECHNICAL_FALLBACK,
      isTechnical: true,
      status
    };
  }
  if (backendMessage) {
    return {
      message: backendMessage,
      isTechnical: false,
      status
    };
  }
  if (err.name === "FetchError") {
    return { message: TECHNICAL_FALLBACK, isTechnical: true, status };
  }
  if (typeof err.message === "string" && err.message && !err.message.startsWith("[")) {
    return { message: err.message, isTechnical: false };
  }
  return { message: TECHNICAL_FALLBACK, isTechnical: true };
}
function getInsufficientBalanceInfo(err) {
  const e = asError(err);
  const raw = e.rawResponse;
  const candidates = [
    e.details,
    e.data,
    e.response?._data,
    raw?.error,
    raw
  ];
  const code = e.code ?? e.data?.code ?? candidates.find((c) => c && "code" in c)?.code;
  if (code !== "insufficient_balance") return null;
  const pick = (key) => {
    for (const c of candidates) {
      if (c && c[key] !== void 0 && c[key] !== null) return c[key];
    }
    return void 0;
  };
  const provider = pick("provider");
  const available = pick("available_euros");
  const required = pick("required_euros");
  return {
    provider: provider === "paypal" ? "paypal" : "stripe",
    available_euros: Number(available ?? 0),
    required_euros: Number(required ?? 0),
    organization_id: pick("organization_id") ?? "",
    donate_url: pick("donate_url") ?? null
  };
}
function toError(err) {
  if (err instanceof Error) return err;
  return new Error(getErrorMessage(err));
}

// src/initials.ts
function getInitials(name, options = {}, maxLettersArg) {
  let fallback;
  let maxLetters;
  if (typeof options === "string") {
    fallback = options;
    maxLetters = maxLettersArg ?? 2;
  } else {
    fallback = options.fallback ?? "?";
    maxLetters = options.maxLetters ?? 2;
  }
  const trimmed = name?.trim();
  if (!trimmed) return fallback;
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length === 0) return fallback;
  return words.slice(0, maxLetters).map((w) => w[0] ?? "").join("").toUpperCase().slice(0, maxLetters);
}

// src/finCagnotte.ts
var MOTIF_JOUR = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ])/;
function jourDeFinCagnotte(valeur) {
  if (typeof valeur !== "string") return null;
  const m = MOTIF_JOUR.exec(valeur.trim());
  if (!m) return null;
  const annee = Number(m[1]);
  const mois = Number(m[2]);
  const jour = Number(m[3]);
  const d = new Date(Date.UTC(annee, mois - 1, jour));
  if (d.getUTCFullYear() !== annee || d.getUTCMonth() !== mois - 1 || d.getUTCDate() !== jour) {
    return null;
  }
  return `${m[1]}-${m[2]}-${m[3]}`;
}
function lendemain(jour) {
  const [a, m, j] = jour.split("-").map(Number);
  const d = new Date(Date.UTC(a, m - 1, j + 1));
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
function clotureCagnotte(valeur, fuseau = ORG_TIMEZONE) {
  const jour = jourDeFinCagnotte(valeur);
  return jour ? instantDepuisHeureLocale(lendemain(jour), "00:00", fuseau) : null;
}
function cagnotteTerminee(valeur, fuseau = ORG_TIMEZONE, maintenant = /* @__PURE__ */ new Date()) {
  const fin = clotureCagnotte(valeur, fuseau);
  return fin !== null && maintenant.getTime() >= fin.getTime();
}
function joursRestantsCagnotte(valeur, fuseau = ORG_TIMEZONE, maintenant = /* @__PURE__ */ new Date()) {
  const fin = clotureCagnotte(valeur, fuseau);
  if (!fin) return null;
  const ms = fin.getTime() - maintenant.getTime();
  if (ms <= 0) return null;
  return Math.ceil(ms / 864e5);
}
function premierJourDeFinCagnotte(fuseau = ORG_TIMEZONE, maintenant = /* @__PURE__ */ new Date()) {
  return toDayKey(maintenant, fuseau);
}

// src/finDeVente.ts
function instant(valeur) {
  if (valeur === null || valeur === void 0 || valeur === "") return null;
  const d = parseDbInstant(valeur);
  return Number.isNaN(d.getTime()) ? null : d;
}
function minuitApresLeJour(jourCle, fuseau) {
  const [a, m, j] = jourCle.split("-").map(Number);
  const lendemain2 = new Date(Date.UTC(a, m - 1, j + 1));
  const pad = (n) => String(n).padStart(2, "0");
  const jour = `${lendemain2.getUTCFullYear()}-${pad(lendemain2.getUTCMonth() + 1)}-${pad(lendemain2.getUTCDate())}`;
  return instantDepuisHeureLocale(jour, "00:00", fuseau);
}
function minuitApres(debut, fuseau) {
  return minuitApresLeJour(toDayKey(debut, fuseau), fuseau);
}
function finEvenement(debut, fin, fuseau = ORG_TIMEZONE) {
  const d = instant(debut);
  const f2 = instant(fin);
  if (f2 && (!d || f2.getTime() > d.getTime())) return f2;
  return d ? minuitApres(d, fuseau) : null;
}
function finDernierCreneau(creneaux, fuseau = ORG_TIMEZONE) {
  let derniere = null;
  for (const c of creneaux) {
    const fin = finEvenement(c.start_date, c.end_date, fuseau);
    if (fin && (!derniere || fin.getTime() > derniere.getTime())) derniere = fin;
  }
  return derniere;
}
var DELAI_EVENEMENT_TERMINE_MS = 24 * 60 * 60 * 1e3;
function evenementTermine(e, maintenant = /* @__PURE__ */ new Date()) {
  if (e.status === "completed") return true;
  if (e.status !== "public" && e.status !== "closed") return false;
  const fin = instant(e.ends_at);
  return fin !== null && maintenant.getTime() >= fin.getTime() + DELAI_EVENEMENT_TERMINE_MS;
}
function statutAfficheEvenement(e, maintenant = /* @__PURE__ */ new Date()) {
  return evenementTermine(e, maintenant) ? "completed" : e.status;
}
function finVenteEnLigneBillet(args) {
  return instant(args.finDeVente) ?? finEvenement(args.debut, args.fin, args.fuseau ?? ORG_TIMEZONE);
}
function venteEnLigneBilletTerminee(args, maintenant = /* @__PURE__ */ new Date()) {
  const fin = finVenteEnLigneBillet(args);
  return fin !== null && maintenant.getTime() >= fin.getTime();
}
function finSaisonAdhesion(finDeSaison, fuseau = ORG_TIMEZONE) {
  if (typeof finDeSaison !== "string" || !finDeSaison.trim()) return null;
  const texte = finDeSaison.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(texte)) return minuitApresLeJour(texte, fuseau);
  const d = instant(texte);
  return d ? minuitApres(d, fuseau) : null;
}
function saisonAdhesionTerminee(finDeSaison, fuseau = ORG_TIMEZONE, maintenant = /* @__PURE__ */ new Date()) {
  const fin = finSaisonAdhesion(finDeSaison, fuseau);
  return fin !== null && maintenant.getTime() >= fin.getTime();
}

// src/nomsParticipants.ts
function cleNomParticipant(nom) {
  return replierPourRecherche(nom).replace(/[-'’.]/g, " ").replace(/\s+/g, " ").trim();
}
function indicesNomsEnDouble(noms) {
  const vus = /* @__PURE__ */ new Set();
  const doublons = [];
  noms.forEach((nom, idx) => {
    const cle = cleNomParticipant(nom);
    if (!cle) return;
    if (vus.has(cle)) doublons.push(idx);
    else vus.add(cle);
  });
  return doublons;
}
var MESSAGE_NOM_EN_DOUBLE = "Ce nom est d\xE9j\xE0 sur un autre billet de la commande : chaque billet doit \xEAtre au nom d'une personne diff\xE9rente.";
var LIMITE_GRATUITS_PAR_PERSONNE = 15;

// src/payment-method.ts
function providerLabel(tx) {
  switch (tx.payment_provider) {
    case "paypal":
      return "PayPal";
    // Perception manuelle (espèces / chèque / virement) : PAS un PSP. Karim 2026-07-04.
    case "manual":
      return "hors-plateforme";
    case "stripe":
    default:
      return "Stripe";
  }
}
function providerLabelOrNull(tx) {
  switch (tx?.payment_provider) {
    case "paypal":
      return "PayPal";
    case "manual":
      return "hors-plateforme";
    case "stripe":
      return "Stripe";
    default:
      return null;
  }
}
function networkFeeLabel(tx) {
  const provider = providerLabelOrNull(tx);
  if (!provider || provider === "hors-plateforme") return "Frais de transaction";
  return `Frais ${provider}`;
}
function distinctProviderLabels(payments) {
  const out = [];
  for (const p of payments) {
    const label = providerLabelOrNull(p);
    if (label && !out.includes(label)) out.push(label);
  }
  return out;
}
function joinFr(items) {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} et ${items[items.length - 1]}`;
}
function paymentsSummaryLine(total, payments, formatAmount) {
  const providers = distinctProviderLabels(payments);
  const amount = formatAmount(total);
  return providers.length ? `${amount} pay\xE9 via ${joinFr(providers)}` : `${amount} pay\xE9`;
}
function cardBrandLabel(brand) {
  const b = brand.toLowerCase();
  if (b === "visa") return "Visa";
  if (b === "mastercard") return "Mastercard";
  if (b === "amex" || b === "american_express") return "American Express";
  if (b === "discover") return "Discover";
  if (b === "diners" || b === "diners_club") return "Diners Club";
  if (b === "jcb") return "JCB";
  if (b === "unionpay") return "UnionPay";
  return "Carte bancaire";
}
function paymentMethodLabel(tx) {
  if (tx.payment_provider === "manual") {
    switch (tx.payment_method_type) {
      case "cash":
        return "Esp\xE8ces";
      case "check":
        return "Ch\xE8que";
      case "transfer":
        return "Virement";
      default:
        return "Esp\xE8ces, ch\xE8que ou virement";
    }
  }
  if (tx.payment_provider === "paypal") {
    if (tx.paypal_payer_email) return `Compte PayPal \xB7 ${tx.paypal_payer_email}`;
    return "Compte PayPal";
  }
  switch (tx.payment_method_type) {
    case "apple_pay":
      return "Apple Pay";
    case "google_pay":
      return "Google Pay";
    case "sepa_debit":
      return "Pr\xE9l\xE8vement SEPA";
    case "bancontact":
      return "Bancontact";
    case "ideal":
      return "iDEAL | Wero";
    case "wero":
      return "Wero";
    case "revolut_pay":
      return "Revolut Pay";
    // Link : paiement en un clic avec la carte enregistrée chez Stripe.
    case "link":
      return "Link";
    case "card":
    default: {
      const brand = tx.card_brand ? cardBrandLabel(tx.card_brand) : "Carte bancaire";
      if (tx.card_last4) return `${brand} \u2022\u2022\u2022\u2022 ${tx.card_last4}`;
      return brand;
    }
  }
}
function paymentMethodIcon(tx) {
  if (tx.payment_provider === "manual") return "i-lucide-banknote";
  if (tx.payment_provider === "paypal") return "i-simple-icons-paypal";
  switch (tx.payment_method_type) {
    case "apple_pay":
      return "i-simple-icons-applepay";
    case "google_pay":
      return "i-simple-icons-googlepay";
    case "sepa_debit":
      return "i-lucide-building-2";
    case "bancontact":
    case "ideal":
    case "card":
    default:
      return "i-lucide-credit-card";
  }
}

// src/payment-provider.ts
function stripeEncaisse(org) {
  return Boolean(org.stripe_account_id) && org.stripe_charges_enabled === true;
}
function paypalEncaisse(org) {
  return Boolean(org.paypal_merchant_id) && org.paypal_charges_enabled === true;
}
function peutEncaisser(org) {
  return stripeEncaisse(org) || paypalEncaisse(org);
}
function prestataireOperationnel(org) {
  if (stripeEncaisse(org)) return "stripe";
  if (paypalEncaisse(org)) return "paypal";
  return null;
}
var CHOIX_MOYEN_MIS_EN_AVANT = false;
function moyenPrincipal(org) {
  if (stripeEncaisse(org)) return "stripe";
  return paypalEncaisse(org) ? "paypal" : "stripe";
}

// src/lead-tracker.ts
var VISITOR_ID_KEY = "donoor_visitor_id";
var SESSION_KEY = "donoor_lead_session";
var SESSION_GAP_MS = 30 * 60 * 1e3;
var VISITOR_ID_RE = /^[A-Za-z0-9_-]{16,40}$/;
function readVisitorCookie() {
  try {
    const motif = new RegExp(`(?:^|;\\s*)${VISITOR_ID_KEY}=([^;]*)`);
    const trouve = document.cookie.match(motif);
    if (!trouve?.[1]) return "";
    return decodeURIComponent(trouve[1]);
  } catch {
    return "";
  }
}
function writeVisitorCookie(id) {
  try {
    const isLocal = /^(localhost|127\.|\[?::1)/.test(window.location.hostname);
    const domain = isLocal ? "" : "; domain=.donoor.org";
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    const oneYear = 365 * 24 * 3600;
    document.cookie = `${VISITOR_ID_KEY}=${id}; path=/; max-age=${oneYear}; SameSite=Lax${domain}${secure}`;
  } catch {
  }
}
function getVisitorId() {
  let id = "";
  try {
    id = localStorage.getItem(VISITOR_ID_KEY) || "";
    if (!VISITOR_ID_RE.test(id)) {
      const herite = readVisitorCookie();
      id = VISITOR_ID_RE.test(herite) ? herite : "";
    }
    if (!VISITOR_ID_RE.test(id)) {
      const bytes = new Uint8Array(18);
      crypto.getRandomValues(bytes);
      id = btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "").slice(0, 24);
    }
    localStorage.setItem(VISITOR_ID_KEY, id);
  } catch {
    if (!VISITOR_ID_RE.test(id)) {
      const herite = readVisitorCookie();
      id = VISITOR_ID_RE.test(herite) ? herite : crypto.randomUUID();
    }
  }
  writeVisitorCookie(id);
  return id;
}
function computeAttribution() {
  const attr = classifyAttribution({
    url: window.location.href,
    referrer: document.referrer || ""
  });
  const out = { source: attr.source, channel: attr.channel };
  const rec = attr;
  for (const k of ATTRIBUTION_PARAM_KEYS) {
    const v = rec[k];
    if (v) out[k] = v;
  }
  return out;
}
function getSession() {
  const now = Date.now();
  let s = null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) s = JSON.parse(raw);
  } catch {
    s = null;
  }
  if (!s || now - s.lastActivity > SESSION_GAP_MS) {
    s = {
      id: crypto.randomUUID(),
      // session_id = UUID (colonne UUID côté DB)
      lastActivity: now,
      attribution: computeAttribution()
    };
  } else {
    s.lastActivity = now;
  }
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
  } catch {
  }
  return s;
}
function startLeadTracker(opts) {
  const visitorId = getVisitorId();
  const endpoint = `${opts.apiBase.replace(/\/$/, "")}/api/public/collect`;
  const FLUSH_DELAY_MS = 2e3;
  const MAX_BATCH = 25;
  let queue = [];
  let flushTimer = null;
  function postBatch(events) {
    const payload = JSON.stringify({ events });
    try {
      if (navigator.sendBeacon) {
        const ok = navigator.sendBeacon(endpoint, new Blob([payload], { type: "application/json" }));
        if (ok) return;
      }
    } catch {
    }
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
      credentials: "include"
    }).catch(() => {
    });
  }
  function flushQueue() {
    if (flushTimer !== null) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    while (queue.length > 0) postBatch(queue.splice(0, MAX_BATCH));
  }
  function enqueue(ev) {
    queue.push(ev);
    if (queue.length >= MAX_BATCH) {
      flushQueue();
      return;
    }
    if (flushTimer === null) flushTimer = setTimeout(flushQueue, FLUSH_DELAY_MS);
  }
  function buildEvent(eventType, extra) {
    const s = getSession();
    return {
      visitorId,
      sessionId: s.id,
      eventType,
      occurredAt: (/* @__PURE__ */ new Date()).toISOString(),
      site: opts.site,
      pagePath: window.location.pathname,
      pageTitle: document.title,
      referrer: document.referrer || "",
      posthogSessionId: opts.getPosthogSessionId?.(),
      ...s.attribution,
      // source, channel, gclid, utm_* (first-touch)
      ...extra
    };
  }
  let pageEnteredAt = Date.now();
  let lastPath = window.location.pathname;
  let dwellSentForPath = "";
  let trackedPath = "";
  function trackPageview() {
    const path = window.location.pathname;
    if (path === trackedPath) return;
    trackedPath = path;
    pageEnteredAt = Date.now();
    lastPath = path;
    enqueue(buildEvent("pageview"));
  }
  function flushDwell() {
    const dwellMs = Date.now() - pageEnteredAt;
    if (dwellMs < 200) return;
    if (dwellSentForPath === lastPath) return;
    dwellSentForPath = lastPath;
    enqueue(buildEvent("page_dwell", { pagePath: lastPath, dwellMs }));
  }
  opts.onRouteChange(() => {
    if (window.location.pathname === lastPath) return;
    flushDwell();
    setTimeout(trackPageview, 0);
  });
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      flushDwell();
      flushQueue();
      return;
    }
    pageEnteredAt = Date.now();
    dwellSentForPath = "";
  };
  const onPageHide = () => {
    flushDwell();
    flushQueue();
  };
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", onPageHide);
  if (document.readyState === "complete") trackPageview();
  else window.addEventListener("load", trackPageview, { once: true });
  return (eventType, meta) => {
    enqueue(buildEvent(eventType, meta ? { meta } : void 0));
  };
}

// src/paiementEnPlusieursFois.ts
var NOMBRES_PARTS_POSSIBLES = [2, 3, 4, 6, 10, 12];
var JOURS_APRES_FOURNITURE = 50;
var MINIMUM_PAR_PART_CENTIMES = 1e3;
var NOMBRE_PARTS_PAR_DEFAUT = 3;
function normaliserNombresParts(reglage) {
  let brut = reglage;
  if (typeof brut === "string") {
    try {
      brut = JSON.parse(brut);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(brut)) return [];
  const permis = new Set(NOMBRES_PARTS_POSSIBLES);
  const vus = /* @__PURE__ */ new Set();
  for (const v of brut) {
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isInteger(n) && permis.has(n)) vus.add(n);
  }
  return [...vus].sort((a, b) => a - b);
}
function plusieursFoisActive(reglage) {
  return normaliserNombresParts(reglage).length > 0;
}
function respecteLesRegles(montant, n, limite) {
  if (Math.floor(montant / n) < MINIMUM_PAR_PART_CENTIMES) return false;
  if (!limite.dernierJour) return true;
  const dates = datesDesParts(limite.premierJour ?? jourCivilParis(), n);
  const derniere = dates[dates.length - 1];
  return !!derniere && derniere <= limite.dernierJour;
}
function nombresPartsProposables(reglage, montantDiviseCentimes, limite) {
  const montant = Math.round(Number(montantDiviseCentimes) || 0);
  if (montant <= 0) return [];
  const coches = normaliserNombresParts(reglage);
  if (!coches.length) return [];
  const permis = coches.filter((n) => respecteLesRegles(montant, n, limite));
  if (permis.length) return permis;
  const plusGrandCoche = coches[coches.length - 1];
  const repli = NOMBRES_PARTS_POSSIBLES.filter(
    (n) => n < plusGrandCoche && respecteLesRegles(montant, n, limite)
  );
  return repli.length ? [repli[repli.length - 1]] : [];
}
function apercuPlusieursFois(reglage, montantDiviseCentimes, limite) {
  const coches = normaliserNombresParts(reglage);
  const proposes = nombresPartsProposables(coches, montantDiviseCentimes, limite);
  return { proposes, reduit: coches.some((n) => !proposes.includes(n)) };
}
function nombrePartsPermis(reglage, montantDiviseCentimes, nombre, limite) {
  return nombresPartsProposables(reglage, montantDiviseCentimes, limite).includes(nombre);
}
function jourParisDe(valeur) {
  if (valeur instanceof Date) return Number.isNaN(valeur.getTime()) ? null : jourCivilParis(valeur);
  if (typeof valeur !== "string" || !valeur.trim()) return null;
  const texte = valeur.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(texte)) return texte;
  const iso = texte.replace(" ", "T");
  const d = new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : jourCivilParis(d);
}
function dernierJourPlusieursFoisAdhesion(tarifs, aujourdhui = jourCivilParis()) {
  let plusTot = null;
  for (const tf of tarifs) {
    if (!tf) continue;
    let fin = null;
    const jours = Number(tf.duration_days) || 0;
    if (tf.duration_type === "season") {
      fin = jourParisDe(tf.season_end_date) ?? ajouterJours(aujourdhui, (jours > 0 ? jours : 365) - 1);
    } else if (jours > 0) {
      fin = ajouterJours(aujourdhui, jours - 1);
    }
    if (fin && (!plusTot || fin < plusTot)) plusTot = fin;
  }
  return plusTot;
}
function dernierJourPlusieursFoisBillet(debutEvenement, aujourdhui = jourCivilParis()) {
  const jour = jourParisDe(debutEvenement);
  const reference = jour && jour > aujourdhui ? jour : aujourdhui;
  return ajouterJours(reference, JOURS_APRES_FOURNITURE);
}
function dernierJourPlusieursFoisBoutique(aujourdhui = jourCivilParis()) {
  return ajouterJours(aujourdhui, JOURS_APRES_FOURNITURE);
}
function decouperEnParts(totalCentimes, nombre) {
  const total = Math.max(0, Math.round(Number(totalCentimes) || 0));
  const n = Math.max(1, Math.floor(Number(nombre) || 1));
  const base = Math.floor(total / n);
  const reste = total - base * n;
  return Array.from({ length: n }, (_, i) => i === 0 ? base + reste : base);
}
function deuxChiffres(n) {
  return n < 10 ? `0${n}` : String(n);
}
function joursDansLeMois(annee, mois1a12) {
  return new Date(Date.UTC(annee, mois1a12, 0)).getUTCDate();
}
function lireJourCivil(jour) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(jour ?? ""));
  if (!m) return null;
  const annee = Number(m[1]);
  const mois = Number(m[2]);
  const j = Number(m[3]);
  if (mois < 1 || mois > 12 || j < 1 || j > joursDansLeMois(annee, mois)) return null;
  return [annee, mois, j];
}
function datesDesParts(premierJour, nombre) {
  const lu = lireJourCivil(premierJour);
  if (!lu) return [];
  const [annee, mois, jour] = lu;
  const n = Math.max(1, Math.floor(Number(nombre) || 1));
  const dates = [];
  for (let i = 0; i < n; i++) {
    const total = mois - 1 + i;
    const a = annee + Math.floor(total / 12);
    const m = total % 12 + 1;
    const j = Math.min(jour, joursDansLeMois(a, m));
    dates.push(`${a}-${deuxChiffres(m)}-${deuxChiffres(j)}`);
  }
  return dates;
}
function jourCivilParis(instant2 = /* @__PURE__ */ new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(instant2);
  const v = (t) => parts.find((p) => p.type === t)?.value ?? "";
  return `${v("year")}-${v("month")}-${v("day")}`;
}
function ajouterJours(jour, jours) {
  const lu = lireJourCivil(jour);
  if (!lu) return jour;
  const d = new Date(Date.UTC(lu[0], lu[1] - 1, lu[2] + Math.round(jours)));
  return `${d.getUTCFullYear()}-${deuxChiffres(d.getUTCMonth() + 1)}-${deuxChiffres(d.getUTCDate())}`;
}
function echeancierAffiche(params) {
  const montants = decouperEnParts(params.montantDiviseCentimes, params.nombre);
  const dates = datesDesParts(params.premierJour, params.nombre);
  const enPlus = Math.max(0, Math.round(params.enPlusLePremierJourCentimes ?? 0));
  return montants.map((m, i) => {
    const frais = params.fraisParPartCentimes ? Math.max(0, Math.round(params.fraisParPartCentimes(m, i + 1))) : 0;
    return {
      numero: i + 1,
      date: dates[i] ?? "",
      montantCentimes: m + frais + (i === 0 ? enPlus : 0)
    };
  });
}
function montantDUnePart(params) {
  const arrondi = (n) => Math.round(n * 100) / 100;
  const net = Number(params.netEuros) || 0;
  const enPlus = Math.max(0, Number(params.enPlusEuros) || 0);
  const soutien = Math.max(0, Number(params.soutienEuros) || 0);
  const { taux, fixe } = params.bareme ?? { taux: 0.015, fixe: 0.25 };
  if (net === 0 && enPlus === 0 && soutien === 0) return 0;
  if (!params.fraisCouverts) return arrondi(net + enPlus + soutien);
  const commission = arrondi(net * (Number(params.tauxCommission) || 0));
  return arrondi((net + enPlus + commission + soutien + fixe) / (1 - taux));
}
function echeancierDuPanier(params) {
  const nets = decouperEnParts(Math.round((Number(params.montantDiviseEuros) || 0) * 100), params.nombre);
  const dates = datesDesParts(params.premierJour, params.nombre);
  return nets.map((c, i) => ({
    numero: i + 1,
    date: dates[i] ?? "",
    montant: montantDUnePart({
      netEuros: c / 100,
      enPlusEuros: i === 0 ? params.enPlusEuros : 0,
      soutienEuros: i === 0 ? params.soutienEuros : 0,
      tauxCommission: params.tauxCommission,
      fraisCouverts: params.fraisCouverts,
      bareme: params.bareme
    })
  }));
}

export { ACTION_TURNSTILE_ADRESSES, ALIAS_PAYS, ANCIENNES_CATEGORIES, ANCIENNES_CATEGORIES_EVENEMENT, ATTENTE_JETON_ADRESSES_MS, ATTRIBUTION_GOOGLE_TEXTE, ATTRIBUTION_OSM_TEXTE, ATTRIBUTION_OSM_URL, ATTRIBUTION_PARAM_KEYS, BAN_URL_RECHERCHE, BESCHEID_ARTEN_DE, CATEGORIES_AGREMENT_BE, CATEGORIES_JURIDIQUES_ELIGIBLES, CATEGORIES_JURIDIQUES_RECHERCHE, CHOIX_MOYEN_MIS_EN_AVANT, CLE_LINK, CODES_AGREMENT_BE, CODES_ZWECKE_DE, COMMUNES_HOMONYMES_PAYS, COMMUNES_HOMONYMES_TERRITOIRES, COMMUNES_TERMINEES_PAR_UN_PAYS, COORDONNEES_DECIMALES, COUCHES_PHOTON, COUCHES_PHOTON_VERIFICATION, COULEURS_ORIGINE, DEFAULT_CURRENCY, DEFAULT_LOCALE, DELAI_EVENEMENT_TERMINE_MS, DELAI_FRAPPE_MS, DELAI_TURNSTILE_MS, DEVISE_PAR_DEFAUT, DOCUMENTS_FRANCE_SEULE, DOCUMENTS_HORS_FRANCE_SEULS, DONOOR_SOCIALS, DONOOR_SOCIAL_URLS, DUREE_SESSION_ADRESSES_PUBLIQUES_S, ECART_LISTE_SUGGESTIONS, ETATS_OPTION_PAYPAL, ETATS_PAYPAL_EN_SUSPENS, FORMATS_CODE_POSTAL, FRAIS_FIXES_TAP_TO_PAY_MAX_CENTIMES, FUSEAU_PAR_DEFAUT, FUSEAU_PAR_TERRITOIRE, FUSEAU_PAYS_MONDE, HAUTEUR_LISTE_SUGGESTIONS_MAX, ICONES_ORIGINE_ACQUISITION, JOURS_APRES_FOURNITURE, LANGUES_SUPPORTEES, LANGUE_PAR_DEFAUT, LIMITE_GRATUITS_PAR_PERSONNE, LOCALE_INTL_PAR_LANGUE, LONGUEUR_MAX_ADRESSE, LONGUEUR_MAX_VILLE, MARGE_LISTE_SUGGESTIONS, MESSAGE_NOM_EN_DOUBLE, MINIMUM_PAR_PART_CENTIMES, MONTANT_MASQUE, MOTIF_ETAPE_ONBOARDING_STRIPE, MOTS_RELATIFS, MOYENS_PAIEMENT_LOCAUX, NOMBRES_PARTS_POSSIBLES, NOMBRE_PARTS_PAR_DEFAUT, NOMBRE_VERIFICATION_NOMINATIM, NOMS_PAYS_FR, OPTIONS_PAYPAL, ORG_TIMEZONE, PAYS, PAYS_AVEC_INTERRUPTEURS, PAYS_AVEC_REGISTRE, PAYS_CODE_POSTAL_5_CHIFFRES, PAYS_HOMONYMES_VILLE, PAYS_NUMERO_AVANT_VOIE, PAYS_OPTIONS_AVANCEES_PAYPAL, PAYS_OUVERTS_PAIEMENTS, PAYS_PAR_DEFAUT, PAYS_PAYPAL_ENVOI_SEULEMENT, PLAFOND_SUGGESTION, PLAFOND_SUGGESTION_ACHAT, PLANCHER_LISTE_SUGGESTIONS, PLANCHER_POURCENTAGE, PREFIXE_HEURE_LOCALE, RECHERCHE_MAX_CARACTERES, RECHERCHE_MIN_CARACTERES, RECONNAISSANCES_LU, REESSAI_TURNSTILE_MIN_MS, REGIMES_RECU_FISCAL, RENOUVELLEMENT_SESSION_ADRESSES_MS, RE_ID_OSM, RE_JETON_ADRESSES_PUBLIQUES, RNA_PATTERN, SCORE_BAN_GEOCODAGE, SCORE_BAN_SUGGESTION, SIGNE_MOINS_UTC, STRIPE_ASSISTANCE_URL, STRIPE_EN_LIGNE, STRIPE_EU_FIXED_FEE, STRIPE_EU_RATE, STRIPE_SUR_PLACE, STRIPE_TAP_TO_PAY_AUTORISATION_CENTIMES, SUFFIXE_FUSEAU_MANUEL, SUGGESTIONS_LIMITE_MAX, SUPPORTED_CURRENCIES, TERRITOIRES_BAN_PARTIELLE, TERRITOIRES_OUTRE_MER, THEMES, THEME_PAR_DEFAUT, TYPES_ACTIVITE, TYPES_NUMERO_OFFICIEL, TYPES_VOIE, URL_SCRIPT_TURNSTILE, ZOOM_RECHERCHE_MAPS, ZWECKE_DE, aDesMorceauxAdresse, aUnNumeroOfficielVerifie, aUneIdentiteOfficielle, aUneIdentiteOfficielleConnue, adresseChoisieDepuisSuggestion, adresseDansZonePhoton, adresseDeclareeOrganisation, adresseDepuisFeatureBan, adresseDepuisFeaturePhoton, adresseDepuisPlaceGoogle, adresseNominatimConfirmantGoogle, adresseOsmSousVilleGoogle, adressePhotonConfirmantGoogle, ajouterJours, analyserAdresseLibre, apercuPlusieursFois, apparierReponsesFormulaire, asCents, asError, asEuros, assistantIA, attributionAffichee, attributionDesSuggestions, billetsEtAdhesionsDeductiblesDansPays, brutAsso, cagnotteTerminee, capacitesStripeLocales, capitalize, cardBrandLabel, casseVille, centsToEuros, classifyAttribution, classifyStripeAccount, cleNomParticipant, clotureCagnotte, codePaysMonde, codePostalDepuisAdresse, codePostalEnChiffres, codePostalEtVilleDuRepertoire, codePostalFrancaisDuTexte, codePostalSaisiValide, codePostalSansPrefixe, commissionTapToPayCentimes, concerneTarif, correspondALaRecherche, creerClientTurnstile, creerSessionAdressesPubliques, datesDesParts, dbAmountToCents, dbAmountToEuros, decalageUtcMinutes, decouperEnParts, decouperLiens, definirFournisseurLocale, definirLocaleAffichage, dejaRembourseParAsso, departementAnnuaireOrganisation, departementDepuisCodes, dernierJourPlusieursFoisAdhesion, dernierJourPlusieursFoisBillet, dernierJourPlusieursFoisBoutique, designeEmail, detailsPostaux, disponibilitePaiementsPays, distinctProviderLabels, documentDeLaReponse, documentDemandablePourPays, documentsImpossiblesPourPays, donoorTipEuros, drapeauPays, dureeValiditeBescheidDe, echeancierAffiche, echeancierDuPanier, elementsVerificationGoogle, encaisseParDonoor, encoderComposantUrl, erreurPrixLibreBillet, estBescheidArtDe, estCategorieAgrementBe, estEligibleSansRnaPossible, estFormeJuridiqueEligible, estFormeNonLucrative, estIdOsm, estJetonAdressesPubliques, estMoyenPaiementLocal, estNomProvisoire, estNumeroNationalBelgeValide, estNumeroOfficielValide, estReconnaissanceLu, estReligieuxParNom, estRsinValide, estSteuernummerValide, estTheme, estTribunalNumeroOfficiel, estTypeActivite, estTypeNumeroOfficiel, estZweckDe, estimateNetReceivedEuros, estimateStripeFeeEuros, etatOptionPaypal, etatsOptionsPaypal, eurosToCents, evenementTermine, exempleCodePostal, exempleIban, exempleNumeroOfficiel, exempleTelephone, exonereParNumeroOfficiel, extractApiError, familleJuridiquePaiement, featureBanConfirmeGoogle, featurePhotonDepuisNominatim, finDernierCreneau, finEvenement, finSaisonAdhesion, finValiditeBescheidDe, finVenteEnLigneBillet, fixRnaEncoding, formatCents, formatCodePostal, formatCompactNumber, formatCurrency, formatCurrencyCompact, formatCurrencyCompactFromCents, formatCurrencyFromCents, formatCurrencyParts, formatDate, formatDateAHeureInParis, formatDateTimeInParis, formatDbAmount, formatEuros, formatFileSize, formatInParis, formatInZone, formatLastAccessInParis, formatNumber, formatPercent, formatRelativeTime, formatRelativeTimeShort, formatSmartDateTimeInParis, formatSmartDateTimeNumeriqueInParis, formaterCoordonnee, formaterLieu, formaterNumeroNational, formaterNumeroOfficiel, formesNonLucratives, fournisseurAdresse, fournisseurComplementaire, fraisStripeEnLigneCentimes, fraisStripeMoyenLocal, fraisStripeSurPlaceCentimes, fuseauDeduit, fuseauDepuisCodePostal, fuseauParDefaut, fuseauValidePourPays, gardeParDonoor, getDonoorTipAmount, getDonoorTipAmountAchat, getErrorMessage, getInitials, getInsufficientBalanceInfo, heureLocaleDepuisInstant, idOsmDeFeature, identifiantOfficielExige, indicatifDuPays, indicatifTelephone, indicesNomsEnDouble, instantDepuisHeureLocale, isMultiDayEvent, isStripeVerificationInProgress, isValidRnaFormat, jetonSessionRecherche, jourCivilParis, jourDeFinCagnotte, joursRestantsCagnotte, langueDepuisLocale, libelleAssociation, libelleAssociations, libelleDecalageUtc, libelleFormeJuridique, libelleFormeNonLucrative, libelleFuseau, libelleHeureLocale, libelleLieuCourt, libelleNumeroOfficiel, libelleTheme, libelleTypeActivite, libelleZweckDe, libellesRegionOrganisation, lienItineraire, lienRechercheMaps, lienSms, lienTelephone, lienWhatsApp, lieuNommeGoogle, ligneAdresseDeclaree, ligneCodePostalVille, ligneHeureLocale, lignesAdresse, lignesEnSegments, linkAllume, lireDemandeStripe, lireIdentifiantDonateurBe, lireJourCivil, lireReglageMoyensLocaux, lireReglagePaiementComplet, listePaysMonde, localeAffichage, longueurMaxNumeroOfficiel, looksLikeRnaPrefix, masquerIdentifiantDonateurBe, masquerNumeroNational, mediateurObligatoireDansPays, memeLibelle, memeVilleSaisie, memeVoie, mentionCotisationDe, messageTelephoneInvalide, modeleCgvDonoorDansPays, montantDUnePart, montantEnLettresAllemand, morceauxConcordants, moyenLocalActifParDefaut, moyenLocalAllume, moyenLocalMensuel, moyenPrincipal, moyensLocauxAllumes, moyensPaiementLocauxActifs, moyensPaiementLocauxDuPays, netAsso, networkFeeLabel, nomPaysDansLangue, nomPaysDepuisCode, nomTerritoireOuPays, nombreEnLettresAllemand, nombrePartsPermis, nombrePhotonPourSuggestions, nombresPartsProposables, normaliserExigencesStripe, normaliserNombresParts, normaliserNumeroNational, normaliserNumeroOfficiel, normaliserRecherche, normaliserRsin, normaliserSteuernummer, normalizeRna, numeroOfficielAffiche, numeroOfficielDuPays, objetsZweckeDe, obtenirJetonTurnstile, orgBorneRefundEuros, orgGrossEuros, orgNetKeptEuros, origineAcquisition, paiementsOuvertsDansPays, parametresRechercheNominatim, parametresRecherchePhoton, parcoursMoyenLocal, parseAmountInput, parseDbInstant, partDonoorMoyenLocalCentimes, partDonoorPct, partDonoorPctLabel, partDonoorTapToPayCentimes, paymentMethodIcon, paymentMethodLabel, paymentsSummaryLine, paypalDansPays, paypalEncaisse, paypalPaysEnvoiSeulement, paysDuTelephone, paysEtTerritoireDepuisIso, paysNommeEnFin, paysParCode, paysSuggereDepuisConnexion, periodBounds, peutEncaisser, placementListeInitial, placerListeSuggestions, plafondRemboursableAsso, plural, pluralCount, plusieursFoisActive, premierJourDeFinCagnotte, prestataireOperationnel, profilPays, providerLabel, providerLabelOrNull, randomFrom, randomInt, recuParAssociationCentimes, recusFiscauxDansPays, registreBrancheDansPays, registreDuNumeroOfficiel, regleRecuFiscal, regleRecuFiscalPays, rendreLisible, replierBlancs, replierPourRecherche, requeteRecherche, ressembleNumeroOfficiel, resultatBanRetenu, rueDepuisSuggestion, rueNumeroEtVoie, saisieNumeriqueNumeroOfficiel, saisonAdhesionTerminee, slugTexte, slugTheme, slugVille, slugify, soutienDonoor, splitOptionRemovalRefunds, startLeadTracker, statutAfficheEvenement, stripHtml, stripeDemandesAuChoix, stripeDemandesViaAssistance, stripeEncaisse, stripeExigencesACompleter, stripePayoutsSuspended, styleListeSuggestions, suggestionsDepuisReponseBan, suggestionsDepuisReponsePhoton, sumCents, sumDonoorTipEuros, sumEuros, sumOrgNetKeptEuros, tarifTapToPayAvec, tauxReductionAnnonce, telephoneAEnregistrer, telephoneAffiche, telephoneInternational, telephoneValide, territoireConnu, territoireDepuisCodes, texteChoixSansDetails, texteItineraireLieu, texteLieu, texteRechercheBan, texteVerificationGoogle, texteVerificationOsm, themeAssociation, themeDepuisCategorie, themeDepuisCodesRna, themeDepuisNom, themeDepuisSlug, toDayBounds, toDayKey, toError, totalAvecFraisCentimes, totalEnLigneCentimes, translateDisabledReason, translateErrorCode, translateOnboardingStep, translateRequirementField, tribunalPourStripe, tribunalRequisNumeroOfficiel, tribunauxNumeroOfficiel, truncate, truncateForMeta, typeNumeroOfficielDuPays, typePaypalDepuisFormeJuridique, typeStripeDepuisFormeJuridique, urlRechercheNumeroOfficiel, urlRegistrePourNumero, venteEnLigneBilletTerminee, villeAnnuaireOrganisation, villeCourteDeclaree, villeDepuisTexte, villeLieuAEcrire, zoneDeRecherche, zonePhotonDepuisZone, zoneVisibleFenetre, zoomCarteDepuisPrecision };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map